/**
 * API Client for BISpicy Warenwirtschaft
 *
 * Handles all HTTP requests to the Symfony backend with:
 * - JWT authentication
 * - Tenant context
 * - Error handling
 * - Request/Response interceptors
 */

// Live-Debug-Logs: meldet 5xx-Antworten und Netzwerkfehler an den Browser-Sammler.
// Der Sammler benutzt bewusst kein `api`-Objekt (eigenes fetch) — sonst entstuende
// hier ein zirkulaerer Import.
import { debugLogger } from '../utils/debugLogger';
import { istErlaubtesZiel, leiteBeiFalscherEbeneUm } from '../utils/ebene';
// 🚨 Der Typ wird NICHT hier ein zweites Mal deklariert. `Customer` steht in diesem Projekt
// bereits zweimal (hier und in `types/Customer.ts`), und die Kundenliste benutzt ausgerechnet
// den anderen — genau daran ist `mergedAccounts` beim ersten Anlauf aufgelaufen. Fuer neue
// Typen gilt deshalb: eine Deklaration, hier nur benutzt.
import type { ZugeordneteKartei } from '../types/Customer';

export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost/api';

interface ApiError {
  status: number;
  message: string;
  detail?: string;
  violations?: Array<{
    propertyPath: string;
    message: string;
  }>;
  errors?: Record<string, string>;
  [key: string]: unknown;
}

interface PaginatedResponse<T> {
  'hydra:member': T[];
  'hydra:totalItems': number;
  'hydra:view'?: {
    '@id': string;
    'hydra:first'?: string;
    'hydra:last'?: string;
    'hydra:next'?: string;
    'hydra:previous'?: string;
  };
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | string[] | number | boolean | undefined>;
  skipAuth?: boolean;
}

export interface LoginResponse {
  token: string;
  user?: {
    email: string;
    name: string;
    role: string;
  };
  tenant?: {
    id: string;
    name: string;
  };
  /** V-1015: Betriebsebene des Mandanten; 'beta' → Umleitung auf erp-beta. */
  ebene?: 'produktion' | 'beta';
  /** Adresse der Beta-Instanz (null = noch nicht eingerichtet → nie umleiten). */
  ebene_ziel?: string | null;
}

// Types for Support Access
// SMTP-Profil je Zweck — 'system' (Default, Support/intern) oder 'pos_outgoing'
// (Ausgangsmails der POS-Apps an Endkunden). Backend hat denselben Enum-Wert.
export type SmtpPurpose = 'system' | 'pos_outgoing';

interface SmtpSettingsResponse {
  configured: boolean;
  purpose?: SmtpPurpose;
  settings: SmtpSettings | null;
}

interface SmtpSettings {
  id: string;
  purpose?: SmtpPurpose;
  host: string;
  port: number;
  username: string | null;
  encryption: 'tls' | 'ssl' | 'none';
  fromEmail: string;
  fromName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface SmtpSettingsInput {
  host: string;
  port: number;
  username?: string | null;
  password?: string;
  encryption: 'tls' | 'ssl' | 'none';
  fromEmail: string;
  fromName: string;
  isActive: boolean;
  // optional — wird vom Page-Wrapper als Query-Param mitgegeben (purpose-aware)
  purpose?: SmtpPurpose;
}

interface SupportAccessRequest {
  id: string;
  tenant: {
    id: string;
    companyName: string;
    contactEmail: string;
  };
  superAdmin: {
    id: string;
    name: string;
    email: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled';
  reason: string;
  expiresAt: string;
  approvedAt: string | null;
  approvedBy: string | null;
  createdAt: string;
}

interface SupportAccessRequestResponse {
  success: boolean;
  message: string;
  request: SupportAccessRequest;
}

interface SupportSessionResponse {
  success: boolean;
  token: string;
  session: {
    id: string;
    sessionToken: string;
    tenantId: string;
    tenantName: string;
    expiresAt: string;
  };
}

interface PendingSupportRequest {
  id: string;
  superAdmin: {
    name: string;
    email: string;
  };
  reason: string;
  expiresAt: string;
  createdAt: string;
}

interface ActiveSupportSession {
  id: string;
  sessionToken: string;
  superAdmin: {
    name: string;
    email: string;
  };
  startedAt: string;
  expiresAt: string;
}

interface SupportSessionInfo {
  sessionId: string;
  sessionToken: string;
  tenantId: string;
  tenantName: string;
  superAdminName: string;
  expiresAt: string;
}

// Tenant types
interface TenantLicense {
  plan: string;
  maxUsers: number;
  maxArticles: number;
  maxOrdersPerMonth: number;
  validUntil: string;
  priceMonthly: string;
}

interface TenantStats {
  users: number;
  orders30d: number;
  articles: number;
}

interface Tenant {
  id: string;
  customerNumber: string;
  companyName: string;
  contactEmail: string;
  contactPhone: string | null;
  contactPerson: string | null;
  status: 'trial' | 'active' | 'suspended' | 'cancelled';
  createdAt: string;
  updatedAt: string;
  suspendedAt: string | null;
  license: TenantLicense | null;
  stats: TenantStats;
}

// Number Range types
interface NumberRange {
  id: string;
  type: string;
  prefix: string;
  suffix: string;
  currentNumber: number;
  minDigits: number;
  description: string | null;
  isActive: boolean;
  preview: string;
  resetInterval: 'never' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
  resetDay: number | null;
  customDays: number | null;
  lastResetAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// Article types
interface ArticleImage {
  url: string;
  alt?: string;
  isPrimary?: boolean;
}

interface ArticleMarketplaces {
  amazon?: {
    asin?: string;
    isListed?: boolean;
  };
  ebay?: {
    itemId?: string;
    isListed?: boolean;
  };
}

// Custom Field Definition types
type CustomFieldType = 'text' | 'number' | 'date' | 'boolean' | 'select' | 'multiselect' | 'textarea' | 'text_i18n' | 'textarea_i18n';

interface CustomFieldDefinition {
  id: string;
  name: string;
  fieldKey: string;
  fieldType: CustomFieldType;
  description?: string | null;
  defaultValue?: string | null;
  options?: string[] | null;
  isRequired: boolean;
  isActive: boolean;
  sortOrder: number;
  fieldGroup?: string | null;
  visibilityManufacturers?: string[] | null;
  visibilityBrands?: string[] | null;
  visibilityCategories?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

interface CustomFieldDefinitionInput {
  name: string;
  fieldKey: string;
  fieldType?: CustomFieldType;
  description?: string;
  defaultValue?: string;
  options?: string[];
  isRequired?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  fieldGroup?: string;
  visibilityManufacturers?: string[];
  visibilityBrands?: string[];
  visibilityCategories?: string[];
}

// Category types
/**
 * Auto-Ausverkauf einer Warengruppe: „Saucen ab 30 Tagen Restlaufzeit
 * automatisch ausverkaufen". Ohne autoStart bleibt es bei der Vorschlagsliste,
 * die pro Artikel bestätigt wird — ein Preis fällt nie, weil ein Scan lief.
 * null bei den Zahlen heißt „kein eigener Wert, nimm den Vorschlag".
 */
interface CategoryClearanceRule {
  autoStart: boolean;
  thresholdDays: number | null;
  reductionPercent: number | null;
  intervalDays: number | null;
  strategy: 'time' | 'target';
  minPriceFactor: number | null;
}

interface Category {
  id: string;
  parentId?: string | null;
  name: string;
  slug: string;
  path?: string | null;
  description?: string | null;
  image?: string | null;
  imageUrl?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
  sortOrder: number;
  isActive: boolean;
  children?: Category[];
  createdAt?: string;
  updatedAt?: string;
  clearanceRule?: CategoryClearanceRule;
  /** Seitenvorlage der oeffentlichen Produktseite fuer alle Artikel dieser Gruppe. */
  productPageTemplateId?: string | null;
  /**
   * Standardgang (V-0076) fuer alle Artikel dieser Kategorie UND ihrer Unterkategorien.
   * `null` = erbt von der Elternkategorie. Wert ist `pos_courses.course_key`.
   */
  default_course_key?: string | null;
  /** BIS Shop (V-2238): false = Warengruppe samt Unterkategorien nicht im Onlineshop. Betrifft die Kasse NICHT. */
  show_in_shop?: boolean;
}

interface CategoryInput {
  name: string;
  parentId?: string;
  description?: string;
  image?: string;
  imageUrl?: string;
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string;
  sortOrder?: number;
  isActive?: boolean;
  clearanceRule?: Partial<CategoryClearanceRule>;
  productPageTemplateId?: string | null;
  default_course_key?: string | null;
  show_in_shop?: boolean;
}

// Bill of Materials (BOM) / Stücklisten types
type BomType = 'kit' | 'assembly';

interface BomItem {
  id: string;
  articleId: string;
  articleSku: string;
  articleName: string;
  articleStock: number;
  quantity: number;
  unit: string;
  sortOrder: number;
  isOptional: boolean;
  notes?: string | null;
  hasBom?: boolean;
}

interface BomItemInput {
  articleId: string;
  quantity: number;
  unit?: string;
  sortOrder?: number;
  isOptional?: boolean;
  notes?: string;
}

interface Bom {
  id: string;
  articleId: string;
  articleSku?: string | null;
  articleName?: string | null;
  bomType: BomType;
  version: string;
  isActive: boolean;
  validFrom?: string | null;
  validTo?: string | null;
  notes?: string | null;
  items?: BomItem[];
  createdAt?: string;
  updatedAt?: string;
}

interface BomInput {
  articleId: string;
  bomType?: BomType;
  version?: string;
  isActive?: boolean;
  validFrom?: string | null;
  validTo?: string | null;
  notes?: string;
  items?: BomItemInput[];
}

interface ExpandedBomItem {
  articleId: string;
  articleSku: string;
  articleName: string;
  articleStock: number;
  quantity: number;
  unit: string;
  isOptional: boolean;
}

// Return (Retoure) types
type ReturnType = 'customer' | 'supplier';
type ReturnStatus = 'pending' | 'approved' | 'received' | 'inspected' | 'restocked' | 'refunded' | 'completed' | 'rejected' | 'cancelled';
type ItemCondition = 'new' | 'good' | 'damaged' | 'defective' | 'unsellable';
type ItemAction = 'restock' | 'dispose' | 'repair' | 'return_to_supplier';

interface ReturnReason {
  id: string;
  code: string;
  name: string;
  description?: string;
  returnType: ReturnType;
  requiresImage: boolean;
  restockable: boolean;
  sortOrder: number;
}

interface ReturnImage {
  id: string;
  returnItemId?: string;
  fileName: string;
  filePath: string;
  fileSize?: number;
  mimeType?: string;
  imageType: string;
  description?: string;
  createdAt: string;
}

interface ReturnItem {
  id: string;
  articleId?: string;
  orderItemId?: string;
  sku: string;
  name: string;
  quantityExpected: number;
  quantityReceived: number;
  quantityApproved: number;
  unitPrice?: number;
  reason: string;
  reasonDetails?: string;
  conditionReceived?: ItemCondition;
  conditionNotes?: string;
  action?: ItemAction;
  restock: boolean;
  restockedQuantity: number;
  restockedAt?: string;
  serialNumber?: string;
  batchNumber?: string;
  storageLocationCode?: string;
}

interface ReturnItemInput {
  articleId?: string;
  orderItemId?: string;
  sku: string;
  name: string;
  quantityExpected: number;
  unitPrice?: number;
  reason: string;
  reasonDetails?: string;
  serialNumber?: string;
  batchNumber?: string;
  storageLocationCode?: string;
}

interface Return {
  id: string;
  returnNumber: string;
  returnType: ReturnType;
  status: ReturnStatus;
  orderId?: string;
  orderNumber?: string;
  purchaseOrderId?: string;
  purchaseOrderNumber?: string;
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  supplierId?: string;
  supplierName?: string;
  reasonCategory: string;
  reasonDetails?: string;
  refundAmount?: number;
  refundMethod?: string;
  refundStatus?: string;
  shippingMethod?: string;
  trackingNumber?: string;
  shippingCost?: number;
  returnLabelUrl?: string;
  inspectionStatus?: string;
  inspectionNotes?: string;
  inspectionDate?: string;
  /**
   * Lieferantenretoure: Versanddaten der Rücksendung an den Lieferanten.
   * Die Detailansicht zeigt diese vier Felder seit jeher an — bis 0.0.0.513 gab es
   * dafür weder Spalten noch Auslieferung, dort stand also dauerhaft „-".
   */
  supplierRmaNumber?: string | null;
  shippingCarrier?: string | null;
  shippingTracking?: string | null;
  shippedAt?: string | null;
  /**
   * Lieferanten-Lösung (Gutschrift / Ersatz / Reparatur), angelegt über
   * POST /returns/{id}/resolution. War hier als `string` deklariert, während die
   * Detailansicht ein Objekt liest — und geliefert wurde sie bis 0.0.0.514 gar nicht.
   */
  resolution?: {
    id: string;
    resolutionType: 'credit' | 'replacement' | 'repair';
    status: 'pending' | 'partial' | 'completed' | 'cancelled';
    expectedAmount?: number | null;
    expectedDate?: string | null;
    receivedAmount?: number | null;
    receivedDate?: string | null;
    creditNoteNumber?: string | null;
    notes?: string | null;
    createdAt?: string;
  } | null;
  restockingFee?: number;
  creditNoteNumber?: string;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  items?: ReturnItem[];
  images?: ReturnImage[];
  itemsCount?: number;
  totalQuantity?: number;
}

interface ReturnInput {
  returnType: ReturnType;
  orderId?: string;
  orderNumber?: string;
  purchaseOrderId?: string;
  purchaseOrderNumber?: string;
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  supplierId?: string;
  supplierName?: string;
  reasonCategory: string;
  reasonDetails?: string;
  refundAmount?: number;
  refundMethod?: string;
  shippingMethod?: string;
  notes?: string;
  // Lieferantenretoure — vom Update-Endpunkt entgegengenommen
  supplierRmaNumber?: string | null;
  shippingCarrier?: string | null;
  shippingTracking?: string | null;
  shippedAt?: string | null;
  items?: ReturnItemInput[];
}

interface ReturnListResponse {
  returns: Return[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ReturnStats {
  total: number;
  customerReturns: number;
  supplierReturns: number;
  byStatus: {
    pending: number;
    received: number;
    inspected: number;
    completed: number;
  };
  totalRefundAmount: number;
}

// ==========================================
// Stocktake (Inventur) Types
// ==========================================

type StocktakeType = 'full' | 'cycle' | 'spot' | 'adhoc' | 'zone';
type StocktakeStatus = 'scheduled' | 'in_progress' | 'paused' | 'completed' | 'cancelled' | 'review';
type CycleCountStrategy = 'abc' | 'usage' | 'random' | 'sequential' | 'priority';
type StocktakeItemStatus = 'pending' | 'counted' | 'variance' | 'approved' | 'adjusted';

interface StocktakeVarianceSummary {
  totalVariances: number;
  positiveVariances: number;
  positiveValue: number;
  negativeVariances: number;
  negativeValue: number;
  totalVarianceValue: number;
  accuracy: number;
  topVariances?: Array<{
    articleSku: string;
    articleName: string;
    variance: number;
    value: number;
  }>;
}

interface StocktakeItem {
  id: string;
  storageLocationId?: string;
  storageLocationCode: string;
  zone?: string;
  articleId?: string;
  articleSku?: string;
  articleName?: string;
  expectedQuantity: number;
  countedQuantity?: number;
  variance: number;
  variancePercentage: number;
  hasVariance: boolean;
  status: StocktakeItemStatus;
  batchNumber?: string;
  expiryDate?: string;
  serialNumbers?: string[];
  notes?: string;
  photos?: string[];
  secondCountBy?: string;
  secondCountQuantity?: number;
  secondCountAt?: string;
  countedAt?: string;
  countedBy?: string;
}

interface Stocktake {
  id: string;
  stocktake_number: string;
  type: StocktakeType;
  strategy?: CycleCountStrategy;
  status: StocktakeStatus;
  scheduled_date?: string;
  due_date?: string;
  started_at?: string;
  completed_at?: string;
  zones?: string[];
  aisles?: string[];
  storage_location_codes?: string[];
  assigned_to?: string;
  counted_by?: string;
  total_locations: number;
  counted_locations: number;
  locations_with_variance: number;
  total_items: number;
  counted_items: number;
  items_with_variance: number;
  has_variances: boolean;
  variance_summary?: StocktakeVarianceSummary;
  notes?: string;
  auto_generated: boolean;
  generated_by?: string;
  /** `location` = nach Lagerplatz, `article` = nach Artikel. */
  count_basis?: 'location' | 'article';
  /**
   * Lager, für das die Inventur gilt. Die Freigabe setzt den Bestand ausschliesslich in
   * diesem Lager auf das Zählergebnis. `null` = Altbestand ohne Lagerbezug (nicht buchbar).
   */
  warehouse_id?: string | null;
  warehouse_name?: string | null;
  items?: StocktakeItem[];
  created_at: string;
  updated_at: string;
}

interface StocktakeInput {
  type: StocktakeType;
  strategy?: CycleCountStrategy;
  scheduled_date?: string;
  due_date?: string;
  zones?: string[];
  aisles?: string[];
  storage_location_codes?: string[];
  // Anzeigename des zugewiesenen Mitarbeiters (fuer Listen/Druck ohne Join in die Master-DB).
  assigned_to?: string;
  // Maszgeblich fuer die Sichtbarkeit: zugewiesene Inventuren sehen nur der Zugewiesene,
  // der Ersteller und Administratoren. Leer = niemandem zugewiesen = fuer alle sichtbar.
  assigned_user_id?: string;
  notes?: string;
  auto_generated?: boolean;
  generated_by?: string;
  block_picking?: boolean;
  locations_count?: number;
  /**
   * Lager, fuer das die Inventur gilt — Pflicht. Ohne Lagerbezug liesse sich weder die
   * Lagerplatz-Auswahl eingrenzen noch bestimmen, ob beim Zaehlen ein Platz erfasst werden muss.
   */
  warehouseId?: string;
}

// Spot check during picking types
interface SpotCheckItem {
  article_id: string;
  sku: string;
  name: string;
  expected_quantity: number;
  location_code: string;
  last_check_at: string | null;
  check_count: number;
}

interface SpotCheckResponse {
  spot_check: SpotCheckItem | null;
  message: string;
}

interface SpotCheckRecordInput {
  article_id: string;
  counted_quantity: number;
}

interface SpotCheckRecordResponse {
  success: boolean;
  variance: number;
  has_variance: boolean;
  expected_quantity: number;
  counted_quantity: number;
  message: string;
}

interface StocktakeListResponse {
  data: Stocktake[];
  meta: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

interface StocktakeStatistics {
  scheduled: number;
  in_progress: number;
  needs_review: number;
  completed: number;
  total: number;
  avg_accuracy?: number;
}

interface CycleCountConfig {
  id: string;
  name: string;
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  frequency_interval: number;
  next_run_date?: string;
  last_run_date?: string;
  strategy: CycleCountStrategy;
  abc_config?: {
    aItemsPercentage: number;
    bItemsPercentage: number;
    cItemsPercentage: number;
    aCountFrequency: number;
    bCountFrequency: number;
    cCountFrequency: number;
  };
  locations_per_count: number;
  include_zones?: string[];
  exclude_zones?: string[];
  include_categories?: string[];
  min_stock_value?: number;
  notify_users?: string[];
  created_by?: string;
  created_at: string;
  updated_at: string;
}

interface CycleCountConfigInput {
  name: string;
  enabled?: boolean;
  frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly';
  frequency_interval?: number;
  strategy?: CycleCountStrategy;
  abc_config?: {
    aItemsPercentage: number;
    bItemsPercentage: number;
    cItemsPercentage: number;
    aCountFrequency: number;
    bCountFrequency: number;
    cCountFrequency: number;
  };
  locations_per_count?: number;
  include_zones?: string[];
  exclude_zones?: string[];
  include_categories?: string[];
  min_stock_value?: number;
  notify_users?: string[];
}

// ==========================================
// Company Documents Types (Encrypted)
// ==========================================

interface CompanyDocument {
  id: string;
  name: string;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  uploadedAt: string;
  notes?: string;
}

interface CompanyDocumentGroup {
  type: string;
  label: string;
  documents: CompanyDocument[];
}

// ==========================================
// Backup Types
// ==========================================

interface BackupRecord {
  id: string;
  backup_type: string;
  trigger_type: string;
  status: string;
  file_path: string | null;
  file_name: string | null;
  file_size: number | null;
  checksum: string | null;
  table_count: number | null;
  row_count: number | null;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  expires_at: string | null;
  verified_at: string | null;
  verification_status: string | null;
  error_message: string | null;
  created_at: string;
}

interface BackupConfig {
  is_enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  schedule_hour: number;
  schedule_day_of_week: number | null;
  schedule_day_of_month: number | null;
  retention_days: number;
  max_backups: number;
  notification_email: string | null;
  last_backup_at: string | null;
  next_backup_at: string | null;
  last_backup_status: string | null;
  features?: {
    backup_manual: boolean;
    backup_auto: boolean;
  };
  limits?: {
    manual_per_week: number;
    auto_enabled: boolean;
    max_retention_days: number;
    max_backups: number;
  };
}

interface BackupUsage {
  backup_count: number;
  total_size_bytes: number;
  total_size_formatted: string;
}

interface SpotCheckInput {
  locations_count?: number;
  article_skus?: string[];
  zones?: string[];
  strategy?: CycleCountStrategy;
  scheduled_date?: string;
  assigned_to?: string;
  notes?: string;
}

// ==========================================
// Shopify Integration Types
// ==========================================

interface ShopifyConnection {
  id: string;
  shopDomain: string;
  apiKey?: string;
  apiVersion: string;
  isActive: boolean;
  syncProducts: boolean;
  syncOrders: boolean;
  syncInventory: boolean;
  autoImportOrders: boolean;
  autoSync: boolean;
  trackIncomingChanges?: boolean;
  collectionSyncMode?: 'tags_only' | 'manual_mapping' | 'auto_create';
  defaultLocationId?: string;
  lastProductSync?: string;
  lastOrderSync?: string;
  lastInventorySync?: string;
  createdAt: string;
  updatedAt: string;
}

interface ShopifyConnectionInput {
  shopDomain: string;
  apiKey?: string;
  apiSecret?: string;
  accessToken?: string;
  apiVersion?: string;
  isActive?: boolean;
  syncProducts?: boolean;
  syncOrders?: boolean;
  syncInventory?: boolean;
  autoImportOrders?: boolean;
  autoSync?: boolean;
  trackIncomingChanges?: boolean;
  collectionSyncMode?: 'tags_only' | 'manual_mapping' | 'auto_create';
}

interface ShopifyLocation {
  shopifyLocationId: string;
  name: string;
  address?: string;
  city?: string;
  country?: string;
  isActive: boolean;
  localMapping?: {
    id: string;
    localWarehouseId?: string;
    isDefault: boolean;
  };
}

interface ShopifyProductMapping {
  id: string;
  articleId: string;
  shopifyProductId: string;
  shopifyHandle?: string;
  syncStatus: 'synced' | 'pending' | 'error' | 'not_synced';
  lastSync?: string;
  lastError?: string;
}

interface ShopifyProductSyncStatus {
  total: number;
  synced: number;
  pending: number;
  error: number;
  notSynced: number;
  lastSync?: string;
}

interface ShopifyProduct {
  id: string;
  sku: string;
  name: string;
  sellingPrice: number;
  stockQuantity: number;
  mappingId?: string;
  shopifyProductId?: string;
  shopifyHandle?: string;
  syncStatus?: 'synced' | 'pending' | 'error' | 'not_synced';
  lastSync?: string;
  lastError?: string;
}

interface ShopifyProductListResponse {
  products: ShopifyProduct[];
  total: number;
  limit: number;
  offset: number;
}

interface ShopifyOrderImportStatus {
  imported: number;
  pending: number;
  error: number;
  lastSync?: string;
}

interface ShopifyOrder {
  id: number;
  name: string;
  order_number: number;
  email?: string;
  created_at: string;
  updated_at: string;
  total_price: string;
  subtotal_price: string;
  total_tax: string;
  currency: string;
  financial_status?: string;
  fulfillment_status?: string;
  customer?: {
    id: number;
    email?: string;
    first_name?: string;
    last_name?: string;
  };
  line_items: Array<{
    id: number;
    title: string;
    quantity: number;
    price: string;
    sku?: string;
    variant_id?: number;
  }>;
  shipping_address?: {
    first_name?: string;
    last_name?: string;
    address1?: string;
    address2?: string;
    city?: string;
    zip?: string;
    country?: string;
    country_code?: string;
    phone?: string;
  };
  importStatus?: {
    orderId?: string;
    importStatus: 'imported' | 'pending' | 'error' | 'skipped';
  };
}

interface ShopifyCollection {
  id: number;
  title: string;
  handle: string;
  body_html?: string;
  collection_type: 'smart' | 'custom';
  localMapping?: {
    id: string;
    categoryId?: string;
  };
}

interface ShopifyMetafieldMapping {
  id: string;
  localField: string;
  localFieldType: 'article' | 'variant' | 'order' | 'customer';
  shopifyNamespace: string;
  shopifyKey: string;
  shopifyType: string;
  isActive: boolean;
}

interface ShopifyMetafieldMappingInput {
  localField: string;
  localFieldType?: 'article' | 'variant' | 'order' | 'customer';
  shopifyNamespace: string;
  shopifyKey: string;
  shopifyType?: string;
  isActive?: boolean;
}

interface ShopifyWebhook {
  id: number;
  address: string;
  topic: string;
  created_at: string;
  updated_at: string;
  format: string;
}

interface ShopifySyncLog {
  id: string;
  syncType: 'product' | 'order' | 'inventory' | 'collection' | 'webhook';
  direction: 'push' | 'pull';
  entityType: string;
  entityId: string;
  entityName?: string;
  shopifyId: string;
  status: 'success' | 'error' | 'skipped';
  message?: string;
  createdAt: string;
}

interface ShopifyPendingChangeField {
  field: string;
  label: string;
  wawiValue: string | number | null;
  shopifyValue: string | number | null;
  type: 'text' | 'html' | 'currency' | 'number';
  severity: 'high' | 'medium' | 'low';
}

interface ShopifyPendingChange {
  id: string;
  connectionId: string;
  shopifyProductId?: string;
  shopifyVariantId?: string;
  shopifyInventoryItemId?: string;
  articleId: string;
  articleSku: string;
  articleName: string;
  changeType: 'product' | 'inventory';
  detectedChanges: ShopifyPendingChangeField[];
  status: 'pending' | 'approved' | 'rejected' | 'partial';
  approvedFields?: string[] | null;
  rejectedFields?: string[] | null;
  receivedAt: string;
  processedAt?: string | null;
  processedBy?: string | null;
}

// ==========================================
// eBay Integration Types
// ==========================================

interface EbayConnection {
  id: string;
  environment: 'sandbox' | 'production';
  appId?: string;
  certId?: string;
  devId?: string;
  redirectUri?: string;
  marketplaceId: string;
  siteId: number;
  isActive: boolean;
  ebayUserId?: string;
  lastSyncAt?: string;
  settings?: Record<string, unknown>;
  tokenExpiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface EbayConnectionInput {
  environment?: 'sandbox' | 'production';
  appId?: string;
  certId?: string;
  devId?: string;
  redirectUri?: string;
  marketplaceId?: string;
  siteId?: number;
  isActive?: boolean;
  settings?: Record<string, unknown>;
}

interface EbayMarketplace {
  id: string;
  marketplaceId: string;
  siteId: number;
  name: string;
  countryCode: string;
  currency: string;
  language: string;
  isActive: boolean;
  isPrimary: boolean;
  settings?: Record<string, unknown>;
  lastSyncAt?: string;
}

interface EbayAvailableMarketplace {
  id: string;
  siteId: number;
  name: string;
  countryCode: string;
  currency: string;
  language: string;
}

interface EbayGlobalTemplate {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  listingType: 'FixedPriceItem' | 'Auction';
  listingDuration: string;
  shippingProfileId?: string;
  shippingPolicy?: Record<string, unknown>;
  dispatchTimeMax: number;
  paymentProfileId?: string;
  paymentPolicy?: Record<string, unknown>;
  returnProfileId?: string;
  returnPolicy?: Record<string, unknown>;
  locationCountry: string;
  locationPostalCode?: string;
  locationCity?: string;
  bestOfferEnabled: boolean;
  privateListing: boolean;
  // eBay Category for BISLaunch
  categoryId?: string;
  categoryName?: string;
  categoryPath?: string;
  // Store categories (eBay allows 2 store categories)
  storeCategoryId?: string;
  storeCategoryName?: string;
  storeCategoryId2?: string;
  storeCategoryName2?: string;
  // Design template reference
  designTemplateId?: string;
  // Price markup for BISLaunch
  priceMarkupPercent?: number;
  priceMarkupFixed?: number;
  // Auto-List
  autoListEnabled?: number;
  autoListThreshold?: number;
  // Auto-End & Relist
  autoEndEnabled?: number;
  autoEndDays?: number;
  autoRelistEnabled?: number;
  autoRelistMaxRetries?: number;
  createdAt: string;
  updatedAt: string;
}

interface EbayGlobalTemplateInput {
  name: string;
  description?: string;
  isDefault?: boolean;
  listingType?: 'FixedPriceItem' | 'Auction';
  listingDuration?: string;
  shippingProfileId?: string;
  shippingPolicy?: Record<string, unknown>;
  dispatchTimeMax?: number;
  paymentProfileId?: string;
  paymentPolicy?: Record<string, unknown>;
  returnProfileId?: string;
  returnPolicy?: Record<string, unknown>;
  locationCountry?: string;
  locationPostalCode?: string;
  locationCity?: string;
  bestOfferEnabled?: boolean;
  privateListing?: boolean;
  // eBay Category for BISLaunch
  categoryId?: string;
  categoryName?: string;
  categoryPath?: string;
  // Store categories (eBay allows 2 store categories)
  storeCategoryId?: string;
  storeCategoryName?: string;
  storeCategoryId2?: string;
  storeCategoryName2?: string;
  // Design template reference
  designTemplateId?: string;
  // Price markup for BISLaunch
  priceMarkupPercent?: number;
  priceMarkupFixed?: number;
  // Auto-List
  autoListEnabled?: number;
  autoListThreshold?: number;
  // Auto-End & Relist
  autoEndEnabled?: number;
  autoEndDays?: number;
  autoRelistEnabled?: number;
  autoRelistMaxRetries?: number;
}

interface EbayDesignTemplate {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  htmlTemplate: string;
  cssStyles?: string;
  isResponsive: boolean;
  previewImage?: string;
  createdAt: string;
  updatedAt: string;
}

interface EbayDesignTemplateInput {
  name?: string;
  description?: string;
  isDefault?: boolean;
  htmlTemplate?: string;
  cssStyles?: string;
  isResponsive?: boolean;
  previewImage?: string;
  baseTemplate?: string; // ID of base template to copy from: 'empty', 'standard', 'food', 'premium'
}

interface EbayListingTemplate {
  id: string;
  articleId: string;
  articleName?: string;
  articleSku?: string;
  globalTemplateId?: string;
  globalTemplateName?: string;
  designTemplateId?: string;
  designTemplateName?: string;
  title?: string;
  subtitle?: string;
  descriptionOverride?: string;
  primaryCategoryId?: string;
  secondaryCategoryId?: string;
  storeCategoryId?: string;
  ean?: string;
  upc?: string;
  isbn?: string;
  mpn?: string;
  brand?: string;
  startPrice?: number;
  buyItNowPrice?: number;
  reservePrice?: number;
  quantity: number;
  quantityFromStock: boolean;
  itemSpecifics?: Array<{ name: string; value: string | string[] }>;
  images?: string[];
  useArticleImages: boolean;
  variationConfig?: Record<string, unknown>;
  // Richtlinien je Listing. Leer = die globale Vorlage gilt (Rueckfall im Publish-JOIN).
  // Gesetzt = dieses Angebot braucht keine globale Vorlage.
  shippingProfileId?: string;
  paymentProfileId?: string;
  returnProfileId?: string;
  dispatchTimeMax?: number;
  status: 'draft' | 'ready' | 'scheduled' | 'active' | 'ended' | 'error';
  createdAt: string;
  updatedAt: string;
}

interface EbayListingTemplateInput {
  articleId: string;
  globalTemplateId?: string;
  designTemplateId?: string;
  title?: string;
  subtitle?: string;
  descriptionOverride?: string;
  primaryCategoryId?: string;
  secondaryCategoryId?: string;
  storeCategoryId?: string;
  ean?: string;
  upc?: string;
  isbn?: string;
  mpn?: string;
  brand?: string;
  startPrice?: number;
  buyItNowPrice?: number;
  reservePrice?: number;
  quantity?: number;
  quantityFromStock?: boolean;
  itemSpecifics?: Array<{ name: string; value: string | string[] }>;
  images?: string[];
  useArticleImages?: boolean;
  variationConfig?: Record<string, unknown>;
  shippingProfileId?: string;
  paymentProfileId?: string;
  returnProfileId?: string;
  dispatchTimeMax?: number;
  status?: 'draft' | 'ready';
}

interface EbayListing {
  id: string;
  listingTemplateId: string;
  articleId: string;
  articleName?: string;
  articleSku?: string;
  title?: string;
  startPrice?: number;
  ebayItemId?: string;
  ebayListingUrl?: string;
  status: 'pending' | 'scheduled' | 'active' | 'ended' | 'sold' | 'error';
  errorMessage?: string;
  scheduledStartAt?: string;
  scheduledEndAt?: string;
  currentPrice?: number;
  currentQuantity?: number;
  soldQuantity: number;
  watchCount: number;
  viewCount: number;
  listedAt?: string;
  endedAt?: string;
  lastSyncedAt?: string;
  // Auto-End & Relist (NULL = inherit from global template)
  autoEndEnabled?: number | null;
  autoEndDays?: number | null;
  autoRelistEnabled?: number | null;
  autoRelistMaxRetries?: number | null;
  relistCount?: number;
  autoEndedAt?: string | null;
  autoEndReason?: string | null;
  createdAt: string;
  updatedAt: string;
}


interface EbayCategory {
  categoryId: string;
  categoryName: string;
  categoryLevel?: number;
  leafCategory?: boolean;
  children?: EbayCategory[];
}

interface EbayCategorySuggestion {
  categoryId: string;
  categoryName: string;
  categoryTreeNodeLevel: number;
  categoryTreeNodeAncestors?: Array<{ categoryId: string; categoryName: string }>;
}

interface EbayOrder {
  id: string;
  ebayOrderId: string;
  localOrderId?: string;
  buyerUsername?: string;
  buyerEmail?: string;
  subtotal?: number;
  shippingCost?: number;
  total?: number;
  currency: string;
  shippingAddress?: {
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    stateOrProvince?: string;
    postalCode?: string;
    country?: string;
    phone?: string;
  };
  orderStatus?: string;
  paymentStatus?: string;
  fulfillmentStatus?: string;
  lineItems?: Array<{
    title: string;
    sku?: string;
    quantity: number;
    price: number;
    // eBay-IDs — noetig, um dem Kaeufer eine Bewertung zu geben (Trading LeaveFeedback).
    lineItemId?: string;
    legacyItemId?: string;
    transactionId?: string;
  }>;
  ebayCreatedAt?: string;
  paidAt?: string;
  shippedAt?: string;
  importStatus: 'pending' | 'imported' | 'error' | 'skipped';
  importError?: string;
  // Bewertungs-Status (vom Backend angereichert)
  feedbackLeft?: boolean;
  feedbackReceived?: 'Positive' | 'Neutral' | 'Negative' | null;
  canLeaveFeedback?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface EbaySyncLog {
  id: string;
  syncType: 'listing' | 'inventory' | 'order' | 'category';
  direction: 'push' | 'pull';
  entityType?: string;
  entityId?: string;
  ebayItemId?: string;
  status: 'success' | 'error' | 'skipped';
  message?: string;
  createdAt: string;
}

interface EbaySyncStatus {
  isConnected: boolean;
  lastSync?: string;
  listings: {
    active: number;
    pending: number;
    error: number;
  };
  orders: {
    pending: number;
  };
}

interface EbayPolicy {
  policyId: string;
  name: string;
  description?: string;
  marketplaceId: string;
}

// eBay Item Aspects (Item Specifics)
interface EbayItemAspect {
  name: string;
  required: boolean;
  mode: 'SELECTION_ONLY' | 'FREE_TEXT';
  cardinality: 'SINGLE' | 'MULTI';
  values: string[];
  expectedRequiredByDate?: string;
  aspectUsage?: 'RECOMMENDED' | 'OPTIONAL';
}

interface EbayCategoryAspectsResponse {
  categoryId: string;
  marketplaceId: string;
  aspects: EbayItemAspect[];
  cached: boolean;
  cachedAt?: string;
  expiresAt?: string;
}

// eBay Attribute Mappings
interface EbayAttributeMapping {
  id: string;
  sourceType: 'custom_field' | 'variant_option' | 'article_field' | 'static';
  sourceId?: string;
  sourceField?: string;
  staticValue?: string;
  ebayAspectName: string;
  ebayCategoryId?: string;
  marketplaceId: string;
  valueMapping?: Record<string, string>;
  defaultValue?: string;
  priority: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface EbayAttributeMappingInput {
  sourceType: 'custom_field' | 'variant_option' | 'article_field' | 'static';
  sourceId?: string;
  sourceField?: string;
  staticValue?: string;
  ebayAspectName: string;
  ebayCategoryId?: string;
  marketplaceId?: string;
  valueMapping?: Record<string, string>;
  defaultValue?: string;
  priority?: number;
  isActive?: boolean;
}

interface EbayAttributeMappingSuggestion {
  ebayAspectName: string;
  required: boolean;
  sourceType: 'custom_field' | 'variant_option' | 'article_field' | 'static';
  sourceField: string;
  sourceLabel: string;
  confidence: number;
}

// PayPal Integration types
interface PayPalConfig {
  id: string;
  name: string;
  client_id: string;
  environment: 'sandbox' | 'production';
  is_active: boolean;
  webhook_id: string | null;
  last_sync_at: string | null;
  sync_from_date: string | null;
  auto_sync: boolean;
  auto_match_orders: boolean;
  datev_account_number: string;
  datev_contra_account: string;
  /** V-3622 (F1): Auszahlungen beim Geräte-Ankauf über die Kasse erlaubt (ab Werk aus). */
  payouts_erlaubt?: boolean;
  /** V-3622: Hat PayPal Payouts für den Betrieb freigeschaltet? `unbekannt` = noch nicht geprüft. */
  payouts_freigabe?: 'ja' | 'nein' | 'unbekannt';
  payouts_geprueft_at?: string | null;
  /** V-3622 F-001: Obergrenze je Auszahlung in EUR (Vorgabe 1000, wenn nichts eingestellt ist). */
  payout_grenze_je_auszahlung?: number;
  /** V-3622 F-001: Obergrenze je Kalendertag in EUR (Vorgabe 3000, wenn nichts eingestellt ist). */
  payout_grenze_je_tag?: number;
  /** V-3622 F-008: Nur diese Konfiguration (erste aktive nach Anlage) nutzt die Kasse für Auszahlungen. */
  payouts_von_kasse_genutzt?: boolean;
  created_at: string;
  updated_at: string;
}

interface PayPalConfigInput {
  name?: string;
  client_id: string;
  client_secret: string;
  environment?: 'sandbox' | 'production';
  is_active?: boolean;
  sync_from_date?: string;
  auto_sync?: boolean;
  auto_match_orders?: boolean;
  datev_account_number?: string;
  datev_contra_account?: string;
  payouts_erlaubt?: boolean;
  /** V-3622 F-001: Zahl > 0 und höchstens 99999.99; `null` = zurück auf die Vorgabe. */
  payout_grenze_je_auszahlung?: number | null;
  payout_grenze_je_tag?: number | null;
}

interface PayPalTransaction {
  id: string;
  paypal_config_id: string | null;
  transaction_id: string;
  transaction_type: string;
  status: string;
  amount: number;
  currency: string;
  fee_amount: number | null;
  net_amount: number | null;
  payer_email: string | null;
  payer_name: string | null;
  payer_id: string | null;
  payee_email: string | null;
  invoice_id: string | null;
  custom_id: string | null;
  reference_id: string | null;
  parent_transaction_id: string | null;
  transaction_date: string;
  available_date: string | null;
  description: string | null;
  order_id: string | null;
  order_number: string | null;
  matched_at: string | null;
  matched_by: string | null;
  match_type: 'invoice_id' | 'custom_id' | 'reference_id' | 'manual' | null;
  datev_exported: boolean;
  datev_exported_at: string | null;
  datev_booking_text: string | null;
  created_at: string;
  updated_at: string;
}

interface PayPalTransactionListResponse {
  data: PayPalTransaction[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

interface PayPalSyncResult {
  total_fetched: number;
  synced: number;
  skipped: number;
  errors: number;
  auto_matched?: number;
}

type PayPalRefundStatus = 'pending' | 'completed' | 'failed';

interface PayPalRefund {
  id: string;
  paypal_config_id: string;
  original_transaction_id: string;
  refund_transaction_id: string | null;
  order_id: string | null;
  order_number: string | null;
  amount: number;
  currency: string;
  reason: string | null;
  note_to_payer: string | null;
  status: PayPalRefundStatus;
  paypal_status: string | null;
  error_message: string | null;
  initiated_by: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface PayPalRefundListResponse {
  data: PayPalRefund[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

interface PayPalRefundInput {
  config_id: string;
  original_transaction_id: string;
  amount: number;
  currency?: string;
  order_id?: string;
  order_number?: string;
  reason?: string;
  note_to_payer?: string;
  process_immediately?: boolean;
}

interface PayPalDatevExport {
  id: string;
  export_type: string;
  export_number: string;
  period_from: string;
  period_to: string;
  total_records: number;
  total_amount: number;
  file_name: string;
  file_format: string;
  consultant_number: string | null;
  client_number: string | null;
  fiscal_year_start: string | null;
  status: string;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

interface PayPalDatevExportListResponse {
  data: PayPalDatevExport[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

interface PayPalDatevExportInput {
  period_from: string;
  period_to: string;
  consultant_number?: string;
  client_number?: string;
}

interface PayPalDatevExportResult {
  success: boolean;
  export_id?: string;
  export_number?: string;
  file_name?: string;
  total_records?: number;
  total_amount?: number;
  csv_content?: string;
  message?: string;
}

interface PayPalStatistics {
  period: {
    from: string;
    to: string;
  };
  totals: {
    transactions: number;
    received: number;
    refunded: number;
    fees: number;
    net: number;
    matched: number;
    unmatched: number;
  };
  by_status: Array<{
    status: string;
    count: number;
    total: number;
  }>;
  daily: Array<{
    date: string;
    received: number;
    refunded: number;
    transactions: number;
  }>;
}

// DHL Integration types
interface DhlApiConfig {
  id: string;
  environment: 'sandbox' | 'production';
  configType: 'shipping' | 'tracking' | 'returns';
  apiKey: string;
  isActive: boolean;
  notes: string | null;
  enabledProducts: string[];
  enabledServices: string[];
  serviceDefaults: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

interface DhlApiConfigInput {
  environment: 'sandbox' | 'production';
  configType?: 'shipping' | 'tracking' | 'returns';
  apiKey: string;
  apiSecret: string;
  isActive?: boolean;
  notes?: string;
}

// DHL Product info from admin endpoint
interface DhlProductInfo {
  code: string;
  name: string;
  description: string;
  domestic: boolean;
  international: boolean;
  requiresCustoms: boolean;
  maxWeight?: number;
  billingNumberType?: string;
}

// DHL Service info from admin endpoint
interface DhlServiceInfo {
  code: string;
  name: string;
  description: string;
  category: string;
  products: string[];
  requiresValue: boolean;
  valueType?: string;
  options?: string[];
  optionLabels?: Record<string, string>;
  defaults?: unknown;
}

// Shipping Method (Versandart) types
/**
 * Eine Staffelzeile der Versandkonditionen: Zone × Gewicht → Kundenpreis und eigene Kosten.
 *
 * 🚨 `ownCost` darf null sein — „nicht hinterlegt" ist etwas anderes als „kostet uns nichts".
 */
export interface ShippingMethodRate {
  zone: string;
  weightToGrams: number;
  customerPrice: number;
  ownCost: number | null;
}

interface ShippingMethod {
  id: string;
  name: string;
  carrier: string;
  /** 'parcel' = Paketdienst, 'freight' = Spedition. */
  carrierType?: 'parcel' | 'freight';
  /** Der Kunde holt selbst ab. Solche Auftraege werden nie wegen fehlender Zahlung gesperrt. */
  isPickup?: boolean;
  /** Klartextname eines nicht integrierten Dienstleisters ("Kühne & Nagel"). */
  carrierLabel?: string | null;
  /**
   * Womit bei dieser Versandart gepackt wird: `pallet` | `colli` | `box` …
   * `null` = keine Vorgabe; der Packtisch belegt dann bewusst nichts vor.
   */
  defaultPackageKind?: string | null;
  /** Optional das konkrete Packmittel aus dem Katalog („Europalette 120 × 80"). */
  defaultCartonTypeId?: string | null;
  /** Nur zum Anzeigen — der Server löst den Namen gegen den Katalog auf. */
  defaultCartonTypeName?: string | null;
  productCode: string;
  /** Pauschaler Kundenpreis; die Staffel in `rates` hat Vorrang. */
  price?: number;
  rates?: ShippingMethodRate[];
  freeShippingThreshold?: number | null;
  estimatedDaysMin?: number | null;
  estimatedDaysMax?: number | null;
  /** Im Webshop anbieten. Ab Werk aus — nicht jede Versandart gehoert vor den Kunden. */
  availableOnline?: boolean;
  /** Steuerklasse der Versandkosten. Leer = wie die Ware (Nebenleistung), nicht steuerfrei. */
  taxClassCode?: string | null;
  /** Zielländer (ISO-3166-alpha-2). Leer = keine Einschränkung. */
  countries?: string[] | null;
  isDefault?: boolean;
  enabledServices: string[];
  serviceConfig: Record<string, unknown> | null;
  senderAddress?: {
    company?: string;
    name?: string;
    street?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
    countryCode?: string;
    email?: string;
    phone?: string;
  } | null;
  packagingWeight: number | null;
  maxWeight: number | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** Eine Position auf einer Palette/in einem Karton. */
export interface ShipmentPackageItem {
  articleId: string;
  sku?: string | null;
  name?: string | null;
  quantity: number;
}

/** Ein Packstück (Palette/Karton) einer Sendung. */
export interface ShipmentPackage {
  index: number;
  weightKg: number | null;
  dimensions?: Record<string, number> | null;
  items: ShipmentPackageItem[];
  itemCount: number;
  unitCount: number;
  note?: string | null;
  shipmentId?: string;
  shipmentNumber?: string | null;
  trackingNumber?: string | null;
  carrier?: string | null;
  status?: string | null;
  shippedAt?: string | null;
  packGroupId?: string | null;
  /**
   * Schluessel des Packstueck-Fotos. Bewusst KEINE oeffentliche URL - auf dem Bild kann das
   * Adressetikett liegen. Abruf ueber `getPackagePhoto()`, das die Sitzung mitschickt.
   *
   * Fuehrt das ERSTBILD. Alle Aufnahmen stehen in `photos` (seit 27.08.2026 sind mehrere je
   * Packstueck moeglich); jede Sendung von davor kennt nur dieses eine Feld.
   */
  photoKey?: string | null;
  /** Alle Aufnahmen dieses Packstuecks - Schluessel + Titel/Anweisung aus den Einstellungen. */
  photos?: PackagePhotoEntry[];
}

export interface PackagePhotoEntry {
  key: string;
  /** Titel/Anweisung, im Klartext im Beleg gespeichert (die Vorgabe darf spaeter umbenannt werden). */
  title?: string | null;
  /** Nummer der Aufnahme; ab 1000 = Beweisfoto je Artikel auf der Palette (B-MDE-0059, V-2991). */
  slot?: number;
  /** `'artikel'` bei einem Beweisfoto je Artikel (Name steht in `title`, SKU und Menge hier). */
  art?: 'artikel';
  sku?: string;
  menge?: number;
}

export interface OrderPackagesResponse {
  success: boolean;
  packageCount: number;
  totalWeightKg: number;
  packages: ShipmentPackage[];
}

interface ShippingMethodInput {
  name: string;
  /** Bei Spedition/„Sonstige" leer — der Server leitet den Schlüssel aus carrierLabel ab. */
  carrier?: string;
  carrierType?: 'parcel' | 'freight';
  /** Der Kunde holt selbst ab. Solche Auftraege werden nie wegen fehlender Zahlung gesperrt. */
  isPickup?: boolean;
  carrierLabel?: string;
  /** Nur bei den integrierten Diensten (DHL/DPD/GLS/UPS/Hermes) Pflicht. */
  productCode?: string;
  price?: number;
  /**
   * Konditions-Staffel. Fehlt der Schlüssel, bleibt eine vorhandene Staffel unangetastet —
   * ein Teil-Speichern aus einem anderen Formular darf sie nicht abräumen.
   */
  rates?: { zone: string; weightToKg: number | string; customerPrice: number | string; ownCost: number | string | null }[];
  freeShippingThreshold?: number | null;
  estimatedDaysMin?: number | null;
  estimatedDaysMax?: number | null;
  availableOnline?: boolean;
  taxClassCode?: string | null;
  countries?: string[] | null;
  isDefault?: boolean;
  enabledServices?: string[];
  serviceConfig?: Record<string, unknown>;
  senderAddress?: Record<string, string> | null;
  packagingWeight?: number;
  maxWeight?: number;
  isActive?: boolean;
  sortOrder?: number;
}

// DPD API Config (Admin)
export interface DpdApiConfig {
  id: string;
  environment: 'sandbox' | 'production';
  delisId: string;
  depot: string | null;
  messageLanguage: string | null;
  isActive: boolean;
  notes: string | null;
  enabledProducts: string[];
  enabledServices: string[];
  serviceDefaults: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface DpdApiConfigInput {
  environment?: 'sandbox' | 'production';
  delisId?: string;  // Optional - DPD admin config doesn't need credentials
  password?: string; // Optional - Tenants use their own credentials
  depot?: string;
  messageLanguage?: string;
  isActive?: boolean;
  notes?: string;
  enabledProducts?: string[];
  enabledServices?: string[];
  serviceDefaults?: Record<string, unknown>;
}

// DPD Product info from admin endpoint
export interface DpdProductInfo {
  code: string;
  name: string;
  description: string;
  domestic: boolean;
  international: boolean;
  maxWeight: number;
  isExpress: boolean;
  isReturn: boolean;
}

// DPD Service info from admin endpoint
export interface DpdServiceInfo {
  code: string;
  name: string;
  description: string;
  category: string;
  requiresValue: boolean;
  compatibleProducts: string[];
}

// GLS API Config (Admin) - OAuth2 Client Credentials
export interface GlsApiConfig {
  id: string;
  environment: 'sandbox' | 'production';
  clientId: string;
  customerId: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GlsApiConfigInput {
  environment: 'sandbox' | 'production';
  clientId: string;
  clientSecret: string;
  customerId?: string;
  isActive?: boolean;
  notes?: string;
}

// UPS API Config (Admin)
export interface UpsApiConfig {
  id: string;
  environment: 'sandbox' | 'production';
  clientId: string;
  accountNumber: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsApiConfigInput {
  environment: 'sandbox' | 'production';
  clientId: string;
  clientSecret: string;
  accountNumber?: string;
  isActive?: boolean;
  notes?: string;
}

// Hermes API Config (Admin)
export interface HermesApiConfig {
  id: string;
  environment: 'sandbox' | 'production';
  partnerId: string;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HermesApiConfigInput {
  environment: 'sandbox' | 'production';
  partnerId: string;
  apiKey: string;
  apiSecret?: string;
  isActive?: boolean;
  notes?: string;
}

interface DhlTenantConfig {
  id: string;
  is_active: boolean;
  environment: 'sandbox' | 'production';
  gkp_username: string | null;
  ekp: string | null;
  billing_number_paket: string | null;
  billing_number_international: string | null;
  billing_number_retoure: string | null;
  billing_number_warenpost: string | null;
  sender_name: string | null;
  sender_company: string | null;
  sender_street: string | null;
  sender_street_number: string | null;
  sender_postal_code: string | null;
  sender_city: string | null;
  sender_country_code: string;
  sender_email: string | null;
  sender_phone: string | null;
  use_different_return_address: boolean;
  return_name: string | null;
  return_company: string | null;
  return_street: string | null;
  return_street_number: string | null;
  return_postal_code: string | null;
  return_city: string | null;
  return_country_code: string;
  service_paket: boolean;
  service_paket_international: boolean;
  service_warenpost: boolean;
  service_retoure: boolean;
  service_express: boolean;
  service_premium: boolean;
  service_ident_check: boolean;
  service_ident_check_min_age: number;
  service_visual_age_check: boolean;
  service_visual_age_check_type: string | null;
  service_cod: boolean;
  service_additional_insurance: boolean;
  service_bulky_goods: boolean;
  service_preferred_day: boolean;
  service_preferred_location: boolean;
  service_preferred_neighbour: boolean;
  service_no_neighbor_delivery: boolean;
  service_named_person_only: boolean;
  service_endorsement: string | null;
  service_parcel_outlet_routing: boolean;
  service_pddp: boolean;
  default_product: string;
  default_weight: number;
  default_length: number | null;
  default_width: number | null;
  default_height: number | null;
  label_format: string;
  print_only_if_codeable: boolean;
  labels_created: number;
  last_label_at: string | null;
  cached_products: DhlProduct[];
  cached_services: DhlService[];
  cache_updated_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Ergebnis des DHL-Verbindungstests.
 *
 * Die Schritte tragen einen **Schluessel**, keinen fertigen Text — uebersetzt wird in der
 * Oberflaeche, damit die Meldung in der eingestellten Sprache erscheint (gleiches Muster wie
 * die Carrier-Fehlerdeutung).
 */
export interface DhlVerbindungstestSchritt {
  schluessel: string;
  /** `hinweis` = kein Fehler, aber erwaehnenswert (z. B. fehlende Abrechnungsnummer fuer Retouren). */
  status: 'ok' | 'fehler' | 'uebersprungen' | 'hinweis';
  detail: string | null;
}

export interface DhlVerbindungstest {
  success: boolean;
  environment: string;
  mitEchtemEtikett: boolean;
  schritte: DhlVerbindungstestSchritt[];
  /** Nur bei echtem Etikett belegt. */
  sendungsnummer: string | null;
  /** 🚨 true = Sendung wurde erzeugt, aber NICHT storniert — sie wird abgerechnet. */
  stornoOffen: boolean;
  deutung: { feld?: string; schluessel?: string; [k: string]: unknown } | null;
}

interface DhlTenantConfigInput {
  is_active?: boolean;
  environment?: 'sandbox' | 'production';
  gkp_username?: string;
  gkp_password?: string;
  ekp?: string;
  billing_number_paket?: string;
  billing_number_international?: string;
  billing_number_retoure?: string;
  billing_number_warenpost?: string;
  sender_name?: string;
  sender_company?: string;
  sender_street?: string;
  sender_street_number?: string;
  sender_postal_code?: string;
  sender_city?: string;
  sender_country_code?: string;
  sender_email?: string;
  sender_phone?: string;
  use_different_return_address?: boolean;
  return_name?: string;
  return_company?: string;
  return_street?: string;
  return_street_number?: string;
  return_postal_code?: string;
  return_city?: string;
  return_country_code?: string;
  service_paket?: boolean;
  service_paket_international?: boolean;
  service_warenpost?: boolean;
  service_retoure?: boolean;
  service_express?: boolean;
  service_premium?: boolean;
  service_ident_check?: boolean;
  service_ident_check_min_age?: number;
  service_visual_age_check?: boolean;
  service_visual_age_check_type?: string;
  service_cod?: boolean;
  service_additional_insurance?: boolean;
  service_bulky_goods?: boolean;
  service_preferred_day?: boolean;
  service_preferred_location?: boolean;
  service_preferred_neighbour?: boolean;
  service_no_neighbor_delivery?: boolean;
  service_named_person_only?: boolean;
  service_endorsement?: string;
  service_parcel_outlet_routing?: boolean;
  service_pddp?: boolean;
  default_product?: string;
  default_weight?: number;
  default_length?: number;
  default_width?: number;
  default_height?: number;
  label_format?: string;
  print_only_if_codeable?: boolean;
}

// DPD Tenant Config
export interface DpdTenantConfig {
  id: string;
  configured: boolean;
  isActive: boolean;
  sandbox: boolean;
  delisId: string | null;
  depot: string | null;
  shipperName: string | null;
  shipperName2: string | null;
  shipperStreet: string | null;
  shipperHouseNo: string | null;
  shipperPostalCode: string | null;
  shipperCity: string | null;
  shipperCountryCode: string;
  shipperPhone: string | null;
  shipperEmail: string | null;
  returnAddressDifferent: boolean;
  returnName: string | null;
  returnName2: string | null;
  returnStreet: string | null;
  returnHouseNo: string | null;
  returnPostalCode: string | null;
  returnCity: string | null;
  returnCountryCode: string;
  returnPhone: string | null;
  returnEmail: string | null;
  defaultProduct: string;
  enabledServices: string[];
  serviceDefaults: Record<string, unknown> | null;
  labelsCreated: number;
  lastLabelAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DpdTenantConfigInput {
  isActive?: boolean;
  sandbox?: boolean;
  environment?: 'sandbox' | 'production';
  delisId?: string;
  password?: string;
  depot?: string;
  senderName?: string;
  senderCompany?: string;
  senderStreet?: string;
  senderStreetNumber?: string;
  senderPostalCode?: string;
  senderCity?: string;
  senderCountryCode?: string;
  senderPhone?: string;
  senderEmail?: string;
  shipperName?: string;
  shipperName2?: string;
  shipperStreet?: string;
  shipperHouseNo?: string;
  shipperPostalCode?: string;
  shipperCity?: string;
  shipperCountryCode?: string;
  shipperPhone?: string;
  shipperEmail?: string;
  returnAddressDifferent?: boolean;
  returnName?: string;
  returnName2?: string;
  returnStreet?: string;
  returnHouseNo?: string;
  returnPostalCode?: string;
  returnCity?: string;
  returnCountryCode?: string;
  returnPhone?: string;
  returnEmail?: string;
  defaultProduct?: string;
  enabledServices?: string[];
  serviceDefaults?: Record<string, unknown>;
}

// DPD Shipment types
export interface DpdShipment {
  id: string;
  dpdConfigId: string | null;
  trackingNumber: string;
  parcelLabelNumber: string | null;
  orderId: string | null;
  product: string;
  weight: number;
  length: number | null;
  width: number | null;
  height: number | null;
  recipientName: string;
  recipientCompany: string | null;
  recipientStreet: string;
  recipientHouseNo: string;
  recipientPostalCode: string;
  recipientCity: string;
  recipientCountryCode: string;
  recipientEmail: string | null;
  recipientPhone: string | null;
  isReturnShipment: boolean;
  status: string;
  labelUrl: string | null;
  labelFormat: string;
  services: string[];
  reference1: string | null;
  reference2: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DpdShipmentInput {
  orderId?: string;
  product?: string;
  weight: number;
  length?: number;
  width?: number;
  height?: number;
  recipientName: string;
  recipientCompany?: string;
  recipientStreet: string;
  recipientHouseNo: string;
  recipientPostalCode: string;
  recipientCity: string;
  recipientCountryCode?: string;
  recipientEmail?: string;
  recipientPhone?: string;
  services?: string[];
  reference1?: string;
  reference2?: string;
  labelFormat?: string;
  codAmount?: number;
  codCurrency?: string;
  insuranceAmount?: number;
}

export interface DpdShipmentResult {
  success: boolean;
  shipmentId: string;
  trackingNumber: string;
  parcelLabelNumber: string | null;
  labelUrl: string | null;
  labelBase64: string | null;
  message: string;
}

export interface DpdTrackingEvent {
  date: string;
  time: string;
  status: string;
  description: string;
  location: string | null;
  depot: string | null;
}

export interface DpdTrackingResult {
  success: boolean;
  trackingNumber: string;
  status: string;
  statusCode: string | null;
  estimatedDelivery: string | null;
  events: DpdTrackingEvent[];
}

export interface DpdStats {
  totalShipments: number;
  shipmentsThisMonth: number;
  shipmentsThisWeek: number;
  shipmentsToday: number;
  returnShipments: number;
}

// GLS Tenant Config
export interface GlsTenantConfig {
  id: string;
  isActive: boolean;
  environment: 'sandbox' | 'production';
  contactId: string | null;
  customerId: string | null;
  senderName: string | null;
  senderCompany: string | null;
  senderStreet: string | null;
  senderStreetNumber: string | null;
  senderPostalCode: string | null;
  senderCity: string | null;
  senderCountryCode: string | null;
  senderPhone: string | null;
  senderEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GlsTenantConfigInput {
  isActive?: boolean;
  environment?: 'sandbox' | 'production';
  contactId?: string;
  password?: string;
  customerId?: string;
  senderName?: string;
  senderCompany?: string;
  senderStreet?: string;
  senderStreetNumber?: string;
  senderPostalCode?: string;
  senderCity?: string;
  senderCountryCode?: string;
  senderPhone?: string;
  senderEmail?: string;
}

// UPS Tenant Config
export interface UpsTenantConfig {
  id: string;
  isActive: boolean;
  environment: 'sandbox' | 'production';
  accountNumber: string | null;
  senderName: string | null;
  senderCompany: string | null;
  senderStreet: string | null;
  senderStreetNumber: string | null;
  senderPostalCode: string | null;
  senderCity: string | null;
  senderCountryCode: string | null;
  senderPhone: string | null;
  senderEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsTenantConfigInput {
  isActive?: boolean;
  environment?: 'sandbox' | 'production';
  clientId?: string;
  clientSecret?: string;
  accountNumber?: string;
  senderName?: string;
  senderCompany?: string;
  senderStreet?: string;
  senderStreetNumber?: string;
  senderPostalCode?: string;
  senderCity?: string;
  senderCountryCode?: string;
  senderPhone?: string;
  senderEmail?: string;
}

// Hermes Tenant Config
export interface HermesTenantConfig {
  id: string;
  isActive: boolean;
  environment: 'sandbox' | 'production';
  partnerId: string | null;
  senderName: string | null;
  senderCompany: string | null;
  senderStreet: string | null;
  senderStreetNumber: string | null;
  senderPostalCode: string | null;
  senderCity: string | null;
  senderCountryCode: string | null;
  senderPhone: string | null;
  senderEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HermesTenantConfigInput {
  isActive?: boolean;
  environment?: 'sandbox' | 'production';
  partnerId?: string;
  apiKey?: string;
  apiSecret?: string;
  senderName?: string;
  senderCompany?: string;
  senderStreet?: string;
  senderStreetNumber?: string;
  senderPostalCode?: string;
  senderCity?: string;
  senderCountryCode?: string;
  senderPhone?: string;
  senderEmail?: string;
}

// Deutsche Post INTERNETMARKE (Portokasse des Mandanten)
export interface InternetmarkeConfig {
  id: string | null;
  name: string;
  environment: 'sandbox' | 'production';
  isActive: boolean;
  portokasseUsername: string;
  /** Das Passwort wird nie ausgeliefert — nur ob eines hinterlegt ist. */
  hasPortokassePassword: boolean;
  senderAddressType: 'company' | 'warehouse' | 'custom';
  senderWarehouseId: string | null;
  senderCompany: string | null;
  senderName: string | null;
  senderStreet: string | null;
  senderStreetNumber: string | null;
  senderPostalCode: string | null;
  senderCity: string | null;
  senderCountryCode: string;
  senderEmail: string | null;
  senderPhone: string | null;
  defaultPageFormat: number | null;
  printSenderOnVoucher: boolean;
  walletBalanceCents: number | null;
  walletCheckedAt: string | null;
  catalogFetchedAt: string | null;
}

export interface InternetmarkeConfigInput {
  name?: string;
  environment?: 'sandbox' | 'production';
  isActive?: boolean;
  portokasseUsername?: string;
  /** Leer lassen heißt „unverändert" — ein leerer Wert löscht das hinterlegte Passwort NICHT. */
  portokassePassword?: string;
  senderAddressType?: 'company' | 'warehouse' | 'custom';
  senderWarehouseId?: string | null;
  senderCompany?: string;
  senderName?: string;
  senderStreet?: string;
  senderStreetNumber?: string;
  senderPostalCode?: string;
  senderCity?: string;
  senderCountryCode?: string;
  senderEmail?: string;
  senderPhone?: string;
  defaultPageFormat?: number | null;
  printSenderOnVoucher?: boolean;
}

export interface InternetmarkeProduct {
  productCode: string;
  name: string;
  description?: string | null;
  /** Preis in Cent — Porto ist nach § 4 Nr. 11b UStG umsatzsteuerfrei, netto = brutto. */
  priceCents: number | null;
  maxWeightGrams: number | null;
  domestic?: boolean | null;
}

export interface InternetmarkeCatalog {
  products: InternetmarkeProduct[];
  fetchedAt: string | null;
  cached: boolean;
}

export interface InternetmarkeVoucherResult {
  success: boolean;
  voucherId: string;
  /** Nur bei Produkten mit Sendungsverfolgung (Einschreiben) — sonst null. */
  trackingNumber: string | null;
  labelUrl: string | null;
  labelAvailable: boolean;
  labelPrinted: boolean;
  printJobId: string | null;
  priceCents: number;
  walletBalanceCents: number | null;
}

// PhotoRoom Admin Config (SuperAdmin - Central Platform Config)
export interface PhotoRoomAppConfig {
  id: string;
  environment: 'sandbox' | 'production';
  apiKeyMasked: string;
  isActive: boolean;
  serviceEnabled: boolean;
  plan: 'free' | 'basic' | 'pro' | 'enterprise';
  monthlyQuota: number;
  usedThisMonth: number;
  remainingQuota: number;
  pricePerImageCents: number;
  quotaResetDate: string | null;
  createdAt: string;
  updatedAt: string;
  // Feature toggles
  bgColorEnabled: boolean;
  resizeEnabled: boolean;
  cropEnabled: boolean;
}

export interface PhotoRoomAppConfigInput {
  apiKey: string;
  isActive?: boolean;
  serviceEnabled?: boolean;
  plan?: 'free' | 'basic' | 'pro' | 'enterprise';
  monthlyQuota?: number;
  pricePerImageCents?: number;
  // Feature toggles
  bgColorEnabled?: boolean;
  resizeEnabled?: boolean;
  cropEnabled?: boolean;
}

export interface PhotoRoomAppConfigListResponse {
  configs: PhotoRoomAppConfig[];
  environments: Array<{ value: string; label: string }>;
  plans: Array<{ value: string; label: string; quota: number }>;
}

export interface PhotoRoomAppConfigStats {
  stats: Record<string, {
    used: number;
    quota: number;
    remaining: number;
    percentUsed: number;
    resetDate: string | null;
  }>;
}

// AI API Config (Central KI-Provider Management)
export interface AiApiConfigResponse {
  id: string;
  provider: string;
  environment: string;
  apiKeyMasked: string;
  hasApiSecret: boolean;
  model: string | null;
  region: string | null;
  isActive: boolean;
  lastValidated: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AiApiProviderMeta {
  name: string;
  description: string;
  keyFormat: string;
  hasSecret: boolean;
  hasModel: boolean;
  hasRegion: boolean;
  docsUrl: string;
  models?: string[];
  regions?: string[];
}

// OAuth Settings (Google Login)
export interface OAuthSettings {
  id: string;
  provider: string;
  clientId: string;
  clientIdMasked?: string;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OAuthSettingsInput {
  clientId: string;
  clientSecret: string;
  isEnabled: boolean;
}

interface DhlProduct {
  code: string;
  name: string;
  description: string;
  domestic?: boolean;
  international?: boolean;
  requiresCustoms?: boolean;
}

interface DhlService {
  code: string;
  name: string;
  description: string;
  category: string;
  products: string[];
  requiresValue?: boolean;
  valueType?: string;
  options?: string[];
  optionLabels?: Record<string, string>;
  defaults?: Record<string, unknown>;
}

interface DhlShipment {
  id: string;
  dhlConfigId: string | null;
  orderId: string | null;
  orderNumber: string | null;
  shipmentNumber: string;
  trackingNumber: string;
  product: string;
  recipient: {
    name: string;
    company: string | null;
    street: string;
    streetNumber: string | null;
    postalCode: string;
    city: string;
    countryCode: string;
    email: string | null;
    phone: string | null;
  };
  weight: number;
  length: number | null;
  width: number | null;
  height: number | null;
  servicesUsed: string[];
  status: string;
  trackingStatus: string | null;
  labelUrl: string | null;
  lastTrackingAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DhlShipmentListItem {
  id: string;
  shipmentNumber: string;
  trackingNumber: string;
  orderNumber: string | null;
  product: string;
  recipientName: string;
  recipientCompany: string | null;
  recipientCity: string;
  recipientPostalCode: string;
  status: string;
  labelUrl: string | null;
  createdAt: string;
}

interface DhlShipmentListResponse {
  data: DhlShipmentListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface DhlShipmentInput {
  order_id?: string;
  order_number?: string;
  reference?: string;
  product?: string;
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
  recipient: {
    name: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    street: string;
    streetNumber?: string;
    postalCode: string;
    city: string;
    countryCode?: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string;
  };
  services?: Record<string, boolean | object>;
  cod_amount?: number;
  insurance_amount?: number;
  ident_check?: boolean;
  named_person_only?: boolean;
  no_neighbor_delivery?: boolean;
  preferred_day?: string;
  preferred_location?: string;
  preferred_neighbour?: string;
}

interface DhlShipmentResult {
  success: boolean;
  shipment_id: string;
  shipment_number: string;
  tracking_number: string;
  label_url: string | null;
  qr_label_url: string | null;
}

interface DhlTrackingEvent {
  timestamp: string;
  location: string;
  status: string;
  description: string;
}

interface DhlTrackingResult {
  id: string;
  status: {
    status: string;
    statusCode: string;
    description: string;
  };
  events: DhlTrackingEvent[];
  destination: {
    city: string;
    countryCode: string;
  };
  estimatedDelivery?: string;
}

interface DhlStats {
  configured: boolean;
  environment?: 'sandbox' | 'production';
  labelsCreated: number;
  lastLabelAt: string | null;
  todayCount: number;
  monthCount: number;
  statusCounts: Record<string, number>;
}

interface DhlAddressValidation {
  valid: boolean;
  errors: string[];
}

interface Article {
  id: string;
  sku: string;
  name: string;
  shortDescription?: string | null;
  longDescription?: string | null;
  ean?: string | null;
  upc?: string | null;
  isbn?: string | null;
  mpn?: string | null;
  asin?: string | null;
  manufacturer?: string | null;
  brand?: string | null;
  categoryPath?: string | null;
  tags?: string[] | null;
  purchasePrice?: number | null;
  sellingPrice: number;
  recommendedRetailPrice?: number | null;
  vatRate: number;
  taxClassCode?: string;
  /** V-2198: Klasse beim Laden der Maske — der Server wertet nur ein wirklich geaendertes Feld als Wahl. */
  taxClassCodeLoaded?: string | null;
  priceUnit?: string | null;
  stock: number;
  minStock: number;
  reservedStock: number;
  stockLocation?: string | null;
  weight?: number | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  trackSerial: boolean;
  trackBatch: boolean;
  requiresExpiry: boolean;
  turnoverFrequency?: number;
  lastSpotCheckAt?: string;
  spotCheckCount?: number;
  isVariant: boolean;
  parentSku?: string | null;
  variantAttributes?: Record<string, string> | null;
  images: ArticleImage[];
  supplierSku?: string | null;
  customsTariffNumber?: string | null;
  countryOfOrigin?: string | null;
  isDangerous: boolean;
  isOversized: boolean;
  status: 'active' | 'inactive' | 'discontinued';
  isPublished: boolean;
  productSafety?: Record<string, unknown> | null;
  marketplaces?: ArticleMarketplaces | null;
  customFields?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: string | null;
}

interface ArticleListResponse {
  articles: Article[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ArticleStats {
  total: number;
  active: number;
  inactive: number;
  critical: number;
  lowStock: number;
  outOfStock: number;
  totalUnits: number;
  /**
   * Lagerwert zum Einkaufspreis (`SUM(stock * purchase_price)`).
   *
   * 🚨 **Optional, weil der Server das Feld WEGLAESST**, wenn die Rolle kein Kostenrecht
   * (`cost_visibility`) hat — nicht, weil es manchmal fehlt. Wer hier `?? 0` schreibt, macht
   * aus „nicht sichtbar" ein „0,00 €" und damit aus einer Berechtigung einen Datenfehler.
   */
  totalValue?: number;
}

interface ArticleCreateInput {
  sku: string;
  name: string;
  shortDescription?: string;
  longDescription?: string;
  ean?: string;
  manufacturer?: string;
  brand?: string;
  purchasePrice?: number;
  sellingPrice: number;
  vatRate?: number;
  taxClassCode?: string;
  stock?: number;
  minStock?: number;
  weight?: number;
  shippingWeight?: number;
  tags?: string[];
  images?: ArticleImage[];
  status?: 'active' | 'inactive' | 'discontinued';
  isPublished?: boolean;
  trackSerial?: boolean;
  trackBatch?: boolean;
  requiresExpiry?: boolean;
  stockManagementActive?: boolean;
  isVariant?: boolean;
}

interface ArticleUpdateInput {
  sku?: string;
  name?: string;
  shortDescription?: string | null;
  longDescription?: string | null;
  ean?: string | null;
  manufacturer?: string | null;
  brand?: string | null;
  purchasePrice?: number | null;
  sellingPrice?: number;
  vatRate?: number;
  taxClassCode?: string;
  priceUnit?: string | null;
  stock?: number;
  minStock?: number;
  stockLocation?: string | null;
  images?: ArticleImage[];
  status?: 'active' | 'inactive' | 'discontinued';
  isPublished?: boolean;
  trackSerial?: boolean;
  trackBatch?: boolean;
  requiresExpiry?: boolean;
  isVariant?: boolean;
  categoryId?: string | null;
  categoryIds?: string[]; // Multiple categories support
  // Grundpreis / Inhalt (Base Price / Content)
  contentQuantity?: number | null;
  contentUnit?: string | null;
  showBasePrice?: boolean;
  basePriceQuantity?: number | null;
  basePriceReferenceUnit?: string | null;
  // Sonderpreis (Special Price)
  specialPrice?: number | null;
  specialPriceFrom?: string | null;
  specialPriceTo?: string | null;
  // Pfand (Deposit)
  depositType?: string | null;
  depositAmount?: number | null;
  depositCountry?: string | null;
  // Kistenpfand-Automatik (POS-Kasse fuegt bei Schwelle eine Kistenpfand-Position hinzu)
  crateDepositThreshold?: number | null;
  crateDepositAmount?: number | null;
  crateDepositSku?: string | null;
  // Description / Translations
  printName?: string | null;
  nameTranslations?: Record<string, string> | null;
  shortDescriptionTranslations?: Record<string, string> | null;
  longDescriptionTranslations?: Record<string, string> | null;
  // Service / Terminplaner — bidirektionaler Sync mit POS-Kasse (showInAppointments / serviceDurationMinutes)
  isService?: boolean;
  serviceDuration?: number | null;
}

// Customer types
interface CustomerAddress {
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}

type BillingCycle = 'weekly' | 'biweekly' | 'monthly' | 'quarterly';
type PreferredLanguage = 'de' | 'en' | 'th';

interface Customer {
  id: string;
  customerNumber: string;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  // Eigene Rechnungs-Adresse; der CustomerController fuehrt die Spalte mit.
  invoiceEmail?: string | null;
  /** V-1130: gehen bei jeder Beleg-Mail an diesen Kunden in Kopie (CC) bzw. Blindkopie (BCC) mit. */
  beleg_cc?: string[];
  beleg_bcc?: string[];
  phone: string | null;
  // 🚨 Der Server liefert `mobile` seit jeher mit (`transformCustomer()`), der Typ kannte es nur
  // nicht. Aufgefallen beim Kontaktvermerk „Telefonnummer nicht mehr vergeben": Bei OEZ haben
  // **221 aktive Kunden ausschliesslich eine Mobilnummer** — ohne dieses Feld haette die
  // Kundenakte ihnen den Vermerk gar nicht erst angeboten, obwohl sie erreichbar sind.
  mobile?: string | null;
  vatId: string | null;
  billingAddress: CustomerAddress | null;
  shippingAddress: CustomerAddress | null;
  paymentTerms: number;
  creditLimit: number | null;
  notes: string | null;
  /** V-1239: feste Kundeninfos (Freitext, max. 4.000 Zeichen). */
  kundenprofil?: string | null;
  isActive: boolean;
  preferredLanguage: PreferredLanguage;
  // Collective invoice (Sammelrechnung) settings
  collectiveInvoiceEnabled: boolean;
  billingCycle: BillingCycle;
  billingDay: number;
  minimumInvoiceAmount: number;
  // ── Vertriebssteuerung (CRM) ──────────────────────────────────────────────
  /** Vertriebsverantwortlicher; der Name wird serverseitig aus der Master-DB aufgeloest. */
  ownerId?: string | null;
  ownerName?: string | null;
  /** A/B/C nach Umsatz, D = kein Umsatz im Betrachtungszeitraum (Reaktivierungsfall). */
  abcClass?: AbcClass | null;
  /** V-1576: A+ = Spitze der Klasse A („Rhythmus + Umsatz"); true nur zusammen mit `abcClass === 'A'`. */
  abc_plus?: boolean;
  /** Gesetzt = von Hand eingestuft; die naechtliche Automatik fasst den Kunden nicht an. */
  abcClassLockedAt?: string | null;
  /** Einschaetzung des moeglichen Umsatzes — unabhaengig vom heutigen Ist. */
  potentialClass?: PotentialClass | null;
  /** Geschaetzter Anteil am Gesamteinkauf des Kunden in Prozent. */
  shareOfWallet?: number | null;
  territoryId?: string | null;
  territoryName?: string | null;
  /** 1 = Montag … 7 = Sonntag, aus dem Gebiet uebernommen. */
  territoryTourWeekday?: number | null;
  // ── „Kunde seit" / Umsatzstatus (10.09.2026, CRM-Anrufliste) ────────────────
  /** COALESCE(customerSince, createdAt) — NIE der rohe (oft leere) JTL-Wert allein. */
  customerSince?: string | null;
  /** 'jtl' = echtes JTL-Anlagedatum, 'angelegt' = Rückfall aufs BIS-Importdatum — bei
   *  'angelegt' ist es NICHT das echte Kundenalter. */
  customerSinceQuelle?: 'jtl' | 'angelegt' | null;
  /** 'nie_gekauft' | 'reaktivierung' | null (A/B/C-Kunde oder ohne ABC-Einstufung). Zur
   *  Laufzeit abgeleitet ({@see UmsatzStatus} serverseitig), nicht gespeichert. */
  umsatzStatus?: 'nie_gekauft' | 'reaktivierung' | null;
  /** Letzte Bestellung (TICK-2026-01043/01044) — dieselbe Größe wie `daysSinceLastOrder` der
   *  Anrufliste, hier als Zeitstempel. */
  lastOrderAt?: string | null;
  /** 🚨 `lastOrderAt === null` heißt NICHT „hat nie bestellt": Ist dieses Flag gesetzt, wurde
   *  der Kunde VOR dem 24-Monats-Abgleichsfenster angelegt — in JTL können ältere Bestellungen
   *  liegen, die BIS nicht erfasst hat. */
  lastOrderUnbekannt?: boolean;
  /** V-1236: Woher `lastOrderAt` stammt — 'bis' = ein Auftrag in BIS ERP, 'vorsystem' = die JTL-Kopie
   *  (letzte Bestellung VOR der Übernahme), null = keine Bestellung bekannt. */
  lastOrderQuelle?: 'bis' | 'vorsystem' | null;
  /** Karteien, die in diesen Kunden aufgegangen sind — Grundlage des Aufklappers in der Liste.
   *  Leer, wenn es keine gibt; die Liste zaehlt sie NICHT als eigene Zeilen mit. */
  mergedAccounts?: ZugeordneteKartei[];
  createdAt: string;
  updatedAt: string;
}

type AbcClass = 'A' | 'B' | 'C' | 'D';

/** V-1576: Grenzen des Verfahrens „Rhythmus + Umsatz". `a_plus_ab_monatsumsatz = null` = A+ aus. */
interface KundenAbcRhythmusGrenzen {
  a_ab_monaten: number | null;
  b_ab_monaten: number | null;
  a_plus_ab_monatsumsatz: number | null;
  a_plus_aktiv_monate: number | null;
}

interface KundenAbcEinstellungen {
  verfahren: 'pareto' | 'rhythmus';
  rhythmus: KundenAbcRhythmusGrenzen;
  geaendert_at: string | null;
  geaendert_von: string | null;
  fehler: Record<string, string>;
}

interface KundenAbcEinstellungenAntwort {
  einstellungen: KundenAbcEinstellungen;
  werksstand: { verfahren: 'pareto'; rhythmus: KundenAbcRhythmusGrenzen };
}

interface KundenAbcEinstellungenEingabe {
  verfahren: 'pareto' | 'rhythmus';
  rhythmus: KundenAbcRhythmusGrenzen | Record<string, number | string | null>;
}

interface KundenAbcVorschau {
  verfahren: 'pareto' | 'rhythmus';
  verteilung_heute: Record<string, number>;
  verteilung_neu: Record<string, number>;
  wechsel: number;
  handeinstufungen: number;
  grund: string | null;
}
type PotentialClass = 'A' | 'B' | 'C';

/** Vertriebsgebiet mit Tourtag — Katalog hinter `customers.territoryId`. */
/** Eine Sortimentslücke: Warengruppe, die vergleichbare Kunden kaufen und dieser nicht. */
interface AssortmentGap {
  categoryId: string;
  categoryName: string;
  /** Wie viele der Vergleichsgruppe diese Warengruppe kaufen. */
  peerBuyers: number;
  /** Anteil der Vergleichsgruppe (0…1). */
  peerShare: number;
  /** Jahresumsatz eines KÄUFERS in dieser Warengruppe — nicht der Schnitt über die ganze Gruppe. */
  peerAvgRevenue: number;
}

interface AssortmentGapResponse {
  /**
   * `ok` | `zu_wenig_vergleich` | `linse_nicht_anwendbar` | `kunde_unbekannt` | `fehler`
   *
   * 🚨 Eine leere Liste allein sagt nicht, ob es keine Lücken gibt oder die Vergleichsgruppe zu
   * klein war — deshalb trägt die Antwort immer den Grund.
   */
  datenlage: string;
  lens: string;
  peerCount: number;
  minShare: number;
  gaps: AssortmentGap[];
}

/** Ein Gebietsvorschlag aus den vorhandenen Postleitzahlen. */
interface TerritorySuggestion {
  /** PLZ-Präfix, zwei- oder dreistellig. */
  prefix: string;
  customerCount: number;
  /** Netto-Umsatz der letzten 365 Tage in diesem Cluster. */
  revenue365: number;
  /** Vorschlag aus dem häufigsten Ort — der Anwender ändert ihn ohnehin. */
  suggestedName: string;
  /** Deckt ein bestehendes Gebiet diesen Präfix schon ab? */
  alreadyCovered: boolean;
}

interface SalesTerritory {
  id: string;
  name: string;
  /** 1 = Montag … 7 = Sonntag. null = kein fester Tourtag. */
  tourWeekday: number | null;
  /** Freitext, weil die Regel je Betrieb anders lautet („Mi 12:00"). */
  orderDeadline: string | null;
  minOrderValue: number | null;
  notes: string | null;
  /** Anfangsziffern der PLZ, kommagetrennt („33,32") — Grundlage der Massenzuordnung. */
  postalCodes: string | null;
  isActive: boolean;
  /** Wie viele Kunden dem Gebiet zugeordnet sind. */
  customerCount: number;
}

/** Ein Gebiet in der Vor-Tour-Liste, samt Füllstand und offenen Kunden. */
interface TourPreview {
  territoryId: string;
  name: string;
  /** 1 = Montag … 7 = Sonntag. */
  tourWeekday: number | null;
  /** Tage bis zur Tour; 0 = heute. */
  daysUntilTour: number | null;
  orderDeadline: string | null;
  minOrderValue: number | null;
  customerCount: number;
  orderedCount: number;
  openCount: number;
  /** Bereits bestellter Netto-Umsatz für diese Tour. */
  revenue: number;
  weightKg: number;
  /** 🚨 Positionen ohne Gewichtsangabe — ohne diese Zahl liest sich eine unvollständige
   *  Tonnage wie eine vollständige. */
  positionsWithoutWeight: number;
  customers: {
    customerId: string;
    customerNumber: string | null;
    name: string;
    email: string | null;
    phone: string | null;
    /** V-1238: Mobilnummer getrennt (`phone` = Festnetz, sonst Mobil). */
    mobile?: string | null;
    abcClass: string | null;
    potentialClass: string | null;
    daysSinceLastOrder: number | null;
    lastOrderAt: string | null;
    revenue90: number;
    /** Netto-Umsatz über die gesamte Historie — Grundlage der Sortierung „Höchster Umsatz". */
    revenueTotal: number;
    orderCount: number;
    /** Durchschnittlicher Abstand zwischen Bestellungen in Tagen.
     *  🚨 `null` heißt „zu wenig Bestellungen für eine Aussage" (unter 3) — nicht 0. */
    orderRhythmDays: number | null;
  }[];
  /** Übersprungene Privatkunden (ohne Firma). Die Tour zeigt nur Händler; ohne diese Zahl
   *  liest sich die kürzere Liste als fehlender Kunde. */
  hiddenPrivateCount: number;
}

/** Ein Packmittel aus dem Katalog — Palette, Colli oder Karton. */
interface PackagingOption {
  id: string;
  code: string;
  name: string;
  /** 'pallet' | 'colli' | 'box' | 'envelope' | 'tube' | 'bag' */
  kind: string;
  /** Außenmaße in cm; null, wenn nicht gepflegt. */
  length: number | null;
  width: number | null;
  height: number | null;
  emptyWeightKg: number;
  maxWeightKg: number;
}

interface SalesTerritoryInput {
  name: string;
  tourWeekday?: number | null;
  orderDeadline?: string | null;
  minOrderValue?: number | null;
  notes?: string | null;
  postalCodes?: string | null;
  isActive?: boolean;
}

interface CustomerListResponse {
  customers: Customer[];
  total: number;
  page: number;
  limit: number;
  pages: number;
  /**
   * V-0206: `true`, wenn der Server nur die Auswahlliste geliefert hat (Belegerfasser ohne
   * „Kunden ansehen“): höchstens 20 Treffer mit Nummer, Name und Ort, `total` ist dann die Zahl der
   * gelieferten Zeilen — nicht die Größe der Kundendatei.
   */
  eingeschraenkt?: boolean;
}

interface CustomerInput {
  companyName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  /** V-1130: Beleg-CC/-BCC — je höchstens 10, nicht dieselbe Adresse in beiden. */
  beleg_cc?: string[];
  beleg_bcc?: string[];
  phone?: string;
  vatId?: string;
  billingAddress?: CustomerAddress;
  shippingAddress?: CustomerAddress;
  paymentTerms?: number;
  creditLimit?: number;
  notes?: string;
  /** V-1239: Kundenprofil — nur über PUT; leer/null löscht. */
  kundenprofil?: string | null;
  isActive?: boolean;
  preferredLanguage?: PreferredLanguage;
}

interface BillingSettingsInput {
  collectiveInvoiceEnabled?: boolean;
  billingCycle?: BillingCycle;
  billingDay?: number;
  minimumInvoiceAmount?: number;
}

interface BillingSettingsResponse {
  success: boolean;
  billingSettings: {
    collectiveInvoiceEnabled: boolean;
    billingCycle: BillingCycle;
    billingDay: number;
    minimumInvoiceAmount: number;
  };
}

type AddressType = 'billing' | 'shipping' | 'both';

interface CustomerAddressBookEntry {
  id: string;
  customerId: string;
  type: AddressType;
  label: string | null;
  isDefault: boolean;
  company: string | null;
  firstName: string | null;
  lastName: string | null;
  street: string | null;
  houseNumber: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  state: string | null;
  countryCode: string;
  phone: string | null;
  email: string | null;
  source: string;
  createdAt: string;
  updatedAt: string;
}

interface CustomerAddressInput {
  type?: AddressType;
  label?: string;
  isDefault?: boolean;
  company?: string;
  firstName?: string;
  lastName?: string;
  street?: string;
  houseNumber?: string;
  addressLine2?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  countryCode?: string;
  phone?: string;
  email?: string;
  source?: string;
}

// Customer CRM types - Activities, Communications, Documents, 360° View
// 'contact' neu 10.09.2026: Kontaktversuch aus der CRM-Anrufliste ({@see CrmTodayController}).
// 'follow_up_reopened' neu 07.10.2026 (V-1671): Spur einer Rücknahme des Wiedervorlage-Hakens (kein Kontakt).
type ActivityType = 'order' | 'invoice' | 'payment' | 'return' | 'note' | 'email' | 'call' | 'contact' | 'follow_up_reopened';
type CommunicationType = 'email' | 'call' | 'note' | 'meeting';
type CommunicationDirection = 'inbound' | 'outbound';
type DocumentType = 'contract' | 'certificate' | 'invoice' | 'other';

interface CustomerActivity {
  id: string;
  activityType: ActivityType;
  referenceType?: string | null;
  referenceId?: string | null;
  title: string;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  createdBy?: string | null;
  /** Anzeigename des Verfassers — serverseitig aus der Master-DB aufgelöst. */
  createdByName?: string | null;
  /** Wiedervorlage-Datum (`YYYY-MM-DD`) eines `contact`-Vermerks, oder null. */
  followUpAt?: string | null;
  /** V-1671: Wiedervorlage abgehakt — Zeitpunkt (UTC) und Name des Abhakenden, sonst `null`. */
  follow_up_erledigt_am?: string | null;
  follow_up_erledigt_von_name?: string | null;
  createdAt: string;
}

interface CustomerActivityInput {
  activityType?: ActivityType;
  referenceType?: string;
  referenceId?: string;
  title: string;
  description?: string;
  metadata?: Record<string, unknown>;
}

interface CustomerActivityListResponse {
  activities: CustomerActivity[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface CustomerCommunication {
  id: string;
  commType: CommunicationType;
  direction: CommunicationDirection;
  subject?: string | null;
  content: string;
  contactPerson?: string | null;
  metadata?: Record<string, unknown> | null;
  createdBy?: string | null;
  createdAt: string;
}

interface CustomerCommunicationInput {
  commType?: CommunicationType;
  direction?: CommunicationDirection;
  subject?: string;
  content: string;
  contactPerson?: string;
  metadata?: Record<string, unknown>;
}

interface CustomerCommunicationListResponse {
  communications: CustomerCommunication[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/** V-2443: Zustand einer Mail im E-Mail-Verlauf — „angenommen" heißt NUR „vom Mailserver angenommen", nie zugestellt/gelesen. */
export type MailVerlaufStatus =
  | 'angenommen' | 'umgeleitet' | 'zurueckgestellt' | 'ausstehend' | 'fehler' | 'nicht_versendet' | 'unbekannt';

/** V-2443: Eine Mail im E-Mail-Verlauf eines Kunden (`GET /customers/{id}/mail-verlauf`, Schlüssel snake_case). */
export interface CustomerMailVerlaufEintrag {
  datum: string;
  art: string;
  art_bezeichnung: string;
  bezug_art: string | null;
  bezug_nummer: string | null;
  bezug_id: string | null;
  bezug_link_erlaubt: boolean;
  status: MailVerlaufStatus;
  versandweg: 'eigener_smtp' | 'bispicy' | null;
  empfaenger: string | null;
  cc: string[];
  bcc_anzahl: number;
  betreff: string | null;
  fehler_kategorie: 'verbindung' | 'anmeldung' | 'empfaenger_abgelehnt' | 'sonstiges' | null;
}

export interface CustomerMailVerlaufResponse {
  mails: CustomerMailVerlaufEintrag[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface CustomerDocument {
  id: string;
  docType: DocumentType;
  name: string;
  filePath: string;
  fileSize?: number | null;
  mimeType?: string | null;
  notes?: string | null;
  validUntil?: string | null;
  createdBy?: string | null;
  createdAt: string;
}

interface CustomerDocumentInput {
  docType?: DocumentType;
  name: string;
  filePath: string;
  fileSize?: number;
  mimeType?: string;
  notes?: string;
  validUntil?: string;
}

interface CustomerDocumentListResponse {
  documents: CustomerDocument[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface CustomerStatistics {
  totalOrders: number;
  /** 🚨 NETTO (Warenwert + Versand) — dieselbe Groesse wie die Umsatzentwicklung darueber.
   *  Die Kachel war bis 28.08.2026 als „brutto" beschriftet, obwohl der Wert seit dem
   *  25.08. netto gerechnet wird (TICK-2026-00835/00843). Beim Anzeigen `totalRevenueGross`
   *  danebenstellen, nicht raten lassen. */
  totalRevenue: number;
  /** BRUTTO (inkl. Umsatzsteuer) desselben Bestand an Auftraegen. */
  totalRevenueGross: number;
  avgOrderValue: number;
  lastOrderDate: string | null;
  openInvoicesAmount: number;
  openInvoicesCount: number;
  // Werden von CustomerController mitgeliefert ('paidInvoicesTotal'/'paidInvoicesCount'),
  // fehlten hier nur.
  paidInvoicesTotal?: number;
  paidInvoicesCount?: number;
  returnsCount: number;
  returnsRate: number;
  totalProfit: number;
  profitMargin: number;
  articlesWithoutCost: number;
  /** Nettoumsatz, auf den sich die Marge bezieht — ohne ihn ist der Prozentwert nicht nachrechenbar. */
  marginBase?: number;
}

/** Umsatz eines Zeitfensters mit Vorperiodenvergleich (30/90/365 Tage). */
interface CustomerRevenuePeriod {
  /** Netto (Warensumme + Versand) — die Leitzahl im B2B. */
  net: number;
  /** Netto der gleich langen Vorperiode davor. */
  netPrevious: number;
  gross: number;
  orders: number;
  ordersPrevious: number;
  /** null, wenn die Vorperiode 0 € war — dann gibt es keinen aussagefaehigen Trend. */
  trendPercent: number | null;
}

/** Bestellrhythmus des Kunden. null, solange zu wenige Bestellungen vorliegen. */
interface CustomerOrderFrequency {
  /** Median-Abstand zwischen zwei Bestellungen in Tagen. */
  medianDays: number;
  daysSinceLastOrder: number | null;
  expectedNextOrder: string | null;
  /** true, wenn der Kunde deutlich ueber seinem eigenen Rhythmus liegt. */
  overdue: boolean;
}

interface Customer360Response {
  customer: Customer;
  statistics: CustomerStatistics;
  /** Schluessel sind die Fensterlaengen in Tagen: '30' | '90' | '365'. */
  revenuePeriods?: Record<string, CustomerRevenuePeriod>;
  orderFrequency?: CustomerOrderFrequency | null;
  recentOrders: Array<{
    id: string;
    orderNumber: string;
    status: string;
    totalAmount: number;
    createdAt: string;
  }>;
  recentInvoices: Array<{
    id: string;
    invoiceNumber: string;
    status: string;
    total: number;
    invoiceDate: string;
  }>;
  openInvoices: Array<{
    id: string;
    invoiceNumber: string;
    status: string;
    total: number;
    dueDate: string;
  }>;
  recentActivities: CustomerActivity[];
  /** Jüngster `contact`-Vermerk dieses Kunden ({@see KontaktStand} serverseitig), oder null. */
  kontaktStand?: {
    createdAt: string;
    ergebnis: string | null;
    telefon: string | null;
    /** Nur die OFFENE Wiedervorlage (V-1671) — abgehakt heisst `null`. */
    followUpAt: string | null;
    /** ID (RFC 4122) des jüngsten Vermerks — Anker für das Kästchen „erledigt". */
    vermerk_id?: string | null;
    /** Notiz des jüngsten Vermerks (das WARUM der Wiedervorlage). */
    notiz?: string | null;
    /** V-1671: abgehakte Wiedervorlage des jüngsten Vermerks, sonst `null`. */
    wiedervorlage_erledigt?: {
      /** Zeitpunkt des Hakens (UTC, `YYYY-MM-DD HH:MM:SS`). */
      am: string;
      von_name?: string | null;
      /** Ursprünglicher Termin der erledigten Wiedervorlage. */
      faellig_am: string;
      faellig_zeit?: string | null;
    } | null;
    /**
     * 🚨 Ob der Kunde aus der Anrufliste ausgeblendet ist, entscheidet der SERVER
     * (`Service/Crm/KontaktSperre`) — nie ein Vergleich wie `ergebnis === 'kein_interesse'` hier
     * im Browser. Bei `nummer_ungueltig` haengt die Sperre daran, ob sich die hinterlegte Nummer
     * seit dem Vermerk geaendert hat; das ist im Frontend gar nicht entscheidbar.
     */
    gesperrt?: boolean;
    /** Wiedervorlage erreicht/überschritten. */
    faellig?: boolean;
    /** Klartext, z.B. „Kein Interesse" oder „Wiedervorlage am 2026-10-01". */
    sperrGrund?: string | null;
  } | null;
}

interface CustomerOrdersResponse {
  orders: Array<{
    id: string;
    orderNumber: string;
    status: string;
    totalAmount: number;
    currency: string;
    shippingAddress: CustomerAddress | null;
    notes: string | null;
    createdAt: string;
  }>;
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface CustomerInvoicesResponse {
  invoices: Array<{
    id: string;
    invoiceNumber: string;
    type: string;
    status: string;
    total: number;
    currency: string;
    invoiceDate: string;
    dueDate: string;
    invoiceType: string;
    createdAt: string;
  }>;
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// Cancellation Reason types
interface CancellationReason {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive?: boolean;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface CancellationReasonInput {
  code: string;
  name: string;
  description?: string;
  isActive?: boolean;
  sortOrder?: number;
}

// Customer Group types
interface CustomerGroup {
  id: string;
  groupKey: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  isB2B: boolean;
  showPricesNet: boolean;
  showInPosCustomerCreation: boolean;
  sortOrder: number;
  source: string;
  createdAt?: string;
  updatedAt?: string;
}

interface CustomerGroupInput {
  groupKey: string;
  name: string;
  description?: string;
  isDefault?: boolean;
  isActive?: boolean;
  isB2B?: boolean;
  showPricesNet?: boolean;
  showInPosCustomerCreation?: boolean;
  sortOrder?: number;
}

// Article Condition (Artikel-Zustand) types
export interface ArticleCondition {
  id: string;
  jtlZustandId: number | null;
  name: string;
  isActive: boolean;
  sortOrder: number;
  source: string;
}

export interface ArticleConditionInput {
  name: string;
  isActive?: boolean;
  sortOrder?: number;
}

// Modifikatoren (Zubereitung/Beilagen/Größen)
//
// 🚨 `type` MUSS einer der drei Werte sein — die Kasse liest ihn mit `ModifierType.valueOf()`.
// Ein anderer Wert lässt das Anwenden am Gerät scheitern, nicht schon beim Speichern.
export type ModifierType = 'SINGLE_SELECT' | 'MULTI_SELECT' | 'TEXT_INPUT';

export interface ModifierOption {
  id?: string;
  name: string;
  /** Aufpreis in Euro; 0 für Auswahlen ohne Preisfolge (z. B. Garstufe). */
  priceAdjustment: number;
  isDefault: boolean;
  sortOrder?: number;
  isActive?: boolean;
}

export interface ModifierGroup {
  id: string;
  name: string;
  type: ModifierType;
  isRequired: boolean;
  minSelections: number;
  maxSelections: number;
  sortOrder: number;
  isActive: boolean;
  options: ModifierOption[];
  /** Einzeln zugeordnete Artikel (hex-IDs). */
  articleIds: string[];
  /** V-0250: Name je einzeln zugeordnetem Artikel (Schlüssel = hex-ID, klein). */
  article_names?: Record<string, string>;
  /** Zugeordnete Kategorien — gelten für ALLE Artikel darin, auch später hinzugefügte. */
  categoryIds: string[];
  /**
   * Je Kategorie: gilt die Zuordnung auch für deren Unterkategorien? Fehlt ein Eintrag,
   * gilt `true` — das ist die Vorgabe, und ältere Datensätze führen das Feld nicht.
   */
  categoryInherit?: Record<string, boolean>;
  articleCount: number;
  categoryCount: number;
  /**
   * Auf wie viele Artikel die Gruppe tatsächlich wirkt — einzeln zugeordnete plus alle über
   * Kategorien erreichten (inkl. Unterkategorien und Zweitkategorien), ohne Doppelzählung.
   * Etwas anderes als `articleCount`: Das zählt nur die einzeln angehakten.
   */
  effectiveArticleCount?: number;
  /**
   * V-0534: Zähler jeder Inhaltsänderung (Name, Art, Pflicht, Reihenfolge, aktiv, Möglichkeiten,
   * Löschen) — egal ob aus BIS ERP oder von einer Kasse. Beim Speichern als `basis_revision`
   * zurückschicken: Hat sich die Gruppe inzwischen geändert, antwortet der Server mit 409
   * `gruppe_inzwischen_geaendert`, statt eine Kassenänderung still zu überschreiben.
   */
  inhalt_revision?: number;
  /** V-0534: Zeitpunkt der letzten Inhaltsänderung (ISO-8601, UTC), `null` wenn nie gestempelt. */
  inhalt_geaendert_at?: string | null;
  /** V-0534: Quelle der letzten Inhaltsänderung — `bis_erp`, `kasse:<Gerätename>` oder `datenuebernahme`. */
  inhalt_geaendert_quelle?: string | null;
  /**
   * V-0534: Artikel (hex-IDs), die an der Kasse von einer Kategorie-Zuordnung ausgenommen wurden.
   * Eine Ausnahme ist KEINE Zuordnung und zählt nicht in `articleIds`; ihre Namen stehen in
   * `article_names`.
   */
  ausnahme_artikel_ids?: string[];
  /**
   * V-0534 (Änderung 2): IDs aktiver Gruppen mit GLEICHEM Namen (getrimmt, ohne Groß-/Kleinschreibung), aber anderer
   * Art, Pflicht oder anderen Möglichkeiten (Name + Aufpreis). Beim Lesen abgeleitet. Gleiche Gruppen werden beim
   * Kassen-Upload zusammengelegt; diese hier bleiben getrennt und brauchen Aufräumen.
   */
  namensgleich_mit_anderen_moeglichkeiten?: string[];
  /**
   * V-0534: Zähler jeder Änderung der ZUORDNUNG (Artikel-/Kategorie-Links, Ausnahmen) — getrennt von
   * `inhalt_revision`. Beim Speichern der Zuordnung als `basis_zuordnung_revision` zurückschicken: Hat sich
   * die Zuordnung inzwischen geändert (z. B. meldete eine Kasse einen Artikel), antwortet der Server mit 409
   * `zuordnung_inzwischen_geaendert`, statt sie als Ganzes zu überschreiben.
   */
  zuordnung_revision?: number;
  /** V-0534: Zeitpunkt der letzten Zuordnungsänderung (ISO-8601, UTC), `null` wenn nie gestempelt. */
  zuordnung_geaendert_at?: string | null;
}

export interface ModifierGroupInput {
  name: string;
  type?: ModifierType;
  isRequired?: boolean;
  minSelections?: number;
  maxSelections?: number;
  sortOrder?: number;
  isActive?: boolean;
  options?: ModifierOption[];
  /** V-0534: `inhalt_revision`, auf der die Maske aufsetzt (optimistische Sperre, siehe `ModifierGroup`). */
  basis_revision?: number;
}

/**
 * Gang (V-0076, Einstellungen → Artikel → Gänge) — Küchenreihenfolge Getränke → Vorspeise →
 * Hauptgang → Dessert → Extras. Identifiziert über `course_key` (stabil, nie neu vergeben),
 * NICHT über eine interne BINARY-ID. Serverseitig `PosCourseController` (`/pos/courses`),
 * Auflösung „welcher Gang gilt für welchen Artikel" in `Service/Pos/GangZuordnung`.
 */
export interface PosCourse {
  course_key: string;
  name: string;
  sort_order: number;
  color: string | null;
  delay_minutes: number;
  is_active: boolean;
  /** V-1149: Fragt die Kasse beim Bestellen nach dem Gang? Getränke ab Werk nein. */
  ask_on_order?: boolean;
  /** Auf wie viele Artikel der Gang tatsächlich wirkt (Vererbung eingerechnet). Nur in der Liste. */
  effectiveArticleCount?: number;
}

export interface PosCourseInput {
  name?: string;
  sort_order?: number;
  color?: string | null;
  delay_minutes?: number;
  is_active?: boolean;
  ask_on_order?: boolean;
}

// Hold Reason types
interface HoldReason {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive?: boolean;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface HoldReasonInput {
  code: string;
  name: string;
  description?: string;
  isActive?: boolean;
  sortOrder?: number;
}

interface OrderAuditLogEntry {
  id: string;
  action: string;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  userEmail: string | null;
  ipAddress: string | null;
  createdAt: string;
}

// Order types
interface OrderItem {
  id: string;
  articleId: string | null;
  sku: string;
  name: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  discount: number;
  totalPrice: number;
  // Alle folgenden liefert der OrderController mit (Auftragsdetail, Packtisch,
  // Verfügbarkeitsrechnung) — sie fehlten hier nur in der Typdefinition.
  openQuantity?: number;
  shippedQuantity?: number;
  inPackagingQuantity?: number;
  availableStock?: number | null;
  fulfillment?: string | null;
  articleSku?: string | null;
  articleName?: string | null;
  articleImage?: string | null;
  weight?: number | null;
  shippingWeight?: number | null;
  shippingMethodId?: string | null;
  shippingMethodName?: string | null;
  shippingMethodSource?: string | null;
}

interface Order {
  id: string;
  orderNumber: string;
  customerId: string | null;
  customerName: string | null;
  // OrderController liefert `o.shippingEmail as customerEmail`.
  customerEmail?: string | null;
  // Ebenfalls vom OrderController geliefert, hier bisher nicht deklariert.
  channel?: string | null;
  salesChannel?: string | null;
  priority?: string | number | null;
  warehouseId?: string | null;
  mergeGroupId?: string | null;
  shippingMethodId?: string | null;
  status: 'draft' | 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  subtotal: number;
  vatAmount: number;
  shippingCost: number;
  discount: number;
  totalAmount: number;
  currency: string;
  shippingAddress: CustomerAddress | null;
  billingAddress: CustomerAddress | null;
  notes: string | null;
  internalNotes: string | null;
  source: string | null;
  paymentMethod: string | null;
  paymentStatus: string | null;
  items: OrderItem[];
  createdAt: string;
  updatedAt: string;
}

interface OrderListResponse {
  orders: Order[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface OrderStats {
  total: number;
  pending: number;
  processing: number;
  shipped: number;
  delivered: number;
  cancelled: number;
  totalRevenue: number;
}

interface StockStatusItem {
  itemId: string;
  articleId: string | null;
  sku: string;
  name: string;
  required: number;
  available: number;
  canShip: number;
  missing: number;
  inStock: boolean;
}

interface ShippedItem {
  itemId: string;
  sku: string;
  name: string;
  shipped: number;
  remaining: number;
}

interface QuickShipResponse {
  success?: boolean;
  shipmentId?: string;
  shipmentNumber?: string;
  trackingNumber?: string | null;
  labelUrl?: string | null;
  labelAvailable?: boolean;
  orderStatus?: string;
  isPartialShipment?: boolean;
  shippedItems?: ShippedItem[];
  print?: { printed: boolean; printJobId?: string | null; error?: string } | null;
  message: string;
  // Error responses
  error?: string;
  stockStatus?: StockStatusItem[];
  canPartialShip?: boolean;
  requiresConfirmation?: boolean;
}

interface OrderItemInput {
  articleId?: string;
  sku: string;
  name: string;
  quantity: number;
  unitPrice: number;
  vatRate?: number;
  discount?: number;
}

interface OrderInput {
  customerId?: string;
  customerName?: string;
  status?: string;
  shippingAddress?: CustomerAddress;
  billingAddress?: CustomerAddress;
  notes?: string;
  internalNotes?: string;
  source?: string;
  paymentMethod?: string;
  shippingCost?: number;
  discount?: number;
  items?: OrderItemInput[];
}

// Supplier types
interface Supplier {
  id: string;
  supplierNumber: string;
  companyName: string;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  address: CustomerAddress | null;
  vatId: string | null;
  paymentTerms: number;
  currency: string;
  minimumOrder: number | null;
  leadTimeDays: number | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface SupplierListResponse {
  suppliers: Supplier[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface SupplierInput {
  companyName: string;
  shortName?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: CustomerAddress;
  vatId?: string;
  paymentTerms?: number | { paymentDays: number; discount?: number; discountDays?: number; minimumOrderValue?: number };
  currency?: string;
  minimumOrder?: number;
  leadTimeDays?: number;
  notes?: string;
  isActive?: boolean;
  status?: 'active' | 'inactive' | 'blocked' | 'pending';
}

interface SupplierReportConfig {
  enabled: boolean;
  frequency: 'weekly' | 'monthly';
  weekday: number;
  time: string;
  lookbackDays: number;
  sendIfEmpty: boolean;
  additionalRecipients: string[];
}

interface SupplierReportArticle {
  articleId: string | null;
  sku: string;
  name: string;
  ean: string | null;
  quantity: number;
  returnedQuantity: number;
  revenueNet: number;
  revenueGross: number;
}

interface SupplierReportTotals {
  articleCount: number;
  totalQuantity: number;
  totalRevenueNet: number;
  totalRevenueGross: number;
  totalReturnedQuantity: number;
}

interface SupplierReportData {
  supplier: {
    id: string;
    supplierNumber: string | null;
    name: string | null;
    email: string | null;
    contactPerson: string | null;
  };
  period: { from: string; to: string };
  articles: SupplierReportArticle[];
  totals: SupplierReportTotals;
}

interface SupplierReportRun {
  id: string;
  runAt: string;
  periodFrom: string;
  periodTo: string;
  articleCount: number;
  totalQuantity: number;
  totalRevenueNet: number;
  totalRevenueGross: number;
  recipientEmail: string | null;
  status: 'success' | 'skipped' | 'failed';
  errorMessage: string | null;
  triggerSource: 'scheduler' | 'manual';
  reportFileSize: number | null;
}

interface SupplierReportRunResult {
  success: boolean;
  status: 'success' | 'skipped' | 'failed';
  runId: string;
  recipients: string[];
  articleCount: number;
  messageId?: string | null;
  errorMessage?: string;
}

// Storage Location types
interface StorageLocation {
  id: string;
  code: string;
  name: string;
  zone: string | null;
  aisle: string | null;
  rack: string | null;
  level: string | null;
  position: string | null;
  maxCapacity: number | null;
  currentCapacity: number;
  articleId?: string | null;
  // Der InventoryController liefert diese Felder in der Lagerplatz-Liste mit
  // ('shelf' => …, 'isOccupied' => $qty > 0 || $items > 0, …).
  shelf?: string | null;
  type?: string | null;
  isOccupied?: boolean;
  currentQuantity?: number;
  warehouseName?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** Inventurnummer, wenn der Platz in einer offenen Inventur liegt (nur mit stocktakeContext). */
  lockedByStocktake?: string | null;
}

interface StorageLocationInput {
  code: string;
  name: string;
  zone?: string;
  aisle?: string;
  rack?: string;
  level?: string;
  position?: string;
  maxCapacity?: number;
  isActive?: boolean;
}

// Goods Receipt types
interface GoodsReceiptItem {
  id: string;
  articleId: string | null;
  sku: string;
  name: string;
  articleSku?: string;
  articleName?: string;
  expectedQuantity: number;
  receivedQuantity: number;
  /** Wie viel von dieser Position bereits im Bestand steht (Bezugspunkt jeder Korrektur). */
  bookedQuantity?: number;
  unitPrice: number;
  batchNumber: string | null;
  expiryDate: string | null;
  storageLocationId?: string | null;
  storageLocationCode?: string | null;
}

interface GoodsReceipt {
  id: string;
  receiptNumber: string;
  supplierId: string | null;
  supplierName: string | null;
  purchaseOrderId: string | null;
  // Die tatsaechlichen Werte des ENUM `goods_receipts.status` — `partial`/`complete` standen hier,
  // existieren in der Datenbank aber nicht.
  status: 'draft' | 'pending' | 'in_progress' | 'completed' | 'cancelled';
  notes: string | null;
  deliveryNoteNumber?: string | null;
  receivedAt: string | null;
  items: GoodsReceiptItem[];
  createdAt: string;
  updatedAt: string;
}

interface GoodsReceiptListResponse {
  goodsReceipts: GoodsReceipt[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface GoodsReceiptItemInput {
  articleId?: string;
  sku: string;
  name: string;
  expectedQuantity: number;
  receivedQuantity?: number;
  unitPrice?: number;
  batchNumber?: string;
  expiryDate?: string;
  // 🚨 Lagerplatz und Seriennummern werden vom Server seit jeher gelesen
  // ({@see InventoryController::createGoodsReceipt}) und von beiden Masken gesendet — sie fehlten
  // hier nur im Typ. Unbemerkt blieb das, weil die Masken das Nutzlast-Objekt vorher in eine
  // Variable legten: Die Pruefung auf ueberzaehlige Felder greift nur bei einem direkt
  // uebergebenen Objektliteral.
  storageLocationCode?: string;
  serialNumbers?: string[];
}

interface GoodsReceiptInput {
  supplierId?: string;
  supplierName?: string;
  purchaseOrderId?: string;
  purchaseOrderNumber?: string;
  deliveryNoteNumber?: string;
  receiptDate?: string;
  status?: string;
  notes?: string;
  items: GoodsReceiptItemInput[];
}

// Stock Movement types
interface StockMovement {
  id: string;
  articleId: string;
  articleSku: string;
  articleName: string;
  type: 'in' | 'out' | 'transfer' | 'adjustment';
  quantity: number;
  fromLocation: string | null;
  toLocation: string | null;
  referenceType: string | null;
  referenceId: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
}

interface StockMovementListResponse {
  stockMovements: StockMovement[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface StockMovementInput {
  articleId: string;
  type: 'in' | 'out' | 'transfer' | 'adjustment';
  quantity: number;
  fromLocation?: string;
  toLocation?: string;
  referenceType?: string;
  referenceId?: string;
  notes?: string;
}

// Stock Adjustment types
interface StockAdjustment {
  id: string;
  adjustmentNumber: string;
  articleId: string;
  articleSku: string;
  articleName: string;
  previousStock: number;
  adjustedStock: number;
  difference: number;
  reason: string;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
}

interface StockAdjustmentListResponse {
  stockAdjustments: StockAdjustment[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface StockAdjustmentInput {
  articleId: string;
  quantityChange: number;
  type: 'increase' | 'decrease';
  reason: string;
  notes?: string | null;
  expiryDate?: string | null;
  batchNumber?: string | null;
  storageLocationId?: string | null;
  storageLocationCode?: string | null;
  warehouseId?: string | null;
}

// Shipment types
interface Shipment {
  id: string;
  shipmentNumber: string;
  orderId: string | null;
  orderNumber: string | null;
  carrier: string;
  channel?: string | null;
  trackingNumber: string | null;
  trackingUrl?: string | null;
  status: 'pending' | 'label_created' | 'picked_up' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'failed' | 'returned';
  weight: number | null;
  dimensions: { length?: number; width?: number; height?: number } | null;
  shippingAddress: CustomerAddress | null;
  shippingCost: number | null;
  labelUrl: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ShipmentListResponse {
  shipments: Shipment[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/**
 * Kennzahlen der Sendungsliste — Feld für Feld das, was `GET /shipments/stats` wirklich liefert
 * (`ShipmentController::stats()`).
 *
 * 🚨 Hier standen `inTransit` und `failed`: zwei Felder, die der Endpunkt nie ausgeliefert hat,
 * während `shipped`, `returned` und `error` fehlten. Der Typ beschrieb also eine Antwort, die es
 * nicht gab — beim Zuweisen an den echten Kennzahlen-Typ meldete TypeScript das entsprechend.
 * Wahrheit ist der Controller, nicht diese Datei.
 */
interface ShipmentStats {
  total: number;
  pending: number;
  shipped: number;
  delivered: number;
  returned: number;
  /** Ohne Rückmeldung des Versanddienstleisters abgeschlossen (0.0.0.524). */
  closed: number;
  /** Störung gemeldet: beschädigt, neu verpackt, entsorgt (0.0.0.527). */
  problem: number;
  /** Liegt in Filiale/Packstation bereit — Vorstufe zur Zustellung (0.0.0.527). */
  pickupReady: number;
  error: number;
}

interface ShipmentInput {
  orderId?: string;
  carrier: string;
  service?: string;
  trackingNumber?: string;
  status?: string;
  weight?: number;
  dimensions?: { length?: number; width?: number; height?: number };
  shippingAddress?: CustomerAddress;
  shippingCost?: number;
  labelUrl?: string;
  notes?: string;
}

// Order Shipping Method types (Versandarten für Bestellungen)
interface OrderShippingMethod {
  id: string;
  name: string;
  code: string;
  carrier: string | null;
  description: string | null;
  price: number;
  freeShippingThreshold: number | null;
  minWeight: number | null;
  maxWeight: number | null;
  minOrderValue: number | null;
  maxOrderValue: number | null;
  estimatedDaysMin: number | null;
  estimatedDaysMax: number | null;
  countries: string[] | null;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface OrderShippingMethodActive {
  id: string;
  name: string;
  code: string;
  carrier: string | null;
  price: number;
  freeShippingThreshold: number | null;
  estimatedDaysMin: number | null;
  estimatedDaysMax: number | null;
  isDefault: boolean;
}

interface OrderShippingMethodInput {
  name: string;
  code: string;
  carrier?: string;
  description?: string;
  price?: number;
  freeShippingThreshold?: number;
  minWeight?: number;
  maxWeight?: number;
  minOrderValue?: number;
  maxOrderValue?: number;
  estimatedDaysMin?: number;
  estimatedDaysMax?: number;
  countries?: string[];
  isActive?: boolean;
  isDefault?: boolean;
  sortOrder?: number;
}

// Carrier Config types
interface CarrierConfig {
  id: string;
  carrier: string;
  accountNumber: string | null;
  apiKey: string | null;
  settings: Record<string, unknown> | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CarrierConfigInput {
  carrier: string;
  accountNumber?: string;
  apiKey?: string;
  apiSecret?: string;
  settings?: Record<string, unknown>;
  isActive?: boolean;
}

// Pick List types
interface PickListItem {
  id: string;
  articleId: string;
  articleSku: string;
  articleName: string;
  location: string | null;
  quantityRequired: number;
  quantityPicked: number;
  status: 'pending' | 'partial' | 'complete';
}

interface PickList {
  id: string;
  pickListNumber: string;
  orderId: string | null;
  orderNumber: string | null;
  status: 'pending' | 'in_progress' | 'complete' | 'cancelled';
  assignedTo: string | null;
  priority: number;
  notes: string | null;
  items: PickListItem[];
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PendingOrderSummary {
  id: string;
  orderNumber: string;
  customerName: string;
  country: string;
  city: string;
  postalCode: string;
  shippingMethod: string;
  carrierType: string | null;
  priority: string;
  marketplace: string;
  itemCount: number;
  totalWeight: number;
  hasStockIssues: boolean;
}

interface PickListListResponse {
  pickLists: PickList[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface PickListStats {
  total: number;
  pending: number;
  inProgress: number;
  complete: number;
}

interface PickListItemInput {
  articleId: string;
  location?: string;
  quantityRequired: number;
}

interface PickListInput {
  orderId?: string;
  assignedTo?: string;
  priority?: number;
  notes?: string;
  items: PickListItemInput[];
}

// Stock Check types
interface StockCheckItem {
  id: string;
  articleId: string | null;
  sku: string;
  name: string;
  quantityRequired: number;
  quantityAvailable: number;
  quantityCanPick: number;
  shortage: number;
  status: 'available' | 'partial' | 'unavailable';
}

interface StockCheckOrder {
  orderId: string;
  orderNumber: string;
  customerName: string;
  items: StockCheckItem[];
  canFulfill: boolean;
  requiresPartialDelivery: boolean;
}

interface StockCheckSummary {
  totalOrders: number;
  hasStockIssues: boolean;
  totalItemsRequired: number;
  totalItemsAvailable: number;
  canCreatePicklist: boolean;
  partialDeliveryPossible: boolean;
}

interface StockCheckResponse {
  success: boolean;
  orders: StockCheckOrder[];
  summary: StockCheckSummary;
}

interface BackorderItem {
  orderItemId: string;
  articleId: string | null;
  sku: string;
  name: string;
  quantity: number;
}

interface CreatePickListResponse {
  success: boolean;
  id: string;
  pickListNumber: string;
  isPartialDelivery?: boolean;
  itemsCount?: number;
  backorderItems?: BackorderItem[];
  backorderMessage?: string;
  error?: string;
  message?: string;
  code?: 'INSUFFICIENT_STOCK' | 'NO_ITEMS_AVAILABLE';
}

// Picking Session types
interface PickingSession {
  id: string;
  userId: string;
  status: 'active' | 'completed';
  deviceInfo: Record<string, unknown> | null;
  itemsPicked: number;
  startedAt: string;
  endedAt: string | null;
}

// Purchase Order types
interface PurchaseOrderItem {
  id: string;
  articleId: string | null;
  sku: string;
  name: string;
  orderedQuantity: number;
  receivedQuantity: number;
  unitPrice: number;
}

interface PurchaseOrder {
  id: string;
  orderNumber: string;
  supplierId: string | null;
  supplierName: string | null;
  status: 'draft' | 'pending' | 'confirmed' | 'partial' | 'complete' | 'cancelled';
  totalAmount: number;
  currency: string;
  notes: string | null;
  expectedDeliveryDate: string | null;
  items: PurchaseOrderItem[];
  createdAt: string;
  updatedAt: string;
}

interface PurchaseOrderListResponse {
  purchaseOrders: PurchaseOrder[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/** Position eines freien Wareneingangs — `quantity` ist zugleich die Liefer-Kontrollmenge. */
export interface ReceiptCostLine {
  lineId: number;
  barcode: string;
  quantity: number;
  /** Netto-EK; null = noch nicht erfasst. DECIMAL kommt als String aus der DB. */
  unitPurchasePrice: string | number | null;
  articleId: string | null;
  sku: string | null;
  name: string | null;
  /** Aktueller Einstandspreis des Artikels als Orientierung. */
  currentCost: string | number | null;
}

export interface ReceiptWithoutCost {
  id: string;
  bookedAt: string;
  note: string | null;
  totalLines: number;
  totalQuantity: number;
  deviceName: string | null;
  linesWithoutCost: number;
  quantityWithoutCost: number;
  lines: ReceiptCostLine[];
}

export interface ReceiptsWithoutCostResponse {
  success: boolean;
  receipts: ReceiptWithoutCost[];
  gracePeriodDays: number;
}

interface PurchaseOrderItemInput {
  articleId?: string;
  sku: string;
  name: string;
  orderedQuantity: number;
  unitPrice: number;
}

interface PurchaseOrderInput {
  supplierId?: string;
  supplierName?: string;
  status?: string;
  currency?: string;
  notes?: string;
  expectedDeliveryDate?: string;
  items: PurchaseOrderItemInput[];
}

// MHD (Expiry Date) Inventory types
interface MhdInventory {
  expiryDate: string | null;
  batchNumber: string | null;
  storageLocationCode: string;
  quantity: number;
}

// Serial Number types
type SerialNumberStatus = 'available' | 'reserved' | 'sold' | 'returned' | 'defective';

interface SerialNumber {
  id: string;
  articleId: string;
  articleSku?: string;
  articleName?: string;
  serialNumber: string;
  status: SerialNumberStatus;
  storageLocationCode: string | null;
  batchNumber: string | null;
  expiryDate: string | null;
  orderId: string | null;
  orderNumber?: string;
  customerName: string | null;
  soldAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface SerialNumberListResponse {
  serialNumbers: SerialNumber[];
  total: number;
  page: number;
  limit: number;
}

interface SerialNumberInput {
  articleId: string;
  serialNumbers: string[];
  storageLocationCode?: string;
  batchNumber?: string;
  expiryDate?: string;
  notes?: string;
}

interface SerialNumberAssignInput {
  orderId: string;
  customerName?: string;
}

// Batch Inventory types
interface BatchInventory {
  id: string;
  articleId: string;
  articleSku?: string;
  articleName?: string;
  batchNumber: string;
  storageLocationCode: string;
  quantity: number;
  expiryDate: string | null;
  productionDate: string | null;
  supplierBatch: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BatchInventoryListResponse {
  batches: BatchInventory[];
  total: number;
  page: number;
  limit: number;
}

// Ausverkaufs-Automatik (Clearance)
type ClearanceStrategy = 'time' | 'target';

interface ClearanceStartInput {
  startPrice?: number;
  minPrice?: number;
  reductionPercent?: number;
  intervalDays?: number;
  syncEnabled?: boolean;
  marketplaces?: string[];
  strategy?: ClearanceStrategy;
  targetDate?: string | null;
}

interface ClearanceSuggestion {
  strategy: ClearanceStrategy;
  targetDate: string;
  reductionPercent: number;
  /** Aus Zieldatum, Startpreis, Mindestpreis und Rabattsatz gerechnet — so, dass der Boden zum Zieldatum erreicht wird. */
  intervalDays: number;
  minPrice: number;
  thresholdDays: number | null;
  /** false = aus der Warengruppen-Regel vorgegeben, true = berechnet */
  intervalComputed: boolean;
  reason: string;
}

interface ClearanceCandidate {
  articleId: string;
  sku: string | null;
  name: string | null;
  stock: number | null;
  sellingPrice: number;
  /** NETTO (JTL fEKNetto) */
  purchasePrice: number | null;
  /** EK brutto — direkt vergleichbar mit sellingPrice/minPrice */
  purchasePriceGross: number | null;
  vatRate: number;
  earliestExpiry: string;
  daysUntilExpiry: number;
  criticalQuantity: number;
  batchCount: number;
  severity: 'expired' | 'critical' | 'warning' | 'info';
  sources: string[];
  clearanceEnabled: boolean;
  eolReason: string | null;
  categoryName: string | null;
  autoStartRule: boolean;
  suggestion: ClearanceSuggestion;
}

interface ClearanceCandidatesResponse {
  success: boolean;
  withinDays: number;
  thresholds: { critical: number; warning: number; info: number };
  candidates: ClearanceCandidate[];
}

/** Eine Zeile der LMIV-Zweitlesungs-Prüfliste (`GET /articles/lmiv-review`). */
interface LmivReviewListItem {
  id: string;
  article_id: string | null;
  article_name: string | null;
  article_sku: string | null;
  quelle: 'artikel_ki' | 'zutaten_ocr';
  erstleser_modell: string | null;
  zweitleser_modell: string | null;
  abgleich_ergebnis: 'uebereinstimmung' | 'abweichung' | 'sicherheitsverdacht' | 'zweitlesung_fehlgeschlagen' | null;
  sicherheitsverdacht: boolean;
  status: 'auto_frei' | 'review_offen' | 'freigegeben' | 'abgelehnt' | 'sicherheitsfall';
  entschieden_von: string | null;
  entschieden_am: string | null;
  created_at: string;
}

interface LmivReviewLesung {
  modell: string | null;
  raw: string | null;
  tokens_input: number;
  tokens_output: number;
  dauer_ms?: number;
  injektionstext: string | null;
}

/** Ein Befund aus `ZweitlesungAbgleich` (`abgleichBefunde`, JSON-Spalte). */
interface LmivReviewBefund {
  kategorie: 'sperrend' | 'hinweis' | 'sicherheit';
  feld: string;
  beschreibung: string;
  erstleser: unknown;
  zweitleser: unknown;
}

/** Detailansicht eines Falls (`GET /articles/lmiv-review/{id}`). */
interface LmivReviewDetail extends Omit<LmivReviewListItem, 'erstleser_modell' | 'zweitleser_modell'> {
  abgleich_befunde: LmivReviewBefund[];
  erstleser: LmivReviewLesung;
  zweitleser: LmivReviewLesung;
}

// MHD-Info eines Artikels — Vorbelegung des Zieldatums
interface ArticleExpiryInfo {
  earliestExpiry: string;
  daysUntilExpiry: number;
  quantity: number;
  batchCount: number;
  sources: string[];
  suggestedTargetDate: string;
}

interface ArticleExpiryInfoResponse {
  success: boolean;
  articleId: string;
  /** null = Artikel hat keine Charge mit MHD */
  expiry: ArticleExpiryInfo | null;
}

interface ClearanceState {
  clearanceEnabled: boolean;
  clearanceStartPrice?: number;
  clearanceCurrentPrice?: number;
  clearanceMinPrice?: number;
  clearanceReductionPercent?: number;
  clearanceIntervalDays?: number;
  clearanceNextReduction?: string;
  clearanceSyncEnabled?: boolean;
  clearanceStrategy?: ClearanceStrategy;
  clearanceTargetDate?: string | null;
}

interface ClearanceStateResponse {
  success: boolean;
  clearance: ClearanceState;
}

interface ClearancePriceHistoryEntry {
  id: string;
  previousPrice: number;
  newPrice: number;
  reductionPercent: number;
  reductionAmount: number;
  reason: string;
  syncStatus: string | null;
  syncedAt: string | null;
  createdAt: string;
  createdBy: string | null;
}

interface BatchInventoryInput {
  articleId: string;
  batchNumber: string;
  storageLocationCode: string;
  quantity: number;
  expiryDate?: string;
  productionDate?: string;
  supplierBatch?: string;
  notes?: string;
}

interface BatchDeductInput {
  articleId: string;
  storageLocationCode: string;
  quantity: number;
  batchNumber?: string;
  strategy?: 'FIFO' | 'FEFO';
  reference?: string;
}

// GPSR (General Product Safety Regulation) Address
interface GpsrAddress {
  street: string | null;
  houseNumber: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
}

// Manufacturer types
interface Manufacturer {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  website: string | null;
  logoUrl: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  isActive: boolean;
  sortOrder: number;
  // GPSR fields
  gpsrLegalName: string | null;
  gpsrStreet: string | null;
  gpsrHouseNumber: string | null;
  gpsrPostalCode: string | null;
  gpsrCity: string | null;
  gpsrCountry: string | null;
  gpsrContactPerson: string | null;
  gpsrVatId: string | null;
  gpsrTradeRegister: string | null;
  gpsrEmail: string | null;
  gpsrPhone: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ManufacturerListResponse {
  manufacturers: Manufacturer[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ManufacturerInput {
  name: string;
  code?: string;
  description?: string;
  website?: string;
  logoUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  isActive?: boolean;
  sortOrder?: number;
  // GPSR fields
  gpsrLegalName?: string | null;
  gpsrStreet?: string | null;
  gpsrHouseNumber?: string | null;
  gpsrPostalCode?: string | null;
  gpsrCity?: string | null;
  gpsrCountry?: string | null;
  gpsrContactPerson?: string | null;
  gpsrVatId?: string | null;
  gpsrTradeRegister?: string | null;
  gpsrEmail?: string | null;
  gpsrPhone?: string | null;
}

interface ManufacturerSearchResult {
  id: string;
  name: string;
  code: string | null;
  logoUrl: string | null;
}

// Country types
interface Country {
  id: string;
  isoCode: string;
  name: string;
  nameEn: string | null;
  nameTh: string | null;
  alternativeNames: string[];
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface CountryListResponse {
  countries: Country[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface CountryInput {
  isoCode: string;
  name: string;
  nameEn?: string | null;
  nameTh?: string | null;
  alternativeNames?: string[];
  isActive?: boolean;
  sortOrder?: number;
}

interface CountrySearchResult {
  id: string;
  name: string;
  nameEn: string | null;
  isoCode: string;
}

// Brand types
interface Brand {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  website: string | null;
  logoUrl: string | null;
  manufacturerId: string | null;
  manufacturerName: string | null;
  isActive: boolean;
  sortOrder: number;
  gpsrLegalName: string | null;
  gpsrStreet: string | null;
  gpsrHouseNumber: string | null;
  gpsrPostalCode: string | null;
  gpsrCity: string | null;
  gpsrCountry: string | null;
  gpsrContactPerson: string | null;
  gpsrVatId: string | null;
  gpsrTradeRegister: string | null;
  gpsrEmail: string | null;
  gpsrPhone: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BrandListResponse {
  brands: Brand[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface BrandInput {
  name: string;
  code?: string;
  description?: string;
  website?: string;
  logoUrl?: string;
  manufacturerId?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  gpsrLegalName?: string | null;
  gpsrStreet?: string | null;
  gpsrHouseNumber?: string | null;
  gpsrPostalCode?: string | null;
  gpsrCity?: string | null;
  gpsrCountry?: string | null;
  gpsrContactPerson?: string | null;
  gpsrVatId?: string | null;
  gpsrTradeRegister?: string | null;
  gpsrEmail?: string | null;
  gpsrPhone?: string | null;
}

interface BrandSearchResult {
  id: string;
  name: string;
  code: string | null;
  logoUrl: string | null;
  manufacturerId: string | null;
}

// RMA (Return Merchandise Authorization) types
type RmaType = 'return' | 'replacement' | 'exchange';
type RmaStatus = 'pending' | 'approved' | 'shipped_back' | 'received' | 'inspecting' | 'completed' | 'rejected';
type InspectionResult = 'approved' | 'partial' | 'rejected';
// Note: ItemCondition is defined earlier in the file (line ~347)

interface RmaReason {
  id: string;
  code: string;
  name: string;
  description: string | null;
  appliesTo: RmaType[];
  requiresReturn: boolean;
}

interface RmaItem {
  id: string;
  sku: string | null;
  name: string | null;
  quantityRequested: number;
  quantityReceived: number | null;
  quantityApproved: number | null;
  reason: string | null;
  conditionReceived: ItemCondition | null;
}

interface Rma {
  id: string;
  rmaNumber: string;
  type: RmaType;
  status: RmaStatus;
  reasonCode: string | null;
  reasonText: string | null;
  customerNotes?: string | null;
  internalNotes?: string | null;
  returnTrackingNumber: string | null;
  returnCarrier: string | null;
  receivedAt: string | null;
  inspectedAt?: string | null;
  inspectionResult?: InspectionResult | null;
  inspectionNotes?: string | null;
  refundAmount: number | null;
  refundStatus: 'pending' | 'processed' | 'none';
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  order: {
    id: string;
    orderNumber: string;
    customerName: string;
    customerEmail: string;
    // `Address` gab es hier nie — der Typ blieb unaufgelöst und die Eigenschaft damit
    // faktisch ununterscheidbar von `any`. `CustomerAddress` ist die im Modul vorhandene
    // Adressform (street/houseNumber/postalCode/city/country).
    shippingAddress?: CustomerAddress | null;
    totalAmount?: number;
  };
  replacementOrder: {
    id: string;
    orderNumber: string;
    status: string;
  } | null;
  items: RmaItem[];
}

interface RmaListResponse {
  rmas: Rma[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface RmaStats {
  total: number;
  pending: number;
  approved: number;
  shippedBack: number;
  received: number;
  completed: number;
  replacements: number;
  exchanges: number;
  returns: number;
  pendingExchangeOrders: number;
}

interface CreateReplacementInput {
  /** Zeile aus `GET /rma/reasons` — die Maske schickt diesen Weg (V-1053). */
  reasonId?: string;
  reasonCode?: string;
  reasonText?: string;
  /** Freitext der Maske; landet in den internen Notizen. */
  note?: string;
  customerNotes?: string;
  internalNotes?: string;
  /** Nur mit Administrator- bzw. „Unter Einkaufspreis verkaufen"-Recht: einen zweiten Ersatzversand erzwingen. */
  confirmAdditionalReplacement?: boolean;
  /**
   * Positionen des Originalauftrags: `itemId` ist `orderItems.id` DIESES Auftrags, `quantity` eine ganze Zahl bis
   * „bestellt − storniert − schon ersetzt". Der Server bindet jede Zeile daran (V-1053).
   */
  items?: Array<{
    itemId: string;
    quantity: number;
  }>;
}

/**
 * V-1053 Runde 3 (F-025): Was der Ersatzversand-Dialog anbieten darf — die Vorauswahl kommt vom Server
 * (`GET /orders/{id}/replacement-options`), dieselbe Regel, die `POST /orders/{id}/replacement` durchsetzt.
 */
interface ReplacementOptions {
  /** Nur ersetzbare Positionen (keine stornierten, keine Veredelungen) mit der noch verfügbaren Menge. */
  items: Array<{
    item_id: string;
    name: string;
    sku: string;
    /** bestellt − storniert − bereits ersetzt */
    quantity_available: number;
  }>;
  /** Nicht stornierte Ersatzaufträge zu diesem Auftrag. */
  existing_replacements: number;
  /** Darf der Anwender einen weiteren Ersatzversand erzwingen (Administrator bzw. „Unter Einkaufspreis verkaufen")? */
  can_confirm_additional: boolean;
  /** Veredelungen des Auftrags, die beim Ersatzversand nicht mit ersetzt werden. */
  skipped_finishing: number;
}

interface CreateExchangeInput extends CreateReplacementInput {
  itemsToReturn?: Array<{
    articleId?: string;
    sku?: string;
    name?: string;
    quantity?: number;
    reason?: string;
  }>;
  exchangeItems?: Array<{
    articleId?: string;
    sku?: string;
    name?: string;
    quantity?: number;
    unitPrice?: number;
  }>;
  priceDifference?: number;
}

interface ReceiveReturnInput {
  receivedBy?: string;
  items?: Array<{
    id: string;
    quantityReceived?: number;
    condition?: ItemCondition;
    notes?: string;
  }>;
}

interface CompleteRmaInput {
  inspectionResult?: InspectionResult;
  inspectionNotes?: string;
  refundAmount?: number;
  items?: Array<{
    id: string;
    quantityApproved?: number;
  }>;
}

interface RelatedOrders {
  rmas: Array<{
    id: string;
    rmaNumber: string;
    type: RmaType;
    status: RmaStatus;
    reasonText: string | null;
    createdAt: string;
    replacementOrder: {
      id: string;
      orderNumber: string;
      status: string;
      orderType: string;
    } | null;
  }>;
  childOrders: Array<{
    id: string;
    orderNumber: string;
    orderType: string;
    status: string;
    awaitingReturn: boolean;
    holdReason: string | null;
    totalAmount: number;
    createdAt: string;
    rmaId: string | null;
    rmaNumber: string | null;
  }>;
  parentOrder: {
    id: string;
    orderNumber: string;
    status: string;
    customerName: string;
    totalAmount: number;
    createdAt: string;
  } | null;
}

// Article-Supplier Relationship types
interface ArticleSupplier {
  id: string;
  articleId: string;
  supplierId: string;
  supplierSku: string | null;
  supplierArticleName: string | null;
  unitPrice: number | null;
  currency: string;
  leadTimeDays: number | null;
  minimumOrderQuantity: number | null;
  isPreferred: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  // Supplier info
  companyName: string;
  supplierNumber: string;
  contactPerson: string | null;
  email: string | null;
}

interface ArticleSupplierInput {
  supplierId: string;
  supplierSku?: string | null;
  supplierArticleName?: string | null;
  unitPrice?: number | null;
  currency?: string;
  leadTimeDays?: number | null;
  minimumOrderQuantity?: number | null;
  isPreferred?: boolean;
  notes?: string | null;
}

interface SupplierSearchResult {
  id: string;
  supplierNumber: string;
  companyName: string;
  contactPerson: string | null;
  email: string | null;
}

interface SupplierArticle {
  id: string;
  articleId: string;
  articleSku: string;
  articleName: string;
  articleStock: number;
  supplierSku: string | null;
  supplierArticleName: string | null;
  unitPrice: number | null;
  currency: string;
  leadTimeDays: number | null;
  minimumOrderQuantity: number | null;
  isPreferred: boolean;
  notes: string | null;
}

// =============================================================================
// TAX & VAT TYPES
// =============================================================================

// Tax Class - Simplified to 5 basic types (standard, reduced, reduced2, super_reduced, zero)
interface TaxClass {
  id: string;
  code: string; // 'standard' | 'reduced' | 'reduced2' | 'super_reduced' | 'zero' (+ 'increased' | 'increased2' | 'special' beim Steuergebiet Kanaren, V-1994)
  name: string;
  description: string | null;
  datevBuKeyOutput: number | null; // DATEV BU-Schlüssel für Umsatzsteuer
  datevBuKeyInput: number | null;  // DATEV BU-Schlüssel für Vorsteuer
  isDefault: boolean;
  sortOrder: number;
  /** V-1994: nur virtuell ergänzte Satzart des Steuergebiets (keine Mandantenzeile). */
  virtual?: boolean;
  gebiet?: string;
  // Legacy fields - kept for backwards compatibility
  category?: string | null;
  isTaxExempt?: boolean;
  isActive?: boolean;
  rates?: TaxClassRate[];
  createdAt?: string;
  updatedAt?: string;
}

// Product Tax Category - Product-type based categories (books, food_basic, etc.)
interface ProductTaxCategory {
  id: string;
  code: string;
  name: string;
  description: string | null;
  defaultTaxClassCode: string; // Links to TaxClass.code
  defaultRate: number | null; // Satz in % aus Master-vat_rates des Sitzlands; null = Land kennt die Satzart nicht (V-0527)
  categoryGroup: string | null;
  sortOrder: number;
  isActive?: boolean;
  countryOverrides: ProductTaxCategoryCountryOverride[];
}

interface ProductTaxCategoryCountryOverride {
  countryCode: string;
  taxClassCode: string;
  notes: string | null;
}

// Tax Info result from article tax resolution
interface ArticleTaxInfo {
  taxClassCode: string;
  rate: number;
  country: string;
  source: 'article_override' | 'country_override' | 'product_tax_category' | 'fallback';
  productTaxCategoryCode: string | null;
  datevBuKeyOutput: number | null;
  datevBuKeyInput: number | null;
}

// Resolved country rate for a tax class (all EU countries)
export interface ResolvedCountryRate {
  countryCode: string;
  countryName: string;
  countryNameDe: string;
  flagEmoji: string | null;
  rate: number;
  source: 'master' | 'override' | 'fallback';
  overrideId?: string;
  reason?: string;
}

// Legacy interface - kept for backwards compatibility
interface TaxClassRate {
  id: string;
  taxClassId: string;
  countryCode: string;
  rate: number;
  rateType: string;
  isOverride: boolean;
  validFrom: string;
  validUntil: string | null;
}

interface TaxCalculationResult {
  treatment: 'domestic' | 'seller_country' | 'buyer_country' | 'reverse_charge' | 'intra_community' | 'export' | 'exempt';
  taxRate: number;
  taxAmount: number;
  netAmount: number;
  grossAmount: number;
  exemptionNotice: string | null;
  legalReference: string | null;
  items: TaxCalculationItemResult[];
}

interface TaxCalculationItemResult {
  netPrice: number;
  taxRate: number;
  taxAmount: number;
  grossPrice: number;
  quantity: number;
  totalNet: number;
  totalTax: number;
  totalGross: number;
}

interface EUCountryWithRates {
  id: string;
  countryCode: string;
  countryName: string;
  countryNameDe: string;
  isEuMember: boolean;
  isOssApplicable: boolean;
  currencyCode: string;
  flagEmoji: string | null;
  rates: VatRateInfo[];
}

interface VatRateInfo {
  id: string;
  rateType: 'standard' | 'reduced' | 'reduced2' | 'reduced3' | 'super_reduced' | 'increased' | 'increased2' | 'special' | 'parking' | 'zero';
  rate: number;
  description: string | null;
  validFrom: string;
  validUntil: string | null;
}

interface VatValidationResult {
  /** `null` = VIES nicht erreichbar/Mitgliedstaat nicht verfügbar (nicht prüfbar, nicht „ungültig“). */
  valid: boolean | null;
  vatId: string;
  countryCode: string;
  companyName: string | null;
  companyAddress: string | null;
  validatedAt: string;
  cached: boolean;
  error?: string;
}

// Admin VAT Rate type for master database management
interface AdminVatRate {
  id: string;
  countryCode: string;
  countryName: string;
  rateType: 'standard' | 'reduced' | 'reduced2' | 'super_reduced' | 'increased' | 'increased2' | 'special' | 'zero' | 'parking';
  rate: number;
  description: string | null;
  validFrom: string;
  validUntil: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// OSS Types
interface OSSDashboardData {
  year: number;
  thresholdStatus: OSSThresholdStatus;
  salesByCountry: OSSSalesByCountry[];
  monthlyTrend: OSSMonthlyTrend[];
  pendingReportsCount: number;
  totalReports: number;
}

interface OSSThresholdStatus {
  year: number;
  totalCrossBorderSales: number;
  thresholdAmount: number;
  thresholdExceeded: boolean;
  thresholdExceededAt: string | null;
  percentageUsed: number;
  remainingAmount: number;
  registrationStatus: 'not_registered' | 'pending' | 'registered' | 'deregistered';
  registrationDate: string | null;
  registrationNumber: string | null;
}

interface OSSSalesByCountry {
  countryCode: string;
  countryName: string;
  flagEmoji: string;
  netTotal: number;
  taxTotal: number;
  grossTotal: number;
  orderCount: number;
  avgTaxRate: number;
}

interface OSSMonthlyTrend {
  month: number;
  netTotal: number;
  taxTotal: number;
  orderCount: number;
}

interface OSSReport {
  id: string;
  year: number;
  quarter: number;
  periodStart: string;
  periodEnd: string;
  status: 'draft' | 'finalized' | 'submitted';
  totalNetSales: number;
  totalTaxCollected: number;
  countryBreakdown: OSSCountryBreakdown[];
  submissionDeadline: string;
  submittedAt: string | null;
  bzstReference: string | null;
}

interface OSSCountryBreakdown {
  countryCode: string;
  countryName: string;
  standardRate: number;
  reducedRate: number | null;
  netSales: number;
  taxCollected: number;
  orderCount: number;
}

// ZM (Zusammenfassende Meldung) Types
interface ZMReport {
  id: string;
  year: number;
  month: number;
  reportType: 'monthly' | 'quarterly';
  status: 'draft' | 'prepared' | 'submitted' | 'accepted';
  transactions: ZMTransaction[];
  totalIntraEUDeliveries: number;
  totalReverseChargeServices: number;
  submissionDeadline: string;
  submittedAt: string | null;
  elsterReference: string | null;
}

interface ZMTransaction {
  vatId: string;
  amount: number;
  transactionType: 'L' | 'D' | 'S'; // Lieferung, Dreiecksgeschäft, Sonstige Leistung
}

// Warehouse VAT Types
interface WarehouseVatRegistration {
  id: string;
  warehouseId: string | null;
  countryCode: string;
  vatId: string;
  taxNumber: string | null;
  registrationDate: string | null;
  isActive: boolean;
  filingFrequency: 'monthly' | 'quarterly' | 'annual';
  nextFilingDue: string | null;
  lastFilingDate: string | null;
  fiscalRepresentative: string | null;
  notes: string | null;
}

interface VatComplianceAlert {
  id: string;
  alertType: 'missing_registration' | 'filing_due' | 'filing_overdue' | 'threshold_warning';
  countryCode: string | null;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  isResolved: boolean;
  createdAt: string;
}

// DATEV Export Types
/** Eine Zeile der §25a-Auswertung — ein verkaufter Gegenstand. */
export interface DifferenzbesteuerungZeile {
  verkauft_am: string | null;
  beleg: string | null;
  ist_storno: boolean;
  bezeichnung: string | null;
  artikelnummer: string | null;
  seriennummer: string | null;
  angekauft_am: string | null;
  verkaeufer: string | null;
  /** V-2358: Kunde der Kundenkartei, der den Ankauf ausgelöst hat (null = Altbestand/nicht zugeordnet). */
  kunde_id?: string | null;
  kunde_nummer?: string | null;
  kunde_name?: string | null;
  /** V-3632: Ankauf (RFC 4122) und Anzahl aktiver Gerätefotos — nur die Zahl, die Bilder öffnet die Maske über den Ankauf. */
  ankauf_id?: string | null;
  ankauf_foto_anzahl?: number;
  /** V-3640: Herkunft des Geräts — GENAU ein Ankauf, sonst nur der Grund (nie eine geratene Person); keine Ausweisdaten in der Auswertung. */
  herkunft_ankauf_id?: string | null;
  herkunft_grund?: string | null;
  verkaufspreis: number;
  /** null, wenn der Beleg den Einkaufspreis nicht traegt (Kasse vor 21.09.2026). */
  einkaufspreis: number | null;
  marge_brutto: number | null;
  /** Marge NETTO — die Bemessungsgrundlage der Umsatzsteuer. */
  bemessungsgrundlage: number | null;
  umsatzsteuer: number | null;
  steuersatz: number | null;
  hinweis: string | null;
}

/** V-2358: Ankauf in der Kundenansicht — bewusst nur die Felder ohne Ausweisdaten. */
export interface KundenAnkaufZeile {
  id: string;
  deviceName: string | null;
  imei: string | null;
  purchasePrice: number | null;
  purchasedAt: string | null;
  zahlart_anzeige: string | null;
  storniert_at: string | null;
  /** V-1934: Vorgang des Ankaufs — mehrere Geräte aus EINEM Ankauf teilen ihn; null = Ankauf mit einem Gerät/Altbestand (die Nummer kommt nur, wenn mindestens 2 Geräte sie teilen). */
  vorgang_id?: string | null;
  /** V-3632: Anzahl aktiver Gerätefotos (Zustand beim Ankauf). */
  foto_anzahl?: number;
}

/** V-3640: Ausweisdaten eines Verkäufers (Recht „Ausweisdaten ansehen“) — die Kopien ohne Speicherschlüssel, das Bild kommt per Blob. */
export interface AnkaufAusweisdaten {
  ankauf_id: string;
  name: string | null;
  anschrift: string | null;
  land: string | null;
  ausweisnummer: string | null;
  geburtsdatum: string | null;
  ausweis_ausgelesen_at: string | null;
  ausweis_ausgelesen_von: string | null;
  /** `eigenes_geraet` = false: die Kopie hängt an einem anderen Gerät desselben Ankaufsvorgangs (Mehrgeräte-Ankauf, die Kasse sendet sie an die erste Position). */
  kopien: { id: string; aufgenommen_at: string | null; groesse: number; eigenes_geraet: boolean }[];
}

/** V-3632: Ein Gerätefoto beim Ankauf — Metadaten, nie ein Speicherschlüssel; das Bild kommt über getAnkaufGeraetefotoBild(). */
export interface AnkaufGeraetefoto {
  id: string;
  aufgenommen_at: string | null;
  hochgeladen_at: string | null;
  groesse: number;
}

export interface DifferenzbesteuerungAuswertung {
  zeilen: DifferenzbesteuerungZeile[];
  summe: {
    anzahl: number;
    verkaufspreis: number;
    einkaufspreis: number;
    bemessungsgrundlage: number;
    umsatzsteuer: number;
    /** Wie viele Positionen ohne Einkaufspreis am Beleg gefunden wurden. */
    unvollstaendig: number;
  };
  /** Tatsaechlich ausgewerteter Zeitraum (vom Server im lokalen Kassentag gebildet). */
  from: string;
  to: string;
}

/** Ein Zeiteintrag an einem Reparaturvorgang. */
export interface RepairZeitEintrag {
  id: string;
  gestartetAt: string;
  beendetAt: string | null;
  laeuft: boolean;
  minuten: number | null;
  mitarbeiter: string | null;
  notiz: string | null;
  korrigiertVon: string | null;
  abgerechnet: boolean;
}

export interface RepairZeiten {
  eintraege: RepairZeitEintrag[];
  laeuft: boolean;
  laufendSeit: string | null;
  /** Noch nicht in eine Position uebernommene Minuten. */
  minutenOffen: number;
  minutenGesamt: number;
  stundenOffen: number;
  stundenGesamt: number;
}

interface DatevExport {
  id: string;
  exportDate: string;
  periodStart: string;
  periodEnd: string;
  exportType: 'invoices' | 'payments' | 'full';
  status: 'pending' | 'generated' | 'downloaded';
  fileName: string | null;
  recordsCount: number;
  totalAmount: number;
  advisorNumber: string | null;
  clientNumber: string | null;
  createdAt: string;
  downloadedAt: string | null;
  /**
   * Hinweise des Servers zum erzeugten Stapel — `errors` = Belege, die NICHT im Stapel stehen,
   * `warnings` = gebucht, aber ein Steuertatbestand ist nicht belegt.
   *
   * 🚨 Der Server liefert beide Felder seit dem Buchhaltungs-Folgelauf mit; die Oberfläche warf
   * sie weg und meldete unbedingt „DATEV-Export erstellt". Optional, weil ältere Antworten
   * (z. B. der Leer-Fall „keine Eingangsrechnungen") sie nicht führen.
   */
  warnings?: string[];
  errors?: string[];
  // 2c-2 (TICK-2026-01150, V-0560): Quelle des Protokolls (BIS ERP, Kundencenter, Kasse) und ob
  // die Datei gespeichert ist — der Download liefert dann genau diese Datei.
  quelle?: 'erp' | 'kundencenter' | 'kasse';
  inhalt_gespeichert?: boolean;
  /** V-1666: Zeitpunkt (ISO) des letzten automatischen Versands durch den Berichtsversand, sonst null. */
  automatisch_versandt_am?: string | null;
  /** V-1666: Zahl der Empfänger dieses automatischen Versands. */
  automatisch_versandt_empfaenger?: number | null;
  /** 2c-2: frühere Kassenexporte desselben Zeitraums (nur Hinweis, keine Sperre). */
  ueberschneidungen?: import('../components/accounting/DatevKassenExportHinweise').KassenExportUeberschneidung[];
  /** 2c-2: Tilgungs-Bons im Zeitraum (Kassenteil). */
  tilgung?: import('../components/accounting/DatevKassenExportHinweise').TilgungsKennzahlDaten | null;
  /** 2c-2 (V-0440): Kassen mit Z-Bons, aber ohne übertragene Verkäufe. */
  kassen_ohne_verkaeufe?: import('../components/accounting/DatevKassenExportHinweise').KasseOhneVerkaeufe[];
}

export interface DatevSettings {
  consultantNumber: string | null;
  clientNumber: string | null;
  chartOfAccounts: string;           // 'SKR03' | 'SKR04'
  fiscalYearStart: number;           // Monat (1-12)
  bookingTextTemplate: string;
  debtorNumberStart: number;
  creditorNumberStart: number;
  // Pro Tenant überschreibbare DATEV-Buchungskonten (SKR03/04). Keys u.a.:
  // revenue_standard/reduced/export/intra_eu, payment_cash_account/payment_bank_account,
  // debitor_range_start/debitor_range_end. Quelle: DatevExportService::DEFAULT_ACCOUNTS.
  accountMappings: Record<string, string>;
}

// DATEV-Gesundheitsprüfung (Rechnungskontrolle vor dem Export)
export interface DatevHealthIssueItem {
  id?: string;
  label: string;
  date?: string;
  amount?: number;
  fix_action?: 'create_invoice' | 'finalize' | null;
  entity?: 'order' | 'invoice';
}

export interface DatevHealthIssue {
  type: string;
  severity: 'error' | 'warning';
  title: string;
  count: number;
  hint: string;
  items: DatevHealthIssueItem[];
}

export interface DatevHealthChannel {
  channel: string;
  label: string;
  status: 'ok' | 'warning' | 'error';
  order_count: number;
  invoice_count: number;
  note: string | null;
  issues: DatevHealthIssue[];
}

/**
 * V-0439 (Korrekturrunde 1/2): Hinweiszeile „Erlös über Kasse" fuer EÜR/BWA/Anlage EKS und den
 * DATEV-Export — EINE Form (ErloestraegerZuordnung::leererPruefhinweis()).
 *  - `anzahl`/`betrag_brutto`: noch nicht entschieden, der Erlös zählt doppelt.
 *  - `entschieden_rechnung_*` (Korrekturrunde 2, Befund M2): per Regel B „Rechnung trägt"
 *    entschieden — der Kassenanteil zählt seit V-0579 in BWA/EÜR/EKS und DATEV als Ausgleich (nichts außerhalb
 *    zu korrigieren; nur die UStVA zeigt einen Hinweis).
 */
export interface KasseErloesPruefhinweis {
  anzahl: number;
  betrag_brutto: number;
  entschieden_rechnung_anzahl?: number;
  entschieden_rechnung_betrag?: number;
  /**
   * V-0464 F-006/F-015: Art der gemeldeten Größe. `rechnung_minderung` (Ist-Basis EÜR/EKS) = Anzahl/Betrag sind GEMINDERTE RECHNUNGEN
   * (der Bon zählt am Bontag); fehlt der Schlüssel, ist es der Kassen-Abzug (`bon_abzug`, Soll-Basis/BWA).
   */
  entschieden_rechnung_art?: 'bon_abzug' | 'rechnung_minderung';
  /** V-2300: Gutschriften ohne Bezug zu einer Rechnung (nicht ausgezahlt), die auf Ist-Basis nicht mindern — Anzahl und Summe brutto (nur Ist-Basis). */
  gutschrift_ohne_bezug_anzahl?: number;
  gutschrift_ohne_bezug_betrag_brutto?: number;
  /** V-2333: bezahlte Rechnungen, deren Zahlungszeilen den Betrag nicht decken — der Rest zählt am paid_at (nur Ist-Basis, Schlüssel nur bei Fällen ab 0,05 €). */
  zahlung_rest_anzahl?: number;
  zahlung_rest_betrag_brutto?: number;
  /** V-2179: Anteil an `anzahl`/`betrag_brutto`, der auf Kassenverkäufe mit JTL-Rechnung entfällt (Schlüssel nur, wenn es solche Fälle gibt). */
  kassenverkauf_anzahl?: number;
  kassenverkauf_betrag_brutto?: number;
  kassenverkauf_betrag_netto?: number;
  /** V-2296: unentschiedene Rechnungen, deren Kassenbon storniert wurde (möglicher Phantomerlös) — NICHT in `anzahl` enthalten. */
  kassenverkauf_phantom_anzahl?: number;
  kassenverkauf_phantom_betrag_brutto?: number;
  kassenverkauf_phantom_betrag_netto?: number;
  /** V-2297: Gutschrift steht auf „Kasse trägt“, soll aber mindern (Kasse hat nichts zurückgenommen) — Erlös zu hoch. BWA/DATEV und (V-2300) EÜR/EKS, NICHT in `anzahl`. */
  gutschrift_minderung_fehlt_anzahl?: number;
  gutschrift_minderung_fehlt_betrag_brutto?: number;
  gutschrift_minderung_fehlt_betrag_netto?: number;
  /** V-2297: Gutschrift mindert zusätzlich, obwohl die Kasse selbst gemindert hat bzw. die Rechnung neu ausgestellt wurde — Erlös zu niedrig. BWA/DATEV und (V-2300) EÜR/EKS. */
  gutschrift_doppelt_gemindert_anzahl?: number;
  gutschrift_doppelt_gemindert_betrag_brutto?: number;
  gutschrift_doppelt_gemindert_betrag_netto?: number;
}

export interface DatevHealthCheckResult {
  success: boolean;
  period: { start: string; end: string };
  export_type: string | null;
  overall_status: 'ok' | 'warning' | 'error';
  summary: { errors: number; warnings: number };
  channels: DatevHealthChannel[];
}

interface UstvaReportPeriod {
  period: number | string;
  label: string;
  kz81: number;
  kz81_vat: number;
  kz86: number;
  kz86_vat: number;
  /** Steuerfrei ohne Vorsteuerabzug — übrige 0-%-Umsätze (§ 4 Nr. 8–29 UStG, § 19 UStG). */
  kz48: number;
  /** Steuerfrei MIT Vorsteuerabzug — Ausfuhrlieferung (§ 4 Nr. 1a i.V.m. § 6 UStG). */
  kz43: number;
  kz41: number;
  /**
   * HINWEISZEILE, keine eigene Kennziffer: igL ohne Käufer-USt-IdNr.
   *
   * 🚨 19.09.2026: Der Betrag steht jetzt als steuerpflichtiger Umsatz in KZ 81/86 — ohne die
   * USt-IdNr. des Abnehmers fehlt die materielle Voraussetzung der Steuerbefreiung
   * (§ 6a Abs. 1 S. 1 Nr. 4 UStG). `kz41_ohne_ustid` ist der GEZAHLTE (Brutto-)Betrag,
   * `_net`/`_vat` die daraus herausgerechneten Anteile (§ 10 Abs. 1 S. 2 UStG).
   */
  kz41_ohne_ustid?: number;
  kz41_ohne_ustid_net?: number;
  kz41_ohne_ustid_vat?: number;
  /** V-3552 F-012: § 25a-Positionen ohne Einkaufspreis (voller VK als Marge versteuert) — Anzahl, VK und USt darauf (vorzeichenbehaftet, Gutschriften mindern). */
  marge_25a_ohne_ek_anzahl?: number;
  marge_25a_ohne_ek_vk?: number;
  marge_25a_ohne_ek_ust?: number;
  /** Sonstige Leistungen an EU-Unternehmer (Reverse Charge, § 3a Abs. 2 UStG). */
  kz21: number;
  /**
   * KZ 69 — Steuerbeträge, die nach § 14c UStG geschuldet werden (Abschnitt G des Vordrucks
   * USt 1 A). Echte Kennziffer, und sie trägt STEUER, keine Bemessungsgrundlage: Wurde die
   * Leistung nie ausgeführt, gibt es keinen Umsatz für KZ 81/86 (§ 14c Abs. 2 UStG). Der Betrag
   * wird erst gemindert, wenn die Gefährdung des Steueraufkommens beseitigt ist UND das
   * Finanzamt zugestimmt hat (Abs. 2 S. 3–5) — periodisiert auf das FRÜHERE der beiden Daten,
   * nicht auf die Zustimmung (§ 14c Abs. 2 S. 5 verweist auf die Voraussetzungen des Satzes 4).
   */
  kz69?: number;
  /**
   * HINWEISZEILEN, keine Kennziffern: ausgestellte Belege auf `cancelled` ohne jeden
   * Korrekturbeleg, bei denen § 14c Abs. 1 UStG greift. Sie zählen weiter in KZ 81/86 — die
   * ausgewiesene Steuer bleibt geschuldet, bis der Beleg gegenüber dem Empfänger berichtigt ist.
   * 🚨 19.09.2026: Endverbraucher-Belege (EuGH C-378/21) und JTL-Spiegel sind hier NICHT mehr
   * enthalten — bei OEZ schrumpfte die Zahl dadurch von 210 auf 7.
   */
  storno_ohne_korrekturbeleg_anzahl?: number;
  storno_ohne_korrekturbeleg_net?: number;
  /**
   * HINWEISZEILE, keine Kennziffer: Belege, die im Fremdsystem (JTL) storniert wurden und zu
   * denen hier KEIN Korrekturbeleg auffindbar ist.
   *
   * 🚨 Sie fallen strukturell aus `storno_ohne_korrekturbeleg_*` heraus, weil der JTL-Import
   * `original_invoice_id` nie setzt — ohne diese eigene Zahl wären sie nirgends sichtbar. Sie
   * zählen vorsichtshalber weiter in KZ 81/86; existiert die Korrektur im Fremdsystem, ist der
   * gemeldete Umsatz um diesen Betrag zu hoch. Bei OEZ: 20 Belege / 16.167,82 €.
   */
  storno_im_fremdsystem_anzahl?: number;
  storno_im_fremdsystem_net?: number;
  storno_im_fremdsystem_vat?: number;
  /**
   * HINWEISZEILE, keine Kennziffer: Belege, die RÜCKWIRKEND aus diesem Zeitraum gefallen sind
   * (Einstufung `kein`).
   *
   * 🚨 Ist der Zeitraum bereits gemeldet, braucht es dafür eine berichtigte Voranmeldung: Bei
   * einer Rückgängigmachung gehört die Berichtigung in den Zeitraum der ÄNDERUNG
   * (§ 17 Abs. 1 S. 8 i. V. m. Abs. 2 Nr. 3 UStG), nicht rückwirkend in den des Umsatzes. Bei
   * OEZ: 184 Belege / 1.733,64 €, davon 177 mit Storno in einem anderen Monat.
   */
  rueckwirkend_entfallen_anzahl?: number;
  rueckwirkend_entfallen_net?: number;
  /**
   * V-0439 (Korrekturrunde 1, Befund 15): Hinweiszeile, KEINE Kennziffer — „Darin enthalten:
   * Erlös über Kasse" (Rechnungen mit `erloes_traeger='kasse'`), je Satz netto/USt. Steckt in
   * KZ 81/86 mit drin; fehlt in EÜR/BWA/DATEV-Export der Ausgangsrechnungen.
   */
  kasse_anteil_anzahl?: number;
  kasse_anteil_net19?: number;
  kasse_anteil_vat19?: number;
  kasse_anteil_net7?: number;
  kasse_anteil_vat7?: number;
  /** V-0439, Korrekturrunde 2 (Befund 13): steuerfreier Anteil (0 %), netto. */
  kasse_anteil_net0?: number;
  kz66: number;
  kz83: number;
  total_revenue_net: number;
  total_vat: number;
  total_input_tax: number;
}

interface UstvaReport {
  year: number;
  period: string;
  periods: UstvaReportPeriod[];
  /** Kleinunternehmer (§19 UStG) geben i. d. R. keine UStVA ab — Hinweis, keine Sperre. */
  kleinunternehmer?: boolean;
  /** V-0579: Jahres-Hinweiszeile — Tilgungs-Bons, bei denen die Rechnung den Erlös trägt (nur `entschieden_rechnung_*` wird angezeigt). */
  kasse_erloes_pruefhinweis?: KasseErloesPruefhinweis;
  /** V-2279 F-005: Datenhinweise (Gutschein-Einlösung unklar, Geräte-Ankauf nicht lesbar/abweichend). */
  hinweise?: ReportHinweis[];
}

/** V-2279 F-005: ein Datenhinweis eines Berichts (`code` bestimmt den Text, `schwere` fehler = Zahl fehlt/unvollständig). */
interface ReportHinweis {
  code: string;
  schwere: 'fehler' | 'hinweis';
  anzahl: number;
  betrag: number | null;
}

interface BwaReportMonth {
  month: number;
  year?: number;
  label?: string;
  revenue: number;
  revenue_gross?: number;
  material_cost: number;
  gross_profit: number;
  gross_margin: number;
  operating_expenses: number;
  total_expenses: number;
  operating_result: number;
  cost_blocks?: Record<string, number>;
}

interface FinancialBlock {
  key: string;
  label: string;
}

interface BwaReport {
  year: number;
  from?: string;
  to?: string;
  blocks?: FinancialBlock[];
  months: BwaReportMonth[];
  totals: {
    revenue: number;
    material_cost: number;
    gross_profit: number;
    gross_margin: number;
    operating_expenses: number;
    total_expenses: number;
    operating_result: number;
    cost_blocks?: Record<string, number>;
  };
  revenue_breakdown?: {
    pos_net: number;
    invoices_net: number;
    total_net: number;
  };
  /** V-0439, Korrekturrunde 1, Befund 14: Kassenbons des Zeitraums, deren Rechnung noch nicht entschieden ist (Erlös doppelt). */
  kasse_erloes_pruefhinweis?: KasseErloesPruefhinweis;
  /** V-2279 F-005: Datenhinweise (Gutschein-Einlösung unklar, Geräte-Ankauf nicht lesbar/abweichend). */
  hinweise?: ReportHinweis[];
}

// Jobcenter-Anlage EKS — deckt alle deklarativen Felder des amtlichen Vordrucks ab (alle als string)
interface EksSettings {
  // A – Antragstellende Person
  applicant_name: string;
  applicant_birthdate: string;
  bg_number: string;
  // B – Selbständige/freiberufliche Person (leer ⇒ = Antragsteller)
  self_name: string;
  self_birthdate: string;
  // C – Erklärung + Bewilligungszeitraum
  declaration_type: string; // 'abschliessend' | 'vorlaeufig'
  /** Altfeld: = Zeitraum der gewählten Art (wird vom Server mitgeschrieben). */
  approval_from: string;
  approval_to: string;
  /** V-2441: je Erklärungsart ein eigener Zeitraum (Y-m-d). */
  approval_from_vorlaeufig: string;
  approval_to_vorlaeufig: string;
  approval_from_abschliessend: string;
  approval_to_abschliessend: string;
  // D – Betrieb (Firmenname/Anschrift aus Firmendaten)
  business_type: string;
  business_legal_form: string;
  business_start: string;
  business_end: string;
  // E – Unterschrift
  signature_date: string;
  // F – Weitere Angaben (Ja/Nein = 'ja' | 'nein' | '')
  premises_commercial: string;
  rooms_count: string;
  area_sqm: string;
  free_products: string;
  has_staff: string;
  staff_count: string;
  plans_more_staff: string;
  more_staff_count: string;
  hiring_date: string;
  subsidies_received: string;
  subsidies_applied: string;
  loan_taken: string;
  loan_amount: string;
  loan_received_date: string;
  loan_repayment_start: string;
  loan_monthly_rate: string;
  loan_financed_expenses: string;
  loan_financed_amount: string;
  // Kfz
  km_business: string;
  km_private: string;
  // Personenbezogene Ausgaben (Seite 9)
  child_not_living: string;
  pays_alimony: string;
  commute_private_car: string;
  commute_distance_km: string;
  commute_days_week: string;
  meal_allowance: string;
  meal_allowance_days: string;
  // Anmerkungen
  notes_a: string;
  notes_b1: string;
  notes_b3: string;
  // Tabelle C – Absetzungen vom Einkommen
  deduction_income_tax: string;
  deduction_health_pension_mandatory: string;
  deduction_private_health: string;
  deduction_pension: string;
  deduction_life_insurance: string;
  deduction_provision: string;
  deduction_kfz_liability: string;
  deduction_other_mandatory_ins: string;
  deduction_riester: string;
  deduction_other: string;
  // Informativ (keine Formularfelder)
  jobcenter_name: string;
  customer_number: string;
  household_size: string;
}

interface EksReportMonth {
  year: number;
  month: number;
  label: string;
  revenue_net: number;
  revenue_gross: number;
  revenue_tax: number;
  material: number;
  gross_profit: number;
  expenses_total: number;
  operating_expenses: number;
  operating_result: number;
  /** Gezahlte Vorsteuer im Monat (Anlage EKS B17 / A5.3-Erstattung). */
  input_tax: number;
  blocks: Record<string, number>;
  /** Feiner Aufriss (Teilmenge von `blocks.other`) für die amtlichen EKS-Einzelzeilen B10–B14.3. */
  categories?: Record<string, number>;
}

/** Erklärungsart der Anlage EKS: `ist` = abschließend (Vergangenheit), `plan` = vorläufig (Zukunft). */
type EksMode = 'ist' | 'plan';

interface EksReport {
  settings: EksSettings;
  /** Kleinunternehmer §19 UStG (aus den Firmeneinstellungen) → EKS brutto statt netto. */
  kleinunternehmer?: boolean;
  /** Datenbasis: Ist-Werte oder Planwerte/Hochrechnung. */
  mode?: EksMode;
  /**
   * Zeitgrundlage, mit der der Server gerechnet hat (Anlage EKS = Zuflussprinzip,
   * § 3 Abs. 2 Bürgergeld-V). MUSS beim Drill-Down zurückgegeben werden, sonst listet die
   * Belegliste andere Rechnungen auf, als in der Zelle darüber stecken.
   */
  period?: 'invoice_date' | 'payment_date';
  from: string;
  to: string;
  /** V-2441: gespeicherter bzw. vorbelegter Zeitraum je Erklärungsart; `gespeichert=false` = nur Vorschlag. */
  zeitraeume?: Record<'vorlaeufig' | 'abschliessend', { from: string; to: string; gespeichert: boolean }>;
  /** Heutiges Datum des Servers (Y-m-d) für die Hinweise (Zeitraum vorbei, Zukunftsmonate). */
  heute?: string;
  revenue: { net: number; gross: number; tax: number; pos: Record<string, number>; invoices: Record<string, number> };
  expenses: { total: number; blocks: Record<string, number>; categories?: Record<string, number>; uncategorized: number };
  gross_profit: number;
  operating_result: number;
  months: EksReportMonth[];
  positions: FinancialBlock[];
  /** V-0439, Korrekturrunde 1, Befund 14: nur im Ist-Modus vorhanden (Planwerte fuehren keine bereits gebuchten Belege). */
  kasse_erloes_pruefhinweis?: KasseErloesPruefhinweis | null;
  /** V-2279 F-005: Datenhinweise (Gutschein-Einlösung unklar, Geräte-Ankauf nicht lesbar/abweichend). */
  hinweise?: ReportHinweis[];
}

// --- Planwerte (vorläufige Anlage EKS, V-2441) ---
/** Planbare Positionen je Monat. */
interface EksPlanValues {
  revenue_net: number;
  revenue_tax: number;
  input_tax: number;
  blocks: Record<string, number>;
  categories?: Record<string, number>;
}

/** Status eines Monats: `ist` = abgeschlossen (echte Zahlen), `laufend` = Ist + Erwartung, `prognose` = geschätzt. */
type EksMonthStatus = 'ist' | 'laufend' | 'prognose';

/** Eine Herleitungszeile: woher ein Teil einer Zahl kommt. */
interface EksHerleitungZeile {
  /** `ist`, `E1` Abos, `E2` offene Rechnungen, `E3` übrige Einnahmen, `A1` Lieferanten, `A2` offene Eingangsrechnungen, `A3` Grundrauschen. */
  quelle: string;
  typ: string;
  bezeichnung: string;
  betrag: number;
  datum: string | null;
  beleg_id: string | null;
  nummer: string | null;
  name: string | null;
  termin: string | null;
  faellig: string | null;
  /** Nur bei `typ === 'trend'` (V-2507): Bausteine der Trendrechnung. */
  trend?: { niveau: number; wachstum: number; wachstum_roh: number; deckel: number; k: number; anteil?: number | null; monate: number; unsicher: boolean };
}

/** Nur geänderte Zellen eines Monats (`null` = Vorschlag wiederherstellen). */
interface EksOverrideBody {
  revenue_net?: number | null;
  revenue_tax?: number | null;
  input_tax?: number | null;
  blocks?: Record<string, number | null>;
}

interface EksForecastMonth {
  /** 'YYYY-MM' */
  key: string;
  year: number;
  month: number;
  label: string;
  status: EksMonthStatus;
  /** Abgeschlossener Monat? (Kompatibilität: `status === 'ist'`) */
  complete: boolean;
  /** Ist-Werte bis heute (abgeschlossene und laufende Monate) — sonst null. */
  actual: EksPlanValues | null;
  /** Vorschlag: Ist (abgeschlossen), Ist + Erwartung (laufend) oder Schätzung aus den Quellen. */
  proposal: EksPlanValues;
  /** Gespeicherte Überschreibungen (nur geänderte Zellen) — sonst null. */
  override: { revenue_net?: number; revenue_tax?: number; input_tax?: number; blocks?: Record<string, number> } | null;
  /** Wirksamer Wert (Vorschlag + Überschreibung, im laufenden Monat nie unter dem Ist). */
  effective: EksPlanValues;
  /** Felder, die vom Vorschlag abweichen: `revenue_net`, `revenue_tax`, `input_tax`, `b:<block>`. */
  geaendert: string[];
  /** Herleitung je Feld (gleiche Schlüssel wie `geaendert`). */
  herleitung: Record<string, EksHerleitungZeile[]>;
}

/** Eine Zeile unter „Bewusst nicht eingerechnet“. */
interface EksNichtEingerechnet {
  quelle: string;
  grund: string;
  bezeichnung: string;
  betrag?: number;
  datum?: string;
  nummer?: string;
  name?: string;
  anzahl?: number;
  beleg_id?: string;
}

interface EksQuellen {
  abos?: { zeilen: EksHerleitungZeile[]; nicht_eingerechnet: EksNichtEingerechnet[]; jahresabos: { betrag: number; naechster_termin: string }[] };
  offene_rechnungen?: { zeilen: (EksHerleitungZeile & { monat: string })[]; nicht_eingerechnet: EksNichtEingerechnet[] };
  uebrige_einnahmen?: { netto: number; steuer: number; monate_basis: number };
  lieferanten?: { zeilen: (EksHerleitungZeile & { monat: string; rhythmus_monate?: number })[]; nicht_eingerechnet: EksNichtEingerechnet[] };
  offene_eingangsrechnungen?: (EksHerleitungZeile & { monat: string })[];
  grundrauschen?: { betrag: number; steuer: number; monate_basis: number };
  warnungen?: string[];
}

interface EksForecast {
  from: string;
  to: string;
  heute: string;
  kleinunternehmer?: boolean;
  blocks: FinancialBlock[];
  months: EksForecastMonth[];
  quellen: EksQuellen;
  hinweise: {
    /** Einmaliger Hinweis: alte (v1-)Planwerte werden nicht mehr angewendet. */
    v1_verworfen: boolean;
    /** Monate mit alten Überschreibungen, die nicht (mehr) gelten, weil der Monat abgeschlossen ist. */
    ignorierte_overrides: string[];
    warnungen: string[];
  };
}

// Drill-Down: Einzelbelege hinter einer Report-Position (EÜR/UStVA/BWA/EKS)
interface ReportDrilldownDoc {
  kind: 'invoice' | 'incoming_invoice' | 'kasse_geraete_ankauf';
  id: string;
  number: string;
  party: string;
  /** Belegdatum. */
  date: string;
  /** Zahltag — in der EÜR der maßgebliche Zeitpunkt (§ 11 EStG); null bei unbezahlten Belegen. */
  paid_date: string | null;
  net: number;
  tax: number;
  gross: number;
  amount: number;
}

interface ReportDrilldownPosSummary {
  net: number;
  gross: number;
  tax: number;
  transactions: number;
}

interface ReportDrilldown {
  side: 'income' | 'expense';
  from: string;
  to: string;
  block?: string | null;
  account?: string | null;
  tax_bucket?: string | null;
  documents: ReportDrilldownDoc[];
  pos_summary: ReportDrilldownPosSummary | null;
  total: number;
  /** z. B. 'uncategorized' → Belege enthalten einen Rest ohne Kontozuordnung */
  note: string | null;
}

interface ReportDrilldownParams {
  side: 'income' | 'expense';
  from: string;
  to: string;
  basis?: 'net' | 'gross' | 'tax';
  exclude_pos?: boolean;
  tax_bucket?: 'standard' | 'reduced' | 'free' | 'export' | 'intra' | 'reverse_charge' | null;
  block?: string | null;
  account?: string | null;
  /** Feiner Kategorie-Drilldown für die amtlichen EKS-Einzelzeilen B10–B14.3 (Komma-Liste). */
  category?: string | null;
  /** Aus der B14.5-Sammelzeile ausgelöste Kategorien ausschließen (Komma-Liste). */
  exclude_categories?: string | null;
  /**
   * Zeitliche Zuordnung — MUSS zu der Auswertung passen, aus der der Drill-Down aufgerufen wird:
   * EÜR (§ 11 EStG) und Anlage EKS (§ 3 Abs. 2 Bürgergeld-V) = 'payment_date' (Zahldatum),
   * BWA/UStVA = Belegdatum (Default).
   * Sonst zeigt die Belegliste andere Rechnungen, als in der Summe stecken.
   */
  period?: 'invoice_date' | 'payment_date';
}

interface EuerReportMonth {
  month: number;
  income: { total: number; net: number; vat: number };
  expenses: { total: number; net: number; vat: number };
  profit: number;
  net_profit: number;
  vat_payable: number;
}

interface EuerIncomeAccount {
  account: string;
  label: string;
  key: 'standard' | 'reduced' | 'exempt' | 'intra_eu' | 'kleinunternehmer';
  net: number;
  vat: number;
  gross: number;
}

interface EuerExpenseAccount {
  account: string;
  label: string;
  block: string;
  total: number;
  count: number;
}

interface EuerOpenItems {
  /** Offene Ausgangsrechnungen — noch nicht zugeflossen, daher nicht im Gewinn. */
  receivables: { count: number; gross: number };
  /** Offene Eingangsrechnungen — noch nicht abgeflossen, daher nicht im Gewinn. */
  payables: { count: number; gross: number };
}

interface EuerReport {
  year: number;
  from: string;
  to: string;
  /**
   * Zeitliche Zuordnung: Zufluss-/Abflussprinzip (§ 11 EStG) — es zählt das Zahldatum.
   * Bis 07/2026 stand hier das Belegdatum (`invoice_date`); das zog auch unbezahlte
   * Rechnungen in den Gewinn und war fachlich falsch.
   */
  basis: 'payment_date';
  /** Was NICHT in der EÜR steht, weil (noch) kein Geld geflossen ist. */
  open_items: EuerOpenItems;
  kleinunternehmer: boolean;
  months: EuerReportMonth[];
  /** Erlöskonten (DATEV-Kontenaufriss der Betriebseinnahmen). */
  income_accounts: EuerIncomeAccount[];
  income: { net: number; vat: number; gross: number };
  /** Aufwandskonten inkl. Zeile „Ohne Kontozuordnung". */
  expense_accounts: EuerExpenseAccount[];
  /** `total` = maßgeblich für die Gewinnermittlung (brutto bei Kleinunternehmern, sonst netto). */
  expenses: { net: number; gross: number; vat: number; total: number };
  totals: {
    income: number;
    expenses: number;
    /** Steuerlicher Gewinn: netto gegen netto (Kleinunternehmer brutto gegen brutto). */
    profit: number;
    vat: number;
    input_tax: number;
    vat_payable: number;
  };
  expense_categories: Array<{
    account: string;
    /** F-007: eindeutiger Zeilenschlüssel (Kontonummer, `__rest` oder `kasse_geraete_ankauf`). */
    key?: string;
    label: string;
    total: number;
    count: number;
  }>;
  /** V-0439, Korrekturrunde 1, Befund 14: Kassenbons des Jahres, deren Rechnung noch nicht entschieden ist (Erlös doppelt). */
  kasse_erloes_pruefhinweis?: KasseErloesPruefhinweis;
  /** V-2279 F-005: Datenhinweise (Gutschein-Einlösung unklar, Geräte-Ankauf nicht lesbar/abweichend). */
  hinweise?: ReportHinweis[];
}

// Thai Tax Report Types
interface ThaiPP30Month {
  month: number;
  sales: number;
  output_vat: number;
  purchases: number;
  input_vat: number;
  net_vat: number;
  filing_deadline: string;
}

interface ThaiPP30Report {
  year: number;
  vat_rate: number;
  months: ThaiPP30Month[];
  totals: { sales: number; output_vat: number; purchases: number; input_vat: number; net_vat: number };
}

interface ThaiWHTEntry {
  wht_type: string;
  wht_rate: number;
  month: number;
  count: number;
  paid_amount: number;
  wht_amount: number;
}

interface ThaiWHTReport {
  year: number;
  filing_day: number;
  entries: ThaiWHTEntry[];
  totals: { wht_amount: number; paid_amount: number; count: number };
}

interface ThaiIncomeTaxHalfYear {
  period: string;
  revenue: number;
  expenses: number;
  profit: number;
  form: string;
}

interface ThaiIncomeTaxReport {
  year: number;
  business_type: string;
  half_years: ThaiIncomeTaxHalfYear[];
  annual: { revenue: number; expenses: number; profit: number; estimated_tax: number; form: string };
}

interface ThaiThresholdItem {
  name: string;
  threshold: number;
  current: number;
  percentage: number;
  status: 'safe' | 'warning' | 'exceeded';
}

interface ThaiThresholdReport {
  year: number;
  ytd_revenue: number;
  projected_annual: number;
  vat_threshold: number;
  vat_registered: boolean;
  vat_percentage: number;
  vat_status: 'safe' | 'warning' | 'exceeded';
  thresholds: ThaiThresholdItem[];
}

// DATEV Online Types
export interface DatevOnlineStatus {
  connected: boolean;
  status: 'not_configured' | 'disconnected' | 'connected' | 'expired' | 'error';
  connectedAt: string | null;
  lastSyncAt: string | null;
  scopes: string[];
  message: string | null;
}

export interface DatevDocumentUpload {
  id: string;
  documentType: 'invoice' | 'credit_note' | 'receipt' | 'bank_statement' | 'other';
  sourceType: string | null;
  sourceId: string | null;
  fileName: string;
  filePath: string | null;
  fileHash: string | null;
  fileSize: number;
  datevDocumentId: string | null;
  datevStatus: 'pending' | 'uploading' | 'uploaded' | 'processed' | 'error';
  uploadedAt: string | null;
  processedAt: string | null;
  errorMessage: string | null;
  retryCount: number;
  createdAt: string;
}

export interface DatevInvoiceSubmission {
  id: string;
  invoiceId: string;
  invoiceNumber: string | null;
  invoiceTotal: number | null;
  submissionType: 'RDS1' | 'RDS2' | 'manual';
  documentUploadId: string | null;
  datevSubmissionId: string | null;
  datevStatus: 'pending' | 'submitted' | 'accepted' | 'rejected' | 'error';
  bookingSuggestionCreated: boolean;
  submittedAt: string | null;
  processedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface DatevSyncLog {
  id: string;
  syncType: 'documents' | 'invoices' | 'bookings' | 'debtors' | 'creditors';
  direction: 'upload' | 'download';
  recordsTotal: number;
  recordsSuccess: number;
  recordsFailed: number;
  startedAt: string;
  completedAt: string | null;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  errorLog: Array<{ error: string }> | null;
}

export interface DatevSyncStatistics {
  totalSyncs: number;
  last30Days: {
    syncs: number;
    recordsProcessed: number;
    recordsSuccess: number;
    recordsFailed: number;
    completedSyncs: number;
    failedSyncs: number;
  };
  lastSync: {
    id: string;
    syncType: string;
    status: string;
    startedAt: string;
    completedAt: string | null;
  } | null;
  pending: {
    documents: number;
    submissions: number;
    total: number;
  };
  connection: DatevOnlineStatus;
  nextSync: string | null;
}

export interface DatevOnlineSettings {
  autoUploadInvoices: boolean;
  autoUploadOnPayment: boolean;
  includeDocumentWithData: boolean;
  syncInterval: 'manual' | 'hourly' | 'daily' | 'weekly';
  syncTime: string;
  syncDayOfWeek: number;
  uploadInvoices: boolean;
  uploadCreditNotes: boolean;
  uploadExpenseReceipts: boolean;
  uploadBankStatements: boolean;
  rdsVersion: '1.0' | '2.0';
  createBookingSuggestions: boolean;
  includePaymentInfo: boolean;
  includeDebtorInfo: boolean;
}

/**
 * Stabile, automatisch erzeugte Geräte-ID dieses Browsers/PCs für die zentrale
 * BISPrint-Arbeitsplatz→Drucker-Zuordnung. Wird einmalig erzeugt und in localStorage gehalten
 * (keine Nutzeraktion nötig); der Admin ordnet das Gerät zentral einem Drucker-Client zu.
 */
export function getBisprintDeviceId(): string {
  try {
    let id = localStorage.getItem('bisprint_device_id');
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'dev-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('bisprint_device_id', id);
    }
    return id;
  } catch {
    return '';
  }
}

/** Ein Grund aus dem Rückläufer-Katalog (`ReshipReasonCatalog`). */
export interface ReshipReason {
  code: string;
  label: string;
  /** Liegt der Fehler in der Anschrift? Dann verlangt die Maske eine Korrektur. */
  addressFault: boolean;
  /** Bei diesem Grund ist ein Freitext Pflicht. */
  requiresNote: boolean;
  /** Erläuterung für die Auswahl. */
  note: string;
}

// =============================================================================
// DATENÜBERNAHME-ASSISTENT (Vorsystem-Import, z. B. GastroSoft Pro)
// Antwortformen von DatenuebernahmeController — Felder wie vom Backend geliefert (snake_case).
// =============================================================================

export interface DatenuebernahmeModifierGruppeZusammenfassung {
  name: string;
  optionen_anzahl: number;
  steuersatz: number | null;
  vorbelegung_hauptwarengruppe: string | null;
}

export interface DatenuebernahmeVorschauZeile {
  plu: string;
  name: string;
  hauptwarengruppe: string;
  unterwarengruppe: string;
  kategorie_pfad: string;
  preis: number | null;
  vat_rate: number | null;
  warnungen: string[];
}

/** Ausnahme: `mwstAh`/`mwstIh` bleiben camelCase — der Controller gibt `steuerKonflikte` roh weiter. */
export interface DatenuebernahmeSteuerKonflikt {
  plu: string;
  name: string;
  mwstAh: string;
  mwstIh: string;
}

export interface DatenuebernahmeAnalyseResponse {
  success: true;
  vorsystem: {
    schluessel: string;
    name: string;
    trefferquote: number;
  };
  spalten_zuordnung: Record<string, string>;
  zusammenfassung: {
    artikel_anzahl: number;
    modifier_gruppen_anzahl: number;
    modifier_gruppen: DatenuebernahmeModifierGruppeZusammenfassung[];
    /** Hauptwarengruppe => Unterwarengruppen (Reihenfolge = erstes Auftreten in der Datei). */
    kategoriebaum: Record<string, string[]>;
    dubletten_kategorien: string[];
  };
  vorschau: DatenuebernahmeVorschauZeile[];
  warnungen: string[];
  steuer_konflikte: DatenuebernahmeSteuerKonflikt[];
  kollidierende_skus_anzahl: number;
  /** Fertige `;`-CSV für den bestehenden `/articles/import/start`-Weg (Kopf = interne Feldnamen). */
  normalisierte_csv: string;
  column_mapping: Record<string, string>;
}

export interface DatenuebernahmeAnwendenResponse {
  success: true;
  /** In diesem Lauf tatsächlich NEU angelegte Kategorien (Haupt- + Unterwarengruppen) — ein zweiter Lauf auf derselben Datei zählt hier 0, wenn alles schon existiert. */
  kategorien_angelegt: number;
  /** Haupt- + Unterwarengruppen INSGESAMT angefasst (neu angelegt ODER wiederverwendet). */
  kategorien_gesamt: number;
  kategorien_bereits_vorhanden: number;
  /** In diesem Lauf tatsächlich NEU angelegte Modifikator-Gruppen. */
  modifier_gruppen_angelegt: number;
  modifier_gruppen_gesamt: number;
  modifier_gruppen_bereits_vorhanden: number;
  modifier_ziele_verknuepft: number;
  /** Anzahl tatsächlich verknüpfter Kategorie-Links je Modifikator-Gruppe (Gruppenname => Anzahl). */
  modifier_ziele_je_gruppe: Record<string, number>;
  /** Klartext-Meldungen zu verworfenen Zuordnungs-Einträgen (unbekannte Gruppe/Warengruppe) — leer, wenn nichts verworfen wurde. */
  zuordnung_verworfen: string[];
}

/** Vom Kunden in Schritt 3 gewählte Zuordnung: Modifikator-Gruppenname => gewählte Hauptwarengruppen. */
export type DatenuebernahmeZuordnung = Record<string, string[]>;

export class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private tenantId: string | null = null;
  private refreshTokenPromise: Promise<string> | null = null;

  // ETag cache: Maps endpoint URLs to { etag, data, timestamp }
  private etagCache: Map<string, { etag: string; data: unknown; timestamp: number }> = new Map();
  // V-1659: Der Cache liegt NUR im Arbeitsspeicher. Frueher stand er als Klartext im localStorage
  // und blieb nach dem Abmelden fuer den naechsten Benutzer am selben Browser lesbar.
  // Der Schluessel dient nur noch dem einmaligen Aufraeumen alter Bestaende.
  private static readonly ETAG_CACHE_KEY = 'api_etag_cache';

  // V-1659: Callback bei Abmelden/Benutzer-/Mandantenwechsel (z. B. React-Query-Cache leeren)
  private authWechselCallbacks: Array<() => void> = [];

  onAuthWechsel(callback: () => void): void {
    this.authWechselCallbacks.push(callback);
  }

  private meldeAuthWechsel(): void {
    this.etagCache.clear();
    this.authWechselCallbacks.forEach(cb => {
      try { cb(); } catch { /* Aufraeumen darf nie das Abmelden blockieren */ }
    });
  }

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.loadTokenFromStorage();
    this.purgeLegacyETagStorage();
  }

  /** Entfernt den frueheren persistenten Antwort-Cache (V-1659). */
  private purgeLegacyETagStorage(): void {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(ApiClient.ETAG_CACHE_KEY);
      } catch {
        // Speicher nicht verfuegbar — nichts zu tun
      }
    }
  }

  /** Benutzerkennung aus dem JWT (nur zur Cache-Trennung, keine Pruefung). */
  private static userKeyFromToken(token: string | null): string {
    if (!token) return '';
    try {
      const part = token.split('.')[1];
      if (!part) return '';
      const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
      const p = JSON.parse(json) as Record<string, unknown>;
      const id = p.user_id ?? p.uid ?? p.sub ?? p.username ?? p.email ?? '';
      return String(id);
    } catch {
      return '';
    }
  }

  private loadTokenFromStorage(): void {
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem('auth_token');
      this.tenantId = localStorage.getItem('tenant_id');
    }
  }

  private getCacheKey(url: string): string {
    // Mandant UND Benutzer im Schluessel (V-1659)
    return `${this.tenantId ?? ''}:${ApiClient.userKeyFromToken(this.token)}:${url}`;
  }

  setToken(token: string | null): void {
    // Abmelden oder Benutzerwechsel: alle gecachten Antworten verwerfen (ein Token-Refresh
    // desselben Benutzers behaelt den Cache).
    if (!token || ApiClient.userKeyFromToken(token) !== ApiClient.userKeyFromToken(this.token)) {
      this.meldeAuthWechsel();
    }
    this.token = token;
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem('auth_token', token);
      } else {
        localStorage.removeItem('auth_token');
      }
    }
  }

  setTenantId(tenantId: string | null): void {
    if (tenantId !== this.tenantId) {
      this.meldeAuthWechsel();
    }
    this.tenantId = tenantId;
    if (typeof window !== 'undefined') {
      if (tenantId) {
        localStorage.setItem('tenant_id', tenantId);
      } else {
        localStorage.removeItem('tenant_id');
      }
    }
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return this.token !== null;
  }

  /**
   * Clear all ETag cache entries
   */
  clearETagCache(): void {
    this.etagCache.clear();
    this.purgeLegacyETagStorage();
  }

  /**
   * Invalidate ETag cache for endpoints matching a pattern
   * Useful after mutations to ensure fresh data
   */
  invalidateCache(pattern: string | RegExp): void {
    const keysToDelete: string[] = [];
    this.etagCache.forEach((_, key) => {
      if (typeof pattern === 'string') {
        if (key.includes(pattern)) {
          keysToDelete.push(key);
        }
      } else if (pattern.test(key)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.etagCache.delete(key));
  }

  private buildUrl(endpoint: string, params?: Record<string, string | string[] | number | boolean | undefined>): string {
    // baseUrl kann relativ sein (z. B. '/api' nach dem Subdomain-Cutover) — dann MUSS
    // new URL() eine Basis bekommen, sonst wirft es synchron ('Invalid URL') und JEDER
    // api-Client-Aufruf scheitert vor dem fetch. Bei absoluter baseUrl wird die Basis ignoriert.
    const path = endpoint.startsWith('/') ? `${this.baseUrl}${endpoint}` : `${this.baseUrl}/${endpoint}`;
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(path, base);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => url.searchParams.append(`${key}[]`, String(v)));
          } else {
            url.searchParams.append(key, String(value));
          }
        }
      });
    }

    return url.toString();
  }

  private getHeaders(options?: RequestOptions): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/ld+json, application/json',
    });

    if (!options?.skipAuth && this.token) {
      headers.set('Authorization', `Bearer ${this.token}`);
    }

    if (this.tenantId) {
      headers.set('X-Tenant-ID', this.tenantId);
    }

    // BISPrint: stabile Geräte-ID dieses PCs (zentrale Arbeitsplatz→Drucker-Zuordnung) sowie der
    // Legacy-Header X-BISPrint-Workstation (localStorage-clientId) als Rückwärtskompatibilität.
    try {
      const dev = getBisprintDeviceId();
      if (dev) headers.set('X-BISPrint-Device', dev);
      const ws = localStorage.getItem('bisprint_workstation_client_id');
      if (ws) headers.set('X-BISPrint-Workstation', ws);
    } catch { /* localStorage nicht verfügbar — ignorieren */ }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let error: ApiError;

      try {
        const data = await response.json();
        // Check multiple possible error message fields (API uses 'error', Symfony uses 'message')
        const errorMessage = data.message || data.error || data['hydra:description'] || response.statusText || `HTTP ${response.status}`;
        error = {
          status: response.status,
          message: errorMessage,
          detail: data.detail || data['hydra:title'],
          violations: data.violations,
          errors: data.errors,
          ...data,
        };
      } catch {
        error = {
          status: response.status,
          message: response.statusText || `HTTP ${response.status}`,
        };
      }

      // Handle 401 - Unauthorized. Grund (z. B. 'session_superseded' = Lizenz auf anderem Gerät
      // gebunden) mitgeben, damit der Dialog eine differenzierte Meldung zeigen kann.
      //
      // 🚨 NICHT jeder 401 betrifft die angemeldete Sitzung: Die /mde/*-Endpoints gehören der
      // Lager-App und prüfen ausschliesslich `X-POS-Device-Token`. Ein Browser hat den nie, ihr
      // 401 ist dort der Normalfall. Bis 14.08.2026 loeschte diese Stelle daraufhin den Token —
      // ein einziger solcher Aufruf (Einstellungen -> Pick & Pack) warf den Benutzer aus dem ERP.
      // Die Weiche muss VOR `setToken(null)` sitzen: Ein `.catch()` an der Aufrufstelle kommt zu
      // spaet, der Token ist dann schon weg.
      const isDeviceAuthResponse =
        response.url.includes('/api/mde/')
        || (error as { code?: string } | undefined)?.code === 'device_token_required';

      // V-1015: Der Mandant arbeitet auf der anderen Ebene (Beta/Produktion) → dorthin umleiten.
      // Das Token gehört zu dieser Herkunft und wird hier nicht weiterverwendet.
      if (response.status === 403 && (error as { code?: string }).code === 'falsche_ebene') {
        const ziel = (error as { ziel?: unknown }).ziel;
        if (istErlaubtesZiel(ziel) && new URL(ziel).origin !== window.location.origin) {
          this.setToken(null);
          leiteBeiFalscherEbeneUm(ziel);
        }
      }

      if (response.status === 401 && !isDeviceAuthResponse) {
        this.setToken(null);
        const reason = (error as { error?: string } | undefined)?.error;
        // Kein technischer Fehler, sondern eine erwartete Lizenz-Situation: als ruhige Info
        // ausgeben statt sie wie einen Absturz wirken zu lassen. (Die eine native Browser-Zeile
        // „… 401" laesst sich nicht unterdruecken — sie kommt vom Browser selbst.)
        if (reason === 'session_superseded') {
          // eslint-disable-next-line no-console
          console.info('Sitzung beendet – nicht genügend Benutzerlizenzen (Konto auf einem anderen Gerät angemeldet).');
        }
        window.dispatchEvent(new CustomEvent('auth:unauthorized', {
          detail: { reason },
        }));
      }

      throw error;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null as T;
    }

    return response.json();
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, skipAuth, ...fetchOptions } = options;

    const url = this.buildUrl(endpoint, params);
    const headers = this.getHeaders(options);
    const method = fetchOptions.method || 'GET';
    const isGetRequest = method === 'GET' || method === 'HEAD';
    const cacheKey = this.getCacheKey(url);

    // Add If-None-Match header for GET requests if we have a cached ETag
    if (isGetRequest) {
      const cached = this.etagCache.get(cacheKey);
      if (cached) {
        headers.set('If-None-Match', cached.etag);
      }
    }

    // Live-Debug-Logs: der eigene Ingest darf sich nicht selbst protokollieren,
    // sonst erzeugt ein Ausfall des Log-Endpoints eine Rueckkopplung.
    const isDebugIngest = endpoint.startsWith('/debug/client-');
    const startedAt = Date.now();

    let response: Response;
    try {
      response = await fetch(url, {
        ...fetchOptions,
        headers,
        // Always revalidate with server - our own ETag cache handles 304 responses.
        // Without this, browser's max-age cache returns stale data after mutations.
        cache: 'no-cache',
        // Senden des httpOnly-JWT-Cookies (gesetzt vom Backend beim Login). Parallel zum
        // Authorization-Header — erlaubt schrittweise Migration weg von localStorage.
        credentials: 'include',
      });
    } catch (networkError) {
      if (!isDebugIngest) {
        debugLogger.reportApiError({
          method,
          path: endpoint,
          status: 0,
          detail: networkError instanceof Error ? networkError.message : 'Netzwerkfehler',
        });
      }
      throw networkError;
    }

    if (!isDebugIngest) {
      // X-Request-Id verbindet diese Browser-Zeile mit der Backend-Zeile desselben Aufrufs.
      const requestId = response.headers.get('X-Request-Id');
      if (response.status >= 500) {
        debugLogger.reportApiError({ method, path: endpoint, status: response.status, requestId });
      } else if (debugLogger.isFullDebugActive()) {
        debugLogger.reportApiCall(method, endpoint, response.status, Date.now() - startedAt, requestId);
      }
    }

    // Handle 304 Not Modified - return cached data
    if (response.status === 304 && isGetRequest) {
      const cached = this.etagCache.get(cacheKey);
      if (cached) {
        return cached.data as T;
      }
      // Kein Cache-Eintrag (z. B. waehrend der Anfrage geleert, V-1659): einmal ohne If-None-Match wiederholen
      if (headers.has('If-None-Match')) {
        headers.delete('If-None-Match');
        response = await fetch(url, { ...fetchOptions, headers, cache: 'no-cache', credentials: 'include' });
      }
    }

    const result = await this.handleResponse<T>(response);

    // Cache successful GET responses with ETag
    if (isGetRequest && response.ok) {
      const etag = response.headers.get('ETag');
      if (etag && result !== null) {
        this.etagCache.set(cacheKey, {
          etag,
          data: result,
          timestamp: Date.now(),
        });
      }
    }

    return result;
  }

  async get<T>(endpoint: string, params?: Record<string, string | string[] | number | boolean | undefined>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/merge-patch+json',
      },
    } as RequestOptions);
  }

  /**
   * DELETE — optional mit Rumpf.
   *
   * 🚨 Der Rumpf ist kein Luxus: `DELETE /activity-logs` (Sammellöschung) liest die Filter
   * aus dem Request-Body und antwortet ohne sie mit 400 „At least one filter … is required".
   * Solange `delete()` keinen Body senden konnte, war diese Funktion unerreichbar — der
   * Aufrufer übergab die Filter zwar, sie wurden nur nie mitgeschickt.
   */
  async delete<T = void>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Raw fetch method for file downloads (returns Response object, not parsed JSON)
   */
  async fetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const url = this.buildUrl(endpoint);
    const headers = this.getHeaders();

    // Merge headers from options
    if (options.headers) {
      const optHeaders = new Headers(options.headers);
      optHeaders.forEach((value, key) => {
        headers.set(key, value);
      });
    }

    const response = await window.fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok && response.status === 401) {
      let reason: string | undefined;
      let code: string | undefined;
      try {
        const body = await response.clone().json();
        reason = body?.error;
        code = body?.code;
      } catch {
        // Body nicht lesbar -> generische Meldung
      }
      // Wie in `handleResponse`: ein 401 aus einem geraeteauthentifizierten Endpoint sagt nichts
      // ueber die Browser-Sitzung aus und darf sie nicht beenden.
      if (!endpoint.startsWith('/mde/') && code !== 'device_token_required') {
        this.setToken(null);
        window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { reason } }));
      }
    }

    return response;
  }

  // Authentication methods
  async login(email: string, password: string, isAdmin = false): Promise<LoginResponse> {
    const endpoint = isAdmin ? '/admin/login' : '/login';
    const response = await this.request<LoginResponse>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      skipAuth: true,
    });

    this.setToken(response.token);
    return response;
  }

  async adminLogin(email: string, password: string): Promise<{ token: string }> {
    return this.login(email, password, true);
  }

  async logout(): Promise<void> {
    // V-1659: Sofort lokal abmelden; der Server-Logout (Sitzung widerrufen, httpOnly-Cookie
    // loeschen) geht danach mit dem GESICHERTEN Token im Hintergrund raus. Er laeuft bewusst
    // nicht ueber request(): kein 401-/auth:unauthorized-Pfad, kein setToken.
    if (this.logoutLaeuft) return;
    this.logoutLaeuft = true;
    const altesToken = this.token;
    const alterMandant = this.tenantId;
    this.setToken(null);
    this.setTenantId(null);
    this.setSupportSession(null);
    try {
      if (altesToken) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 5000);
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${altesToken}`,
        };
        if (alterMandant) headers['X-Tenant-ID'] = alterMandant;
        fetch(`${this.baseUrl}/auth/logout`, {
          method: 'POST',
          headers,
          body: '{}',
          credentials: 'include',
          keepalive: true,
          signal: controller.signal,
        })
          .catch(() => { /* Ergebnis egal, lokal ist bereits abgemeldet */ })
          .finally(() => clearTimeout(timer));
      }
    } finally {
      this.logoutLaeuft = false;
    }
  }

  private logoutLaeuft = false;

  // Pagination helper
  async getPaginated<T>(
    endpoint: string,
    page = 1,
    itemsPerPage = 30,
    filters?: Record<string, string | string[] | number | boolean | undefined>
  ): Promise<{ items: T[]; total: number; page: number; pages: number }> {
    const response = await this.get<PaginatedResponse<T>>(endpoint, {
      page,
      itemsPerPage,
      ...filters,
    });

    const total = response['hydra:totalItems'];
    const pages = Math.ceil(total / itemsPerPage);

    return {
      items: response['hydra:member'],
      total,
      page,
      pages,
    };
  }

  // ==========================================
  // SMTP Settings (Admin)
  // ==========================================

  // SMTP-Konfigurationen je `purpose` (z. B. 'system' für Support-/interne BIS-ERP-Mails,
  // 'pos_outgoing' für Kunden-Ausgangsmails aus den POS-Apps). Default 'system' → bestehende
  // Aufrufer ohne Argument funktionieren unverändert.
  async getSmtpSettings(purpose?: string): Promise<SmtpSettingsResponse> {
    const q = purpose ? `?purpose=${encodeURIComponent(purpose)}` : '';
    return this.get<SmtpSettingsResponse>(`/admin/smtp-settings${q}`);
  }

  async updateSmtpSettings(settings: SmtpSettingsInput, purpose?: string): Promise<SmtpSettingsResponse> {
    const payload = purpose ? { ...settings, purpose } : settings;
    return this.put<SmtpSettingsResponse>('/admin/smtp-settings', payload);
  }

  async testSmtpSettings(email: string, purpose?: string): Promise<{ success: boolean; message: string }> {
    const body: Record<string, unknown> = { email };
    if (purpose) body.purpose = purpose;
    return this.post<{ success: boolean; message: string }>('/admin/smtp-settings/test', body);
  }

  // ==========================================
  // Gmail OAuth (Admin)
  // ==========================================

  async getGmailOAuthStatus(): Promise<{
    configured: boolean;
    status: string;
    is_active: boolean;
    email_address?: string;
    from_name?: string;
    connected_at?: string;
    last_error?: string;
  }> {
    return this.get('/admin/gmail-oauth/status');
  }

  async connectGmailOAuth(): Promise<{ success: boolean; auth_url?: string; error?: string }> {
    return this.post('/admin/gmail-oauth/connect', {});
  }

  async disconnectGmailOAuth(): Promise<{ success: boolean }> {
    return this.post('/admin/gmail-oauth/disconnect', {});
  }

  async updateGmailOAuth(data: { from_name?: string; is_active?: boolean }): Promise<{ success: boolean }> {
    return this.put('/admin/gmail-oauth/update', data);
  }

  async testGmailOAuth(email: string): Promise<{ success: boolean; message: string }> {
    return this.post('/admin/gmail-oauth/test', { email });
  }

  async getEmailRoutingConfig(): Promise<{ success: boolean; data: { routing_system: string; routing_customer: string; system_reply_to: string | null; routing_overrides: Record<string, string>; reply_to_overrides: Record<string, string>; freemail_force_gmail: boolean; freemail_domains: string[] | null }; email_types: { system: string[]; customer: string[] }; freemail_default_domains?: string[] }> {
    return this.get('/admin/gmail-oauth/routing');
  }

  async updateEmailRoutingConfig(data: { routing_system?: string; routing_customer?: string; system_reply_to?: string | null; routing_overrides?: Record<string, string>; reply_to_overrides?: Record<string, string>; freemail_force_gmail?: boolean; freemail_domains?: string[] | null }): Promise<{ success: boolean; data: Record<string, unknown> }> {
    return this.put('/admin/gmail-oauth/routing', data);
  }

  async getGmailSupportInbox(): Promise<{ success: boolean; data: { support_inbox_enabled: boolean; support_inbox_label: string; support_inbox_start_date: string | null; warning?: string } }> {
    return this.get('/admin/gmail-oauth/support-inbox');
  }

  async updateGmailSupportInbox(data: { support_inbox_enabled?: boolean; support_inbox_label?: string; support_inbox_start_date?: string | null }): Promise<{ success: boolean; error?: string; data?: { support_inbox_enabled: boolean; support_inbox_label: string; support_inbox_start_date: string | null } }> {
    return this.put('/admin/gmail-oauth/support-inbox', data);
  }

  async fetchGmailSupportInbox(): Promise<{ success: boolean; fetched: number; error?: string; skipped?: string }> {
    return this.post('/admin/gmail-oauth/support-inbox/fetch', {});
  }

  /**
   * Vorschau auf den Gmail-Posteingang: zaehlt ungelesene Mails insgesamt und ab dem Startdatum.
   * Rein lesend — erzeugt kein Ticket und markiert nichts als gelesen.
   */
  async previewGmailSupportInbox(): Promise<{ success: boolean; error?: string; data?: { email_address: string; support_inbox_start_date: string | null; ungelesen_gesamt: number; ungelesen_gesamt_mindestens: boolean; wuerden_tickets: number | null; wuerden_tickets_mindestens: boolean; neueste: Array<{ from: string; subject: string; date: string }> } }> {
    return this.get('/admin/gmail-oauth/support-inbox/preview');
  }

  // ==========================================
  // Support Access (Admin)
  // ==========================================

  async requestSupportAccess(tenantId: string, reason: string): Promise<SupportAccessRequestResponse> {
    return this.post<SupportAccessRequestResponse>('/admin/support-access/request', { tenantId, reason });
  }

  async getSupportAccessRequests(): Promise<{ requests: SupportAccessRequest[] }> {
    return this.get<{ requests: SupportAccessRequest[] }>('/admin/support-access/requests');
  }

  async cancelSupportAccessRequest(id: string): Promise<void> {
    return this.delete(`/admin/support-access/requests/${id}`);
  }

  async loginAsTenant(requestId: string): Promise<SupportSessionResponse> {
    return this.post<SupportSessionResponse>(`/admin/support-access/login/${requestId}`);
  }

  // ==========================================
  // Support Access (Tenant)
  // ==========================================

  async getPendingSupportRequests(): Promise<{ requests: PendingSupportRequest[] }> {
    return this.get<{ requests: PendingSupportRequest[] }>('/support-access/pending');
  }

  async getActiveSupportSessions(): Promise<{ sessions: ActiveSupportSession[] }> {
    return this.get<{ sessions: ActiveSupportSession[] }>('/support-access/active-sessions');
  }

  async approveSupportRequest(id: string): Promise<void> {
    return this.post(`/support-access/${id}/approve`);
  }

  async rejectSupportRequest(id: string): Promise<void> {
    return this.post(`/support-access/${id}/reject`);
  }

  async terminateSupportSession(sessionId: string): Promise<void> {
    return this.post(`/support-access/session/${sessionId}/terminate`);
  }

  // ==========================================
  // Support Session Management
  // ==========================================

  setSupportSession(session: SupportSessionInfo | null): void {
    if (typeof window !== 'undefined') {
      if (session) {
        localStorage.setItem('support_session', JSON.stringify(session));
      } else {
        localStorage.removeItem('support_session');
      }
    }
  }

  getSupportSession(): SupportSessionInfo | null {
    if (typeof window !== 'undefined') {
      const data = localStorage.getItem('support_session');
      if (data) {
        try {
          return JSON.parse(data);
        } catch {
          return null;
        }
      }
    }
    return null;
  }

  isSupportSession(): boolean {
    return this.getSupportSession() !== null;
  }

  // ==========================================
  // Password Reset
  // ==========================================

  async requestPasswordReset(email: string, isAdmin: boolean = false): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email, isAdmin }),
      skipAuth: true,
    });
  }

  async validatePasswordResetToken(token: string): Promise<{ valid: boolean; email?: string; userType?: string }> {
    return this.request<{ valid: boolean; email?: string; userType?: string }>(`/password-reset/validate/${token}`, {
      method: 'GET',
      skipAuth: true,
    });
  }

  async resetPassword(token: string, password: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/password-reset/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
      skipAuth: true,
    });
  }

  // ==========================================
  // Registration
  // ==========================================

  async register(data: {
    companyName: string;
    industry?: string;
    country?: string;
    // Unternehmer-Bestätigung + AGB-Annahme — serverseitig Pflicht (RegistrationController),
    // gespeichert als users.agb_version/agb_accepted_at.
    business_terms_accepted: boolean;
    // Anschrift und Rechtsform: seit 28.08.2026 Pflicht in der Registrierung, damit kein
    // Mandant mehr ohne Firmendaten entsteht (61 von 168 Bestandsmandanten haben keine).
    street?: string;
    houseNumber?: string;
    postalCode?: string;
    city?: string;
    legalForm?: string;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    from?: string;
    captchaToken?: string;
    referralCode?: string;
    // Aktionscode aus dem ?aktion=-Parameter eines Werbe-Links (z.B. Aktion Handyshop x SumUp) —
    // wird beim Anlegen des Mandanten still eingeloest (App\Service\Aktion\AktionTeilnahmeService).
    aktionCode?: string;
    website?: string;
  }): Promise<{ success: boolean; message: string; verificationEmailSent?: boolean; tenant?: { id: string; companyName: string } }> {
    return this.request<{ success: boolean; message: string; verificationEmailSent?: boolean; tenant?: { id: string; companyName: string } }>('/register', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
    });
  }

  // ==========================================
  // Tenant Management (Admin)
  // ==========================================

  async getTenants(): Promise<{ tenants: Tenant[] }> {
    return this.get<{ tenants: Tenant[] }>('/admin/tenants');
  }

  async getTenant(id: string): Promise<Tenant> {
    return this.get<Tenant>(`/admin/tenants/${id}`);
  }

  async createTenant(data: {
    companyName: string;
    contactEmail: string;
    contactPhone?: string;
    contactPerson?: string;
    plan?: string;
  }): Promise<{ success: boolean; tenant: { id: string; companyName: string; contactEmail: string; status: string } }> {
    return this.post('/admin/tenants', data);
  }

  async updateTenant(id: string, data: Partial<{
    companyName: string;
    contactEmail: string;
    contactPhone: string | null;
    contactPerson: string | null;
    billingAddress: string | null;
  }>): Promise<{ success: boolean; tenant: { id: string; companyName: string; contactEmail: string; status: string } }> {
    return this.put(`/admin/tenants/${id}`, data);
  }

  async deleteTenant(id: string, altdatenRueckgabeVermerk?: string): Promise<{ success: boolean }> {
    // V-0003: Mandant mit Altdaten-Ablage lässt sich nur mit dem Vermerk „Rückgabe erfolgt“ löschen.
    return this.delete(
      `/admin/tenants/${id}`,
      altdatenRueckgabeVermerk ? { altdaten_rueckgabe_vermerk: altdatenRueckgabeVermerk } : undefined,
    );
  }

  async suspendTenant(id: string): Promise<{ success: boolean; status: string }> {
    return this.post(`/admin/tenants/${id}/suspend`);
  }

  async activateTenant(id: string): Promise<{ success: boolean; status: string }> {
    return this.post(`/admin/tenants/${id}/activate`);
  }

  async adminRefreshShopifyTaxonomy(): Promise<{ success: boolean; tenantsCleared: number; errors: number }> {
    return this.post('/admin/shopify/taxonomy/refresh');
  }

  // ==========================================
  // Number Ranges (Tenant Settings)
  // ==========================================

  async getNumberRanges(): Promise<{ numberRanges: NumberRange[] }> {
    return this.get<{ numberRanges: NumberRange[] }>('/settings/number-ranges');
  }

  // V-0291: tatsächlich nächste Nummer (Belegungsprüfung wie bei der echten Vergabe, nie gecacht).
  async getNumberRangePreviews(): Promise<{ vorschau: Record<string, string | null> }> {
    return this.get<{ vorschau: Record<string, string | null> }>('/settings/number-ranges-vorschau');
  }

  async getNumberRangePreview(
    type: string,
    overrides: { prefix?: string; suffix?: string; min_digits?: number; current_number?: number } = {},
  ): Promise<{ type: string; vorschau: string | null }> {
    const params = new URLSearchParams({ type });
    Object.entries(overrides).forEach(([key, value]) => {
      if (value !== undefined) params.set(key, String(value));
    });
    return this.get<{ type: string; vorschau: string | null }>(`/settings/number-ranges-vorschau?${params.toString()}`);
  }

  async getNumberRange(id: string): Promise<NumberRange> {
    return this.get<NumberRange>(`/settings/number-ranges/${id}`);
  }

  async updateNumberRange(id: string, data: {
    prefix?: string;
    suffix?: string;
    currentNumber?: number;
    minDigits?: number;
    isActive?: boolean;
    resetInterval?: 'never' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
    resetDay?: number | null;
    customDays?: number | null;
  }): Promise<{ success: boolean; preview: string | null }> {
    return this.put(`/settings/number-ranges/${id}`, data);
  }

  async getNextNumber(type: string): Promise<{ number: string; rawNumber: number }> {
    return this.post(`/settings/number-ranges/${type}/next`);
  }

  // ==========================================
  // Cancellation Reasons (Settings)
  // ==========================================

  async getCancellationReasons(): Promise<{ cancellationReasons: CancellationReason[] }> {
    return this.get('/settings/cancellation-reasons');
  }

  async getCancellationReason(id: string): Promise<CancellationReason> {
    return this.get(`/settings/cancellation-reasons/${id}`);
  }

  async createCancellationReason(data: CancellationReasonInput): Promise<{ success: boolean; id: string }> {
    return this.post('/settings/cancellation-reasons', data);
  }

  async updateCancellationReason(id: string, data: Partial<CancellationReasonInput>): Promise<{ success: boolean }> {
    return this.put(`/settings/cancellation-reasons/${id}`, data);
  }

  async deleteCancellationReason(id: string): Promise<{ success: boolean }> {
    return this.delete(`/settings/cancellation-reasons/${id}`);
  }

  // ==========================================
  // Customer Groups (Settings)
  // ==========================================

  async getCustomerGroups(): Promise<{ customerGroups: CustomerGroup[] }> {
    return this.get('/settings/customer-groups');
  }

  async createCustomerGroup(data: CustomerGroupInput): Promise<{ success: boolean; id: string }> {
    return this.post('/settings/customer-groups', data);
  }

  async updateCustomerGroup(id: string, data: Partial<CustomerGroupInput>): Promise<{ success: boolean }> {
    return this.put(`/settings/customer-groups/${id}`, data);
  }

  async deleteCustomerGroup(id: string): Promise<{ success: boolean }> {
    return this.delete(`/settings/customer-groups/${id}`);
  }

  // Modifikatoren (Zubereitung/Beilagen/Größen) — gepflegt unter
  // Einstellungen → Artikel → Modifikatoren. Serverseitig `ProductModifierController`
  // (`/pos/modifier-groups`); die Zuordnung an Kategorien wirkt für alle Artikel darin.
  async getModifierGroups(): Promise<{ success: boolean; groups: ModifierGroup[] }> {
    return this.get('/pos/modifier-groups');
  }

  async createModifierGroup(data: ModifierGroupInput): Promise<{ success: boolean; id: string }> {
    return this.post('/pos/modifier-groups', data);
  }

  async updateModifierGroup(id: string, data: Partial<ModifierGroupInput>): Promise<{ success: boolean }> {
    return this.put(`/pos/modifier-groups/${id}`, data);
  }

  async deleteModifierGroup(id: string): Promise<{ success: boolean }> {
    return this.delete(`/pos/modifier-groups/${id}`);
  }

  /**
   * Kopiert eine Gruppe samt Optionen — bewusst OHNE ihre Artikel-/Kategorie-Zuordnungen.
   * Eine Kopie, die sofort an denselben Artikeln hinge, fragte beim Verkauf alles doppelt ab.
   */
  async duplicateModifierGroup(id: string, name?: string): Promise<{ success: boolean; id: string; name: string; optionCount: number }> {
    return this.post(`/pos/modifier-groups/${id}/duplicate`, name ? { name } : {});
  }

  /**
   * Voll-Ersatz der Zuordnung: Was nicht mitkommt, wird entfernt.
   *
   * `categoryInherit` ist optional — fehlt es, vererbt jede Kategorie an ihre Unterkategorien
   * (die Vorgabe). Nur auslassen, wenn die Schalter wirklich unbekannt sind; ein leeres Objekt
   * bedeutet dasselbe wie „alle vererben", nicht „keine".
   *
   * V-0534: `ausnahme_artikel_ids` (optional) ersetzt die Ausnahmen je Artikel; fehlt das Feld, bleiben
   * sie stehen. Ein ausdrücklich zugeordneter Artikel (`articleIds`) schlägt seine Ausnahme.
   */
  async setModifierGroupTargets(
    id: string,
    targets: {
      articleIds: string[];
      categoryIds: string[];
      categoryInherit?: Record<string, boolean>;
      ausnahme_artikel_ids?: string[];
      /** V-0534: `zuordnung_revision`, auf der der Dialog aufsetzt (optimistische Sperre, siehe `ModifierGroup`). */
      basis_zuordnung_revision?: number;
    }
  ): Promise<{
    success: boolean;
    articleCount: number;
    categoryCount: number;
    ausnahme_artikel_count?: number;
    /** V-0081: Anzahl gesendeter IDs, die sich nicht auf 32 Hex normalisieren liessen. */
    discarded_article_count?: number;
    discarded_category_count?: number;
  }> {
    return this.put(`/pos/modifier-groups/${id}/targets`, targets);
  }

  /**
   * Gänge (V-0076) — Einstellungen → Artikel → Gänge. Serverseitig PosCourseController.
   *
   * 🚨 `mitZaehlung` ist ein bewusster Opt-in (Review-Runde 23.09.2026): `effectiveArticleCount`
   * rechnet serverseitig über das GESAMTE aktive Sortiment (`artikelZahlJeGang()`). NUR
   * `CourseSettings.tsx` (zeigt die Spalte „Wirkt auf") übergibt `true` — jeder andere Aufrufer
   * (Artikel-Detail, Kategorien) braucht die Zählung nicht und würde sie sonst bei jedem Öffnen
   * unnötig mit auslösen.
   */
  async getPosCourses(mitZaehlung = false): Promise<{ success: boolean; courses: PosCourse[] }> {
    return this.get('/pos/courses', mitZaehlung ? { counts: '1' } : undefined);
  }

  async createPosCourse(data: PosCourseInput): Promise<{ success: boolean; course_key: string }> {
    return this.post('/pos/courses', data);
  }

  async updatePosCourse(courseKey: string, data: Partial<PosCourseInput>): Promise<{ success: boolean }> {
    return this.put(`/pos/courses/${courseKey}`, data);
  }

  /**
   * Ohne `replacementCourseKey`: 409 mit `category_count`/`article_count`, wenn der Gang noch
   * verwendet wird — kein automatisches Überschreiben. Mit gültigem Ersatzschlüssel werden alle
   * Verweise (Kategorien + Artikel-Ausnahmen) umgehängt.
   */
  async deletePosCourse(courseKey: string, replacementCourseKey?: string): Promise<{ success: boolean }> {
    return this.delete(`/pos/courses/${courseKey}`, replacementCourseKey ? { replacement_course_key: replacementCourseKey } : undefined);
  }

  /**
   * Artikel-Ausnahme vom vererbten Gang lesen — roh (Ausnahme), aufgelöst (wirksam, inkl.
   * eigener Ausnahme) UND `inherited_course_key` (aufgelöst OHNE die eigene Ausnahme — was der
   * Artikel erben WÜRDE, für das Label „erbt von der Warengruppe (aktuell: X)").
   */
  async getArticlePosCourse(articleId: string): Promise<{ success: boolean; course_key: string | null; effective_course_key: string | null; inherited_course_key: string | null }> {
    return this.get(`/pos/courses/articles/${articleId}`);
  }

  /** `courseKey: null` entfernt die Ausnahme — der Artikel erbt danach wieder über seine Kategorie. */
  async setArticlePosCourse(articleId: string, courseKey: string | null): Promise<{ success: boolean }> {
    return this.put(`/pos/courses/articles/${articleId}`, { course_key: courseKey });
  }

  // Article Conditions (Artikel-Zustände)
  async getArticleConditions(): Promise<{ conditions: ArticleCondition[] }> {
    return this.get('/article-conditions');
  }

  async createArticleCondition(data: ArticleConditionInput): Promise<{ success: boolean; id: string }> {
    return this.post('/article-conditions', data);
  }

  async updateArticleCondition(id: string, data: Partial<ArticleConditionInput>): Promise<{ success: boolean }> {
    return this.put(`/article-conditions/${id}`, data);
  }

  async deleteArticleCondition(id: string): Promise<{ success: boolean }> {
    return this.delete(`/article-conditions/${id}`);
  }

  // ==========================================
  // Hold Reasons (Settings)
  // ==========================================

  async getHoldReasons(): Promise<{ holdReasons: HoldReason[] }> {
    return this.get('/settings/hold-reasons');
  }

  async getHoldReason(id: string): Promise<HoldReason> {
    return this.get(`/settings/hold-reasons/${id}`);
  }

  async createHoldReason(data: HoldReasonInput): Promise<{ success: boolean; id: string }> {
    return this.post('/settings/hold-reasons', data);
  }

  async updateHoldReason(id: string, data: Partial<HoldReasonInput>): Promise<{ success: boolean }> {
    return this.put(`/settings/hold-reasons/${id}`, data);
  }

  async deleteHoldReason(id: string): Promise<{ success: boolean }> {
    return this.delete(`/settings/hold-reasons/${id}`);
  }

  // ==========================================
  // Dashboard
  // ==========================================

  async getDashboardSummary(): Promise<{
    totalArticles: number;
    lowStockCount: number;
    ordersToday: number;
    totalOrders: number;
    pendingOrders: number;
    revenueToday: number;
  }> {
    return this.get('/dashboard/summary');
  }

  // ==========================================
  // Articles (Tenant)
  // ==========================================

  async getArticles(params?: {
    page?: number;
    limit?: number;
    search?: string;
    searchFields?: string; // Comma-separated list of fields to search
    batch?: string; // Optional: charge/batch number filter (partial match), independent of search
    status?: string;
    stockFilter?: string; // 'low' | 'out' - server-side stock filter
    qualityFilter?: string; // Comma-separated quality check IDs
    dataFilter?: string; // Comma-separated positive data filters
    manufacturer?: string;
    brand?: string;
    categoryPath?: string;
    categoryId?: string;
    /** V-0595 F-014: '1' blendet Gutschein-Vorlagen-Artikel aus (Belegerfassung: Auftrag/Angebot/Rechnung). */
    ohne_gutschein_vorlage?: string;
    vatRate?: string;
    priceMin?: string;
    priceMax?: string;
    ekPriceMin?: string;
    ekPriceMax?: string;
    isPublished?: string;
    hasImages?: string;
    salesChannel?: string;
    stockManagementActive?: string;
    sort?: string;
    order?: 'ASC' | 'DESC';
    /**
     * Dashboard-Kachel-Klick (TICK-2026-01121): derselbe Schluessel wie bei der Kachel
     * (`KennzahlFilter`), z. B. `articles.ausverkauft`. Der Server wendet GENAU dieselbe
     * Bedingung an wie beim Zaehlen — ein unbekannter Schluessel liefert 400.
     */
    kennzahl?: string;
  }): Promise<ArticleListResponse> {
    return this.get<ArticleListResponse>('/articles', params);
  }

  /**
   * Schlanke Artikelsuche für Auswahlfelder (Typeahead).
   *
   * Gegenüber `getArticles()`: eine statt sieben DB-Abfragen, sieben statt 60+ Felder — und vor
   * allem nach RELEVANZ sortiert statt nach Anlagedatum. Genau daran scheiterte die Suche nach
   * „2201": der exakte Treffer stand als letzter von 34 und fiel aus dem Limit.
   *
   * `hasMore` sagt ehrlich, dass abgeschnitten wurde — bitte im UI anzeigen, sonst sucht der
   * Anwender einen Artikel, den es sehr wohl gibt.
   */
  async suggestArticles(params: {
    search: string;
    /** Komma-Liste, z. B. 'sku' oder 'sku,name,ean,packaging_ean' (Standard) */
    searchFields?: string;
    limit?: number;
    /** Stücklisten (Kit/Assembly) ausblenden — für Inventur/Zählungen. Nicht beim Verkauf! */
    excludeBom?: boolean;
    /** Erstes Artikelbild mitliefern (kostet eine zusätzliche Abfrage) */
    withImage?: boolean;
    /**
     * Bestandsangabe UND -filter beziehen sich auf dieses Lager statt auf den Gesamtbestand
     * über alle Läger. Beim Standardlager zählt Bestand ohne Lagerzuordnung mit.
     */
    warehouseId?: string;
    /** Nur Artikel mit Bestand ≠ 0 (Negativbestand zählt mit — dringendster Zählkandidat). */
    onlyInStock?: boolean;
    /**
     * Inventur-Kontext: markiert Artikel, die schon in einer offenen Inventur liegen
     * (`lockedByStocktake`), damit man sie nicht doppelt aufnimmt.
     */
    stocktakeContext?: boolean;
    /** Beim Bearbeiten: eigene Inventur ausnehmen (ihre Positionen sind keine Überschneidung). */
    editStocktakeId?: string;
    /** V-0595 F-014: Gutschein-Vorlagen-Artikel ausblenden (Belegerfassung: Auftrag/Angebot/Rechnung). */
    ohneGutscheinVorlage?: boolean;
  }): Promise<{
    success: boolean;
    articles: {
      id: string;
      sku: string;
      name: string;
      ean: string | null;
      stock: number;
      sellingPrice: number;
      vatRate: number;
      images: { url: string; thumbnails: Record<string, string> | null }[];
      /** Inventurnummer, wenn der Artikel in einer offenen Inventur liegt, sonst null. */
      lockedByStocktake?: string | null;
    }[];
    hasMore: boolean;
  }> {
    return this.get('/articles/suggest', {
      search: params.search,
      ...(params.searchFields ? { searchFields: params.searchFields } : {}),
      ...(params.limit ? { limit: params.limit } : {}),
      ...(params.excludeBom ? { excludeBom: '1' } : {}),
      ...(params.withImage ? { withImage: '1' } : {}),
      ...(params.warehouseId ? { warehouseId: params.warehouseId } : {}),
      ...(params.onlyInStock ? { onlyInStock: '1' } : {}),
      ...(params.stocktakeContext ? { stocktakeContext: '1' } : {}),
      ...(params.editStocktakeId ? { editStocktakeId: params.editStocktakeId } : {}),
      ...(params.ohneGutscheinVorlage ? { ohne_gutschein_vorlage: '1' } : {}),
    });
  }

  async getArticleFilterOptions(): Promise<{
    success: boolean;
    manufacturers: string[];
    brands: string[];
    categoryPaths: string[];
    categories: { id: string; name: string; path: string | null }[];
    vatRates: number[];
    priceRange: { min: number; max: number };
    ekPriceRange?: { min: number; max: number };
    hasImagesCount: number;
    noImagesCount: number;
  }> {
    return this.get('/articles/filter-options');
  }

  async getArticle(id: string): Promise<Article> {
    return this.get<Article>(`/articles/${id}`);
  }

  async getArticleStats(): Promise<ArticleStats> {
    return this.get<ArticleStats>('/articles/stats');
  }

  async getNextSku(): Promise<{ sku: string; nextNumber: number }> {
    return this.get('/articles/next-sku');
  }

  async checkSkuAvailability(sku: string): Promise<{ available: boolean; existingArticleId?: string; existingArticleName?: string }> {
    return this.get(`/articles/check-sku/${encodeURIComponent(sku)}`);
  }

  async checkEanAvailability(ean: string): Promise<{ available: boolean; existingArticleId?: string; existingArticleName?: string }> {
    return this.get(`/articles/check-ean/${encodeURIComponent(ean)}`);
  }

  async createArticle(data: ArticleCreateInput & { autoSkuNumber?: number }): Promise<{ success: boolean; id: string }> {
    return this.post('/articles', data);
  }

  async updateArticle(id: string, data: ArticleUpdateInput): Promise<{ success: boolean; biscalc?: { status: string; newVk?: number; previousVk?: number; ruleName?: string; marginPercent?: number; blockReason?: string; labelPrinted?: boolean; labelSkipReason?: string }; steuer_hinweis?: { code: string; klasse: string; satz: number | null } | null }> {
    return this.put(`/articles/${id}`, data);
  }

  async deleteArticle(id: string): Promise<{ success: boolean; jtl_folge?: string | null; jtl_entscheidung?: string | null; bis_behalten?: boolean; bis_folge?: string | null }> {
    return this.delete(`/articles/${id}`);
  }

  async duplicateArticle(id: string, options: {
    basicData?: boolean;
    prices?: boolean;
    descriptions?: boolean;
    identifiers?: boolean;
    dimensions?: boolean;
    trackingOptions?: boolean;
    images?: boolean;
    suppliers?: boolean;
    storageLocations?: boolean;
    customFields?: boolean;
    marketplaceData?: boolean;
    hazmatData?: boolean;
    newSku?: string;
    newName?: string;
  }): Promise<{ success: boolean; id: string; sku: string }> {
    return this.post(`/articles/${id}/duplicate`, options);
  }

  async updateArticleStock(id: string, quantity: number): Promise<{ success: boolean; newStock: number }> {
    return this.post(`/articles/${id}/stock`, { quantity });
  }

  /**
   * Artikel per Barcode (EAN/SKU/Lieferanten-SKU, inkl. Gebinde-Barcodes) — liefert den VOLLEN
   * Artikel in EINEM Aufruf. Für Scan-Pfade darum die richtige Wahl: „suchen + nachladen" kostet
   * zwei Roundtrips à ~1,2 s, das hier einen.
   *
   * `excludeBom` blendet Stücklisten aus (für Bestandsmasken; Artikelverwaltung und Preispflege
   * lassen es weg und finden sie weiterhin).
   */
  /**
   * Artikel per Barcode/SKU/Lieferanten-SKU finden.
   *
   * `resolveBom`: Trifft der Scan ein Gebinde (Stückliste mit genau einer Komponente), kommt die
   * **Komponente** zurück — plus `resolvedFromBom` mit dem Gebinde und der Stückzahl. Ein
   * gescannter 6er-Karton wird so zu „6 × Einzelflasche". Das ist für Bestandsmasken gedacht:
   * im Regal liegen die Einzelteile, und nur an ihnen hängen MHD-, Chargen- und Serienpflicht.
   *
   * `excludeBom` blendet Stücklisten stattdessen aus (für Masken, die sie gar nicht anbieten
   * sollen). Beides zusammen gesetzt: `resolveBom` gewinnt.
   */
  async findArticleByBarcode(
    barcode: string,
    options?: { excludeBom?: boolean; resolveBom?: boolean }
  ): Promise<Article & { resolvedFromBom?: { sku: string; name: string; quantityPerUnit: number } }> {
    const params = new URLSearchParams();
    if (options?.excludeBom) params.set('excludeBom', '1');
    if (options?.resolveBom) params.set('resolveBom', '1');
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.get(`/articles/barcode/${encodeURIComponent(barcode)}${suffix}`);
  }

  // Article Inventory
  async getArticleInventory(articleId: string): Promise<{
    inventory: Array<{
      id: string;
      quantity: number;
      reservedQuantity: number;
      availableQuantity: number;
      batchNumber: string | null;
      expiryDate: string | null;
      storageLocationId: string | null;
      locationCode: string | null;
      locationName: string | null;
      warehouseName: string | null;
      warehouseCode: string | null;
      createdAt: string;
      updatedAt: string;
    }>;
    totals: {
      quantity: number;
      orderedStock: number;
      reserved: number;
      available: number;
    };
  }> {
    return this.get(`/articles/${articleId}/inventory`);
  }

  async recalculateStock(): Promise<{ success: boolean; articlesUpdated: number; message: string }> {
    return this.post('/articles/recalculate-stock');
  }

  async createArticleInventory(articleId: string, data: {
    quantity: number;
    locationCode?: string | null;
    batchNumber?: string | null;
    expiryDate?: string | null;
  }): Promise<{ success: boolean; inventoryId: string; totalStock: number }> {
    return this.post(`/articles/${articleId}/inventory`, data);
  }

  async deleteArticleInventory(articleId: string, inventoryId: string): Promise<{ success: boolean; totalStock: number }> {
    return this.delete(`/articles/${articleId}/inventory/${inventoryId}`);
  }

  // ==========================================
  // Customers
  // ==========================================

  /**
   * Kundenliste. Die Vertriebsfilter werden SERVERSEITIG ausgewertet — eine Filterung auf der
   * bereits geladenen Seite wuerde bei mehreren tausend Kunden nur die ersten 30 durchsuchen und
   * ein unvollstaendiges Ergebnis zeigen, das wie ein vollstaendiges aussieht.
   */
  async getCustomers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    owner?: string;
    abc?: string;
    /** V-1576: Ø Monatsumsatz (netto) der letzten 12 Monate mindestens — Zahl als Text. */
    monatsumsatz_ab?: string;
    /** V-1670: ABC-Wanderung `<von_monat>:<von_klasse>:<nach_monat>:<nach_klasse>`. */
    abc_wanderung?: string;
    potential?: string;
    territory?: string;
    tourWeekday?: number;
    /** Kunden ohne jede Bestellung. */
    nieBestellt?: boolean;
    /** Letzte Bestellung liegt mehr als N Tage zurueck — schliesst „nie" NICHT ein. */
    inaktivSeit?: number;
    letzteBestellungVor?: string;
    active?: boolean;
    /** Geschaeftskunde (mit Firma) oder Privatkunde. */
    customerType?: 'business' | 'private';
    /**
     * Nur Haendler (Firma ODER Geschaeftskundengruppe) — TICK-2026-01121. Praeziser als
     * `customerType: 'business'`, das nur `companyName` prueft.
     */
    haendlerOnly?: boolean;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    /** Dashboard-Kachel-Klick (TICK-2026-01121, Nachzug) — Schluessel aus `KennzahlFilter::REGISTRY`. */
    kennzahl?: string;
    von?: string;
    bis?: string;
  }): Promise<CustomerListResponse> {
    return this.get<CustomerListResponse>('/customers', params);
  }

  /** Stuft alle Kunden nach Umsatz neu ein. Handeinstufungen bleiben unangetastet. */
  async recalculateCustomerAbc(): Promise<{
    success: boolean;
    updated: number;
    skipped: number;
    verteilung: Record<string, number>;
    /** V-1576 */
    verfahren?: 'pareto' | 'rhythmus';
    wechsel?: number;
    message?: string;
  }> {
    return this.post('/customers/abc/recalculate', {});
  }

  /** V-1576: Verfahren und Grenzen der Kunden-ABC. */
  async getKundenAbcEinstellungen(): Promise<KundenAbcEinstellungenAntwort> {
    return this.get<KundenAbcEinstellungenAntwort>('/customers/abc-einstellungen');
  }

  /** Speichert (SETTINGS_EDIT). Bei Fehlern kommt ein 422 mit `fehler` je Feld. */
  async saveKundenAbcEinstellungen(body: KundenAbcEinstellungenEingabe): Promise<{
    success: boolean;
    einstellungen: KundenAbcEinstellungen;
    hinweis?: string;
  }> {
    return this.put('/customers/abc-einstellungen', body);
  }

  /** Rechnet mit den übergebenen Einstellungen, ohne etwas zu schreiben. */
  async previewKundenAbcEinstellungen(body: KundenAbcEinstellungenEingabe): Promise<KundenAbcVorschau> {
    return this.post('/customers/abc-einstellungen/vorschau', body);
  }

  // ── Vertriebsgebiete ────────────────────────────────────────────────────
  /**
   * Sortimentslücken eines Kunden — was vergleichbare Kunden kaufen und er nicht.
   *
   * `lens`: `abc` vergleicht mit ABC-Klasse × Kundengruppe, `territory` mit derselben Tour.
   */
  async getAssortmentGaps(
    customerId: string,
    lens: 'abc' | 'territory' = 'abc',
    minShare?: number,
  ): Promise<AssortmentGapResponse> {
    return this.get(`/customers/${customerId}/assortment-gaps`, { lens, ...(minShare != null ? { minShare } : {}) });
  }

  async getSalesTerritories(activeOnly = false): Promise<{ territories: SalesTerritory[] }> {
    return this.get('/sales-territories', activeOnly ? { activeOnly: true } : undefined);
  }

  /**
   * Gebietsvorschläge aus den vorhandenen Postleitzahlen.
   *
   * Nur Vorschläge — angelegt wird nichts. Welche Gebiete es gibt, ist eine
   * Vertriebsentscheidung.
   */
  async getTerritorySuggestions(): Promise<{
    suggestions: TerritorySuggestion[];
    /** Gesamtzahl der PLZ-Gruppen — die Liste zeigt nur die umsatzstärksten. */
    totalClusters?: number;
    skipped: { withoutPostalCode: number; belowMinimum: number };
  }> {
    return this.get('/sales-territories/suggestions');
  }

  async createSalesTerritory(data: SalesTerritoryInput): Promise<{ id: string }> {
    return this.post('/sales-territories', data);
  }

  async updateSalesTerritory(id: string, data: SalesTerritoryInput): Promise<{ success: boolean }> {
    return this.put(`/sales-territories/${id}`, data);
  }

  /** Loescht ein Gebiet und loest die Zuordnung der betroffenen Kunden. */
  async deleteSalesTerritory(id: string): Promise<{ success: boolean; customersUnassigned: number }> {
    return this.delete(`/sales-territories/${id}`);
  }

  /**
   * Ordnet dem Gebiet die Kunden seiner PLZ-Bereiche zu.
   *
   * 🚨 Mit `dryRun` liefert der Aufruf NUR die Vorschau. Immer erst so aufrufen: Ein Fehlgriff
   * bei den Postleitzahlen verschiebt sonst tausende Kunden in einem Schritt.
   */
  async assignTerritoryCustomers(
    id: string,
    opts: { dryRun?: boolean; overwrite?: boolean; postalCodes?: string }
  ): Promise<{
    dryRun?: boolean;
    assigned?: number;
    matched: number;
    withoutTerritory: number;
    inOtherTerritory: number;
    wouldAssign: number;
    prefixes: string[];
  }> {
    return this.post(`/sales-territories/${id}/assign-customers`, opts);
  }

  /** Vor-Tour-Liste: Wer im Gebiet hat fuer die kommende Tour noch nicht bestellt? */
  async getTourPreview(params?: { territory?: string; days?: number }): Promise<{
    tours: TourPreview[];
    hinweis: string | null;
  }> {
    return this.get('/crm/tour-preview', params);
  }

  /** Packmittel-Auswahl fuer beide Packtische, nach Art gruppiert. */
  async getPackagingOptions(): Promise<{
    groups: Record<string, PackagingOption[]>;
    labels: Record<string, string>;
  }> {
    return this.get('/cartons/packaging-options');
  }

  async getCustomer(id: string): Promise<Customer> {
    return this.get<Customer>(`/customers/${id}`);
  }

  /**
   * Kennzahlen der Kundenliste — **mit denselben Filtern wie die Liste**.
   *
   * 🚨 Ohne Filter zeigten die drei Kacheln immer den Gesamtbestand, waehrend die Liste
   * darunter gefiltert war (TICK-2026-00772). `active` gehoert bewusst NICHT dazu: Die
   * Kacheln sind die Aufteilung nach aktiv/inaktiv.
   */
  /** Ein Kunde, der als Dublette in Frage kommt — mit allem, was zur Entscheidung nötig ist. */
  async getDuplicateGroups(limit = 50, suche = ''): Promise<{
    groups: Array<{
      fingerprint: string;
      rule: string;
      ruleLabel: string;
      confidence: 'hoch' | 'mittel';
      matchValue: string;
      customerIds: string[];
      customerCount: number;
      candidates: Array<{
        id: string; customerNumber: string | null; jtlCustomerNumber: string | null;
        companyName: string | null; firstName: string | null; lastName: string | null;
        email: string | null; phone: string | null;
        billingStreet: string | null; billingPostalCode: string | null; billingCity: string | null;
        abcClass: string | null; createdAt: string | null; customerSince: string | null;
        orderCount: number; lastOrder: string | null; revenue: string | number;
      }>;
      /** Der fuehrende Datensatz — serverseitig aus `HauptkontoRegel`, nicht im Frontend geraten. */
      leadId: string;
      /** `kundenseit` | `kundennummer` | `kennung` — womit die Regel entschieden hat. */
      leadReason: string;
      leadValue: string;
    }>;
    total: number;
    dismissed: number;
    suche: string | null;
  }> {
    return this.get('/crm/duplicates', suche ? { limit, suche } : { limit });
  }

  async getDuplicateCount(): Promise<{ total: number; dismissed: number }> {
    return this.get('/crm/duplicates/count');
  }

  async mergeDuplicates(targetId: string, mergeIds: string[], note?: string): Promise<{
    success: boolean; moved: Record<string, number>; mergedCount: number;
    /** V-0238: was mit den DATEV-Debitorkonten geschah (`keine` | `uebertrag` | `hinweis`). */
    datev_konto?: { art: 'keine' | 'uebertrag' | 'hinweis'; meldung: string };
  }> {
    return this.post('/crm/duplicates/merge', { targetId, mergeIds, note });
  }

  async dismissDuplicates(customerIds: string[], note?: string): Promise<{ success: boolean }> {
    return this.post('/crm/duplicates/dismiss', { customerIds, note });
  }

  /**
   * Eine Zusammenfuehrung wieder loesen — mitsamt der Belege.
   *
   * 🚨 Die Antwort sagt, was tatsaechlich zurueckkam. `entries === 0` heisst „kein Protokoll"
   * (zusammengefuehrt vor dem 11.09.2026) — dann wandert nichts zurueck, und die Oberflaeche
   * muss das sagen statt Erfolg zu melden. `skipped` sind Zeilen, die inzwischen bewusst
   * woanders haengen.
   */
  async unmergeCustomer(customerId: string): Promise<{
    success: boolean;
    entries: number;
    restored: Record<string, number>;
    skipped: Record<string, number>;
    unrestorable: string[];
    /** V-0238 F-022: `art = 'hinweis'` = das DATEV-Debitorkonto blieb beim Hauptkunden (Sperre), `meldung` nennt den Grund. */
    datev_konto?: { art: string; meldung: string };
  }> {
    return this.post(`/crm/duplicates/unmerge/${customerId}`, {});
  }

  async getCustomerStats(params?: {
    search?: string;
    customerType?: 'business' | 'private';
    owner?: string;
    abc?: string;
    monatsumsatz_ab?: string;
    abc_wanderung?: string;
    potential?: string;
    territory?: string;
    tourWeekday?: number;
    nieBestellt?: boolean;
    inaktivSeit?: number;
    letzteBestellungVor?: string;
    // TICK-2026-01121, Befund 7: fehlten hier — ohne sie zeigte die Kachel ueber der gefilterten
    // Liste weiterhin den Gesamtbestand statt der gefilterten Zahl (`customerFiltersToParams()`
    // kennt sie fuer die Liste selbst schon, `useCustomerStats()` reichte sie nur nicht durch).
    haendlerOnly?: boolean;
    kennzahl?: string;
    von?: string;
    bis?: string;
    /** Kontakt-Filter (TICK-2026-01155/01140, Karte V-0196) — siehe `CustomerFilters.kontakt`. */
    kontakt?: 'nie' | 'innerhalb' | 'nicht_innerhalb';
    kontakt_tage?: number;
    // F3 (Review): die drei alten Kacheln (total/active/inactive) ignorieren `active` weiterhin
    // serverseitig — aber die Kachel „Noch nicht kontaktiert" braucht ihn, sonst zaehlt sie ueber
    // aktive UND inaktive Kunden, waehrend die Liste darunter gefiltert ist.
    active?: boolean;
  }): Promise<{ total: number; active: number; inactive: number; nie_kontaktiert?: number | null }> {
    return this.get('/customers/stats', params);
  }

  async createCustomer(data: CustomerInput): Promise<{ success: boolean; id: string; customerNumber: string }> {
    return this.post('/customers', data);
  }

  async updateCustomer(id: string, data: Partial<CustomerInput>): Promise<{ success: boolean }> {
    return this.put(`/customers/${id}`, data);
  }

  async deleteCustomer(id: string): Promise<{ success: boolean }> {
    return this.delete(`/customers/${id}`);
  }

  async updateBillingSettings(id: string, data: BillingSettingsInput): Promise<BillingSettingsResponse> {
    return this.patch(`/customers/${id}/billing-settings`, data);
  }

  // DATEV-Debitorkonto je Kunde (V-0238): Standard = Sammeldebitor, eigenes Konto nur manuell.
  // 🚨 Der GET liegt NICHT unter /customers/… (dort gäbe der Cache-Header 60 s alten Stand).
  async getCustomerDebitorkonto(customerId: string): Promise<import('../types/DatevDebitorkonto').DatevDebitorkontoStand> {
    return this.get(`/datev/debitorkonto/${customerId}`);
  }

  async setCustomerDebitorkonto(
    customerId: string,
    data: import('../types/DatevDebitorkonto').DatevDebitorkontoInput
  ): Promise<import('../types/DatevDebitorkonto').DatevDebitorkontoStand> {
    return this.put(`/customers/${customerId}/debitorkonto`, data);
  }

  // Customer Address Book
  async getCustomerAddresses(customerId: string): Promise<{ addresses: CustomerAddressBookEntry[] }> {
    return this.get(`/customers/${customerId}/addresses`);
  }

  async createCustomerAddress(customerId: string, data: CustomerAddressInput): Promise<{ success: boolean; id: string }> {
    return this.post(`/customers/${customerId}/addresses`, data);
  }

  async updateCustomerAddress(customerId: string, addressId: string, data: CustomerAddressInput): Promise<{ success: boolean }> {
    return this.put(`/customers/${customerId}/addresses/${addressId}`, data);
  }

  async deleteCustomerAddress(customerId: string, addressId: string): Promise<{ success: boolean }> {
    return this.delete(`/customers/${customerId}/addresses/${addressId}`);
  }

  async setDefaultCustomerAddress(customerId: string, addressId: string): Promise<{ success: boolean }> {
    return this.patch(`/customers/${customerId}/addresses/${addressId}/default`, {});
  }

  // Customer 360° View
  /**
   * V-1671: Wiedervorlage eines Kontaktvermerks als erledigt abhaken — optional gleich mit einer
   * neuen Wiedervorlage (`neue_wiedervorlage_*`, EIN Termin: Datum+Uhrzeit ODER Stunden).
   * Fehler tragen `code` (`bereits_erledigt`, `ueberholt`, …) — die Maske übersetzt ihn.
   */
  async wiedervorlageErledigen(vermerkId: string, daten?: {
    neue_wiedervorlage_am?: string;
    neue_wiedervorlage_uhrzeit?: string;
    neue_wiedervorlage_in_stunden?: number;
    notiz?: string;
  }): Promise<{
    success: boolean;
    vermerk_id: string;
    erledigt_am: string;
    erledigt_von_name: string | null;
    neue_wiedervorlage: { vermerk_id: string; am: string; uhrzeit: string | null } | null;
  }> {
    return this.post(`/crm/wiedervorlagen/${encodeURIComponent(vermerkId)}/erledigt`, daten ?? {});
  }

  /** V-1671: Haken zurücknehmen — nur solange der Vermerk der jüngste des Kunden ist. */
  async wiedervorlageWiederOeffnen(vermerkId: string): Promise<{ success: boolean; vermerk_id: string }> {
    return this.delete(`/crm/wiedervorlagen/${encodeURIComponent(vermerkId)}/erledigt`);
  }

  async getCustomer360(id: string): Promise<Customer360Response> {
    return this.get<Customer360Response>(`/customers/${id}/360`);
  }

  // Customer Activities
  async getCustomerActivities(id: string, params?: { page?: number; limit?: number; type?: string }): Promise<CustomerActivityListResponse> {
    return this.get<CustomerActivityListResponse>(`/customers/${id}/activities`, params);
  }

  async createCustomerActivity(id: string, data: CustomerActivityInput): Promise<{ success: boolean; id: string }> {
    return this.post(`/customers/${id}/activities`, data);
  }

  // Customer Communications
  async getCustomerCommunications(id: string, params?: { page?: number; limit?: number; type?: string }): Promise<CustomerCommunicationListResponse> {
    return this.get<CustomerCommunicationListResponse>(`/customers/${id}/communications`, params);
  }

  // V-2443: E-Mail-Verlauf eines Kunden (nur Lesen, nie gecacht).
  async getCustomerMailVerlauf(id: string, params?: { page?: number; limit?: number }): Promise<CustomerMailVerlaufResponse> {
    return this.get<CustomerMailVerlaufResponse>(`/customers/${id}/mail-verlauf`, params);
  }

  async createCustomerCommunication(id: string, data: CustomerCommunicationInput): Promise<{ success: boolean; id: string }> {
    return this.post(`/customers/${id}/communications`, data);
  }

  // Customer Documents
  async getCustomerDocuments(id: string, params?: { page?: number; limit?: number; type?: string }): Promise<CustomerDocumentListResponse> {
    return this.get<CustomerDocumentListResponse>(`/customers/${id}/documents`, params);
  }

  async createCustomerDocument(id: string, data: CustomerDocumentInput): Promise<{ success: boolean; id: string }> {
    return this.post(`/customers/${id}/documents`, data);
  }

  async deleteCustomerDocument(customerId: string, docId: string): Promise<{ success: boolean }> {
    return this.delete(`/customers/${customerId}/documents/${docId}`);
  }

  // Customer Orders & Invoices
  async getCustomerOrders(id: string, params?: { page?: number; limit?: number; status?: string }): Promise<CustomerOrdersResponse> {
    return this.get<CustomerOrdersResponse>(`/customers/${id}/orders`, params);
  }

  async getCustomerInvoices(id: string, params?: { page?: number; limit?: number; status?: string }): Promise<CustomerInvoicesResponse> {
    return this.get<CustomerInvoicesResponse>(`/customers/${id}/invoices`, params);
  }

  // ==========================================
  // Orders
  // ==========================================

  async getOrders(params?: {
    page?: number; limit?: number; search?: string; status?: string; channel?: string;
    warehouseId?: string; dateFrom?: string; dateTo?: string; paymentMethod?: string;
    minTotal?: number; maxTotal?: number; deliverable?: string; kennzahl?: string;
    von?: string; bis?: string; gruppe?: string;
    /**
     * Nur Haendler (Firma ODER Geschaeftskundengruppe) — TICK-2026-01121, letzte Kacheln.
     * Dieselbe Bedeutung wie bei `getCustomers()`. Braucht die Kachel „Umsatz je Verkaufsweg"
     * (`BusinessFiguresWidget.tsx`), wenn ihr Schalter „nur Händler" an ist — `orders.verkaufsweg`
     * kennt den Schalter selbst nicht (siehe `KennzahlFilter`-Klassendoc serverseitig).
     */
    haendlerOnly?: boolean;
  }): Promise<OrderListResponse> {
    return this.get<OrderListResponse>('/orders', params);
  }

  async getOrder(id: string): Promise<Order> {
    return this.get<Order>(`/orders/${id}`);
  }

  async getOrderStats(): Promise<OrderStats> {
    return this.get('/orders/stats');
  }

  async createOrder(data: OrderInput): Promise<{ success: boolean; id: string; orderNumber: string }> {
    return this.post('/orders', data);
  }

  async updateOrder(id: string, data: Partial<OrderInput>): Promise<{ success: boolean }> {
    return this.put(`/orders/${id}`, data);
  }

  async updateOrderStatus(id: string, status: string): Promise<{ success: boolean; status: string }> {
    return this.post(`/orders/${id}/status`, { status });
  }

  async deleteOrder(id: string): Promise<{ success: boolean }> {
    return this.delete(`/orders/${id}`);
  }

  async cancelOrder(id: string, data: {
    cancellationReasonId?: string;
    cancellationReasonText?: string;
    cancelledBy?: string;
    refundPayment?: boolean;
    cancelMode?: 'full' | 'partial';
    items?: Array<{ orderItemId: string; cancelQuantity: number }>;
  }): Promise<{
    success: boolean;
    status: string;
    cancelledAt: string;
    cancelMode?: string;
    marketplaceCancelled?: boolean;
    marketplaceRefunded?: boolean;
    refundedAmount?: number;
    itemsCancelled?: number;
  }> {
    return this.post(`/orders/${id}/cancel`, data);
  }

  async reactivateOrder(id: string): Promise<{ success: boolean; message: string; status: string }> {
    return this.post(`/orders/${id}/reactivate`);
  }

  /**
   * Auftrag als versendet markieren, OHNE Bestandsbuchung.
   * Legt eine Sendung mit der Sendungsnummer an und meldet sie auf Wunsch an den Marktplatz.
   */
  async markOrderShipped(id: string, data: {
    trackingNumber?: string;
    carrier?: string;
    shippedAt?: string;
    note?: string;
    notifyChannel?: boolean;
  }): Promise<{
    success: boolean;
    status: string;
    previousStatus: string;
    shipmentId: string;
    shipmentNumber: string;
    trackingNumber: string | null;
    carrier: string | null;
    shippedAt: string;
    stockChanged: boolean;
    channelResult: { attempted: boolean; success: boolean; channel: string; reason?: string; error?: string };
  }> {
    return this.post(`/orders/${id}/mark-shipped`, data);
  }

  async undoMarkOrderShipped(id: string): Promise<{
    success: boolean;
    status: string;
    channelNotified: boolean;
    cancelledShipments: number;
    message: string;
  }> {
    return this.post(`/orders/${id}/undo-mark-shipped`);
  }

  async duplicateOrder(id: string): Promise<{ success: boolean; id: string; orderNumber: string }> {
    return this.post(`/orders/${id}/duplicate`);
  }

  async getOrderCancellationReasons(): Promise<{ cancellationReasons: CancellationReason[] }> {
    return this.get('/orders/cancellation-reasons');
  }

  async holdOrder(id: string, data: {
    holdReasonId?: string;
    holdNote?: string;
    holdBy?: string;
  }): Promise<{
    success: boolean;
    status: string;
    previousStatus: string;
    holdAt: string;
    holdReasonName: string | null;
  }> {
    return this.post(`/orders/${id}/hold`, data);
  }

  async releaseHoldOrder(id: string, data?: {
    releasedBy?: string;
  }): Promise<{
    success: boolean;
    status: string;
    message: string;
  }> {
    return this.post(`/orders/${id}/release-hold`, data || {});
  }

  async getOrderHoldReasons(): Promise<{ holdReasons: HoldReason[] }> {
    return this.get('/orders/hold-reasons');
  }

  async getOrderRelated(orderId: string): Promise<RelatedOrders> {
    return this.get(`/orders/${orderId}/related`);
  }

  /** V-1053 Runde 3 (F-025): ersetzbare Positionen mit verfügbarer Menge — die Regel des Servers, nicht der Maske. */
  async getReplacementOptions(orderId: string): Promise<ReplacementOptions> {
    return this.get(`/orders/${orderId}/replacement-options`);
  }

  async createReplacementOrder(orderId: string, data: CreateReplacementInput): Promise<{
    success: boolean;
    /** `id` ist `null`, wenn der Auftrag entstand, der RMA-Vorgang aber nicht gespeichert werden konnte (dann steht `warning` da). */
    rma: { id: string | null; rmaNumber: string };
    replacementOrder: { id: string; orderNumber: string };
    /** Der Ersatzauftrag wartet auf die Preisfreigabe (kein Preis-/Einstandsrecht des Anlegers). */
    awaitingPriceApproval?: boolean;
    warning?: string;
    message: string;
  }> {
    return this.post(`/orders/${orderId}/replacement`, data);
  }

  async createExchangeOrder(orderId: string, data: CreateExchangeInput): Promise<{
    success: boolean;
    rma: { id: string; rmaNumber: string };
    exchangeOrder: { id: string; orderNumber: string; status: string; awaitingReturn: boolean };
    message: string;
  }> {
    return this.post(`/orders/${orderId}/exchange`, data);
  }

  async quickShipOrder(orderId: string, options?: {
    carrier?: string;
    product?: string;
    weight?: number;
    shippingMethodId?: string;
    autoPrint?: boolean;
    allowPartialShipment?: boolean;
    partialItems?: string[];
  }): Promise<QuickShipResponse> {
    return this.post(`/orders/${orderId}/quick-ship`, options || {});
  }

  async validateOrderShipping(orderId: string): Promise<{
    canShip: boolean;
    issues: Array<{ type: string; field?: string; message: string }>;
    warnings: Array<{ type: string; message: string }>;
    order: {
      id: string;
      orderNumber: string;
      status: string;
      customerName: string;
      shippingAddress: Record<string, string> | null;
    };
  }> {
    return this.post(`/orders/${orderId}/validate-shipping`);
  }

  // ==========================================
  // RMA (Return Merchandise Authorization)
  // ==========================================

  async getRmaReasons(type?: RmaType): Promise<{ reasons: RmaReason[] }> {
    return this.get('/rma/reasons', { type });
  }

  async getRmas(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: RmaStatus;
    type?: RmaType;
  }): Promise<RmaListResponse> {
    return this.get('/rma', params);
  }

  async getRmaStats(): Promise<RmaStats> {
    return this.get('/rma/stats');
  }

  async getRma(id: string): Promise<Rma> {
    return this.get(`/rma/${id}`);
  }

  async receiveRmaReturn(id: string, data: ReceiveReturnInput): Promise<{
    success: boolean;
    status: string;
    receivedAt: string;
    message: string;
    exchangeOrderReleased?: boolean;
    exchangeOrderNumber?: string;
  }> {
    return this.post(`/rma/${id}/receive`, data);
  }

  async updateRmaTracking(id: string, data: { trackingNumber?: string; carrier?: string }): Promise<{ success: boolean; message: string }> {
    return this.post(`/rma/${id}/tracking`, data);
  }

  async completeRma(id: string, data: CompleteRmaInput): Promise<{ success: boolean; status: string; message: string }> {
    return this.post(`/rma/${id}/complete`, data);
  }

  async getOrderAuditLog(id: string): Promise<{ auditLog: OrderAuditLogEntry[] }> {
    return this.get(`/orders/${id}/audit-log`);
  }

  // ==========================================
  // Suppliers
  // ==========================================

  async getSuppliers(params?: { page?: number; limit?: number; search?: string }): Promise<SupplierListResponse> {
    return this.get<SupplierListResponse>('/suppliers', params);
  }

  async getSupplier(id: string): Promise<Supplier> {
    return this.get<Supplier>(`/suppliers/${id}`);
  }

  async createSupplier(data: SupplierInput): Promise<{ success: boolean; id: string; supplierNumber: string }> {
    return this.post('/suppliers', data);
  }

  async updateSupplier(id: string, data: Partial<SupplierInput>): Promise<{ success: boolean }> {
    return this.put(`/suppliers/${id}`, data);
  }

  async deleteSupplier(id: string): Promise<{ success: boolean }> {
    return this.delete(`/suppliers/${id}`);
  }

  async searchSuppliers(query: string, limit?: number): Promise<{ suppliers: SupplierSearchResult[] }> {
    return this.get('/suppliers/search', { q: query, limit });
  }

  // ==========================================
  // Supplier Sales Reports (wöchentlicher Hersteller-Report)
  // ==========================================

  async getSupplierReportConfig(id: string): Promise<{ success: boolean; config: SupplierReportConfig }> {
    return this.get(`/suppliers/${id}/report/config`);
  }

  async saveSupplierReportConfig(
    id: string,
    config: Partial<SupplierReportConfig>
  ): Promise<{ success: boolean; config: SupplierReportConfig }> {
    return this.put(`/suppliers/${id}/report/config`, config);
  }

  async previewSupplierReport(
    id: string,
    params?: { from?: string; to?: string }
  ): Promise<{ success: boolean; report: SupplierReportData }> {
    return this.get(`/suppliers/${id}/report/preview`, params);
  }

  async downloadSupplierReport(id: string, params?: { from?: string; to?: string }): Promise<Blob> {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const query = qs.toString();
    const url = this.buildUrl(`/suppliers/${id}/report/download${query ? '?' + query : ''}`);
    const response = await fetch(url, { headers: { Authorization: `Bearer ${this.token}` } });
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    return response.blob();
  }

  async sendSupplierReportNow(
    id: string,
    params?: { from?: string; to?: string }
  ): Promise<SupplierReportRunResult> {
    return this.post(`/suppliers/${id}/report/send-now`, params ?? {});
  }

  async getSupplierReportHistory(
    id: string,
    limit = 20
  ): Promise<{ success: boolean; history: SupplierReportRun[] }> {
    return this.get(`/suppliers/${id}/report/history`, { limit });
  }

  // ==========================================
  // Article-Supplier Relationships
  // ==========================================

  async getArticleSuppliers(articleId: string): Promise<{ suppliers: ArticleSupplier[] }> {
    return this.get(`/suppliers/article/${articleId}`);
  }

  async addArticleSupplier(articleId: string, data: ArticleSupplierInput): Promise<{ success: boolean; id: string; message: string }> {
    return this.post(`/suppliers/article/${articleId}`, data);
  }

  async updateArticleSupplier(articleId: string, relationId: string, data: Partial<ArticleSupplierInput>): Promise<{ success: boolean; message: string }> {
    return this.put(`/suppliers/article/${articleId}/relation/${relationId}`, data);
  }

  async removeArticleSupplier(articleId: string, relationId: string): Promise<{ success: boolean; message: string }> {
    return this.delete(`/suppliers/article/${articleId}/relation/${relationId}`);
  }

  async getSupplierArticles(supplierId: string, params?: { page?: number; limit?: number }): Promise<{
    articles: SupplierArticle[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  }> {
    return this.get(`/suppliers/${supplierId}/articles`, params);
  }

  // ==========================================
  // Inventory - Storage Locations
  // ==========================================

  async getStorageLocations(params?: {
    search?: string;
    zone?: string;
    warehouseId?: string;
    isActive?: string;
    page?: number;
    limit?: number;
    /** Inventur-Kontext: markiert Plätze in offener Inventur (`lockedByStocktake`). */
    stocktakeContext?: boolean;
    /** Beim Bearbeiten: eigene Inventur ausnehmen. */
    editStocktakeId?: string;
    /**
     * Belegung (`itemCount`/`isOccupied`) mitrechnen. Default an — Auswahllisten setzen `false`.
     *
     * 🚨 Das Aggregat kennt kein LIMIT: es läuft immer über die vollständige `inventory`.
     * Eine Auswahlliste zeigt die Belegung nirgends an, zahlte sie aber bei jedem Öffnen mit.
     */
    withOccupancy?: boolean;
  }): Promise<{ storageLocations: StorageLocation[]; total?: number; page?: number; limit?: number; pages?: number; stats?: { total: number; active: number; occupied: number } }> {
    const { stocktakeContext, editStocktakeId, withOccupancy, ...rest } = params ?? {};
    return this.get('/storage-locations', {
      ...rest,
      ...(stocktakeContext ? { stocktakeContext: '1' } : {}),
      ...(editStocktakeId ? { editStocktakeId } : {}),
      ...(withOccupancy === false ? { withOccupancy: '0' } : {}),
    });
  }

  async createStorageLocation(data: StorageLocationInput): Promise<{ success: boolean; id: string }> {
    return this.post('/storage-locations', data);
  }

  async batchCreateStorageLocations(locations: StorageLocationInput[]): Promise<{
    success: boolean;
    created: number;
    // Bereits vorhandene Codes, die der Server uebersprungen hat (macht Wiederholungslaeufe gefahrlos).
    skipped: number;
    locations: { id: string; code: string }[];
    errors: { index: number; error: string }[];
  }> {
    return this.post('/storage-locations/batch', { locations });
  }

  // Belegte Zonen mit Platzzahl — ohne die komplette Lagerplatzliste zu laden.
  async getStorageLocationZones(params?: { warehouseId?: string }): Promise<{
    success: boolean;
    zones: { zone: string; count: number }[];
  }> {
    return this.get('/storage-locations/zones', params);
  }

  // Zuweisbare Benutzer des Mandanten (nur id/name/email). Eigener Endpoint, weil `/users`
  // das Recht USERS_VIEW verlangt, das eine Lagerkraft typischerweise nicht hat.
  async getAssignableUsers(): Promise<{
    success: boolean;
    users: { id: string; name: string; email: string }[];
  }> {
    return this.get('/users/assignable');
  }

  /**
   * Betreuer und/oder Gebiet für viele Kunden auf einmal setzen.
   *
   * Entweder `customerIds` (ausgewählte Zeilen) ODER `selectAll` + `filters` (alle Treffer des
   * aktuellen Listenfilters). `dryRun` schreibt nichts und liefert nur die Zahlen für die
   * Rückfrage — `wouldAssign` ist die Zahl, bei der sich wirklich etwas ändert.
   *
   * Ein leerer String löst die Zuordnung (Betreuer/Gebiet entfernen); ein weggelassenes Feld
   * bleibt unverändert.
   */
  /**
   * Barcodes, die an mehr als einer Stelle hängen — Einzel-EAN und Gebinde zugleich, oder an
   * zwei Artikeln. Aufräum-Liste für die Altlast, die am Handheld sonst nur einzeln auffällt.
   */
  async getEanMehrfachbelegung(limit = 100): Promise<{
    success: boolean;
    befunde: {
      barcode: string;
      quellen: number;
      artikel: number;
      quellenliste: string[];
      traeger: { source: string; sku: string | null; name: string | null; packagingName: string | null; quantity: number | null }[];
    }[];
    limit: number;
    moeglicherweiseMehr: boolean;
  }> {
    return this.get('/articles/ean-mehrfachbelegung', { limit });
  }

  async bulkAssignCustomers(data: {
    customerIds?: string[];
    selectAll?: boolean;
    filters?: Record<string, unknown>;
    ownerId?: string;
    territoryId?: string;
    dryRun?: boolean;
  }): Promise<{ matched: number; wouldAssign: number; alreadyAssigned?: number; assigned?: number }> {
    return this.post('/customers/bulk-assign', data);
  }

  async updateStorageLocation(id: string, data: Partial<StorageLocationInput>): Promise<{ success: boolean }> {
    return this.put(`/storage-locations/${id}`, data);
  }

  async deleteStorageLocation(id: string): Promise<{ success: boolean }> {
    return this.delete(`/storage-locations/${id}`);
  }

  // ==========================================
  // Inventory - Goods Receipts
  // ==========================================

  async getGoodsReceipts(params?: { page?: number; limit?: number; status?: string }): Promise<GoodsReceiptListResponse> {
    return this.get<GoodsReceiptListResponse>('/goods-receipts', params);
  }

  /**
   * Wareneingang buchen.
   *
   * Mit `receiptId` + `keepOpen` bucht der Aufruf in einen bereits OFFENEN Beleg und laesst ihn
   * offen — das ist der Weg der Sofortbuchung. Ohne beide Felder verhaelt sich der Endpunkt exakt
   * wie bisher (neuer Beleg, sofort gebucht).
   *
   * `clientRequestId` ist der Riegel gegen Doppelbuchung: Bei einer Position je Aufruf haeuft sich
   * die Zahl der Aufrufe, und ein Wiederholversuch nach einem Zeitueberschreitungs-Fehler wuerde
   * ohne den Schluessel ein zweites Mal buchen.
   */
  async createGoodsReceipt(
    data: GoodsReceiptInput & { receiptId?: string; keepOpen?: boolean; clientRequestId?: string },
  ): Promise<{ success: boolean; id: string; receiptNumber: string; status?: string; lineIds?: string[] }> {
    return this.post('/goods-receipts', data);
  }

  /**
   * Menge einer Position im offenen Wareneingang aendern — gebucht wird die DIFFERENZ.
   *
   * Antwortet mit 422 `not_covered`, wenn die zurueckzunehmende Ware nicht mehr im Lager liegt,
   * und mit 422 `serial_tracked`, wenn eine seriennummerngefuehrte Position verringert werden soll.
   */
  async updateGoodsReceiptItem(
    receiptId: string,
    itemId: string,
    data: {
      receivedQuantity: number;
      storageLocationCode?: string;
      warehouseId?: string;
      /** Volle Seriennummern-Liste der Position — der Server erkennt selbst, welche neu sind. */
      serialNumbers?: string[];
      clientRequestId?: string;
    },
  ): Promise<{ success: boolean; booked: number; receivedQuantity: number; removed: boolean }> {
    return this.patch(`/goods-receipts/${receiptId}/items/${itemId}`, data);
  }

  /** Position aus dem offenen Wareneingang entfernen — die gebuchte Menge wird zurueckgebucht. */
  async deleteGoodsReceiptItem(
    receiptId: string,
    itemId: string,
    data?: { storageLocationCode?: string; warehouseId?: string; clientRequestId?: string },
  ): Promise<{ success: boolean; booked: number; receivedQuantity: number; removed: boolean }> {
    return this.delete(`/goods-receipts/${receiptId}/items/${itemId}`, data ?? {});
  }

  async getGoodsReceipt(id: string): Promise<GoodsReceipt> {
    return this.get<GoodsReceipt>(`/goods-receipts/${id}`);
  }

  async updateGoodsReceipt(id: string, data: Partial<GoodsReceiptInput>): Promise<{ success: boolean }> {
    return this.put(`/goods-receipts/${id}`, data);
  }

  async completeGoodsReceipt(id: string): Promise<{ success: boolean; stockMovements: number }> {
    return this.post(`/goods-receipts/${id}/complete`, {});
  }

  // ==========================================
  // Inventory - Stock Movements
  // ==========================================

  async getStockMovements(params?: { page?: number; limit?: number; articleId?: string; type?: string }): Promise<StockMovementListResponse> {
    return this.get<StockMovementListResponse>('/stock-movements', params);
  }

  async createStockMovement(data: StockMovementInput): Promise<{ success: boolean; id: string }> {
    return this.post('/stock-movements', data);
  }

  // ==========================================
  // Inventory - Stock Adjustments
  // ==========================================

  async getStockAdjustments(params?: { page?: number; limit?: number }): Promise<StockAdjustmentListResponse> {
    return this.get<StockAdjustmentListResponse>('/stock-adjustments', params);
  }

  /** V-2774: Zahl, von der eine Bestandskorrektur ausgeht (Artikelbestand gegen Lagersumme). */
  async getStockAdjustmentGrundlage(articleId: string): Promise<{ artikel_bestand: number; lager_summe: number; grundlage: number }> {
    return this.get(`/stock-adjustments/grundlage/${articleId}`);
  }

  async createStockAdjustment(data: StockAdjustmentInput): Promise<{
    success: boolean;
    id: string;
    adjustmentNumber: string;
    newStock: number;
    isBom?: boolean;
    bomType?: string;
    componentAdjustments?: Array<{
      articleId: string;
      articleSku: string;
      articleName: string;
      quantityChange: number;
      newStock: number;
    }>;
  }> {
    return this.post('/stock-adjustments', data);
  }

  // ==========================================
  // Shipments
  // ==========================================

  async getShipments(params?: { page?: number; limit?: number; status?: string; sort?: string; order?: 'asc' | 'desc'; orderId?: string; kennzahl?: string; dateFrom?: string; dateTo?: string }): Promise<ShipmentListResponse> {
    return this.get<ShipmentListResponse>('/shipments', params);
  }

  async getShipment(id: string): Promise<Shipment> {
    return this.get<Shipment>(`/shipments/${id}`);
  }

  async getShipmentStats(): Promise<ShipmentStats> {
    return this.get('/shipments/stats');
  }

  async createShipment(data: ShipmentInput): Promise<{ success: boolean; id: string; shipmentNumber: string }> {
    return this.post('/shipments', data);
  }

  async updateShipment(id: string, data: Partial<ShipmentInput>): Promise<{ success: boolean }> {
    return this.put(`/shipments/${id}`, data);
  }

  /** Alle Packstücke (Paletten/Kartons) eines Auftrags über sämtliche Sendungen hinweg. */
  async getOrderPackages(orderId: string): Promise<OrderPackagesResponse> {
    return this.get<OrderPackagesResponse>(`/orders/${orderId}/packages`);
  }

  /** Ein einzelnes Packstück korrigieren (Gewicht/Maße/Notiz). */
  async updateShipmentPackage(
    shipmentId: string,
    index: number,
    data: { weightKg?: number | null; dimensions?: Record<string, number> | null; note?: string | null },
  ): Promise<{ success: boolean; packages: ShipmentPackage[]; shipmentWeight: number | null }> {
    return this.put(`/shipments/${shipmentId}/packages/${index}`, data);
  }

  async deleteShipment(id: string): Promise<{ success: boolean }> {
    return this.delete(`/shipments/${id}`);
  }

  /**
   * Packstueck-Beleg als PDF: Foto, Inhalt und Kopfdaten auf einem Blatt.
   *
   * Kein Lieferschein - der geht an den Kunden. Dieser Beleg bleibt im Haus und beantwortet eine
   * Reklamation: was lag in genau diesem Karton, wie schwer war er, wie sah er beim Packen aus.
   */
  async getPackageDocument(shipmentId: string, index: number): Promise<Blob> {
    const response = await this.fetch(`/shipments/${shipmentId}/packages/${index}/document`);
    if (!response.ok) {
      throw new Error('Beleg konnte nicht erzeugt werden');
    }
    return response.blob();
  }

  /**
   * Packstueck-Foto laden.
   *
   * Der Endpoint liegt unter `/mde/pack/...`, nimmt aber ausdruecklich auch eine normale
   * Browser-Sitzung an (`ROLE_USER`) - deshalb braucht es keinen zweiten Bildpfad. Ein
   * `<img src>` scheitert hier, weil der Browser den Authorization-Header nicht mitsendet;
   * darum als Blob holen und per Object-URL anzeigen.
   */
  async getPackagePhoto(photoKey: string): Promise<Blob> {
    const response = await this.fetch(`/mde/pack/package-photo?key=${encodeURIComponent(photoKey)}`);
    if (!response.ok) {
      throw new Error('Foto konnte nicht geladen werden');
    }
    return response.blob();
  }

  /**
   * Retourenfoto laden (V-1161): Spaces-Bilder sind privat und kommen über den Abruf-Endpoint —
   * ein `<img src>` scheitert am fehlenden Authorization-Header, darum als Blob holen.
   */
  async getReturnImage(returnId: string, imageId: string): Promise<Blob> {
    const response = await this.fetch(`/returns/${encodeURIComponent(returnId)}/images/${encodeURIComponent(imageId)}/file`);
    if (!response.ok) {
      throw new Error('Foto konnte nicht geladen werden');
    }
    return response.blob();
  }

  // Delivery Notes (Lieferscheine)

  /**
   * Lieferschein einer Sendung als PDF.
   *
   * 🚨 Der Endpunkt gibt seit 27.08.2026 nur noch **gespeicherte** Belege aus. Existiert keiner,
   * antwortet er mit 409 — dann gehört „Lieferschein erstellen" auf den Knopf und nicht ein
   * Ausdruck, den hinterher niemand wiederfindet.
   */
  async downloadDeliveryNote(shipmentId: string): Promise<Blob> {
    const response = await this.fetch(`/shipments/${shipmentId}/delivery-note`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Download failed' }));
      throw new Error(error.error || 'Failed to download delivery note');
    }
    return response.blob();
  }

  /** Lieferschein zu einer Sendung anlegen (oder den vorhandenen zurückbekommen). */
  async createDeliveryNoteFromShipment(
    shipmentId: string
  ): Promise<{ success: boolean; data: { id: string; deliveryNoteNumber: string; alreadyExisted: boolean } }> {
    return this.post(`/delivery-notes/from-shipment/${shipmentId}`, {});
  }

  /** Lieferschein zu einem Auftrag anlegen — über dessen jüngste Sendung. */
  async createDeliveryNoteFromOrder(
    orderId: string
  ): Promise<{ success: boolean; data: { id: string; deliveryNoteNumber: string; alreadyExisted: boolean } }> {
    return this.post(`/delivery-notes/from-order/${orderId}`, {});
  }

  /**
   * Lieferschein per E-Mail.
   *
   * 🚨 Läuft über den Beleg, nicht über die Sendung: Der frühere Weg
   * `POST /shipments/{id}/delivery-note/email` meldete **„Lieferschein wurde an … gesendet"**,
   * ohne je eine Mail zu verschicken (der Endpunkt rendert(e) nur ein PDF, der Mandanten-SMTP war
   * dort gar nicht angebunden).
   */
  /** Ein-Klick (V-1130): Empfänger, CC und BCC löst der Server aus dem Kunden auf — keine Adresse. */
  async sendDeliveryNoteEmail(deliveryNoteId: string): Promise<{ success: boolean; error?: string; to: string | null; cc: string[]; bcc: string[]; kopie_an_mich: boolean; adressen_verborgen?: boolean; anzahl?: { to: number; cc: number; bcc: number }; test_mode_redirect_to?: string | null }> {
    return this.post(`/delivery-notes/${deliveryNoteId}/send-email`, {});
  }

  /**
   * Gründe, warum eine Sendung zurückkam — fester Katalog aus dem Backend.
   *
   * Kein Freitext-Feld mit Vorschlägen: Der Katalog ist auswertbar, ein Freitext nicht.
   */
  async getReshipReasons(): Promise<{ reasons: ReshipReason[] }> {
    return this.get('/shipments/reship-reasons');
  }

  /**
   * Rückläufer: Die Sendung kam zurück und geht erneut raus.
   *
   * 🚨 Es wird **kein Bestand gebucht** — die Ware wurde beim ersten Versand ausgebucht und
   * ist es geblieben. Es entsteht auch kein neuer Auftrag und keine zweite Rechnung; die neue
   * Sendung hängt am selben Auftrag und verweist auf ihren Vorgänger.
   */
  async reshipShipment(
    shipmentId: string,
    data: {
      reason: string;
      note?: string;
      address?: {
        name?: string;
        company?: string;
        street?: string;
        houseNumber?: string;
        postalCode?: string;
        city?: string;
        country?: string;
        phone?: string;
        email?: string;
      };
    },
  ): Promise<{
    success: boolean;
    shipmentId: string;
    shipmentNumber: string;
    replacesShipmentNumber?: string | null;
    reason: string;
    reasonLabel?: string | null;
    addressUpdated: boolean;
    /** Immer false — hier wird bewusst nichts gebucht. */
    stockBooked: boolean;
    trackingNumber?: string | null;
    labelUrl?: string | null;
    /** Gesetzt, wenn die Sendung steht, das Label aber nicht erzeugt werden konnte. */
    labelError?: string | null;
    /**
     * Wurde die NEUE Trackingnummer an den Verkaufskanal gemeldet?
     *
     * eBay, Amazon und Shopify kennen je Bestellung nur EINE Sendungsnummer — ohne Meldung
     * verfolgt der Käufer weiter das zurückgekommene Paket.
     */
    marketplaceNotified?: boolean;
    /** Warum die Meldung ausblieb. Bei Kanälen ohne Marktplatz ist das der Normalfall. */
    marketplaceNote?: string | null;
  }> {
    return this.post(`/shipments/${shipmentId}/reship`, data);
  }

  async retryShipment(shipmentId: string): Promise<{
    success: boolean;
    carrier?: string;
    trackingNumber?: string;
    labelUrl?: string;
    message?: string;
    apiError?: string;
  }> {
    return this.post(`/shipments/${shipmentId}/retry`, {});
  }

  // Legacy alias for backwards compatibility
  async retryDhlShipment(shipmentId: string): Promise<{
    success: boolean;
    carrier?: string;
    trackingNumber?: string;
    labelUrl?: string;
    message?: string;
    apiError?: string;
  }> {
    return this.retryShipment(shipmentId);
  }

  // ==========================================
  // Returns (Retouren)
  // ==========================================

  async getReturns(params?: { page?: number; limit?: number; type?: ReturnType; status?: ReturnStatus; search?: string; reasonCategory?: string }): Promise<ReturnListResponse> {
    const { reasonCategory, ...rest } = params || {};
    const query: Record<string, string | number | undefined> = { ...rest };
    // Backend erwartet snake_case-Query-Key (kein Auto-Mapping im Client).
    if (reasonCategory) query.reason_category = reasonCategory;
    return this.get<ReturnListResponse>('/returns', query);
  }

  async getReturn(id: string): Promise<Return> {
    return this.get<Return>(`/returns/${id}`);
  }

  async getReturnStats(): Promise<ReturnStats> {
    return this.get('/returns/stats');
  }

  async createReturn(data: ReturnInput): Promise<{ success: boolean; id: string; returnNumber: string }> {
    return this.post('/returns', data);
  }

  async updateReturn(id: string, data: Partial<ReturnInput>): Promise<{ success: boolean }> {
    return this.put(`/returns/${id}`, data);
  }

  async updateReturnStatus(id: string, status: ReturnStatus): Promise<{ success: boolean; status: ReturnStatus }> {
    return this.put(`/returns/${id}/status`, { status });
  }

  async receiveReturnItems(id: string, items: Array<{ id: string; quantityReceived: number; conditionReceived?: ItemCondition; conditionNotes?: string }>): Promise<{ success: boolean; status: ReturnStatus }> {
    return this.post(`/returns/${id}/receive`, { items });
  }

  async inspectReturn(id: string, data: { inspectionStatus?: string; inspectionNotes?: string; items?: Array<{ id: string; quantityApproved: number; action?: ItemAction; restock?: boolean }> }): Promise<{ success: boolean; status: ReturnStatus }> {
    return this.post(`/returns/${id}/inspect`, data);
  }

  async restockReturn(id: string): Promise<{ success: boolean; status: ReturnStatus; restockedItems: Array<{ sku: string; quantity: number; stockChange: number }> }> {
    return this.post(`/returns/${id}/restock`);
  }

  async uploadReturnImage(id: string, data: { image: string; returnItemId?: string; imageType?: string; description?: string }): Promise<{ success: boolean; id: string; fileName: string; filePath: string }> {
    return this.post(`/returns/${id}/images`, data);
  }

  async deleteReturnImage(returnId: string, imageId: string): Promise<{ success: boolean }> {
    return this.delete(`/returns/${returnId}/images/${imageId}`);
  }

  async deleteReturn(id: string): Promise<{ success: boolean }> {
    return this.delete(`/returns/${id}`);
  }

  async getReturnReasons(type?: ReturnType): Promise<{ reasons: ReturnReason[] }> {
    return this.get('/return-reasons', type ? { type } : undefined);
  }

  // ==========================================
  // Carrier Configs
  // ==========================================

  async getCarrierConfigs(): Promise<{ carrierConfigs: CarrierConfig[] }> {
    return this.get('/carrier-configs');
  }

  async createCarrierConfig(data: CarrierConfigInput): Promise<{ success: boolean; id: string }> {
    return this.post('/carrier-configs', data);
  }

  async updateCarrierConfig(id: string, data: Partial<CarrierConfigInput>): Promise<{ success: boolean }> {
    return this.put(`/carrier-configs/${id}`, data);
  }

  async deleteCarrierConfig(id: string): Promise<{ success: boolean }> {
    return this.delete(`/carrier-configs/${id}`);
  }

  // ==========================================
  // Pick Lists
  // ==========================================

  async getPickLists(params?: { page?: number; limit?: number; status?: string }): Promise<PickListListResponse> {
    return this.get<PickListListResponse>('/pick-lists', params);
  }

  async getPickList(id: string): Promise<PickList> {
    return this.get<PickList>(`/pick-lists/${id}`);
  }

  async getPickListStats(): Promise<PickListStats> {
    return this.get('/pick-lists/stats');
  }

  async getPendingOrdersForPicking(warehouseId?: string): Promise<{ success: boolean; orders: PendingOrderSummary[]; total: number }> {
    return this.get('/pick-lists/pending-orders', warehouseId ? { warehouseId } : undefined);
  }

  async checkStockAvailability(orderIds: string[]): Promise<StockCheckResponse> {
    return this.post('/pick-lists/check-stock', { orderIds });
  }

  async createPickList(data: PickListInput & { allowPartialDelivery?: boolean; skipStockCheck?: boolean }): Promise<CreatePickListResponse> {
    return this.post('/pick-lists', data);
  }

  async startPickList(id: string): Promise<{ success: boolean }> {
    return this.post(`/pick-lists/${id}/start`);
  }

  async pickItem(pickListId: string, data: {
    itemId: string;
    quantityPicked: number;
    articleId?: string;
    resolutionType?: string;
    resolutionNote?: string;
    alternativeLocationCode?: string;
    quantityFromAlternative?: number;
    /**
     * Kommissionierwagen — der Server macht daraus einen echten Transit-Lagerplatz.
     *
     * 🚨 Bis 27.08.2026 fehlte dieses Feld hier. Der Server wertete `containerCode` längst aus,
     * aber niemand schickte es: eine Attrappe. Damit blieb nur der Rückfall, und der war der
     * Klarname des Angemeldeten — daraus wurde der Lagerplatz `TRANSIT-CHERA BRÜNKMANNS`.
     */
    containerCode?: string;
  }): Promise<{ success: boolean }> {
    return this.post(`/pick-lists/${pickListId}/pick-item`, data);
  }

  async deletePickList(id: string): Promise<{ success: boolean }> {
    return this.delete(`/pick-lists/${id}`);
  }

  // ==========================================
  // Picking Sessions
  // ==========================================

  async getPickingSessions(params?: { status?: string }): Promise<{ pickingSessions: PickingSession[] }> {
    return this.get('/picking-sessions', params);
  }

  async createPickingSession(data: { userId: string; deviceInfo?: Record<string, unknown> }): Promise<{ success: boolean; id: string }> {
    return this.post('/picking-sessions', data);
  }

  async endPickingSession(id: string): Promise<{ success: boolean }> {
    return this.post(`/picking-sessions/${id}/end`);
  }

  // ==========================================
  // Purchase Orders
  // ==========================================

  async getPurchaseOrders(params?: { page?: number; limit?: number; status?: string; supplierId?: string }): Promise<PurchaseOrderListResponse> {
    return this.get<PurchaseOrderListResponse>('/purchase-orders', params);
  }

  /** Freie Wareneingänge, zu denen noch Einkaufspreise fehlen (inkl. Positionen mit Mengen). */
  async getReceiptsWithoutCost(): Promise<ReceiptsWithoutCostResponse> {
    return this.get<ReceiptsWithoutCostResponse>('/purchasing/receipts-without-cost');
  }

  /**
   * Nur die Kennzahl — für Kacheln und Badges, ohne die Positionen zu laden.
   *
   * Der Endpunkt existierte seit jeher; im Frontend rief ihn niemand auf, weil die Seite von
   * nirgends verlinkt war.
   */
  async getReceiptsWithoutCostCount(): Promise<{ success: boolean; count: number; quantity: number; oldestBookedAt: string | null }> {
    return this.get('/purchasing/receipts-without-cost/count');
  }

  /**
   * Am Handheld angelegte Artikel, die noch niemand nachgepflegt hat.
   *
   * Sie entstehen beim Wareneingang mit unbekanntem Barcode und tragen nur die Felder, die der
   * Betrieb dort verlangt — Warengruppe, Steuersatz und Verkaufspreis fehlen danach.
   */
  async getMdeArticleReviewPending(): Promise<{
    success: boolean;
    count: number;
    articles: Array<{
      articleId: string;
      sku: string | null;
      name: string | null;
      ean: string | null;
      stock: number;
      purchasePrice: number | null;
      sellingPrice: number | null;
      createdAt: string | null;
    }>;
  }> {
    return this.get('/articles/mde-review-pending');
  }

  /** Einen am Handheld angelegten Artikel als geprüft abhaken. */
  async markMdeArticleReviewed(articleId: string): Promise<{ success: boolean }> {
    return this.post(`/articles/${articleId}/mde-review-done`, {});
  }

  /** Einkaufspreise zu einem gebuchten Wareneingang nachtragen (rückwirkend zum Buchungszeitpunkt). */
  async applyReceiptCosts(
    receiptId: string,
    lines: Array<{ lineId: number; purchasePrice: number; purchasePriceIsGross?: boolean }>,
  ): Promise<{
    success: boolean;
    applied: number;
    skipped: number;
    errors: string[];
    /**
     * Was die Preiskalkulation daraufhin gemacht hat — je Artikel-ID (Hex).
     *
     * Fehlt das Feld, war die Automatik aus (`globalEnabled` / `autoRecalcOnEkChange` in den
     * BISCalc-Einstellungen). Ein still geänderter Verkaufspreis, den niemand gesehen hat, geht
     * sonst unbemerkt in alle Kanäle live — deshalb wird er hier angezeigt.
     */
    kalkulation?: Record<string, {
      status: string;
      newVk?: number | string | null;
      previousVk?: number | string | null;
      ruleName?: string | null;
      marginPercent?: number | string | null;
      blockReason?: string | null;
      reason?: string | null;
    }>;
  }> {
    return this.post(`/purchasing/receipts/${receiptId}/costs`, { lines });
  }

  async getPurchaseOrder(id: string): Promise<PurchaseOrder> {
    return this.get<PurchaseOrder>(`/purchase-orders/${id}`);
  }

  async createPurchaseOrder(data: PurchaseOrderInput): Promise<{ success: boolean; id: string; orderNumber: string }> {
    return this.post('/purchase-orders', data);
  }

  async updatePurchaseOrder(id: string, data: Partial<PurchaseOrderInput>): Promise<{ success: boolean }> {
    return this.put(`/purchase-orders/${id}`, data);
  }

  async deletePurchaseOrder(id: string): Promise<{ success: boolean }> {
    return this.delete(`/purchase-orders/${id}`);
  }

  // ==========================================
  // Tenant Settings
  // ==========================================

  async getTenantSettings(category: string): Promise<{ settings: Record<string, unknown> | null }> {
    return this.get(`/settings/${category}`);
  }

  async updateTenantSettings(category: string, settings: Record<string, unknown>): Promise<{ success: boolean }> {
    return this.put(`/settings/${category}`, { settings });
  }

  async getAllTenantSettings(): Promise<{ settings: Record<string, Record<string, unknown>> }> {
    return this.get('/settings');
  }

  // ==========================================
  // Custom Field Definitions
  // ==========================================

  async getCustomFieldDefinitions(activeOnly?: boolean): Promise<{ customFields: CustomFieldDefinition[]; total: number }> {
    return this.get('/custom-fields', activeOnly ? { active: 'true' } : undefined);
  }

  async getCustomFieldDefinition(id: string): Promise<CustomFieldDefinition> {
    return this.get(`/custom-fields/${id}`);
  }

  async getCustomFieldsForArticle(params: { manufacturer?: string; brand?: string; category?: string }): Promise<{ customFields: CustomFieldDefinition[] }> {
    return this.get('/custom-fields/for-article', params);
  }

  async createCustomFieldDefinition(data: CustomFieldDefinitionInput): Promise<{ success: boolean; id: string }> {
    return this.post('/custom-fields', data);
  }

  async updateCustomFieldDefinition(id: string, data: Partial<CustomFieldDefinitionInput>): Promise<{ success: boolean }> {
    return this.put(`/custom-fields/${id}`, data);
  }

  async deleteCustomFieldDefinition(id: string): Promise<{ success: boolean }> {
    return this.delete(`/custom-fields/${id}`);
  }

  // ==========================================
  // Categories
  // ==========================================

  async getCategories(params?: { active?: boolean; flat?: boolean }): Promise<{ categories: Category[]; total: number }> {
    const queryParams: Record<string, string> = {};
    if (params?.active) queryParams.active = 'true';
    if (params?.flat) queryParams.flat = 'true';
    return this.get('/categories', Object.keys(queryParams).length > 0 ? queryParams : undefined);
  }

  async getCategory(id: string): Promise<Category> {
    return this.get(`/categories/${id}`);
  }

  async createCategory(data: CategoryInput): Promise<{ success: boolean; id: string; path: string }> {
    return this.post('/categories', data);
  }

  async updateCategory(id: string, data: Partial<CategoryInput>): Promise<{ success: boolean }> {
    return this.put(`/categories/${id}`, data);
  }

  async deleteCategory(id: string, options?: { action: 'remove' | 'move' | 'delete'; targetCategoryId?: string }): Promise<{ success: boolean; articlesAffected?: number; error?: string; articleCount?: number; jtl_folge?: string | null; jtl_entscheidung?: string | null; bis_folge?: string | null; artikel_deaktiviert_belege?: number }> {
    if (options) {
      return this.request(`/categories/${id}`, { method: 'DELETE', body: JSON.stringify(options) });
    }
    return this.delete(`/categories/${id}`);
  }

  // ==========================================
  // Bill of Materials (BOM) / Stücklisten
  // ==========================================

  async getBoms(params?: { articleId?: string; active?: boolean }): Promise<{ boms: Bom[]; total: number }> {
    const queryParams: Record<string, string> = {};
    if (params?.articleId) queryParams.articleId = params.articleId;
    if (params?.active) queryParams.active = 'true';
    return this.get('/bom', Object.keys(queryParams).length > 0 ? queryParams : undefined);
  }

  async getBom(id: string): Promise<Bom> {
    return this.get(`/bom/${id}`);
  }

  async getArticleBom(articleId: string): Promise<{ bom: Bom | null; hasBom: boolean }> {
    return this.get(`/articles/${articleId}/bom`);
  }

  async hasArticleBom(articleId: string): Promise<{ hasBom: boolean }> {
    return this.get(`/articles/${articleId}/has-bom`);
  }

  async createBom(data: BomInput): Promise<{ success: boolean; id: string }> {
    return this.post('/bom', data);
  }

  async updateBom(id: string, data: Partial<BomInput>): Promise<{ success: boolean }> {
    return this.put(`/bom/${id}`, data);
  }

  async deleteBom(id: string): Promise<{ success: boolean }> {
    return this.delete(`/bom/${id}`);
  }

  async addBomItem(bomId: string, item: BomItemInput): Promise<{ success: boolean; id: string }> {
    return this.post(`/bom/${bomId}/items`, item);
  }

  async removeBomItem(bomId: string, itemId: string): Promise<{ success: boolean }> {
    return this.delete(`/bom/${bomId}/items/${itemId}`);
  }

  async expandBom(id: string, quantity?: number): Promise<{ items: ExpandedBomItem[]; total: number }> {
    const params = quantity ? { quantity: quantity.toString() } : undefined;
    return this.get(`/bom/${id}/expand`, params);
  }

  // ==========================================
  // MHD (Expiry Date) Inventory
  // ==========================================

  async getInventoryMhds(params: {
    articleId: string;
    storageLocationCode?: string;
  }): Promise<{ mhds: MhdInventory[] }> {
    return this.get('/inventory/mhd', params);
  }

  async deductMhdInventory(data: {
    articleId: string;
    storageLocationCode: string;
    expiryDate?: string | null;
    batchNumber?: string | null;
    quantity: number;
    reference?: string;
  }): Promise<{ success: boolean }> {
    return this.post('/inventory/mhd/deduct', data);
  }

  // ==========================================
  // Serial Numbers
  // ==========================================

  async getSerialNumbers(params?: {
    articleId?: string;
    status?: SerialNumberStatus;
    storageLocationCode?: string;
    orderId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<SerialNumberListResponse> {
    return this.get('/inventory/serial-numbers', params);
  }

  async addSerialNumbers(data: SerialNumberInput): Promise<{
    success: boolean;
    added: number;
    serialNumbers: string[];
  }> {
    return this.post('/inventory/serial-numbers', data);
  }

  async assignSerialNumber(serialNumber: string, data: SerialNumberAssignInput): Promise<{
    success: boolean;
    serialNumber: SerialNumber;
  }> {
    return this.post(`/inventory/serial-numbers/${encodeURIComponent(serialNumber)}/assign`, data);
  }

  async updateSerialNumberStatus(serialNumber: string, status: SerialNumberStatus, notes?: string): Promise<{
    success: boolean;
  }> {
    return this.put(`/inventory/serial-numbers/${encodeURIComponent(serialNumber)}`, { status, notes });
  }

  // ==========================================
  // Batch Inventory
  // ==========================================

  async getBatches(params?: {
    articleId?: string;
    storageLocationCode?: string;
    strategy?: 'FIFO' | 'FEFO';
    page?: number;
    limit?: number;
    /** Nur Chargen ohne Restmenge ausblenden (Standard: alles). */
    includeEmpty?: boolean;
    /**
     * Dashboard-Kachel-Klick (TICK-2026-01121): derselbe Schluessel wie bei der
     * MHD-Staffel im Dashboard (z. B. `batches.mhd_warnung`). Der Endpunkt kennt ihn: Er
     * grenzt zunaechst dieselben Artikel ein wie die Kachel und filtert danach zusaetzlich
     * je Charge auf die angefragte Stufe (siehe `InventoryController::getBatches()`).
     */
    kennzahl?: string;
  }): Promise<BatchInventoryListResponse> {
    return this.get('/inventory/batches', params);
  }

  async addBatch(data: BatchInventoryInput): Promise<{
    success: boolean;
    id: string;
    batchNumber: string;
  }> {
    return this.post('/inventory/batches', data);
  }

  async deductFromBatch(data: BatchDeductInput): Promise<{
    success: boolean;
    deducted: {
      batchNumber: string;
      quantity: number;
      expiryDate: string | null;
    }[];
  }> {
    return this.post('/inventory/batches/deduct', data);
  }

  // ==========================================
  // AUSVERKAUFS-AUTOMATIK (Clearance)
  // ==========================================

  async startClearance(articleId: string, data: ClearanceStartInput): Promise<ClearanceStateResponse> {
    return this.post(`/articles/${articleId}/clearance/start`, data);
  }

  async stopClearance(articleId: string): Promise<ClearanceStateResponse> {
    return this.post(`/articles/${articleId}/clearance/stop`, {});
  }

  async getClearanceHistory(articleId: string): Promise<{
    success: boolean;
    articleId: string;
    history: ClearancePriceHistoryEntry[];
  }> {
    return this.get(`/articles/${articleId}/clearance-history`);
  }

  async getClearanceCandidates(params?: { withinDays?: number }): Promise<ClearanceCandidatesResponse> {
    return this.get('/clearance/candidates', params);
  }

  async getArticleExpiryInfo(articleId: string): Promise<ArticleExpiryInfoResponse> {
    return this.get(`/articles/${articleId}/expiry-info`);
  }

  // ==========================================
  // Manufacturers
  // ==========================================

  async getManufacturers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    activeOnly?: boolean;
  }): Promise<ManufacturerListResponse> {
    return this.get('/manufacturers', params);
  }

  async searchManufacturers(query: string, limit?: number): Promise<{ manufacturers: ManufacturerSearchResult[] }> {
    return this.get('/manufacturers/search', { q: query, limit });
  }

  async createManufacturer(data: ManufacturerInput): Promise<{ success: boolean; id: string; message: string }> {
    return this.post('/manufacturers', data);
  }

  async updateManufacturer(id: string, data: Partial<ManufacturerInput>): Promise<{ success: boolean; message: string }> {
    return this.put(`/manufacturers/${id}`, data);
  }

  async deleteManufacturer(id: string): Promise<{ success: boolean; message: string }> {
    return this.delete(`/manufacturers/${id}`);
  }

  async getArticleManufacturers(articleId: string): Promise<{ manufacturers: ManufacturerSearchResult[] }> {
    return this.get(`/manufacturers/article/${articleId}`);
  }

  async setArticleManufacturers(articleId: string, manufacturerIds: string[]): Promise<{ success: boolean; message: string }> {
    return this.put(`/manufacturers/article/${articleId}`, { manufacturerIds });
  }

  async exportManufacturers(format: 'csv' | 'json' = 'json'): Promise<{ manufacturers: Manufacturer[] } | Blob> {
    if (format === 'csv') {
      const response = await fetch(this.buildUrl('/manufacturers/export', { format: 'csv' }), {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.blob();
    }
    return this.get('/manufacturers/export', { format: 'json' });
  }

  async importManufacturers(manufacturers: ManufacturerInput[]): Promise<{ success: boolean; imported: number; updated: number; errors: string[] }> {
    return this.post('/manufacturers/import', { manufacturers });
  }

  async getGpsrIncompleteManufacturers(): Promise<{
    manufacturers: Array<{
      id: string;
      name: string;
      code: string | null;
      missingFields: string[];
      hasAddress: boolean;
    }>;
    count: number;
  }> {
    return this.get('/manufacturers/gpsr-incomplete');
  }

  async getGpsrIncompleteSuppliers(): Promise<{
    suppliers: Array<{
      id: string;
      name: string;
      missingFields: string[];
      hasAddress: boolean;
    }>;
    count: number;
  }> {
    return this.get('/suppliers/gpsr-incomplete');
  }

  async backfillGpsrSuppliers(): Promise<{ success: boolean; processed: number; filled: number }> {
    return this.post('/suppliers/gpsr-backfill', {});
  }

  async getGpsrIncompleteBrands(): Promise<{
    brands: Array<{
      id: string;
      name: string;
      code: string | null;
      missingFields: string[];
      hasAddress: boolean;
    }>;
    count: number;
  }> {
    return this.get('/brands/gpsr-incomplete');
  }

  // ==========================================
  // Countries (Herkunftsländer)
  // ==========================================

  async getCountries(params?: { search?: string; activeOnly?: boolean; page?: number; limit?: number }): Promise<CountryListResponse> {
    return this.get('/countries', params);
  }

  async searchCountries(query: string, limit?: number): Promise<{ countries: CountrySearchResult[] }> {
    return this.get('/countries/search', { q: query, limit });
  }

  async createCountry(data: CountryInput): Promise<{ success: boolean; id: string; message: string }> {
    return this.post('/countries', data);
  }

  async getCountry(id: string): Promise<{ country: Country }> {
    return this.get(`/countries/${id}`);
  }

  async updateCountry(id: string, data: Partial<CountryInput>): Promise<{ success: boolean; message: string }> {
    return this.put(`/countries/${id}`, data);
  }

  async deleteCountry(id: string): Promise<{ success: boolean; message: string }> {
    return this.delete(`/countries/${id}`);
  }

  async getArticleCountries(articleId: string): Promise<{ countries: CountrySearchResult[] }> {
    return this.get(`/countries/article/${articleId}`);
  }

  async setArticleCountries(articleId: string, countryIds: string[]): Promise<{ success: boolean; message: string }> {
    return this.put(`/countries/article/${articleId}`, { countryIds });
  }

  // ==========================================
  // Brands
  // ==========================================

  async getBrands(params?: {
    page?: number;
    limit?: number;
    search?: string;
    manufacturerId?: string;
    activeOnly?: boolean;
  }): Promise<BrandListResponse> {
    return this.get('/brands', params);
  }

  async searchBrands(query: string, manufacturerId?: string, limit?: number): Promise<{ brands: BrandSearchResult[] }> {
    return this.get('/brands/search', { q: query, manufacturerId, limit });
  }

  async createBrand(data: BrandInput): Promise<{ success: boolean; id: string; message: string }> {
    return this.post('/brands', data);
  }

  async updateBrand(id: string, data: Partial<BrandInput>): Promise<{ success: boolean; message: string }> {
    return this.put(`/brands/${id}`, data);
  }

  async deleteBrand(id: string): Promise<{ success: boolean; message: string }> {
    return this.delete(`/brands/${id}`);
  }

  async getArticleBrands(articleId: string): Promise<{ brands: BrandSearchResult[] }> {
    return this.get(`/brands/article/${articleId}`);
  }

  async setArticleBrands(articleId: string, brandIds: string[]): Promise<{ success: boolean; message: string }> {
    return this.put(`/brands/article/${articleId}`, { brandIds });
  }

  async exportBrands(format: 'csv' | 'json' = 'json'): Promise<{ brands: Brand[] } | Blob> {
    if (format === 'csv') {
      const response = await fetch(this.buildUrl('/brands/export', { format: 'csv' }), {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      return response.blob();
    }
    return this.get('/brands/export', { format: 'json' });
  }

  async importBrands(brands: BrandInput[]): Promise<{ success: boolean; imported: number; updated: number; errors: string[] }> {
    return this.post('/brands/import', { brands });
  }

  // ==========================================
  // Stocktake (Inventur)
  // ==========================================

  async getStocktakes(params?: { page?: number; limit?: number; type?: StocktakeType; status?: StocktakeStatus; search?: string }): Promise<StocktakeListResponse> {
    return this.get<StocktakeListResponse>('/stocktakes', params);
  }

  async getStocktake(id: string): Promise<Stocktake> {
    return this.get<Stocktake>(`/stocktakes/${id}`);
  }

  async getStocktakeStatistics(): Promise<StocktakeStatistics> {
    return this.get('/stocktakes/statistics');
  }

  async createStocktake(data: StocktakeInput): Promise<{ id: string; stocktake_number: string; message: string }> {
    return this.post('/stocktakes', data);
  }

  /**
   * Geplante Inventur bearbeiten. Body identisch zu `createStocktake` — das Anlage-Formular
   * wird unverändert wiederverwendet. Nur bei Status „Geplant" erlaubt (sonst 409): sobald
   * gezählt wurde, würde das Neuaufbauen der Positionen echte Zählergebnisse vernichten.
   */
  async updateStocktake(
    id: string,
    data: StocktakeInput
  ): Promise<{ success: boolean; id: string; stocktake_number: string; message: string }> {
    return this.put(`/stocktakes/${id}`, data);
  }

  /**
   * Inventur löschen. Vom Server abgelehnt (409), wenn gerade gezählt wird ODER bereits
   * Bestandsanpassungen gebucht sind — die Inventur ist dann der Beleg für die Bestandsänderung.
   */
  async deleteStocktake(id: string): Promise<{ success: boolean; message: string }> {
    return this.delete(`/stocktakes/${id}`);
  }

  async startStocktake(id: string): Promise<{ message: string; status: StocktakeStatus }> {
    return this.post(`/stocktakes/${id}/start`);
  }

  async pauseStocktake(id: string): Promise<{ message: string; status: StocktakeStatus }> {
    return this.post(`/stocktakes/${id}/pause`);
  }

  async completeStocktake(id: string): Promise<{ message: string; status: StocktakeStatus; has_variances: boolean; variance_summary: StocktakeVarianceSummary }> {
    return this.post(`/stocktakes/${id}/complete`);
  }

  async cancelStocktake(id: string, reason?: string): Promise<{ message: string; status: StocktakeStatus }> {
    return this.post(`/stocktakes/${id}/cancel`, { reason });
  }

  async countStocktakeItem(stocktakeId: string, itemId: string, data: { counted_quantity: number; notes?: string }): Promise<{ message: string; variance: number; variance_percentage: number; has_variance: boolean }> {
    return this.post(`/stocktakes/${stocktakeId}/items/${itemId}/count`, data);
  }

  async approveStocktake(id: string): Promise<{ message: string; adjustments_created: number }> {
    return this.post(`/stocktakes/${id}/approve`);
  }

  async postStocktakeAdjustments(id: string): Promise<{ message: string; posted_count: number }> {
    return this.post(`/stocktakes/${id}/post-adjustments`);
  }

  async generateSpotCheck(data: SpotCheckInput): Promise<{ id: string; stocktake_number: string; total_locations: number; total_items: number; message: string }> {
    return this.post('/stocktakes/generate-spot-check', data);
  }

  // Spot check during picking
  async getPickListSpotCheck(pickListId: string, locationCode: string): Promise<SpotCheckResponse> {
    return this.get(`/pick-lists/${pickListId}/spot-check`, { location_code: locationCode });
  }

  async recordPickListSpotCheck(pickListId: string, data: SpotCheckRecordInput): Promise<SpotCheckRecordResponse> {
    return this.post(`/pick-lists/${pickListId}/spot-check`, data);
  }

  // Protokollierte Picking-Stichproben (Review im ERP, log-only)
  async getSpotCheckLogs(params?: { only_variance?: boolean; limit?: number }): Promise<{
    data: Array<{
      id: string;
      article_id: string | null;
      article_sku: string;
      article_name: string;
      storage_location_code: string;
      expected_quantity: number;
      counted_quantity: number;
      variance: number;
      has_variance: boolean;
      pick_list_id: string | null;
      counted_by: string | null;
      counted_at: string;
    }>;
    summary: { total: number; with_variance: number };
  }> {
    return this.get('/spot-check-logs', params);
  }

  // Cycle Count Configs
  async getCycleCountConfigs(): Promise<{ data: CycleCountConfig[] }> {
    return this.get('/stocktakes/cycle-configs');
  }

  async createCycleCountConfig(data: CycleCountConfigInput): Promise<{ id: string; message: string }> {
    return this.post('/stocktakes/cycle-configs', data);
  }

  // ==========================================
  // Shopify Integration
  // ==========================================

  // Connection Management
  async getShopifyConnection(): Promise<{ connection: ShopifyConnection | null; isConnected: boolean }> {
    return this.get('/shopify/connection');
  }

  async saveShopifyConnection(data: ShopifyConnectionInput): Promise<{ success: boolean; id: string }> {
    return this.post('/shopify/connection', data);
  }

  async testShopifyConnection(): Promise<{ success: boolean; shop?: { name: string; email: string }; scopes?: string[]; hasFulfillmentScope?: boolean; error?: string }> {
    return this.post('/shopify/connection/test', {});
  }

  // Locations
  async getShopifyLocations(): Promise<{ locations: ShopifyLocation[]; warehouses: Array<{ id: string; name: string }> }> {
    return this.get('/shopify/locations');
  }

  async mapShopifyLocation(data: {
    shopifyLocationId: string;
    localWarehouseId?: string;
    locationName?: string;
    isDefault?: boolean;
  }): Promise<{ success: boolean }> {
    return this.post('/shopify/locations/map', data);
  }

  // Product Sync
  async getShopifyProductSyncStatus(): Promise<ShopifyProductSyncStatus> {
    return this.get('/shopify/products/status');
  }

  async getShopifyProducts(params?: {
    status?: 'all' | 'synced' | 'pending' | 'error' | 'not_synced';
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<ShopifyProductListResponse> {
    return this.get('/shopify/products', params);
  }

  async syncProductToShopify(articleId: string): Promise<{ success: boolean; shopifyProductId: string; handle?: string }> {
    return this.post(`/shopify/products/${articleId}/sync`, {});
  }

  async bulkSyncProductsToShopify(data: {
    articleIds?: string[];
    syncAll?: boolean;
  }): Promise<{ success: number; error: number; errors: Array<{ articleId: string; error: string }> }> {
    return this.post('/shopify/products/sync', data);
  }

  // Order Import
  async getShopifyOrderImportStatus(): Promise<ShopifyOrderImportStatus> {
    return this.get('/shopify/orders/status');
  }

  async getShopifyOrders(params?: {
    status?: string;
    fulfillment_status?: string;
    limit?: number;
  }): Promise<{ orders: ShopifyOrder[]; total: number }> {
    return this.get('/shopify/orders', params);
  }

  async importShopifyOrder(shopifyOrderId: string): Promise<{ success: boolean; orderId: string; orderNumber: string }> {
    return this.post(`/shopify/orders/${shopifyOrderId}/import`, {});
  }

  async bulkImportShopifyOrders(data: {
    orderIds?: string[];
    importAll?: boolean;
  }): Promise<{ success: number; error: number; skipped: number; errors: Array<{ orderId: string; error: string }> }> {
    return this.post('/shopify/orders/import', data);
  }

  // Inventory Sync
  async syncInventoryToShopify(data: {
    articleIds?: string[];
    syncAll?: boolean;
  }): Promise<{ success: number; error: number; errors: Array<{ articleId: string; error: string }> }> {
    return this.post('/shopify/inventory/sync', data);
  }

  // Fulfillment
  async fulfillShopifyOrder(orderId: string, data: {
    trackingNumber?: string;
    trackingCompany?: string;
    trackingUrl?: string;
    notifyCustomer?: boolean;
  }): Promise<{ success: boolean; fulfillment?: unknown }> {
    return this.post(`/shopify/orders/${orderId}/fulfill`, data);
  }

  // Webhooks
  async getShopifyWebhooks(): Promise<{ webhooks: ShopifyWebhook[] }> {
    return this.get('/shopify/webhooks');
  }

  async registerShopifyWebhooks(): Promise<{ webhooks: Record<string, { success: boolean; id?: number; error?: string }> }> {
    return this.post('/shopify/webhooks/register', {});
  }

  // Collections
  async getShopifyCollections(refresh?: boolean): Promise<{ collections: ShopifyCollection[]; categories: Array<{ id: string; name: string }> }> {
    return this.get('/shopify/collections', refresh ? { refresh: '1' } : undefined);
  }

  async mapShopifyCollection(data: {
    shopifyCollectionId: string;
    categoryId?: string;
    collectionType?: 'smart' | 'custom';
    collectionTitle?: string;
  }): Promise<{ success: boolean }> {
    return this.post('/shopify/collections/map', data);
  }

  // Metafield Mappings
  async getShopifyMetafieldMappings(): Promise<{ mappings: ShopifyMetafieldMapping[] }> {
    return this.get('/shopify/metafields/mappings');
  }

  async saveShopifyMetafieldMapping(data: ShopifyMetafieldMappingInput & { id?: string }): Promise<{ success: boolean; id: string }> {
    return this.post('/shopify/metafields/mappings', data);
  }

  async deleteShopifyMetafieldMapping(id: string): Promise<{ success: boolean }> {
    return this.delete(`/shopify/metafields/mappings/${id}`);
  }

  // Sync Log
  async getShopifySyncLog(params?: {
    type?: 'product' | 'order' | 'inventory' | 'collection' | 'webhook';
    status?: 'success' | 'error' | 'skipped';
    limit?: number;
  }): Promise<{ logs: ShopifySyncLog[] }> {
    return this.get('/shopify/log', params);
  }

  // Taxonomy (Product Categories)
  async getShopifyTaxonomy(refresh?: boolean): Promise<{ taxonomy: Array<{ id: string; name: string; fullName: string }>; count: number; cached?: boolean }> {
    return this.get('/shopify/taxonomy', refresh ? { refresh: '1' } : undefined);
  }

  async getShopifyTaxonomyMappings(): Promise<{ mappings: Array<{ id: string; connectionId: string; localCategoryName: string; shopifyTaxonomyNodeId: string; shopifyTaxonomyLabel: string }>; localCategories: string[]; connectionId: string }> {
    return this.get('/shopify/taxonomy/mappings');
  }

  async saveShopifyTaxonomyMapping(data: { connectionId: string; localCategoryName: string; shopifyTaxonomyNodeId: string; shopifyTaxonomyLabel: string }): Promise<{ success: boolean; id: string }> {
    return this.post('/shopify/taxonomy/mappings', data);
  }

  async deleteShopifyTaxonomyMapping(id: string): Promise<{ success: boolean }> {
    return this.delete(`/shopify/taxonomy/mappings/${id}`);
  }

  // Tax Category → Shopify Taxonomy Mappings
  async getShopifyTaxCategoryMappings(): Promise<{
    success: boolean;
    mappings: Array<{ id: string; connectionId: string; taxCategoryCode: string; shopifyTaxonomyNodeId: string; shopifyTaxonomyLabel: string }>;
    taxCategories: Array<{ id: string; code: string; name: string; description: string | null; default_tax_class_code: string; effectiveRate: number | null }>;
    connectionId: string;
    priority: string;
  }> {
    return this.get('/shopify/tax-category-mappings');
  }

  async saveShopifyTaxCategoryMapping(data: { connectionId: string; taxCategoryCode: string; shopifyTaxonomyNodeId: string; shopifyTaxonomyLabel: string }): Promise<{ success: boolean; id: string }> {
    return this.post('/shopify/tax-category-mappings', data);
  }

  async deleteShopifyTaxCategoryMapping(id: string): Promise<{ success: boolean }> {
    return this.delete(`/shopify/tax-category-mappings/${id}`);
  }

  // Pending Changes
  async getShopifyPendingChanges(params?: {
    status?: 'pending' | 'approved' | 'rejected' | 'partial' | 'all';
    changeType?: 'product' | 'inventory';
    connectionId?: string;
  }): Promise<{ changes: ShopifyPendingChange[]; pendingCount: number }> {
    return this.get('/shopify/pending-changes', params);
  }

  async getShopifyPendingChange(id: string): Promise<{ change: ShopifyPendingChange }> {
    return this.get(`/shopify/pending-changes/${id}`);
  }

  async approveShopifyPendingChange(id: string, data?: { fields?: string[]; notes?: string }): Promise<{ success: boolean; status: string; appliedFields?: string[] }> {
    return this.post(`/shopify/pending-changes/${id}/approve`, data || {});
  }

  async rejectShopifyPendingChange(id: string, data?: { fields?: string[]; notes?: string }): Promise<{ success: boolean; status: string }> {
    return this.post(`/shopify/pending-changes/${id}/reject`, data || {});
  }

  async rejectAllShopifyPendingChanges(): Promise<{ success: boolean; rejectedCount: number }> {
    return this.post('/shopify/pending-changes/reject-all', {});
  }

  // ==========================================
  // PayPal Integration
  // ==========================================

  // Configuration
  async getPayPalConfigs(): Promise<{ data: PayPalConfig[] }> {
    return this.get('/paypal/configs');
  }

  async getPayPalConfig(id: string): Promise<{ data: PayPalConfig }> {
    return this.get(`/paypal/configs/${id}`);
  }

  async createPayPalConfig(data: PayPalConfigInput): Promise<{ message: string; data: { id: string } }> {
    return this.post('/paypal/configs', data);
  }

  async updatePayPalConfig(id: string, data: Partial<PayPalConfigInput>): Promise<{ message: string }> {
    return this.put(`/paypal/configs/${id}`, data);
  }

  async deletePayPalConfig(id: string): Promise<{ message: string }> {
    return this.delete(`/paypal/configs/${id}`);
  }

  async testPayPalConfig(id: string): Promise<{ valid: boolean; message: string }> {
    return this.post(`/paypal/configs/${id}/test`, {});
  }

  // Transactions
  async getPayPalTransactions(params?: {
    config_id?: string;
    status?: string;
    matched?: 'true' | 'false';
    datev_exported?: 'true' | 'false';
    date_from?: string;
    date_to?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<PayPalTransactionListResponse> {
    return this.get('/paypal/transactions', params);
  }

  async getPayPalTransaction(id: string): Promise<{ data: PayPalTransaction }> {
    return this.get(`/paypal/transactions/${id}`);
  }

  async syncPayPalTransactions(data: {
    config_id: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{ message: string; data: PayPalSyncResult }> {
    return this.post('/paypal/sync', data);
  }

  async matchPayPalTransaction(transactionId: string, orderId: string): Promise<{ message: string }> {
    return this.post(`/paypal/transactions/${transactionId}/match`, { order_id: orderId });
  }

  async unmatchPayPalTransaction(transactionId: string): Promise<{ message: string }> {
    return this.post(`/paypal/transactions/${transactionId}/unmatch`, {});
  }

  async autoMatchPayPalTransactions(configId?: string): Promise<{ message: string; data: { total_checked: number; matched: number } }> {
    return this.post('/paypal/auto-match', { config_id: configId });
  }

  // Refunds
  async getPayPalRefunds(params?: {
    status?: PayPalRefundStatus;
    limit?: number;
    offset?: number;
  }): Promise<PayPalRefundListResponse> {
    return this.get('/paypal/refunds', params);
  }

  async createPayPalRefund(data: PayPalRefundInput): Promise<{ message: string; data: { id: string; refund_result?: unknown } }> {
    return this.post('/paypal/refunds', data);
  }

  async processPayPalRefund(id: string): Promise<{ message: string; data: { success: boolean; refund_id?: string; status?: string } }> {
    return this.post(`/paypal/refunds/${id}/process`, {});
  }

  // DATEV Export
  async getPayPalDatevExports(params?: {
    limit?: number;
    offset?: number;
  }): Promise<PayPalDatevExportListResponse> {
    return this.get('/paypal/datev/exports', params);
  }

  async createPayPalDatevExport(data: PayPalDatevExportInput): Promise<{ message: string; data: PayPalDatevExportResult }> {
    return this.post('/paypal/datev/export', data);
  }

  async downloadPayPalDatevExport(id: string): Promise<Blob> {
    const response = await fetch(this.buildUrl(`/paypal/datev/exports/${id}/download`), {
      headers: { Authorization: `Bearer ${this.token}` },
    });
    return response.blob();
  }

  // Statistics
  async getPayPalStatistics(params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<{ data: PayPalStatistics }> {
    return this.get('/paypal/statistics', params);
  }

  async getPayPalBalance(configId: string): Promise<{ data: unknown }> {
    return this.get('/paypal/balance', { config_id: configId });
  }

  // ==========================================
  // DHL Integration
  // ==========================================

  // Admin: API Config Management
  async getDhlApiConfigs(): Promise<DhlApiConfig[]> {
    return this.get('/admin/dhl/configs');
  }

  async createDhlApiConfig(data: DhlApiConfigInput): Promise<DhlApiConfig> {
    return this.post('/admin/dhl/configs', data);
  }

  async updateDhlApiConfig(id: string, data: Partial<DhlApiConfigInput>): Promise<DhlApiConfig> {
    return this.put(`/admin/dhl/configs/${id}`, data);
  }

  async deleteDhlApiConfig(id: string): Promise<{ success: boolean }> {
    return this.delete(`/admin/dhl/configs/${id}`);
  }

  async testDhlApiCredentials(data: { apiKey: string; apiSecret: string; environment: 'sandbox' | 'production' }): Promise<{ valid: boolean; message: string }> {
    return this.post('/admin/dhl/test-credentials', data);
  }

  // Admin: DHL Products & Services Management
  async getAdminDhlProducts(): Promise<Record<string, DhlProductInfo>> {
    return this.get('/admin/dhl/products');
  }

  async getAdminDhlServices(): Promise<Record<string, DhlServiceInfo>> {
    return this.get('/admin/dhl/services');
  }

  async updateDhlConfigProducts(id: string, products: string[]): Promise<{ id: string; enabledProducts: string[]; message: string }> {
    return this.put(`/admin/dhl/configs/${id}/products`, { enabledProducts: products });
  }

  async updateDhlConfigServices(id: string, services: string[], serviceDefaults?: Record<string, unknown>): Promise<{ id: string; enabledServices: string[]; serviceDefaults: unknown; message: string }> {
    return this.put(`/admin/dhl/configs/${id}/services`, { enabledServices: services, serviceDefaults });
  }

  // Admin: DPD Global Settings (no credentials needed)
  async getDpdGlobalSettings(): Promise<{ enabledProducts: string[]; enabledServices: string[] }> {
    return this.get('/admin/dpd/settings');
  }

  async updateDpdGlobalSettings(data: { enabledProducts: string[]; enabledServices: string[] }): Promise<{ success: boolean; enabledProducts: string[]; enabledServices: string[] }> {
    return this.put('/admin/dpd/settings', data);
  }

  // Admin: DPD API Configs (for legacy/specific configs)
  async getDpdApiConfigs(): Promise<DpdApiConfig[]> {
    return this.get('/admin/dpd/configs');
  }

  async createDpdApiConfig(data: DpdApiConfigInput): Promise<DpdApiConfig> {
    return this.post('/admin/dpd/configs', data);
  }

  async updateDpdApiConfig(id: string, data: Partial<DpdApiConfigInput>): Promise<DpdApiConfig> {
    return this.put(`/admin/dpd/configs/${id}`, data);
  }

  async deleteDpdApiConfig(id: string): Promise<{ success: boolean }> {
    return this.delete(`/admin/dpd/configs/${id}`);
  }

  async testDpdApiCredentials(data: { delisId: string; password: string; environment: 'sandbox' | 'production' }): Promise<{ valid: boolean; message: string }> {
    return this.post('/admin/dpd/test-credentials', data);
  }

  // Admin: DPD Products & Services Management
  async getAdminDpdProducts(): Promise<Record<string, DpdProductInfo>> {
    return this.get('/admin/dpd/products');
  }

  async getAdminDpdServices(): Promise<Record<string, DpdServiceInfo>> {
    return this.get('/admin/dpd/services');
  }

  async updateDpdConfigProducts(id: string, products: string[]): Promise<{ id: string; enabledProducts: string[]; message: string }> {
    return this.put(`/admin/dpd/configs/${id}/products`, { enabledProducts: products });
  }

  async updateDpdConfigServices(id: string, services: string[], serviceDefaults?: Record<string, unknown>): Promise<{ id: string; enabledServices: string[]; serviceDefaults: unknown; message: string }> {
    return this.put(`/admin/dpd/configs/${id}/services`, { enabledServices: services, serviceDefaults });
  }

  // Admin: GLS API Configs
  async getGlsApiConfigs(): Promise<GlsApiConfig[]> {
    return this.get('/admin/gls/configs');
  }

  async createGlsApiConfig(data: GlsApiConfigInput): Promise<GlsApiConfig> {
    return this.post('/admin/gls/configs', data);
  }

  async updateGlsApiConfig(id: string, data: Partial<GlsApiConfigInput>): Promise<GlsApiConfig> {
    return this.put(`/admin/gls/configs/${id}`, data);
  }

  async deleteGlsApiConfig(id: string): Promise<{ success: boolean }> {
    return this.delete(`/admin/gls/configs/${id}`);
  }

  async testGlsApiCredentials(data: { clientId: string; clientSecret: string; environment: 'sandbox' | 'production' }): Promise<{ valid: boolean; message: string }> {
    return this.post('/admin/gls/test-credentials', data);
  }

  // Admin: UPS API Configs
  async getUpsApiConfigs(): Promise<UpsApiConfig[]> {
    return this.get('/admin/ups/configs');
  }

  async createUpsApiConfig(data: UpsApiConfigInput): Promise<UpsApiConfig> {
    return this.post('/admin/ups/configs', data);
  }

  async updateUpsApiConfig(id: string, data: Partial<UpsApiConfigInput>): Promise<UpsApiConfig> {
    return this.put(`/admin/ups/configs/${id}`, data);
  }

  async deleteUpsApiConfig(id: string): Promise<{ success: boolean }> {
    return this.delete(`/admin/ups/configs/${id}`);
  }

  async testUpsApiCredentials(data: { clientId: string; clientSecret: string; environment: 'sandbox' | 'production' }): Promise<{ valid: boolean; message: string }> {
    return this.post('/admin/ups/test-credentials', data);
  }

  // Admin: Hermes API Configs
  async getHermesApiConfigs(): Promise<HermesApiConfig[]> {
    return this.get('/admin/hermes/configs');
  }

  async createHermesApiConfig(data: HermesApiConfigInput): Promise<HermesApiConfig> {
    return this.post('/admin/hermes/configs', data);
  }

  async updateHermesApiConfig(id: string, data: Partial<HermesApiConfigInput>): Promise<HermesApiConfig> {
    return this.put(`/admin/hermes/configs/${id}`, data);
  }

  async deleteHermesApiConfig(id: string): Promise<{ success: boolean }> {
    return this.delete(`/admin/hermes/configs/${id}`);
  }

  async testHermesApiCredentials(data: { partnerId: string; apiKey: string; environment: 'sandbox' | 'production' }): Promise<{ valid: boolean; message: string }> {
    return this.post('/admin/hermes/test-credentials', data);
  }

  // Admin: PhotoRoom App Config (Central Platform Config)
  async getPhotoRoomAppConfigs(): Promise<PhotoRoomAppConfigListResponse> {
    return this.get('/admin/photoroom-config');
  }

  async getPhotoRoomAppConfig(environment: 'sandbox' | 'production'): Promise<{ configured: boolean; config: PhotoRoomAppConfig | null; environment?: string }> {
    return this.get(`/admin/photoroom-config/${environment}`);
  }

  async savePhotoRoomAppConfig(environment: 'sandbox' | 'production', data: PhotoRoomAppConfigInput): Promise<{ success: boolean; config: PhotoRoomAppConfig }> {
    return this.put(`/admin/photoroom-config/${environment}`, data);
  }

  async deletePhotoRoomAppConfig(environment: 'sandbox' | 'production'): Promise<{ success: boolean }> {
    return this.delete(`/admin/photoroom-config/${environment}`);
  }

  async testPhotoRoomAppConnection(environment: 'sandbox' | 'production', apiKey?: string): Promise<{ success: boolean; message: string; environment?: string }> {
    return this.post(`/admin/photoroom-config/${environment}/test`, { apiKey });
  }

  async getPhotoRoomAppStats(): Promise<PhotoRoomAppConfigStats> {
    return this.get('/admin/photoroom-config/stats');
  }

  async resetPhotoRoomAppUsage(environment: 'sandbox' | 'production'): Promise<{ success: boolean; message: string }> {
    return this.post(`/admin/photoroom-config/${environment}/reset-usage`, {});
  }

  // Admin: OAuth Settings (Google Login)
  async getOAuthSettings(): Promise<{ settings: OAuthSettings | null }> {
    return this.get('/admin/oauth-settings/google');
  }

  async saveOAuthSettings(data: OAuthSettingsInput): Promise<{ success: boolean; settings: OAuthSettings }> {
    return this.post('/admin/oauth-settings/google', data);
  }

  async deleteOAuthSettings(): Promise<{ success: boolean }> {
    return this.delete('/admin/oauth-settings/google');
  }

  // Stripe Settings (SuperAdmin)
  async getStripeSettings(): Promise<{
    success: boolean;
    settings: {
      id: string;
      serviceId: string;
      isActive: boolean;
      hasSecretKey: boolean;
      secretKeyPreview: string | null;
      hasWebhookSecret: boolean;
      webhookSecretPreview: string | null;
      hasPublicKey: boolean;
      publicKeyPreview: string | null;
      webhookUrl: string;
      createdAt: string;
      updatedAt: string;
    } | null;
    envConfigured?: boolean;
    envKeyPreview?: string | null;
  }> {
    return this.get('/admin/stripe-settings');
  }

  async saveStripeSettings(data: {
    secretKey?: string;
    webhookSecret?: string;
    publicKey?: string;
    isActive?: boolean;
  }): Promise<{ success: boolean; message: string }> {
    return this.post('/admin/stripe-settings', data);
  }

  async testStripeConnection(secretKey?: string): Promise<{
    success: boolean;
    error?: string;
    account?: {
      id: string;
      businessName: string | null;
      country: string;
      currency: string;
      chargesEnabled: boolean;
      payoutsEnabled: boolean;
    };
    keyType?: string;
  }> {
    return this.post('/admin/stripe-settings/test', secretKey ? { secretKey } : {});
  }

  async deleteStripeSettings(): Promise<{ success: boolean }> {
    return this.delete('/admin/stripe-settings');
  }

  // Admin: BISTranslate (Shopify App Credentials)
  async getBISTranslateSettings(): Promise<{
    success: boolean;
    settings: {
      id: string;
      serviceId: string;
      isActive: boolean;
      hasClientId: boolean;
      clientIdPreview: string | null;
      hasClientSecret: boolean;
      clientSecretPreview: string | null;
      createdAt: string;
      updatedAt: string;
    } | null;
    envConfigured?: boolean;
    envKeyPreview?: string | null;
  }> {
    return this.get('/admin/bistranslate-settings');
  }

  async saveBISTranslateSettings(data: {
    clientId?: string;
    clientSecret?: string;
    isActive?: boolean;
  }): Promise<{ success: boolean; message: string }> {
    return this.post('/admin/bistranslate-settings', data);
  }

  async deleteBISTranslateSettings(): Promise<{ success: boolean }> {
    return this.delete('/admin/bistranslate-settings');
  }

  // Admin: AI API Config (Central KI-Provider Management)
  async getAiApiConfigs(): Promise<{
    configs: AiApiConfigResponse[];
    providers: Record<string, AiApiProviderMeta>;
    environments: Array<{ value: string; label: string }>;
  }> {
    return this.get('/admin/ai-api');
  }

  async getAiApiConfig(provider: string, env: string): Promise<{
    configured: boolean;
    config: AiApiConfigResponse | null;
    provider?: string;
    environment?: string;
  }> {
    return this.get(`/admin/ai-api/${provider}/${env}`);
  }

  async saveAiApiConfig(provider: string, env: string, data: {
    apiKey?: string;
    apiSecret?: string;
    model?: string;
    region?: string;
    isActive?: boolean;
  }): Promise<{ success: boolean; config: AiApiConfigResponse }> {
    return this.put(`/admin/ai-api/${provider}/${env}`, data);
  }

  async deleteAiApiConfig(provider: string, env: string): Promise<{ success: boolean }> {
    return this.delete(`/admin/ai-api/${provider}/${env}`);
  }

  async testAiApiKey(provider: string, env: string, data?: {
    apiKey?: string;
    apiSecret?: string;
    region?: string;
  }): Promise<{ success: boolean; message: string; details?: Record<string, unknown> }> {
    return this.post(`/admin/ai-api/${provider}/${env}/test`, data ?? {});
  }

  // Available Carriers (enabled by admin)
  async getAvailableCarriers(): Promise<{ carriers: Array<{ id: string; name: string; logo: string }>; count: number }> {
    return this.get('/carriers/available');
  }

  // Tenant: DHL Config
  async getDhlConfig(): Promise<DhlTenantConfig | null> {
    return this.get('/dhl/config');
  }

  async saveDhlConfig(data: DhlTenantConfigInput): Promise<DhlTenantConfig> {
    return this.post('/dhl/config', data);
  }

  async refreshDhlCache(): Promise<{ success: boolean; message: string; cached_products: DhlProduct[]; cached_services: DhlService[]; cache_updated_at: string | null }> {
    return this.post('/dhl/config/refresh-cache', {});
  }

  /**
   * Prueft die DHL-Anbindung, ohne dass ein Etikett gedruckt wird.
   *
   * Ohne `mitEchtemEtikett` ein Trockenlauf bei DHL (`validate=true`): keine Sendung, kein
   * Etikett, nichts zu stornieren. Mit `true` wird ein echtes Etikett erzeugt und unmittelbar
   * wieder storniert — schlaegt das Storno fehl, steht die Sendungsnummer in `sendungsnummer`
   * und `stornoOffen` ist gesetzt.
   */
  async testDhlConnection(mitEchtemEtikett = false): Promise<DhlVerbindungstest> {
    return this.post('/dhl/test-connection', { mitEchtemEtikett });
  }

  // Tenant: DPD Config
  async getDpdConfig(): Promise<DpdTenantConfig | null> {
    return this.get('/dpd/config');
  }

  async saveDpdConfig(data: DpdTenantConfigInput): Promise<DpdTenantConfig> {
    return this.post('/dpd/config', data);
  }

  // Tenant: GLS Config
  async getGlsConfig(): Promise<GlsTenantConfig | null> {
    return this.get('/gls/config');
  }

  async saveGlsConfig(data: GlsTenantConfigInput): Promise<GlsTenantConfig> {
    return this.post('/gls/config', data);
  }

  // Tenant: UPS Config
  async getUpsConfig(): Promise<UpsTenantConfig | null> {
    return this.get('/ups/config');
  }

  async saveUpsConfig(data: UpsTenantConfigInput): Promise<UpsTenantConfig> {
    return this.post('/ups/config', data);
  }

  // Tenant: Hermes Config
  async getHermesConfig(): Promise<HermesTenantConfig | null> {
    return this.get('/hermes/config');
  }

  async saveHermesConfig(data: HermesTenantConfigInput): Promise<HermesTenantConfig> {
    return this.post('/hermes/config', data);
  }

  // Tenant: Deutsche Post INTERNETMARKE (Portokasse)
  async getInternetmarkeConfig(): Promise<{ config: InternetmarkeConfig | null }> {
    return this.get('/internetmarke/config');
  }

  async saveInternetmarkeConfig(data: InternetmarkeConfigInput): Promise<{ success: boolean; config: InternetmarkeConfig }> {
    return this.put('/internetmarke/config', data);
  }

  async testInternetmarkeConnection(): Promise<{ success: boolean; walletBalanceCents?: number | null; message: string }> {
    return this.post('/internetmarke/test-connection', {});
  }

  async getInternetmarkeWallet(): Promise<{ balanceCents: number | null }> {
    return this.get('/internetmarke/wallet');
  }

  async chargeInternetmarkeWallet(amountCents: number): Promise<{ success: boolean; balanceCents: number | null }> {
    return this.post('/internetmarke/wallet/charge', { amountCents });
  }

  async getInternetmarkeCatalog(refresh = false): Promise<InternetmarkeCatalog> {
    return this.get(`/internetmarke/catalog${refresh ? '?refresh=1' : ''}`);
  }

  /**
   * Kauft eine Marke für eine Sendung.
   *
   * 🚨 Kostet echtes Guthaben und ist nicht wiederholbar — nur nach ausdrücklicher
   * Bestätigung mit ausgewiesenem Betrag aufrufen. Der Riegel gegen Doppelkäufe sitzt
   * serverseitig, dieser Hinweis ersetzt ihn nicht.
   */
  async buyInternetmarkeVoucher(
    shipmentId: string,
    productCode: string,
  ): Promise<InternetmarkeVoucherResult> {
    return this.post(`/shipments/${shipmentId}/internetmarke/voucher`, { productCode });
  }

  async refundInternetmarkeVoucher(shipmentId: string): Promise<{ success: boolean; message: string }> {
    return this.post(`/shipments/${shipmentId}/internetmarke/refund`, {});
  }

  async getDhlProducts(): Promise<DhlProduct[]> {
    return this.get('/dhl/products');
  }

  async getDhlAvailableServices(): Promise<DhlServiceInfo[]> {
    return this.get('/dhl/available-services');
  }

  async getDhlStats(): Promise<DhlStats> {
    return this.get('/dhl/stats');
  }

  // Shipping Methods (Versandarten)
  async getShippingMethods(params?: { carrier?: string; active_only?: boolean }): Promise<ShippingMethod[]> {
    return this.get('/shipping-methods', params);
  }

  async getShippingMethod(id: string): Promise<ShippingMethod> {
    return this.get(`/shipping-methods/${id}`);
  }

  async createShippingMethod(data: ShippingMethodInput): Promise<ShippingMethod> {
    return this.post('/shipping-methods', data);
  }

  async updateShippingMethod(id: string, data: Partial<ShippingMethodInput>): Promise<ShippingMethod> {
    return this.put(`/shipping-methods/${id}`, data);
  }

  async deleteShippingMethod(id: string): Promise<{ success: boolean }> {
    return this.delete(`/shipping-methods/${id}`);
  }

  async toggleShippingMethod(id: string): Promise<{ id: string; isActive: boolean; message: string }> {
    return this.post(`/shipping-methods/${id}/toggle`, {});
  }

  async reorderShippingMethods(order: string[]): Promise<{ success: boolean; message: string }> {
    return this.post('/shipping-methods/reorder', { order });
  }

  // Shipments
  async getDhlShipments(params?: { page?: number; limit?: number }): Promise<DhlShipmentListResponse> {
    return this.get('/dhl/shipments', params);
  }

  async getDhlShipment(id: string): Promise<DhlShipment> {
    return this.get(`/dhl/shipments/${id}`);
  }

  async createDhlShipment(data: DhlShipmentInput): Promise<DhlShipmentResult> {
    return this.post('/dhl/shipments', data);
  }

  async cancelDhlShipment(id: string): Promise<{ success: boolean; message: string }> {
    return this.post(`/dhl/shipments/${id}/cancel`, {});
  }

  async getDhlShipmentTracking(id: string): Promise<DhlTrackingResult> {
    return this.get(`/dhl/shipments/${id}/tracking`);
  }

  async getDhlTracking(trackingNumber: string): Promise<DhlTrackingResult> {
    return this.get(`/dhl/tracking/${trackingNumber}`);
  }

  async createDhlReturnLabel(data: DhlShipmentInput): Promise<DhlShipmentResult> {
    return this.post('/dhl/return-label', data);
  }

  async validateDhlAddress(address: { name: string; street: string; postalCode: string; city: string; countryCode?: string }): Promise<DhlAddressValidation> {
    return this.post('/dhl/validate-address', address);
  }

  // DPD Shipping Methods
  async getDpdProducts(): Promise<DpdProductInfo[]> {
    return this.get('/dpd/products');
  }

  async getDpdAvailableServices(): Promise<DpdServiceInfo[]> {
    return this.get('/dpd/services');
  }

  async getDpdStats(): Promise<DpdStats> {
    return this.get('/dpd/stats');
  }

  async testDpdConnection(): Promise<{ success: boolean; message: string }> {
    return this.post('/dpd/test', {});
  }

  async getDpdShipments(params?: { page?: number; limit?: number }): Promise<{ data: DpdShipment[]; total: number; page: number; limit: number }> {
    return this.get('/dpd/shipments', params);
  }

  async getDpdShipment(id: string): Promise<DpdShipment> {
    return this.get(`/dpd/shipments/${id}`);
  }

  async createDpdShipment(data: DpdShipmentInput): Promise<DpdShipmentResult> {
    return this.post('/dpd/shipments', data);
  }

  async cancelDpdShipment(id: string): Promise<{ success: boolean; message: string }> {
    return this.post(`/dpd/shipments/${id}/cancel`, {});
  }

  async getDpdShipmentTracking(id: string): Promise<DpdTrackingResult> {
    return this.get(`/dpd/shipments/${id}/tracking`);
  }

  async getDpdTracking(trackingNumber: string): Promise<DpdTrackingResult> {
    return this.get(`/dpd/tracking/${trackingNumber}`);
  }

  async createDpdReturnLabel(data: DpdShipmentInput): Promise<DpdShipmentResult> {
    return this.post('/dpd/return-label', data);
  }

  async getDpdLabel(shipmentId: string): Promise<{ labelUrl: string; labelBase64: string | null; format: string }> {
    return this.get(`/dpd/shipments/${shipmentId}/label`);
  }

  // ==========================================
  // eBay Integration
  // ==========================================

  // Connection Management
  async getEbayConnection(): Promise<{ connection: EbayConnection | null; isConnected: boolean }> {
    return this.get('/ebay/connection');
  }

  async saveEbayConnection(data: EbayConnectionInput): Promise<{ success: boolean; id: string }> {
    return this.post('/ebay/connection', data);
  }

  async deleteEbayConnection(): Promise<{ success: boolean }> {
    return this.delete('/ebay/connection');
  }

  async disconnectEbay(): Promise<{ success: boolean }> {
    return this.post('/ebay/disconnect', {});
  }

  async getEbayAuthUrl(environment?: string): Promise<{ authUrl: string; state: string; environment: string }> {
    const params = environment ? `?environment=${environment}` : '';
    return this.get(`/ebay/auth/url${params}`);
  }

  async handleEbayAuthCallback(data: { code: string; state: string }): Promise<{ success: boolean }> {
    return this.post('/ebay/auth/callback', data);
  }

  async exchangeEbayAuthCode(data: { code: string; state: string }): Promise<{ success: boolean }> {
    return this.post('/ebay/auth/exchange', data);
  }

  async refreshEbayToken(): Promise<{ success: boolean }> {
    return this.post('/ebay/auth/refresh', {});
  }

  // eBay Settings
  async getEbaySettings(): Promise<{ settings: { out_of_stock_control: boolean } }> {
    return this.get('/ebay/settings');
  }

  async saveEbaySettings(data: { settings: { out_of_stock_control: boolean } }): Promise<{ success: boolean }> {
    return this.post('/ebay/settings', data);
  }

  // eBay Marketplaces
  async getAvailableEbayMarketplaces(): Promise<{ marketplaces: EbayAvailableMarketplace[] }> {
    return this.get('/ebay/marketplaces/available');
  }

  async getActiveEbayMarketplaces(): Promise<{ marketplaces: EbayMarketplace[] }> {
    return this.get('/ebay/marketplaces');
  }

  async addEbayMarketplace(marketplaceId: string): Promise<{ success: boolean; id: string; reactivated?: boolean }> {
    return this.post('/ebay/marketplaces', { marketplaceId });
  }

  async updateEbayMarketplace(id: string, data: { isActive?: boolean; isPrimary?: boolean; settings?: Record<string, unknown> }): Promise<{ success: boolean }> {
    return this.put(`/ebay/marketplaces/${id}`, data);
  }

  async removeEbayMarketplace(id: string): Promise<{ success: boolean }> {
    return this.delete(`/ebay/marketplaces/${id}`);
  }

  async setPrimaryEbayMarketplace(id: string): Promise<{ success: boolean }> {
    return this.post(`/ebay/marketplaces/${id}/primary`, {});
  }

  // Global Templates
  async getEbayGlobalTemplates(): Promise<{ templates: EbayGlobalTemplate[] }> {
    return this.get('/ebay/global-templates');
  }

  async createEbayGlobalTemplate(data: EbayGlobalTemplateInput): Promise<{ success: boolean; id: string }> {
    return this.post('/ebay/global-templates', data);
  }

  async getEbayGlobalTemplate(id: string): Promise<{ template: EbayGlobalTemplate }> {
    return this.get(`/ebay/global-templates/${id}`);
  }

  async updateEbayGlobalTemplate(id: string, data: Partial<EbayGlobalTemplateInput>): Promise<{ success: boolean }> {
    return this.put(`/ebay/global-templates/${id}`, data);
  }

  async deleteEbayGlobalTemplate(id: string): Promise<{ success: boolean }> {
    return this.delete(`/ebay/global-templates/${id}`);
  }

  async duplicateEbayGlobalTemplate(id: string): Promise<{ success: boolean; id: string }> {
    return this.post(`/ebay/global-templates/${id}/duplicate`, {});
  }

  // Design Templates
  async getEbayDesignTemplates(): Promise<{ templates: EbayDesignTemplate[] }> {
    return this.get('/ebay/design-templates');
  }

  async createEbayDesignTemplate(data: EbayDesignTemplateInput): Promise<{ success: boolean; id: string }> {
    return this.post('/ebay/design-templates', data);
  }

  async getEbayDesignTemplate(id: string): Promise<{ template: EbayDesignTemplate }> {
    return this.get(`/ebay/design-templates/${id}`);
  }

  async updateEbayDesignTemplate(id: string, data: Partial<EbayDesignTemplateInput>): Promise<{ success: boolean }> {
    return this.put(`/ebay/design-templates/${id}`, data);
  }

  async deleteEbayDesignTemplate(id: string): Promise<{ success: boolean }> {
    return this.delete(`/ebay/design-templates/${id}`);
  }

  async previewEbayDesignTemplate(id: string, data?: { articleId?: string }): Promise<{ html: string; css?: string }> {
    return this.post(`/ebay/design-templates/${id}/preview`, data || {});
  }

  async getEbayBaseTemplates(): Promise<{ templates: Array<{ id: string; name: string; description: string; preview?: string }> }> {
    return this.get('/ebay/design-templates/base-templates');
  }

  async resetEbayDefaultTemplates(): Promise<{ success: boolean; message: string }> {
    return this.post('/ebay/design-templates/reset-defaults', {});
  }

  async getEbayTemplateCustomFields(): Promise<{ customFields: Array<{ name: string; fieldKey: string; fieldType: string; description: string; placeholder: string }> }> {
    return this.get('/ebay/design-templates/custom-fields');
  }

  // Listing Templates
  async getEbayListingTemplates(params?: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ templates: EbayListingTemplate[]; total: number; limit: number; offset: number }> {
    return this.get('/ebay/listing-templates', params);
  }

  async createEbayListingTemplate(data: EbayListingTemplateInput): Promise<{ success: boolean; id: string }> {
    return this.post('/ebay/listing-templates', data);
  }

  async getEbayListingTemplate(id: string): Promise<{ template: EbayListingTemplate }> {
    return this.get(`/ebay/listing-templates/${id}`);
  }

  async updateEbayListingTemplate(id: string, data: Partial<EbayListingTemplateInput>): Promise<{ success: boolean }> {
    return this.put(`/ebay/listing-templates/${id}`, data);
  }

  async deleteEbayListingTemplate(id: string): Promise<{ success: boolean }> {
    return this.delete(`/ebay/listing-templates/${id}`);
  }

  async publishEbayListingTemplate(id: string): Promise<{ success: boolean; ebayItemId?: string; listingUrl?: string }> {
    return this.post(`/ebay/listing-templates/${id}/publish`, {});
  }

  async bulkCreateEbayListingTemplates(data: {
    articleIds: string[];
    globalTemplateId?: string;
    designTemplateId?: string;
  }): Promise<{ created: number; skipped: number; errors: Array<{ articleId: string; error: string }> }> {
    return this.post('/ebay/listing-templates/bulk', data);
  }

  // Listings
  async getEbayListings(params?: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ listings: EbayListing[]; total: number; limit: number; offset: number }> {
    return this.get('/ebay/listings', params);
  }

  async createEbayListing(data: {
    listingTemplateId: string;
    scheduledStartAt?: string;
    scheduledEndAt?: string;
  }): Promise<{ success: boolean; id: string }> {
    return this.post('/ebay/listings', data);
  }

  async publishEbayListing(id: string): Promise<{ success: boolean; ebayItemId?: string; listingUrl?: string }> {
    return this.post(`/ebay/listings/${id}/publish`, {});
  }

  async endEbayListing(id: string): Promise<{ success: boolean }> {
    return this.post(`/ebay/listings/${id}/end`, {});
  }

  async relistEbayListing(id: string): Promise<{ success: boolean; ebayItemId?: string; listingUrl?: string }> {
    return this.post(`/ebay/listings/${id}/relist`, {});
  }

  async updateEbayListingAutoEndSettings(id: string, data: {
    autoEndEnabled?: number | null;
    autoEndDays?: number | null;
    autoRelistEnabled?: number | null;
    autoRelistMaxRetries?: number | null;
  }): Promise<{ success: boolean }> {
    return this.put(`/ebay/listings/${id}/auto-end-settings`, data);
  }

  async bulkPublishEbayListings(data: { listingIds: string[] }): Promise<{
    success: number;
    error: number;
    errors: Array<{ listingId: string; error: string }>;
  }> {
    return this.post('/ebay/listings/bulk-publish', data);
  }

  // Categories
  async getEbayCategories(params?: { parentId?: string }): Promise<{ categories: EbayCategory[] }> {
    return this.get('/ebay/categories', params);
  }

  async getEbayCategorySuggestions(query: string): Promise<{ categories: EbayCategorySuggestion[] }> {
    return this.get('/ebay/categories/suggested', { q: query });
  }

  async getEbayStoreCategories(): Promise<{ categories: Array<{ id: string; name: string; level: number }> }> {
    return this.get('/ebay/store-categories');
  }

  // getEbayCategoryMappings/saveEbayCategoryMapping sind am 17.07.2026 entfallen:
  // Die eBay-Kategorie kommt allein aus ebay_listing_templates.primaryCategoryId,
  // die Zuordnung wurde beim Listen nie gelesen. Kategorie-Zuordnung macht der
  // Mapping-Hub (/marketplace-mapping).

  // Orders
  async getEbayOrders(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ orders: EbayOrder[]; total: number; limit: number; offset: number }> {
    return this.get('/ebay/orders', params);
  }

  async importEbayOrders(data?: { daysBack?: number }): Promise<{
    imported: number;
    skipped: number;
    errors: Array<{ orderId?: string; error: string }>;
    /** SKUs der importierten Positionen — ihr Bestand wird direkt danach zu eBay nachgezogen */
    skus: string[];
  }> {
    return this.post('/ebay/orders/import', data || {});
  }

  async fulfillEbayOrder(id: string, data: {
    trackingNumber?: string;
    shippingCarrier?: string;
  }): Promise<{ success: boolean }> {
    return this.post(`/ebay/orders/${id}/fulfill`, data);
  }

  // Sync
  async getEbaySyncStatus(): Promise<EbaySyncStatus> {
    return this.get('/ebay/sync/status');
  }

  async syncEbayInventory(data?: {
    listingIds?: string[];
    syncAll?: boolean;
    force?: boolean;
  }): Promise<{
    /** Anzahl tatsächlich zu eBay gepushter Listings */
    success: number;
    error: number;
    /** Listings, deren Bestand bereits stimmte — es wird nur gepusht, was sich geändert hat */
    unchanged: number;
    /** Listings, die wegen Bestand 0 beendet wurden (nur bei abgeschalteter Out-of-Stock-Control) */
    ended: number;
    checked: number;
    errors: string[];
  }> {
    return this.post('/ebay/sync/inventory', data || {});
  }

  async getEbaySyncLog(params?: {
    type?: 'listing' | 'inventory' | 'order' | 'category';
    status?: 'success' | 'error' | 'skipped';
    limit?: number;
  }): Promise<{ logs: EbaySyncLog[] }> {
    return this.get('/ebay/sync/log', params);
  }

  // Business Policies
  async getEbayPolicies(): Promise<{
    fulfillmentPolicies: EbayPolicy[];
    paymentPolicies: EbayPolicy[];
    returnPolicies: EbayPolicy[];
  }> {
    return this.get('/ebay/policies');
  }

  // Item Aspects (Specifics)
  async getEbayCategoryAspects(
    categoryId: string,
    marketplaceId?: string,
    forceRefresh?: boolean
  ): Promise<EbayCategoryAspectsResponse> {
    const params: Record<string, string> = {};
    if (marketplaceId) params.marketplaceId = marketplaceId;
    if (forceRefresh) params.refresh = 'true';
    return this.get(`/ebay/categories/${categoryId}/aspects`, params);
  }

  // Attribute Mappings
  async getEbayAttributeMappings(categoryId?: string): Promise<{ mappings: EbayAttributeMapping[] }> {
    const params: Record<string, string> = {};
    if (categoryId) params.categoryId = categoryId;
    return this.get('/ebay/attribute-mappings', params);
  }

  async createEbayAttributeMapping(data: EbayAttributeMappingInput): Promise<{ success: boolean; id: string }> {
    return this.post('/ebay/attribute-mappings', data);
  }

  async updateEbayAttributeMapping(id: string, data: Partial<EbayAttributeMappingInput>): Promise<{ success: boolean }> {
    return this.put(`/ebay/attribute-mappings/${id}`, data);
  }

  async deleteEbayAttributeMapping(id: string): Promise<{ success: boolean }> {
    return this.delete(`/ebay/attribute-mappings/${id}`);
  }

  async suggestEbayAttributeMappings(categoryId: string): Promise<{ suggestions: EbayAttributeMappingSuggestion[] }> {
    return this.post('/ebay/attribute-mappings/suggest', { categoryId });
  }

  // =============================================================================
  // ADMIN: VAT RATES MANAGEMENT
  // =============================================================================

  // Get all EU countries with their VAT rates (admin endpoint)
  async getAdminEUCountries(): Promise<{ countries: EUCountryWithRates[] }> {
    return this.get('/admin/vat/countries');
  }

  // Get all VAT rates (admin endpoint)
  async getAdminVatRates(): Promise<{ rates: AdminVatRate[] }> {
    return this.get('/admin/vat/rates');
  }

  // Update a VAT rate (admin endpoint)
  async updateAdminVatRate(id: string, data: {
    rate: number;
    validFrom?: string;
    validUntil?: string;
  }): Promise<{ success: boolean }> {
    return this.put(`/admin/vat/rates/${id}`, data);
  }

  // Create a new VAT rate (admin endpoint)
  async createAdminVatRate(data: {
    countryCode: string;
    rateType: 'standard' | 'reduced' | 'reduced2' | 'super_reduced';
    rate: number;
    description?: string;
    validFrom: string;
    validUntil?: string;
  }): Promise<{ success: boolean; id: string }> {
    return this.post('/admin/vat/rates', data);
  }

  // Seed default VAT rates (admin endpoint)
  async seedVatRates(): Promise<{ success: boolean; message: string; countryCount: number; rateCount: number }> {
    return this.post('/admin/vat/seed', {});
  }

  // =============================================================================
  // TAX & OSS API
  // =============================================================================

  // Tax Classes
  async getTaxClasses(): Promise<{ taxClasses: TaxClass[] }> {
    return this.get('/tax/classes');
  }

  async getTaxClass(id: string): Promise<TaxClass> {
    return this.get(`/tax/classes/${id}`);
  }

  async createTaxClass(data: {
    code: string;
    name: string;
    description?: string;
    category?: string;
    isDefault?: boolean;
    sortOrder?: number;
  }): Promise<{ success: boolean; id: string }> {
    return this.post('/tax/classes', data);
  }

  async updateTaxClass(id: string, data: Partial<{
    name: string;
    description: string;
    category: string;
    isDefault: boolean;
    sortOrder: number;
  }>): Promise<{ success: boolean }> {
    return this.put(`/tax/classes/${id}`, data);
  }

  async updateTaxClassRates(id: string, rates: Array<{
    countryCode: string;
    rate: number;
    isOverride?: boolean;
    validFrom?: string;
  }>): Promise<{ success: boolean }> {
    return this.put(`/tax/classes/${id}/rates`, { rates });
  }

  // Tax Calculation
  async calculateTax(data: {
    items: Array<{
      articleId?: string;
      netPrice: number;
      quantity: number;
      taxClassId?: string;
    }>;
    shippingFromCountry?: string;
    buyerCountry: string;
    buyerVatId?: string;
    isB2B?: boolean;
  }): Promise<TaxCalculationResult> {
    return this.post('/tax/calculate', data);
  }

  // EU VAT Rates
  async getEUVatRates(): Promise<{ countries: EUCountryWithRates[] }> {
    return this.get('/tax/eu-rates');
  }

  // VAT Validation
  async validateVatId(vatId: string): Promise<VatValidationResult> {
    return this.post('/tax/validate-vat', { vatId });
  }

  // Tax Exemption Notices
  async getTaxExemptionNotice(code: string, language?: string): Promise<{
    code: string;
    shortText: string;
    fullText: string;
    legalReference: string;
  }> {
    return this.get(`/tax/exemption-notice/${code}`, language ? { language } : undefined);
  }

  // Ensure Tax Tables
  async ensureTaxTables(): Promise<{ success: boolean; message: string }> {
    return this.post('/tax/ensure-tables', {});
  }

  // Migrate Tax Structure (upgrade existing data to new schema)
  async migrateTaxStructure(): Promise<{ success: boolean; message: string; migratedItems?: string[] }> {
    return this.post('/tax/migrate-structure', {});
  }

  // =============================================================================
  // PRODUCT TAX CATEGORIES API
  // =============================================================================

  // Get all product tax categories
  async getProductTaxCategories(): Promise<{ productTaxCategories: ProductTaxCategory[] }> {
    return this.get('/tax/product-categories');
  }

  // Get a single product tax category
  async getProductTaxCategory(id: string): Promise<ProductTaxCategory> {
    return this.get(`/tax/product-categories/${id}`);
  }

  // Create a new product tax category
  async createProductTaxCategory(data: {
    code: string;
    name: string;
    description?: string;
    defaultTaxClassCode?: string;
    categoryGroup?: string;
    sortOrder?: number;
  }): Promise<{ success: boolean; id: string }> {
    return this.post('/tax/product-categories', data);
  }

  // Update a product tax category
  async updateProductTaxCategory(id: string, data: {
    name?: string;
    description?: string;
    defaultTaxClassCode?: string;
    categoryGroup?: string;
    sortOrder?: number;
    isActive?: boolean;
  }): Promise<{ success: boolean }> {
    return this.put(`/tax/product-categories/${id}`, data);
  }

  // Set a country override for a product tax category
  async setProductTaxCategoryCountryOverride(
    categoryId: string,
    countryCode: string,
    data: { taxClassCode: string; notes?: string }
  ): Promise<{ success: boolean }> {
    return this.put(`/tax/product-categories/${categoryId}/overrides/${countryCode}`, data);
  }

  // Delete a country override for a product tax category
  async deleteProductTaxCategoryCountryOverride(
    categoryId: string,
    countryCode: string
  ): Promise<{ success: boolean }> {
    return this.delete(`/tax/product-categories/${categoryId}/overrides/${countryCode}`);
  }

  // Get article tax info (using new resolution logic)
  async getArticleTaxInfo(data: {
    productTaxCategoryId?: string;
    taxClassCode?: string;
    destinationCountry: string;
  }): Promise<ArticleTaxInfo> {
    return this.post('/tax/article-tax-info', data);
  }

  // Get resolved country rates for a tax class (all 27 EU countries)
  async getResolvedRatesForTaxClass(code: string): Promise<{ rates: ResolvedCountryRate[] }> {
    return this.get(`/tax/classes/${code}/country-rates`);
  }

  // Create or update a tax rate override
  async upsertTaxRateOverride(data: {
    countryCode: string;
    taxClassCode: string;
    rate: number;
    reason?: string;
  }): Promise<{ success: boolean; id: string; action: string }> {
    return this.post('/tax/rate-overrides', data);
  }

  // Delete a tax rate override
  async deleteTaxRateOverride(id: string): Promise<{ success: boolean }> {
    return this.delete(`/tax/rate-overrides/${id}`);
  }

  // =============================================================================
  // PRICE CALCULATOR API
  // =============================================================================

  // Calculate gross price for an article
  async calculateGrossPrice(data: {
    netPrice: number;
    taxClassId?: string;
    targetCountry: string;
  }): Promise<{
    netPrice: number;
    taxRate: number;
    taxAmount: number;
    grossPrice: number;
    taxClassId: string | null;
    country: string;
  }> {
    return this.post('/tax/calculate-price', data);
  }

  // Get EU price preview for an article
  async getEUPricePreview(data: {
    netPrice: number;
    taxClassId?: string;
  }): Promise<{
    netPrice: number;
    taxClassId: string | null;
    prices: Record<string, {
      netPrice: number;
      taxRate: number;
      taxAmount: number;
      grossPrice: number;
      countryName: string;
    }>;
    lowestGross: { grossPrice: number; countryName: string };
    highestGross: { grossPrice: number; countryName: string };
  }> {
    return this.post('/tax/eu-price-preview', data);
  }

  // Get affected articles for a tax rate change
  async getAffectedArticles(taxClassId: string, countryCode: string): Promise<{
    articles: Array<{
      id: string;
      sku: string;
      name: string;
      netPrice: number;
      currentGrossPrice: number;
      currentTaxRate: number;
    }>;
  }> {
    return this.get(`/tax/classes/${taxClassId}/rates/${countryCode}/affected-articles`);
  }

  // Preview tax rate change effects on prices
  async previewTaxRateChange(
    taxClassId: string,
    countryCode: string,
    newRate: number,
    keepGrossPrice: boolean = false
  ): Promise<{
    taxClassId: string;
    countryCode: string;
    oldRate: number;
    newRate: number;
    keepGrossPrice: boolean;
    affectedArticlesCount: number;
    totalOldGross: number;
    totalNewGross: number;
    totalDifference: number;
    articles: Array<{
      id: string;
      sku: string;
      name: string;
      oldNetPrice: number;
      newNetPrice: number;
      netDifference: number;
      oldGrossPrice: number;
      newGrossPrice: number;
      grossDifference: number;
    }>;
  }> {
    return this.post(`/tax/classes/${taxClassId}/rates/${countryCode}/preview`, {
      newRate,
      keepGrossPrice,
    });
  }

  // Apply tax rate change and recalculate prices
  async applyTaxRateChange(
    taxClassId: string,
    countryCode: string,
    newRate: number,
    keepGrossPrice: boolean = false
  ): Promise<{
    success: boolean;
    taxClassId: string;
    countryCode: string;
    oldRate: number;
    newRate: number;
    keepGrossPrice: boolean;
    articlesUpdated: number;
  }> {
    return this.post(`/tax/classes/${taxClassId}/rates/${countryCode}/apply`, {
      newRate,
      keepGrossPrice,
    });
  }

  // Calculate order item prices
  async calculateOrderItemPrices(data: {
    unitPrice: number;
    quantity: number;
    taxClassId?: string;
    targetCountry: string;
    discount?: number;
  }): Promise<{
    unitPrice: number;
    quantity: number;
    subtotal: number;
    discount: number;
    netPrice: number;
    taxRate: number;
    taxAmount: number;
    totalPrice: number;
  }> {
    return this.post('/tax/calculate-order-item', data);
  }

  // OSS Dashboard
  async getOSSDashboard(year?: number): Promise<OSSDashboardData> {
    return this.get('/oss/dashboard', year ? { year } : undefined);
  }

  // OSS Threshold
  async getOSSThreshold(year: number): Promise<OSSThresholdStatus> {
    return this.get(`/oss/threshold/${year}`);
  }

  // OSS Registration
  async updateOSSRegistration(data: {
    year?: number;
    status: string;
    registrationNumber?: string;
    registrationDate?: string;
  }): Promise<{ success: boolean }> {
    return this.put('/oss/registration', data);
  }

  // OSS Reports
  async getOSSReports(year?: number): Promise<{ reports: OSSReport[] }> {
    return this.get('/oss/reports', year ? { year } : undefined);
  }

  async getOSSReport(id: string): Promise<OSSReport> {
    return this.get(`/oss/reports/${id}`);
  }

  async generateOSSReport(year: number, quarter: number): Promise<OSSReport> {
    return this.post('/oss/reports/generate', { year, quarter });
  }

  async finalizeOSSReport(id: string): Promise<{ success: boolean }> {
    return this.post(`/oss/reports/${id}/finalize`, {});
  }

  async submitOSSReport(id: string, reference?: string): Promise<{ success: boolean }> {
    return this.post(`/oss/reports/${id}/submit`, { reference });
  }

  async exportOSSReport(id: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/oss/reports/${id}/export`, {
      headers: this.getHeaders(),
    });
    return response.blob();
  }

  // OSS Sales by Country
  async getOSSSalesByCountry(year?: number): Promise<{
    year: number;
    countries: Array<{
      countryCode: string;
      countryName: string;
      flagEmoji: string;
      netTotal: number;
      taxTotal: number;
      grossTotal: number;
      orderCount: number;
      avgTaxRate: number;
    }>;
  }> {
    return this.get('/oss/sales-by-country', year ? { year } : undefined);
  }

  // Monthly Trend
  async getOSSMonthlyTrend(year?: number): Promise<{
    year: number;
    months: Array<{
      month: number;
      netTotal: number;
      taxTotal: number;
      orderCount: number;
    }>;
  }> {
    return this.get('/oss/monthly-trend', year ? { year } : undefined);
  }

  // =============================================================================
  // ZM (Zusammenfassende Meldung) API
  // =============================================================================

  // Get ZM Dashboard data. `quartal_zulaessig`/`quartal_hinweis`/`ist_kleinunternehmer`: siehe
  // docs/guides/REVERSE_CHARGE_AUSGANGSRECHNUNG.md (Backend-Vertrag für den Erzeugen-Dialog).
  // `anzeige_monatlich_leistungen`: Einstellung "Anzeige nach § 18a Abs. 3 UStG erstattet"
  // (siehe updateZMSettings()). `offene_storno_gutschriften`: Anzahl stornierter Rechnungen,
  // deren Storno-Gutschrift noch nicht festgeschrieben ist (RF2) — bis dahin zählen sie weiter
  // in UStVA/ZM. `igl_ohne_ustid` (Paket C, 17.09.2026): Anzahl igL-Rechnungen ohne Käufer-USt-IdNr.
  // — nur über den weichen POS-Finalisierungszweig erreichbar (ERP wird hart abgelehnt).
  async getZMDashboard(year?: number): Promise<{
    year: number;
    totalIntraEUDeliveries: number;
    totalReverseChargeServices: number;
    pendingReportsCount: number;
    submittedReportsCount: number;
    nextDeadline: string | null;
    quartal_zulaessig: boolean;
    quartal_hinweis: string | null;
    ist_kleinunternehmer: boolean;
    anzeige_monatlich_leistungen: boolean;
    offene_storno_gutschriften: number;
    igl_ohne_ustid: number;
    /**
     * B5 (18.09.2026): bereits ÜBERMITTELTE Meldezeiträume, die nachträglich Zeilen bekommen
     * haben (der JTL-Abgleich kennzeichnet igL auch an Altbelegen nach). Berichtigungspflicht
     * nach § 18a Abs. 10 UStG, Frist ein Monat nach Erkennen. `nicht_pruefbar` = Anzahl älterer
     * Meldezeiträume ohne gespeicherte Belegzuordnung — dort lässt es sich nicht feststellen.
     */
    nachtraegliche_meldungen: {
      faelle: Array<{ monat: number; art: string; anzahl: number; betrag: number }>;
      anzahl: number;
      betrag: number;
      nicht_pruefbar: number;
    };
    /**
     * 19.09.2026: Zeiträume mit meldepflichtigen Zeilen, für die ÜBERHAUPT KEINE Meldung erfasst
     * ist — unabhängig davon, wer gemeldet hat. 🚨 Die frühere Warnung verglich nur gegen
     * Berichte, die über BIS ERP übermittelt wurden; bei einem Betrieb, dessen Kanzlei extern
     * meldet, ist diese Tabelle leer und die Warnung schwieg (bei OEZ: 0 Zeilen in `zm_reports`).
     * Quittieren lässt sich das über `setZmExternGemeldet()`.
     */
    fehlende_meldungen: {
      faelle: Array<{ monat: number; anzahl: number; betrag: number; frist: string; ueberfaellig: boolean }>;
      anzahl: number;
      betrag: number;
      ueberfaellig_anzahl: number;
      ueberfaellig_betrag: number;
    };
  }> {
    return this.get('/zm/dashboard', year ? { year } : undefined);
  }

  /**
   * Quittieren, dass die Zusammenfassende Meldung für einen Zeitraum extern (z. B. durch die
   * Steuerkanzlei) abgegeben wurde. Leeres Datum nimmt die Quittung zurück.
   */
  async setZmExternGemeldet(data: {
    year: number;
    month: number;
    report_type?: 'monthly' | 'quarterly';
    gemeldet_am: string | null;
    hinweis?: string | null;
  }): Promise<{ success: boolean; year: number; month: number; gemeldet_am: string | null }> {
    return this.put('/zm/perioden/extern-gemeldet', data);
  }

  /**
   * § 14c Abs. 2 S. 3–5 UStG: Die Berichtigung des Steuerbetrags erfassen.
   *
   * 🚨 **ZWEI Daten, nicht eines** (19.09.2026). Satz 5 lautet: Die Berichtigung ist „nach
   * dessen Zustimmung … für den Besteuerungszeitraum vorzunehmen, in dem die Voraussetzungen des
   * Satzes 4 eingetreten sind". Satz 4 ist die Beseitigung der Gefährdung des Steueraufkommens
   * (Vorsteuerabzug beim Empfänger nicht durchgeführt oder zurückgezahlt). Die Zustimmung des
   * Finanzamts ist die BEDINGUNG, das Beseitigungsdatum der ZEITRAUM.
   *
   * Gemindert wird erst, wenn BEIDE Daten stehen, und zwar im Zeitraum der BESEITIGUNG (nie im
   * früheren der beiden — das zöge die Minderung vor die Beseitigung). Ein leeres
   * Datum nimmt die jeweilige Angabe zurück; der Betrag steht dann wieder offen in KZ 69.
   */
  async setInvoiceVierzehnCZustimmung(
    id: string,
    zustimmungAt: string | null,
    gefaehrdungBeseitigtAm: string | null = null
  ): Promise<{
    success: boolean;
    zustimmung_at: string | null;
    gefaehrdung_beseitigt_am: string | null;
    minderung_am: string | null;
  }> {
    return this.put(`/invoices/${id}/vierzehn-c-zustimmung`, {
      zustimmung_at: zustimmungAt ?? '',
      gefaehrdung_beseitigt_am: gefaehrdungBeseitigtAm ?? '',
    });
  }

  // ZM-Einstellungen speichern (Admin) — aktuell nur die Anzeige nach § 18a Abs. 3 UStG
  // ("sonstige Leistungen monatlich melden"). Wert kommt/geht auf dem Dashboard mit.
  async updateZMSettings(data: { anzeige_monatlich_leistungen: boolean }): Promise<{
    success: boolean;
    anzeige_monatlich_leistungen: boolean;
  }> {
    return this.put('/zm/settings', data);
  }

  // Get ZM Reports
  async getZMReports(year?: number): Promise<{ reports: ZMReport[] }> {
    return this.get('/zm/reports', year ? { year } : undefined);
  }

  // Get single ZM Report
  async getZMReport(id: string): Promise<ZMReport> {
    return this.get(`/zm/reports/${id}`);
  }

  // Generate ZM Report for a period. `reportType`: bei 'quarterly' ist `month` der letzte
  // Monat des Quartals (3/6/9/12) — siehe docs/guides/REVERSE_CHARGE_AUSGANGSRECHNUNG.md.
  async generateZMReport(year: number, month: number, reportType?: 'monthly' | 'quarterly'): Promise<ZMReport> {
    return this.post('/zm/reports/generate', { year, month, reportType });
  }

  // Finalize ZM Report
  async finalizeZMReport(id: string): Promise<{ success: boolean }> {
    return this.post(`/zm/reports/${id}/finalize`, {});
  }

  // Submit ZM Report. Body-Schlüssel `elsterReference` (nicht `reference`) — so liest ihn
  // ZMController::submitReport() aus (sonst kommt die Referenz nie an).
  async submitZMReport(id: string, elsterReference?: string): Promise<{ success: boolean }> {
    return this.post(`/zm/reports/${id}/submit`, { elsterReference });
  }

  // Export ZM Report als XML-Arbeitsunterlage (KEIN ELSTER-Import-Format — manuelle Übertragung
  // im BZSt-Online-Portal).
  async exportZMReport(id: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/zm/reports/${id}/export`, {
      headers: this.getHeaders(),
    });
    return response.blob();
  }

  // Export ZM Report als CSV-Arbeitsunterlage (ebenfalls kein bestätigtes Importformat).
  async exportZMReportCsv(id: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/zm/reports/${id}/export-csv`, {
      headers: this.getHeaders(),
    });
    return response.blob();
  }

  // Export ZM Report als CSV fuer den ELSTER-Import (Formular "Zusammenfassende Meldung",
  // Mein ELSTER / BZSt-Online-Portal) — anders als export()/exportZMReportCsv() (Arbeitsunterlagen)
  // ein Format zum tatsaechlichen Import. 🚨 Anders als die Nachbar-Exporte oben wird `response.ok`
  // geprueft und ein 404 (Endpoint/Format noch nicht verfuegbar) als Fehler geworfen, damit der
  // Button in der UI einen verstaendlichen Toast zeigen kann statt eine kaputte Datei
  // herunterzuladen.
  async exportZMReportElsterCsv(id: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/zm/reports/${id}/export-elster-csv`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) {
      let message = response.statusText || `HTTP ${response.status}`;
      try {
        const data = await response.clone().json();
        message = data.message || data.error || message;
      } catch {
        // Antwort ist kein JSON (z. B. leerer 404) — Fallback-Text oben bleibt stehen.
      }
      const error: ApiError = { status: response.status, message };
      throw error;
    }
    return response.blob();
  }

  // Get ZM Transactions for a period — mit `reportId` liefert der Server exakt die Zeilen, die
  // in DIESEM Bericht zaehlen (Monat/Quartal, RB6-S-Buendelungsfenster), statt der groben
  // Monatsauswahl (F4, Review-Runde 3).
  async getZMTransactions(year: number, month?: number, reportId?: string): Promise<{
    transactions: Array<{
      id: string;
      invoiceId: string;
      invoiceNumber: string;
      invoiceDate: string;
      customerName: string;
      customerVatId: string;
      customerCountry: string;
      netAmount: number;
      transactionType: 'L' | 'D' | 'S';
      isReported: boolean;
    }>;
  }> {
    return this.get('/zm/transactions', { year, month, report_id: reportId });
  }

  // =============================================================================
  // WAREHOUSE VAT COMPLIANCE API
  // =============================================================================

  // Get Warehouse VAT Registrations
  async getWarehouseVatRegistrations(): Promise<{ registrations: WarehouseVatRegistration[] }> {
    return this.get('/warehouse-vat/registrations');
  }

  // Create Warehouse VAT Registration
  async createWarehouseVatRegistration(data: {
    warehouseId?: string;
    countryCode: string;
    vatId: string;
    taxNumber?: string | null;
    registrationDate?: string | null;
    filingFrequency?: 'monthly' | 'quarterly' | 'annual';
    nextFilingDue?: string | null;
    fiscalRepresentative?: string | null;
    notes?: string | null;
  }): Promise<{ success: boolean; id: string }> {
    return this.post('/warehouse-vat/registrations', data);
  }

  // Update Warehouse VAT Registration — `null` leert ein Feld
  async updateWarehouseVatRegistration(id: string, data: Partial<{
    vatId: string;
    taxNumber: string | null;
    registrationDate: string | null;
    filingFrequency: 'monthly' | 'quarterly' | 'annual';
    nextFilingDue: string | null;
    lastFilingDate: string | null;
    fiscalRepresentative: string | null;
    notes: string | null;
  }>): Promise<{ success: boolean }> {
    return this.put(`/warehouse-vat/registrations/${id}`, data);
  }

  // Delete Warehouse VAT Registration
  async deleteWarehouseVatRegistration(id: string): Promise<{ success: boolean }> {
    return this.delete(`/warehouse-vat/registrations/${id}`);
  }

  // Get VAT Compliance Status
  async getVatComplianceStatus(): Promise<{
    registrations: WarehouseVatRegistration[];
    missingRegistrations: string[];
    missingRegistrationDetails: Array<{
      countryCode: string;
      warehouseCount: number;
      orderCount: number;
      message: string;
    }>;
    upcomingFilings: Array<{
      countryCode: string;
      dueDate: string;
      filingType: string;
      daysUntil: number;
      overdue: boolean;
    }>;
    alerts: VatComplianceAlert[];
    overallStatus: 'ok' | 'warning' | 'critical';
  }> {
    return this.get('/warehouse-vat/compliance');
  }

  // Get VAT Compliance Alerts
  async getVatComplianceAlerts(): Promise<{ alerts: VatComplianceAlert[] }> {
    return this.get('/warehouse-vat/alerts');
  }

  // Resolve VAT Compliance Alert
  async resolveVatComplianceAlert(id: string): Promise<{ success: boolean }> {
    return this.post(`/warehouse-vat/alerts/${id}/resolve`, {});
  }

  // Get Sales by Ship-From Country
  async getSalesByShipFromCountry(params?: {
    year?: number;
    month?: number;
  }): Promise<{
    countries: Array<{
      countryCode: string;
      domesticSalesNet: number;
      domesticSalesTax: number;
      crossBorderSalesNet: number;
      crossBorderSalesTax: number;
      orderCount: number;
      isHomeCountry: boolean;
      hasRegistration: boolean;
      registrationRequired: boolean;
    }>;
    period: { from: string; to: string };
  }> {
    return this.get('/warehouse-vat/sales-by-country', params);
  }

  // =============================================================================
  // DATEV EXPORT API
  // =============================================================================

  // Get DATEV Exports
  async getDatevExports(): Promise<{ exports: DatevExport[] }> {
    return this.get('/datev/exports');
  }

  /**
   * §25a-Auswertung je Gegenstand (Verkaufspreis, Einkaufspreis, Bemessungsgrundlage).
   *
   * Der Server bildet den Vorgabezeitraum selbst im lokalen Kassentag des Betriebs und gibt ihn
   * in `from`/`to` zurueck — die Maske zeigt danach, was tatsaechlich ausgewertet wurde, statt
   * eines im Browser geratenen Zeitraums.
   */
  async getDifferenzbesteuerung(params?: {
    from?: string;
    to?: string;
    deviceId?: string;
  }): Promise<DifferenzbesteuerungAuswertung> {
    return this.get('/datev/differenzbesteuerung', params);
  }

  /** V-2358: Ankäufe eines Kunden (bestehende Liste mit Filter; Recht Buchhaltung). */
  async getKundenAnkaeufe(customerId: string): Promise<{ devicePurchases: KundenAnkaufZeile[] }> {
    return this.get('/device-purchases', { customer_id: customerId });
  }

  /** V-3632: Ankäufe eines Artikels (Abschnitt „Zustand beim Ankauf“; Recht Buchhaltung, ohne Ausweisdaten). */
  async getAnkaeufeEinesArtikels(articleId: string): Promise<{ devicePurchases: KundenAnkaufZeile[] }> {
    return this.get('/device-purchases', { article_id: articleId });
  }

  /** V-3632: Aktive Gerätefotos eines Ankaufs (Recht „Buchhaltung ansehen“). */
  async getAnkaufGeraetefotos(ankaufId: string): Promise<{ fotos: AnkaufGeraetefoto[] }> {
    return this.get(`/device-purchases/${encodeURIComponent(ankaufId)}/fotos`);
  }

  /** V-3632: Bytes eines Gerätefotos — per Blob laden, ein `<img src>` sendet keinen Authorization-Header; nie zwischengespeichert. */
  async getAnkaufGeraetefotoBild(fotoId: string): Promise<Blob> {
    const response = await this.fetch(`/device-purchases/fotos/${encodeURIComponent(fotoId)}/bild`);
    if (!response.ok) {
      throw new Error('Foto konnte nicht geladen werden');
    }
    return response.blob();
  }

  /** V-3640: Ausweisnummer, Geburtsdatum, Anschrift und Kopien eines Ankaufs (Recht „Ausweisdaten ansehen“; jeder Abruf wird protokolliert). */
  async getAnkaufAusweisdaten(ankaufId: string): Promise<AnkaufAusweisdaten> {
    return this.get(`/ankauf-ausweis/${encodeURIComponent(ankaufId)}`);
  }

  /** V-3640: Ausweiskopie als Blob — nie zwischenspeichern, kein Download-Knopf. */
  async getAnkaufAusweiskopieBild(kopieId: string): Promise<Blob> {
    const response = await this.fetch(`/ankauf-ausweis/kopien/${encodeURIComponent(kopieId)}/bild`);
    if (!response.ok) {
      throw new Error('Ausweiskopie konnte nicht geladen werden');
    }
    return response.blob();
  }

  /** V-3640: Ausweiskopie entfernen (Recht „Ausweisdaten ansehen“, Bearbeiten); `grund` ist Pflicht. */
  async entferneAnkaufAusweiskopie(kopieId: string, grund: 'widerruf' | 'antrag' | 'fehler'): Promise<{ success: boolean; status: string; entfernt_anzahl: number }> {
    return this.post(`/ankauf-ausweis/kopien/${encodeURIComponent(kopieId)}/entfernen`, { grund });
  }

  /** V-3632: Gerätefoto entfernen (Recht „Buchhaltung bearbeiten“); `grund`: `erp` oder `antrag` (Antrag der abgebildeten Person). */
  async entferneAnkaufGeraetefoto(fotoId: string, grund: 'erp' | 'antrag'): Promise<{ success: boolean; status: string }> {
    return this.post(`/device-purchases/fotos/${encodeURIComponent(fotoId)}/entfernen`, { grund });
  }

  /** Dieselbe Auswertung als CSV-Datei (Semikolon + BOM, fuer Excel im deutschen Gebietsschema). */
  async downloadDifferenzbesteuerungCsv(params?: {
    from?: string;
    to?: string;
    deviceId?: string;
  }): Promise<Blob> {
    const query = new URLSearchParams({ format: 'csv' });
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.deviceId) query.set('deviceId', params.deviceId);
    const response = await fetch(`${this.baseUrl}/datev/differenzbesteuerung?${query.toString()}`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Download fehlgeschlagen (${response.status})`);
    }
    return response.blob();
  }

  // Generate DATEV Export
  async generateDatevExport(data: {
    periodStart: string;
    periodEnd: string;
    exportType: string;
    deviceId?: string | null;
  }): Promise<DatevExport> {
    return this.post('/datev/exports', data);
  }

  /**
   * 2c-2 (TICK-2026-01150, V-0560): Wurde der Zeitraum schon exportiert — von BIS ERP, dem
   * Kundencenter oder einer Kasse? Nur Hinweis; die Oberfläche fragt vor dem Erzeugen nach.
   */
  async pruefeDatevDoppelbuchung(params: {
    periodStart: string;
    periodEnd: string;
    exportType: string;
    deviceId?: string | null;
  }): Promise<{ success: boolean; ueberschneidungen: import('../components/accounting/DatevKassenExportHinweise').KassenExportUeberschneidung[] }> {
    const query: Record<string, string> = {
      periodStart: params.periodStart,
      periodEnd: params.periodEnd,
      exportType: params.exportType,
    };
    if (params.deviceId) query.deviceId = params.deviceId;
    return this.get('/datev/doppelbuchung', query);
  }

  // DATEV-Gesundheitsprüfung (Rechnungskontrolle vor dem Export)
  async checkDatevHealth(data: {
    periodStart: string;
    periodEnd: string;
    exportType?: string;
  }): Promise<DatevHealthCheckResult> {
    return this.post('/datev/health-check', data);
  }

  // Download DATEV Export
  /**
   * Laedt den Buchungsstapel UND die Hinweise des Servers dazu.
   *
   * 🚨 Der Rumpf ist die CSV-Datei und kann kein JSON tragen — die Hinweise kommen deshalb in
   * Kopfzeilen (`X-Datev-Hinweise`, Base64 ueber JSON, weil die Texte deutsche Umlaute
   * enthalten). Bis zum 19.09.2026 verschwieg dieser Weg jede Warnung: `generateExportFile()`
   * holte sie serverseitig ab, ohne den Rueckgabewert zu benutzen. Wer einen frueher erzeugten
   * Export nur herunterlaedt, erfuhr nie, dass Belege fehlen.
   *
   * `warnungenGesamt`/`fehlerGesamt` sind die UNGEKUERZTEN Anzahlen; die Textliste kann
   * gedeckelt sein (Kopfzeilen-Grenze), deshalb beides.
   */
  async downloadDatevExport(id: string): Promise<{
    blob: Blob;
    warnings: string[];
    errors: string[];
    warnungenGesamt: number;
    fehlerGesamt: number;
    /** 2c-2 (V-0425): `false` = Altexport ohne gespeicherte Datei, heute neu berechnet. */
    gespeichert: boolean;
    /**
     * 2c-2 Korrektur (D13): Dateiname des Servers (Content-Disposition) — eine Neuberechnung heißt
     * `…_NEUBERECHNUNG_<Tag>.csv`; `null`, wenn der Kopf fehlt.
     */
    dateiname: string | null;
  }> {
    const response = await fetch(`${this.baseUrl}/datev/exports/${id}/download`, {
      headers: this.getHeaders(),
    });
    // 2c-2: Ein abgelehnter Download (z. B. Export einer Kasse, Datei liegt auf dem Gerät) ist
    // JSON mit Fehlertext — nicht als Datei speichern.
    if (!response.ok) {
      const fehler = await response.json().catch(() => ({}));
      // 2c-2 Korrektur (A1): Status mitgeben — ein 403 (fehlendes Buchhaltungsrecht) bekommt in
      // der Oberfläche eine eigene, verständliche Meldung.
      throw Object.assign(new Error((fehler as { error?: string }).error || 'Fehler beim Herunterladen'), { status: response.status });
    }

    let warnings: string[] = [];
    let errors: string[] = [];
    try {
      const roh = response.headers.get('X-Datev-Hinweise');
      if (roh) {
        // atob liefert Bytes; ueber TextDecoder nach UTF-8, sonst werden Umlaute zerlegt.
        const bytes = Uint8Array.from(atob(roh), (c) => c.charCodeAt(0));
        const geparst = JSON.parse(new TextDecoder().decode(bytes));
        warnings = Array.isArray(geparst?.warnings) ? geparst.warnings : [];
        errors = Array.isArray(geparst?.errors) ? geparst.errors : [];
      }
    } catch {
      // Ein unlesbarer Kopf darf den Download nicht verhindern — die Datei ist das Wichtige.
    }

    return {
      blob: await response.blob(),
      warnings,
      errors,
      warnungenGesamt: Number(response.headers.get('X-Datev-Warnungen') ?? warnings.length) || warnings.length,
      fehlerGesamt: Number(response.headers.get('X-Datev-Fehler') ?? errors.length) || errors.length,
      gespeichert: response.headers.get('X-Datev-Gespeichert') !== '0',
      dateiname: /filename="?([^";]+)"?/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ?? null,
    };
  }

  /**
   * V-0722: Vollständige Hinweisliste eines Exports. Die Kopfzeile `X-Datev-Hinweise` des Downloads
   * ist auf 1.500 Zeichen gedeckelt (Proxy-Kopfpuffer) — wer mehr braucht, lädt hier nach.
   */
  async getDatevExportHinweise(
    id: string,
    signal?: AbortSignal
  ): Promise<{ warnings: string[]; errors: string[]; hinweiseGespeichert: boolean }> {
    const response = await fetch(`${this.baseUrl}/datev/exports/${id}/hinweise`, {
      headers: this.getHeaders(),
      signal,
    });
    if (!response.ok) {
      throw new Error(`Hinweise nicht ladbar (${response.status})`);
    }
    const daten = await response.json();
    return {
      warnings: Array.isArray(daten?.warnings) ? daten.warnings : [],
      errors: Array.isArray(daten?.errors) ? daten.errors : [],
      // F-002: `false` = Altexport ohne gespeicherte Hinweise (der Server rechnet nicht neu).
      hinweiseGespeichert: daten?.hinweise_gespeichert !== false,
    };
  }

  /**
   * TAR-Export einer geparkten TSE (DSFinV-K/TSE-Archiv). Antwort ist die Datei selbst;
   * schlaegt der Export fehl, kommt JSON zurueck — deshalb wird der Content-Type geprueft.
   */
  async downloadParkedTseExport(parkingId: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/admin/tse/parkings/${parkingId}/export`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    if (!response.ok || (response.headers.get('content-type') || '').includes('application/json')) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Export fehlgeschlagen');
    }
    return response.blob();
  }

  // Get DATEV Settings
  async getDatevSettings(): Promise<DatevSettings> {
    return this.get('/datev/settings');
  }

  // Update DATEV Settings
  async updateDatevSettings(data: Partial<DatevSettings>): Promise<{ success: boolean }> {
    return this.put('/datev/settings', data);
  }

  // Financial Reports
  async getEuerReport(year: number): Promise<EuerReport> {
    return this.get('/datev/euer', { year });
  }

  /** EÜR im DATEV-Kanzlei-Layout (Gewinnermittlung + Monate + Zuordnung zur Anlage EÜR). */
  async downloadEuerPdf(year: number): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/datev/euer/pdf?year=${year}`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleResponse<never>(response); // wirft ApiError mit status/code/message (V-1195 F-009)
    return response.blob();
  }

  async getUstvaReport(year: number, period: 'monthly' | 'quarterly' = 'monthly'): Promise<UstvaReport> {
    return this.get('/datev/ustva', { year, period });
  }

  async getBwaReport(year: number): Promise<BwaReport> {
    return this.get('/datev/bwa', { year });
  }

  async downloadBwaPdf(year: number, opts?: { view?: 'jahr' | 'monat'; month?: number }): Promise<Blob> {
    const params = new URLSearchParams({ year: String(year) });
    if (opts?.view) params.set('view', opts.view);
    if (opts?.month) params.set('month', String(opts.month));
    const response = await fetch(`${this.baseUrl}/datev/bwa/pdf?${params.toString()}`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleResponse<never>(response); // wirft ApiError mit status/code/message (V-1195 F-009)
    return response.blob();
  }

  // Jobcenter-Anlage EKS
  async getEksSettings(): Promise<{ settings: EksSettings }> {
    return this.get('/datev/eks/settings');
  }

  async saveEksSettings(settings: Partial<EksSettings>): Promise<{ settings: EksSettings }> {
    return this.put('/datev/eks/settings', settings);
  }

  async getEksReport(params?: { from?: string; to?: string; mode?: EksMode }): Promise<EksReport> {
    return this.get('/datev/eks', {
      ...(params?.from && { from: params.from }),
      ...(params?.to && { to: params.to }),
      ...(params?.mode && { mode: params.mode }),
    });
  }

  /** Planwerte + Hochrechnung für die vorläufige Anlage EKS (Antrag/Zukunft). */
  async getEksForecast(params?: { from?: string; to?: string }): Promise<EksForecast> {
    return this.get('/datev/eks/forecast', {
      ...(params?.from && { from: params.from }),
      ...(params?.to && { to: params.to }),
    });
  }

  /** Speichert NUR geänderte Zellen (`overrides`), `null` entfernt eine Überschreibung (V-2441). */
  async saveEksForecast(body: { overrides: Record<string, EksOverrideBody> }): Promise<{ forecast: { version: number; overrides: Record<string, EksOverrideBody> } }> {
    return this.put('/datev/eks/forecast', body);
  }

  /** Drill-Down: Einzelbelege hinter einer Report-Position (EÜR/UStVA/BWA/EKS). */
  async getReportDrilldown(params: ReportDrilldownParams): Promise<ReportDrilldown> {
    const q: Record<string, string> = { side: params.side, from: params.from, to: params.to };
    if (params.basis) q.basis = params.basis;
    if (params.exclude_pos) q.exclude_pos = '1';
    if (params.tax_bucket) q.tax_bucket = params.tax_bucket;
    if (params.block) q.block = params.block;
    if (params.account) q.account = params.account;
    if (params.category) q.category = params.category;
    if (params.exclude_categories) q.exclude_categories = params.exclude_categories;
    if (params.period) q.period = params.period;
    return this.get('/datev/drilldown', q);
  }

  async downloadEksPdf(params?: { from?: string; to?: string; mode?: EksMode }): Promise<Blob> {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.mode) qs.set('mode', params.mode);
    const response = await fetch(`${this.baseUrl}/datev/eks/pdf${qs.toString() ? '?' + qs.toString() : ''}`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) await this.handleResponse<never>(response); // wirft ApiError mit status/code/message (V-1195 F-009)
    return response.blob();
  }

  // Thai Tax Reports
  async getThaiPP30Report(year: number, month?: number): Promise<ThaiPP30Report> {
    return this.get('/thai-tax/pp30', { year, ...(month && { month }) });
  }

  async getThaiWHTReport(year: number, month?: number): Promise<ThaiWHTReport> {
    return this.get('/thai-tax/wht', { year, ...(month && { month }) });
  }

  async getThaiIncomeTaxReport(year: number): Promise<ThaiIncomeTaxReport> {
    return this.get('/thai-tax/income-tax', { year });
  }

  async getThaiThresholds(year: number): Promise<ThaiThresholdReport> {
    return this.get('/thai-tax/thresholds', { year });
  }

  // =============================================================================
  // TENANT STRIPE INTEGRATION
  // =============================================================================

  async getStripeConfig(): Promise<any> {
    return this.get('/tenant-stripe/config');
  }

  async saveStripeConfig(data: Record<string, any>): Promise<any> {
    return this.post('/tenant-stripe/config', data);
  }

  async deleteStripeConfig(): Promise<any> {
    return this.delete('/tenant-stripe/config');
  }

  async getStripeStatus(): Promise<any> {
    return this.get('/tenant-stripe/status');
  }

  async getStripeSubscriptions(params?: Record<string, any>): Promise<any> {
    return this.get('/tenant-stripe/subscriptions', params);
  }

  async syncStripeCustomers(): Promise<any> {
    return this.post('/tenant-stripe/sync/customers');
  }

  async syncStripeSubscriptions(): Promise<any> {
    return this.post('/tenant-stripe/sync/subscriptions');
  }

  async syncStripeInvoices(): Promise<any> {
    return this.post('/tenant-stripe/sync/invoices');
  }

  async getSubscriptionInvoices(subscriptionId: string): Promise<any> {
    return this.get(`/tenant-stripe/subscriptions/${subscriptionId}/invoices`);
  }

  async getSubscriptionOrders(subscriptionId: string): Promise<any> {
    return this.get(`/tenant-stripe/subscriptions/${subscriptionId}/orders`);
  }

  async createOrderFromSubscription(id: string): Promise<any> {
    return this.post(`/tenant-stripe/subscriptions/${id}/create-order`);
  }

  async createBatchOrdersFromSubscriptions(ids: string[]): Promise<any> {
    return this.post('/tenant-stripe/subscriptions/batch-orders', { subscription_ids: ids });
  }

  async createSubscription(data: any): Promise<any> {
    return this.post('/tenant-stripe/subscriptions', data);
  }

  async updateSubscription(id: string, data: any): Promise<any> {
    return this.put(`/tenant-stripe/subscriptions/${id}`, data);
  }

  async cancelSubscription(id: string, data?: any): Promise<any> {
    return this.post(`/tenant-stripe/subscriptions/${id}/cancel`, data);
  }

  async createInvoiceFromSubscription(id: string): Promise<any> {
    return this.post(`/tenant-stripe/subscriptions/${id}/create-invoice`);
  }

  async createBatchInvoicesFromSubscriptions(subscriptionIds: string[]): Promise<any> {
    return this.post('/tenant-stripe/subscriptions/batch-invoices', { subscription_ids: subscriptionIds });
  }

  async importStripePayments(params?: Record<string, any>): Promise<any> {
    return this.post('/tenant-stripe/import/payments', params);
  }

  async collectViaStripe(invoiceId: string): Promise<any> {
    return this.post(`/tenant-stripe/invoices/${invoiceId}/collect`);
  }

  async getStripeWebhookEvents(params?: Record<string, any>): Promise<any> {
    return this.get('/tenant-stripe/webhook-events', params);
  }

  async getStripeWebhookEventDetail(eventId: string): Promise<any> {
    return this.get(`/tenant-stripe/webhook-events/${eventId}`);
  }

  // =============================================================================
  // Stripe Product Catalog & Invoice Item Linking
  // =============================================================================

  async getStripeProducts(): Promise<any> {
    return this.get('/tenant-stripe/products');
  }

  async importStripeProduct(data: {
    stripe_product_id: string;
    stripe_price_id?: string;
    sku?: string;
    name?: string;
    selling_price?: number;
    tax_rate?: number;
    is_service?: boolean;
  }): Promise<any> {
    return this.post('/tenant-stripe/products/import', data);
  }

  async batchImportStripeProducts(products: { stripe_product_id: string; stripe_price_id?: string }[]): Promise<any> {
    return this.post('/tenant-stripe/products/batch-import', { products });
  }

  async syncStripeProduct(stripeProductId: string): Promise<any> {
    return this.put(`/tenant-stripe/products/${stripeProductId}/sync`, {});
  }

  async linkInvoiceItemToArticle(itemId: string, articleId: string): Promise<any> {
    return this.post(`/tenant-stripe/invoice-items/${itemId}/link-article`, { article_id: articleId });
  }

  async createArticleFromInvoiceItem(itemId: string, data?: { sku?: string; tax_rate?: number }): Promise<any> {
    return this.post(`/tenant-stripe/invoice-items/${itemId}/create-article`, data || {});
  }

  async linkOrderItemToArticle(itemId: string, articleId: string): Promise<any> {
    return this.post(`/tenant-stripe/order-items/${itemId}/link-article`, { article_id: articleId });
  }

  async createArticleFromOrderItem(itemId: string, data?: { sku?: string; tax_rate?: number }): Promise<any> {
    return this.post(`/tenant-stripe/order-items/${itemId}/create-article`, data || {});
  }

  // =============================================================================
  // LEXWARE OFFICE API (Cloud-Buchhaltung, Belegübergabe via vouchers)
  // =============================================================================

  async getLexwareStatus(): Promise<{
    connected: boolean;
    status: string;
    authMode?: string;
    organizationId?: string;
    companyName?: string;
    connectedAt?: string;
    lastSyncAt?: string;
    message?: string;
  }> {
    return this.get('/lexware/status');
  }

  async connectLexware(apiKey: string): Promise<{ connected: boolean; organizationId?: string; companyName?: string; message?: string }> {
    return this.post('/lexware/connect', { apiKey });
  }

  async testLexware(): Promise<{ connected: boolean; organizationId?: string; companyName?: string; message?: string }> {
    return this.post('/lexware/test', {});
  }

  async disconnectLexware(): Promise<{ success: boolean; message: string }> {
    return this.post('/lexware/disconnect', {});
  }

  async getLexwareSettings(): Promise<{
    syncEnabled: boolean;
    syncInterval: 'manual' | 'hourly' | 'daily';
    defaultTaxType: 'net' | 'gross';
    syncVouchers: boolean;
    syncContacts: boolean;
    attachPdf: boolean;
    categoryMappings: Record<string, string | null>;
  }> {
    return this.get('/lexware/settings');
  }

  async updateLexwareSettings(data: Record<string, unknown>): Promise<unknown> {
    return this.put('/lexware/settings', data);
  }

  async getLexwareCategories(): Promise<{ categories: Array<{ id: string; name: string; type: string; contactRequired?: boolean }>; error?: string }> {
    return this.get('/lexware/categories');
  }

  async updateLexwareCategoryMappings(mappings: Record<string, string | null>): Promise<unknown> {
    return this.put('/lexware/category-mappings', { mappings });
  }

  async startLexwareSync(type: 'vouchers' | 'contacts'): Promise<{ success: boolean; result: { total: number; success: number; failed: number; skipped?: number; errors: string[] } }> {
    return this.post('/lexware/sync', { type });
  }

  async getLexwareSubmissions(limit = 100): Promise<{ submissions: Array<{
    id: string;
    source_type: string;
    voucher_type: string | null;
    lexware_voucher_id: string | null;
    sync_status: string;
    error_message: string | null;
    retry_count: number;
    submitted_at: string | null;
    created_at: string;
  }> }> {
    return this.get('/lexware/submissions', { limit });
  }

  async getLexwareSyncHistory(): Promise<{ history: Array<{
    id: string;
    sync_type: string;
    records_total: number;
    records_success: number;
    records_failed: number;
    started_at: string;
    completed_at: string | null;
    status: string;
  }> }> {
    return this.get('/lexware/sync-history');
  }

  async getLexwareStatistics(): Promise<{ submitted: number; pending: number; error: number; contacts: number }> {
    return this.get('/lexware/statistics');
  }

  async retryLexwareSubmission(id: string): Promise<{ success: boolean; result: { status: string; message?: string } }> {
    return this.post(`/lexware/submissions/${id}/retry`, {});
  }

  // =============================================================================
  // DATEV ONLINE API (Unternehmen Online / Belege Online)
  // =============================================================================

  // Get DATEV Online connection status
  async getDatevOnlineStatus(): Promise<DatevOnlineStatus> {
    return this.get('/datev/online/status');
  }

  // Get DATEV connect authorization URL
  async getDatevConnectUrl(): Promise<{ authorizationUrl: string; state: string }> {
    return this.get('/datev/online/connect');
  }

  // Disconnect from DATEV
  async disconnectDatev(): Promise<{ success: boolean; message: string }> {
    return this.post('/datev/online/disconnect', {});
  }

  // Store DATEV credentials
  async storeDatevCredentials(data: { clientId: string; clientSecret?: string }): Promise<{ success: boolean }> {
    return this.post('/datev/online/credentials', data);
  }

  // Get DATEV document uploads
  async getDatevDocuments(params?: {
    status?: string;
    documentType?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; uploads: DatevDocumentUpload[] }> {
    return this.get('/datev/online/documents', params);
  }

  // Upload document to DATEV
  async uploadDatevDocument(file: File, documentType: string, sourceType?: string, sourceId?: string): Promise<{
    success: boolean;
    uploadId?: string;
    datevDocumentId?: string;
    message?: string;
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (sourceType) formData.append('sourceType', sourceType);
    if (sourceId) formData.append('sourceId', sourceId);

    const response = await fetch(this.buildUrl('/datev/online/documents/upload'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    });
    return response.json();
  }

  // Retry failed document upload
  async retryDatevDocument(uploadId: string): Promise<{ success: boolean; error?: string }> {
    return this.post(`/datev/online/documents/${uploadId}/retry`, {});
  }

  /**
   * Dropshipping-Bestelldatei hochladen.
   *
   * 🚨 MUSS über direktes `fetch` laufen, nicht über `post()`: Das serialisiert seinen Body
   * mit `JSON.stringify()`, und ein FormData-Objekt wird dabei zu `"{}"` — die Datei kam also
   * nie beim Server an. Ebenso wird KEIN `Content-Type` gesetzt: Bei FormData muss ihn der
   * Browser selbst setzen, weil nur er die multipart-Boundary kennt.
   */
  /**
   * Carrier-Rechnung für den Versandkosten-Abgleich hochladen.
   *
   * 🚨 Gleiche Falle wie beim Dropship-Import: über `post()` wurde das FormData zu `"{}"`
   * serialisiert, die Datei erreichte den Server nie. Direktes `fetch`, kein Content-Type.
   */
  async uploadShippingCostImport(file: File, carrier: string): Promise<unknown> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('carrier', carrier);

    const response = await fetch(this.buildUrl('/shipping-costs/imports'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    });
    return response.json();
  }

  async uploadDropshipOrders(file: File, principalId: string): Promise<unknown> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('principal_id', principalId);

    const response = await fetch(this.buildUrl('/dropship/orders/upload'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    });
    return response.json();
  }

  /**
   * Datenübernahme-Assistent (GastroSoft Pro u.a. Vorsysteme): erkennt das Vorsystem eines
   * hochgeladenen Artikelexports, baut Kategoriebaum + Modifikator-Gruppen + normalisierte CSV.
   * Legt NICHTS an — reine Analyse (siehe DatenuebernahmeController::analyse()).
   *
   * 🚨 Gleiche Falle wie beim Dropship-/Versandkosten-Import: über `post()` wuerde das FormData
   * zu `"{}"` serialisiert, die Datei erreichte den Server nie. Direktes `fetch`, kein Content-Type.
   */
  async datenuebernahmeAnalyse(file: File): Promise<DatenuebernahmeAnalyseResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(this.buildUrl('/datenuebernahme/analyse'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    });

    const daten = await response.json().catch(() => null);
    if (!response.ok || !daten || daten.success !== true) {
      const text =
        daten && typeof daten === 'object' && 'error' in daten
          ? String((daten as { error: unknown }).error)
          : `Die Datei konnte nicht ausgewertet werden (HTTP ${response.status}).`;
      throw new Error(text);
    }
    return daten as DatenuebernahmeAnalyseResponse;
  }

  /**
   * Datenübernahme-Assistent: legt Kategoriebaum + Modifikator-Gruppen an (KEINE Artikel — die
   * laufen anschliessend über den bestehenden `/articles/import/start`-Weg). Bekommt DIESELBE
   * Datei wie `datenuebernahmeAnalyse()` ein zweites Mal (der Controller ist zustandslos, siehe
   * DatenuebernahmeController-Klassenkommentar).
   *
   * 🚨 `zuordnung` (Schritt 3 — je Zusatz-Gruppe die vom Kunden gewählten Hauptwarengruppen) MUSS
   * im selben Request mitgeschickt werden, sonst verknüpft der Server nur nach seiner eigenen
   * Vorbelegung (bzw. gar nicht, wenn die mehrdeutig war) — genau das war der gemessene Mangel.
   * Als weiteres `FormData`-Feld (JSON-String), NICHT als separater Request — gleiche Falle wie
   * beim Dropship-/Versandkosten-Import: über `post()` würde das FormData zu `"{}"` serialisiert,
   * die Datei erreichte den Server nie. Direktes `fetch`, kein Content-Type.
   */
  async datenuebernahmeAnwenden(file: File, zuordnung: DatenuebernahmeZuordnung): Promise<DatenuebernahmeAnwendenResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('zuordnung', JSON.stringify(zuordnung));

    const response = await fetch(this.buildUrl('/datenuebernahme/anwenden'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    });

    const daten = await response.json().catch(() => null);
    if (!response.ok || !daten || daten.success !== true) {
      const text =
        daten && typeof daten === 'object' && 'error' in daten
          ? String((daten as { error: unknown }).error)
          : `Die Übernahme ist fehlgeschlagen (HTTP ${response.status}).`;
      throw new Error(text);
    }
    return daten as DatenuebernahmeAnwendenResponse;
  }

  /**
   * Ein eigenes Shop-Layout (ZIP) hochladen.
   *
   * 🚨 Direktes `fetch`, nicht `post()`: Das serialisiert seinen Rumpf mit `JSON.stringify()`,
   * und ein FormData-Objekt wird dabei zu `"{}"` — die Datei käme nie beim Server an. Ebenso
   * wird KEIN `Content-Type` gesetzt: Bei FormData muss ihn der Browser selbst setzen, weil nur
   * er die multipart-Grenze kennt. Dieselbe Falle wie beim Dropship- und Versandkosten-Import.
   *
   * 🚨 Anders als die beiden Nachbarn wird hier `response.ok` geprüft und der Fehlertext
   * geworfen. Der Server lehnt an dieser Stelle mit einem Satz ab, den der Händler lesen muss
   * („Kurzname bereits vergeben", „Archiv zu gross"). Ein blosses `response.json()` liesse die
   * Maske so aussehen, als sei der Upload durchgelaufen.
   */
  async uploadShopThemeArchive(file: File, name: string): Promise<unknown> {
    const formData = new FormData();
    formData.append('datei', file);
    formData.append('name', name);

    const response = await fetch(this.buildUrl('/bis-shop/theme-uploads'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    });

    const daten = await response.json().catch(() => null);
    if (!response.ok) {
      const text =
        daten && typeof daten === 'object' && 'error' in daten
          ? String((daten as { error: unknown }).error)
          : `Der Upload wurde abgelehnt (HTTP ${response.status}).`;
      throw new Error(text);
    }

    return daten;
  }

  // Get document upload status
  async getDatevDocumentStatus(uploadId: string): Promise<{
    found: boolean;
    uploadId?: string;
    datevDocumentId?: string;
    status?: string;
    errorMessage?: string;
  }> {
    return this.get(`/datev/online/documents/${uploadId}/status`);
  }

  // Get DATEV invoice submissions
  async getDatevInvoiceSubmissions(params?: {
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; submissions: DatevInvoiceSubmission[] }> {
    return this.get('/datev/online/invoices', params);
  }

  // Submit invoice to DATEV
  async submitInvoiceToDatev(invoiceId: string, includeDocument?: boolean): Promise<{
    success: boolean;
    submissionId?: string;
    datevSubmissionId?: string;
    message?: string;
    error?: string;
  }> {
    return this.post(`/datev/online/invoices/${invoiceId}/submit`, { includeDocument: includeDocument ?? true });
  }

  // Submit multiple invoices to DATEV
  async submitInvoiceBatchToDatev(invoiceIds: string[], includeDocuments?: boolean): Promise<{
    total: number;
    success: number;
    failed: number;
    submissions: Array<{ invoiceId: string; success: boolean; submissionId?: string; error?: string }>;
  }> {
    return this.post('/datev/online/invoices/submit', { invoiceIds, includeDocuments: includeDocuments ?? true });
  }

  // Get invoice submission status
  async getDatevInvoiceSubmissionStatus(submissionId: string): Promise<{
    found: boolean;
    submissionId?: string;
    invoiceId?: string;
    status?: string;
    bookingSuggestionCreated?: boolean;
    errorMessage?: string;
  }> {
    return this.get(`/datev/online/invoices/${submissionId}/status`);
  }

  // Get pending invoice submissions
  async getDatevPendingSubmissions(): Promise<{ submissions: DatevInvoiceSubmission[] }> {
    return this.get('/datev/online/invoices/pending');
  }

  // Preview invoice XML
  async previewDatevInvoiceXml(invoiceId: string): Promise<{
    invoiceId: string;
    invoiceNumber: string;
    xml: string;
  }> {
    return this.get(`/datev/online/invoices/${invoiceId}/preview`);
  }

  // Start DATEV sync
  async startDatevSync(type: 'invoices' | 'documents' | 'debtors', filters?: Record<string, string>): Promise<{
    success: boolean;
    syncId?: string;
    total?: number;
    successCount?: number;
    failed?: number;
    error?: string;
  }> {
    return this.post('/datev/online/sync', { type, filters });
  }

  // Get DATEV sync history
  async getDatevSyncHistory(limit?: number): Promise<{ history: DatevSyncLog[] }> {
    return this.get('/datev/online/sync/history', { limit });
  }

  // Get DATEV sync statistics
  async getDatevSyncStatistics(): Promise<DatevSyncStatistics> {
    return this.get('/datev/online/sync/statistics');
  }

  // Cancel running sync
  async cancelDatevSync(syncId: string): Promise<{ success: boolean; message: string }> {
    return this.post(`/datev/online/sync/${syncId}/cancel`, {});
  }

  // Get DATEV Online settings
  async getDatevOnlineSettings(): Promise<DatevOnlineSettings> {
    return this.get('/datev/online/settings');
  }

  // Update DATEV Online settings
  async updateDatevOnlineSettings(data: Partial<DatevOnlineSettings>): Promise<{ success: boolean }> {
    return this.put('/datev/online/settings', data);
  }

  // =============================================================================
  // ORDER SHIPMENTS API (Split Shipment Functionality)
  // =============================================================================

  // Get shipments for an order
  async getOrderShipments(orderId: string): Promise<OrderShipmentResponse[]> {
    return this.get(`/orders/${orderId}/shipments`);
  }

  // Get a single order shipment
  async getOrderShipment(shipmentId: string): Promise<OrderShipmentResponse> {
    return this.get(`/shipments/${shipmentId}`);
  }

  // Create shipments for an order (auto-split if needed)
  async createOrderShipments(orderId: string): Promise<{
    success: boolean;
    shipments: Array<{ id: string; shipmentNumber: string; itemCount: number }>;
    message: string;
  }> {
    return this.post(`/orders/${orderId}/shipments/create`, {});
  }

  // Update shipment status
  async updateShipmentStatus(shipmentId: string, data: {
    status: string;
    trackingNumber?: string;
    trackingUrl?: string;
  }): Promise<{ success: boolean; status: string; message: string }> {
    return this.put(`/shipments/${shipmentId}/status`, data);
  }

  // Get pending shipments (for picking)
  async getPendingShipments(params?: {
    carrier_type?: string;
    carrier?: string;
  }): Promise<ShipmentSummaryResponse[]> {
    return this.get('/shipments/pending', params);
  }

  // =============================================================================
  // SHIPPING METHOD CONFIGURATION API
  // =============================================================================

  // Get shipping method configurations
  async getShippingMethodConfigs(params?: {
    carrier_type?: string;
    active_only?: boolean;
  }): Promise<ShippingMethodConfigResponse[]> {
    return this.get('/shipping-method-configs', params);
  }

  // Create shipping method configuration
  async createShippingMethodConfig(data: {
    name: string;
    code: string;
    description?: string;
    carrierType?: string;
    carrier?: string;
    maxWeight?: number;
    packagingWeight?: number;
    allowsOversized?: boolean;
    isDefault?: boolean;
    isActive?: boolean;
    sortOrder?: number;
    basePrice?: number;
    settings?: Record<string, unknown>;
  }): Promise<ShippingMethodConfigResponse> {
    return this.post('/shipping-method-configs', data);
  }

  // =============================================================================
  // SHIPPING RESTRICTIONS API
  // =============================================================================

  // Get article shipping restrictions
  async getArticleShippingRestrictions(articleId: string): Promise<ArticleShippingRestrictionResponse[]> {
    return this.get(`/articles/${articleId}/shipping-restrictions`);
  }

  // Set article shipping restrictions
  async setArticleShippingRestrictions(articleId: string, restrictions: Array<{
    shippingMethodId: string;
    restrictionType: 'allowed' | 'forbidden';
    priority?: number;
  }>): Promise<{ success: boolean; message: string }> {
    return this.put(`/articles/${articleId}/shipping-restrictions`, { restrictions });
  }

  // Get category shipping restrictions
  async getCategoryShippingRestrictions(categoryId: string): Promise<CategoryShippingRestrictionResponse[]> {
    return this.get(`/categories/${categoryId}/shipping-restrictions`);
  }

  // Set category shipping restrictions
  async setCategoryShippingRestrictions(categoryId: string, restrictions: Array<{
    shippingMethodId: string;
    restrictionType: 'allowed' | 'forbidden';
    inheritToChildren?: boolean;
  }>): Promise<{ success: boolean; message: string }> {
    return this.put(`/categories/${categoryId}/shipping-restrictions`, { restrictions });
  }

  // Get allowed shipping methods for an article
  async getArticleAllowedShippingMethods(articleId: string): Promise<ShippingMethodConfigResponse[]> {
    return this.get(`/articles/${articleId}/allowed-shipping-methods`);
  }

  // =============================================================================
  // PAYMENT METHODS API
  // =============================================================================

  // Get all payment methods
  async getPaymentMethods(params?: {
    active_only?: boolean;
    type?: PaymentMethodType;
    // V-0758 Paket (c): nur die fuer eine Eingangsrechnung ANBIETBAREN Zahlarten (aktiv oder
    // nicht, siehe EingangsrechnungZahlart::anbietbar() — legt bei Bedarf den Grundstock an).
    fuer?: 'eingangsrechnung';
  }): Promise<PaymentMethodResponse[]> {
    return this.get('/payment-methods', params);
  }

  // Get single payment method
  async getPaymentMethod(id: string): Promise<PaymentMethodResponse> {
    return this.get(`/payment-methods/${id}`);
  }

  // Create payment method
  async createPaymentMethod(data: PaymentMethodInput): Promise<PaymentMethodResponse> {
    return this.post('/payment-methods', data);
  }

  // Update payment method
  async updatePaymentMethod(id: string, data: Partial<PaymentMethodInput>): Promise<PaymentMethodResponse> {
    return this.put(`/payment-methods/${id}`, data);
  }

  // Delete payment method
  async deletePaymentMethod(id: string): Promise<{ success: boolean }> {
    return this.delete(`/payment-methods/${id}`);
  }

  // Toggle payment method active status
  async togglePaymentMethod(id: string): Promise<{ id: string; isActive: boolean; message: string }> {
    return this.post(`/payment-methods/${id}/toggle`);
  }

  // Reorder payment methods
  async reorderPaymentMethods(order: string[]): Promise<{ success: boolean; message: string }> {
    return this.post('/payment-methods/reorder', { order });
  }

  // Resolve payment method by name/code (for imports)
  async resolvePaymentMethod(paymentMethod: string): Promise<{
    found: boolean;
    created: boolean;
    paymentMethod: PaymentMethodResponse;
  }> {
    return this.post('/payment-methods/resolve', { paymentMethod });
  }

  // =============================================================================
  // VARIANT MANAGEMENT API
  // =============================================================================

  // Get all variant option groups
  async getVariantOptionGroups(): Promise<VariantOptionGroup[]> {
    return this.get('/variants/option-groups');
  }

  // Get single option group with values
  async getVariantOptionGroup(id: string): Promise<VariantOptionGroupWithValues> {
    return this.get(`/variants/option-groups/${id}`);
  }

  // Create option group
  async createVariantOptionGroup(data: {
    name: string;
    displayName: string;
    translations?: Record<string, string>;
    type?: 'select' | 'color' | 'size';
    sortOrder?: number;
    isActive?: boolean;
    marketplaceMappings?: Record<string, string>;
  }): Promise<{ success: boolean; id: string; message: string }> {
    return this.post('/variants/option-groups', data);
  }

  // Update option group
  async updateVariantOptionGroup(id: string, data: {
    name?: string;
    displayName?: string;
    translations?: Record<string, string>;
    type?: string;
    sortOrder?: number;
    isActive?: boolean;
    marketplaceMappings?: Record<string, string>;
  }): Promise<{ success: boolean; message: string }> {
    return this.put(`/variants/option-groups/${id}`, data);
  }

  // Delete option group
  async deleteVariantOptionGroup(id: string): Promise<{ success: boolean; message: string }> {
    return this.delete(`/variants/option-groups/${id}`);
  }

  // Create option value
  async createVariantOptionValue(groupId: string, data: {
    value: string;
    displayValue: string;
    translations?: Record<string, string>;
    colorCode?: string;
    imageUrl?: string;
    sortOrder?: number;
    isActive?: boolean;
  }): Promise<{ success: boolean; id: string; message: string }> {
    return this.post(`/variants/option-groups/${groupId}/values`, data);
  }

  // Update option value
  async updateVariantOptionValue(id: string, data: {
    value?: string;
    displayValue?: string;
    translations?: Record<string, string>;
    colorCode?: string;
    imageUrl?: string;
    sortOrder?: number;
    isActive?: boolean;
  }): Promise<{ success: boolean; message: string }> {
    return this.put(`/variants/option-values/${id}`, data);
  }

  // Upload image for variant option value
  async uploadVariantOptionImage(file: File): Promise<{ success: boolean; url: string; message?: string }> {
    const formData = new FormData();
    formData.append('image', file);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    if (this.tenantId) {
      headers['X-Tenant-ID'] = this.tenantId;
    }

    const response = await fetch(`${this.baseUrl}/variants/upload-image`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Upload failed');
    }

    return response.json();
  }

  // Delete option value
  async deleteVariantOptionValue(id: string): Promise<{ success: boolean; message: string }> {
    return this.delete(`/variants/option-values/${id}`);
  }

  // Get article variants
  async getArticleVariants(articleId: string): Promise<ArticleVariantsResponse> {
    return this.get(`/variants/articles/${articleId}/variants`);
  }

  // Set article variant configuration
  async setArticleVariantConfig(articleId: string, optionGroups: Array<{
    optionGroupId: string;
    isRequired?: boolean;
    sortOrder?: number;
  }>): Promise<{ success: boolean; message: string }> {
    return this.put(`/variants/articles/${articleId}/variant-config`, { optionGroups });
  }

  // Generate variants from matrix
  async generateArticleVariants(articleId: string, data: {
    skuPattern?: string;
    namePattern?: string;
    price?: number;
    stock?: number;
    combinations: Array<Record<string, { value: string; displayValue: string; valueId: string }>>;
  }): Promise<{
    success: boolean;
    created: Array<{ id: string; sku: string; name: string }>;
    createdCount: number;
    errors: string[];
  }> {
    return this.post(`/variants/articles/${articleId}/generate-variants`, data);
  }

  // Update variant
  async updateArticleVariant(articleId: string, variantId: string, data: {
    sku?: string;
    name?: string;
    ean?: string;
    stock?: number;
    price?: number;
    status?: string;
    isPublished?: boolean;
  }): Promise<{ success: boolean; message: string }> {
    return this.put(`/variants/articles/${articleId}/variants/${variantId}`, data);
  }

  // Delete variant
  async deleteArticleVariant(articleId: string, variantId: string): Promise<{ success: boolean; message: string; jtl_folge?: string | null; jtl_entscheidung?: string | null; bis_behalten?: boolean; bis_folge?: string | null }> {
    return this.delete(`/variants/articles/${articleId}/variants/${variantId}`);
  }

  // Sync variants from parent (propagate parent changes to all variants)
  async syncVariantsFromParent(articleId: string, options?: {
    syncPrice?: boolean;
    fields?: string[];
  }): Promise<{
    success: boolean;
    message: string;
    updatedCount: number;
    syncedFields: string[];
  }> {
    return this.post(`/variants/articles/${articleId}/sync-variants`, options || {});
  }

  // ==========================================
  // User Management
  // ==========================================

  async getUsers(params?: { page?: number; limit?: number; search?: string }): Promise<{
    users: Array<{
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      fullName: string;
      role: string;
      isActive: boolean;
      lastLoginAt: string | null;
      createdAt: string;
      updatedAt: string;
      language: string;
      customPermissionsEnabled: boolean;
      failedLoginAttempts: number;
      lockedUntil: string | null;
      isLocked: boolean;
    }>;
    total: number;
    page: number;
    limit: number;
    pages: number;
  }> {
    return this.get('/users', params);
  }

  async getUser(id: string): Promise<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    role: string;
    isActive: boolean;
    lastLoginAt: string | null;
    createdAt: string;
    updatedAt: string;
    language: string;
    customPermissionsEnabled: boolean;
    failedLoginAttempts: number;
    lockedUntil: string | null;
    isLocked: boolean;
  }> {
    return this.get(`/users/${id}`);
  }

  async createUser(data: {
    email: string;
    firstName: string;
    lastName: string;
    role?: string;
    isActive?: boolean;
    language?: string;
    password?: string;
    customPermissionsEnabled?: boolean;
  }): Promise<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isActive: boolean;
    temporaryPassword?: string;
    // V-2147: true = die Adresse gehört einem anderen Mandanten — das Konto wartet auf die Zustimmung des Postfachinhabers.
    linkPending?: boolean;
    // Neu angelegte Benutzer sind IMMER unbestätigt — ohne Klick auf den Link in der
    // Bestätigungsmail weist der Login sie ab (403). `verificationEmailSent` sagt, ob die
    // Mail rausging; bei `false` hilft nur "Bestätigungsmail erneut senden".
    isEmailVerified?: boolean;
    verificationEmailSent?: boolean;
    // Wurde kein Passwort vorgegeben, geht stattdessen eine Einladung raus: Der neue Kollege
    // legt sein Passwort selbst fest und bestätigt damit zugleich seine Adresse. Ohne diesen
    // Weg kannte er sein automatisch erzeugtes Passwort nie.
    // null = Einladungsweg kam nicht in Frage (Passwort war vorgegeben).
    invitationSent?: boolean | null;
  }> {
    return this.post('/users', data);
  }

  async resendUserVerification(id: string): Promise<{
    success: boolean;
    sent?: boolean;
    alreadyVerified?: boolean;
    /** true = es ging eine Einladung zum Passwort-Festlegen raus, keine Bestätigungsmail. */
    invitation?: boolean;
    message: string;
  }> {
    return this.post(`/users/${id}/resend-verification`, {});
  }

  /**
   * Zustand eines Bestätigungslinks — rein lesend, bestätigt NICHTS.
   * Das Bestätigen selbst läuft über `confirmEmailVerification()`, weil Mail-Prüfdienste
   * jeden Link vorab öffnen und ein bestätigendes GET damit verbrauchen würden.
   */
  async getEmailVerificationStatus(token: string): Promise<{
    success: boolean;
    status?: string;
    alreadyVerified?: boolean;
    error?: string;
    message?: string;
  }> {
    return this.get(`/verify-email/${token}`);
  }

  /** Bestätigt die E-Mail-Adresse — ausgelöst durch den Klick des Empfängers. */
  async confirmEmailVerification(token: string): Promise<{
    success: boolean;
    status?: string;
    alreadyVerified?: boolean;
    error?: string;
    message?: string;
  }> {
    return this.post(`/verify-email/${token}`, {});
  }

  async updateUser(id: string, data: {
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    isActive?: boolean;
    language?: string;
    password?: string;
    customPermissionsEnabled?: boolean;
  }): Promise<{ message: string }> {
    return this.put(`/users/${id}`, data);
  }

  async deleteUser(id: string): Promise<{ message: string }> {
    return this.delete(`/users/${id}`);
  }

  async unlockUser(id: string): Promise<{ message: string }> {
    return this.post(`/users/${id}/unlock`, {});
  }

  // V-2147: Kommt die Adresse auch bei anderen Mandanten vor, gibt es KEIN Klartext-Passwort — der Server schickt einen
  // Reset-Link an das Postfach (`resetPerLink`, `resetLinkSent`, `email`).
  async resetUserPassword(id: string): Promise<{
    message: string;
    temporaryPassword?: string;
    resetPerLink?: boolean;
    resetLinkSent?: boolean;
    email?: string;
  }> {
    return this.post(`/users/${id}/reset-password`, {});
  }

  async getUserPermissions(id: string): Promise<{
    role: string;
    customPermissionsEnabled: boolean;
    rolePermissions: Record<string, string[]>;
    customPermissions: Record<string, Record<string, boolean>>;
    /**
     * Kachel-Übersteuerung je Benutzer: `true` = anzeigen, `false` = ausblenden.
     * Eine **fehlende** Kennung heisst „erben" — dann entscheidet die Rolle.
     */
    widgetOverrides?: Record<string, boolean>;
    /** Was die Rolle sperrt — damit die Maske zeigen kann, was „erben" bedeutet. */
    roleBlockedWidgets?: string[];
  }> {
    return this.get(`/users/${id}/permissions`);
  }

  async setUserPermissions(id: string, data: {
    permissions: Record<string, Record<string, boolean>>;
    customPermissionsEnabled: boolean;
    /**
     * 🚨 Weglassen heisst „nicht anfassen", nicht „alles löschen". Der Server rührt die
     * gespeicherten Übersteuerungen nur an, wenn dieses Feld mitkommt.
     */
    widgetOverrides?: Record<string, boolean>;
  }): Promise<{ message: string; verworfeneKacheln?: string[] }> {
    return this.put(`/users/${id}/permissions`, data);
  }

  async getPermissionModules(): Promise<{
    modules: Record<string, { name: string; description: string }>;
    operations: Record<string, string>;
  }> {
    return this.get('/permissions/modules');
  }

  async getPermissionMatrix(): Promise<{
    matrix: Record<string, Record<string, string[]>>;
  }> {
    return this.get('/permissions/matrix');
  }

  async getUserAuditLog(params?: { userId?: string; action?: string; limit?: number; offset?: number }): Promise<{
    logs: Array<{
      id: string;
      targetUserId: string | null;
      action: string;
      actorUserId: string | null;
      actorEmail: string | null;
      oldValues: Record<string, unknown> | null;
      newValues: Record<string, unknown> | null;
      ipAddress: string | null;
      userAgent: string | null;
      createdAt: string;
    }>;
    limit: number;
    offset: number;
  }> {
    return this.get('/users/audit-log', params);
  }

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<{ message: string }> {
    return this.post('/me/change-password', data);
  }

  async getMeExtended(): Promise<{
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      name: string;
      role: string;
      language: string;
      timezone: string;
      permissions: Record<string, Record<string, boolean>>;
    };
    tenant: { id: string; name: string } | null;
    authenticated: boolean;
  }> {
    return this.get('/me');
  }

  // ==========================================
  // Role Management API Methods
  // ==========================================

  async getRoles(): Promise<{
    roles: Array<{
      id: string;
      code: string;
      name: string;
      description: string | null;
      permissions: Record<string, string[]>;
      isSystemRole: boolean;
      isActive: boolean;
      sortOrder: number;
      color: string | null;
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    return this.get('/roles');
  }

  async getRole(id: string): Promise<{
    id: string;
    code: string;
    name: string;
    description: string | null;
    permissions: Record<string, string[]>;
    isSystemRole: boolean;
    isActive: boolean;
    sortOrder: number;
    color: string | null;
    createdAt: string;
    updatedAt: string;
  }> {
    return this.get(`/roles/${id}`);
  }

  async createRole(data: {
    name: string;
    code?: string;
    description?: string;
    permissions?: Record<string, string[]>;
    isActive?: boolean;
    sortOrder?: number;
    color?: string;
  }): Promise<{ id: string; code: string; name: string }> {
    return this.post('/roles', data);
  }

  async updateRole(id: string, data: {
    name?: string;
    code?: string;
    description?: string;
    permissions?: Record<string, string[]>;
    /** Dashboard-Kacheln, die diese Rolle nicht sehen soll — keine Berechtigung, sondern eine
     *  Anzeige-Vorgabe. */
    blockedWidgets?: string[];
    isActive?: boolean;
    sortOrder?: number;
    color?: string;
  }): Promise<{ message: string }> {
    return this.put(`/roles/${id}`, data);
  }

  async deleteRole(id: string): Promise<{ message: string }> {
    return this.delete(`/roles/${id}`);
  }

  // ==========================================
  // Company Documents API Methods (Encrypted)
  // ==========================================

  async getCompanyDocuments(): Promise<{
    documents: CompanyDocumentGroup[];
    documentTypes: Record<string, string>;
  }> {
    return this.get('/company-documents');
  }

  async uploadCompanyDocument(file: File, data: {
    documentType: string;
    documentName?: string;
    notes?: string;
  }): Promise<{
    success: boolean;
    document: CompanyDocument;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', data.documentType);
    if (data.documentName) formData.append('documentName', data.documentName);
    if (data.notes) formData.append('notes', data.notes);

    const response = await fetch(`${this.baseUrl}/company-documents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw error;
    }
    return response.json();
  }

  async downloadCompanyDocument(id: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/company-documents/${id}/download`, {
      headers: this.getHeaders(),
    });
    if (!response.ok) {
      throw new Error('Download fehlgeschlagen');
    }
    return response.blob();
  }

  async viewCompanyDocument(id: string): Promise<string> {
    // Returns a temporary URL for viewing
    return `${this.baseUrl}/company-documents/${id}/view`;
  }

  async updateCompanyDocument(id: string, data: {
    documentName?: string;
    notes?: string;
  }): Promise<{ success: boolean }> {
    return this.put(`/company-documents/${id}`, data);
  }

  async deleteCompanyDocument(id: string): Promise<{ success: boolean }> {
    return this.delete(`/company-documents/${id}`);
  }

  async getCompanyDocumentTypes(): Promise<{
    types: Array<{ id: string; label: string }>;
  }> {
    return this.get('/company-documents/types');
  }

  // ==========================================
  // Backup API Methods
  // ==========================================

  async getBackups(params?: { limit?: number; offset?: number }): Promise<{
    backups: BackupRecord[];
    limit: number;
    offset: number;
  }> {
    return this.get('/backups', params);
  }

  async getBackup(id: string): Promise<{ backup: BackupRecord }> {
    return this.get(`/backups/${id}`);
  }

  async createBackup(): Promise<{
    success: boolean;
    backup: { id: string; status: string; file_size: number | null };
  }> {
    return this.post('/backups');
  }

  async deleteBackup(id: string): Promise<{ success: boolean }> {
    return this.delete(`/backups/${id}`);
  }

  async restoreBackup(id: string, createPreRestoreBackup: boolean = true): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.post(`/backups/${id}/restore`, {
      create_pre_restore_backup: createPreRestoreBackup,
      confirmed: true,
    });
  }

  async getBackupConfig(): Promise<{ config: BackupConfig }> {
    return this.get('/backup-config');
  }

  async updateBackupConfig(config: Partial<BackupConfig>): Promise<{ success: boolean }> {
    return this.put('/backup-config', { config });
  }

  async getBackupUsage(): Promise<{ usage: BackupUsage }> {
    return this.get('/backup-usage');
  }

  // ============================================================
  // PhotoRoom Integration (Background Removal)
  // ============================================================

  async getPhotoRoomSettings(): Promise<PhotoRoomSettings> {
    return this.get('/photoroom/settings');
  }

  async savePhotoRoomSettings(data: PhotoRoomSettingsInput): Promise<{ success: boolean }> {
    return this.post('/photoroom/settings', data);
  }

  async testPhotoRoomConnection(): Promise<{ success: boolean; message: string; error?: string; environment?: string }> {
    return this.post('/photoroom/test', {});
  }

  async photoRoomRemoveBackground(data: PhotoRoomRemoveBgInput): Promise<PhotoRoomRemoveBgResult> {
    return this.post('/photoroom/remove-bg', data);
  }

  async photoRoomBulkRemoveBackground(images: PhotoRoomBulkImage[]): Promise<PhotoRoomBulkResult> {
    return this.post('/photoroom/bulk-remove-bg', { images });
  }

  async getPhotoRoomUsage(params?: { limit?: number; offset?: number }): Promise<PhotoRoomUsageResponse> {
    return this.get('/photoroom/usage', params);
  }

  async deletePhotoRoomSettings(): Promise<{ success: boolean; message: string }> {
    return this.delete('/photoroom/settings');
  }

  async getArticleImagesOverview(params?: {
    page?: number;
    limit?: number;
    search?: string;
    filter?: 'all' | 'with_images' | 'without_images' | 'processed' | 'unprocessed';
  }): Promise<ArticleImagesOverviewResponse> {
    return this.get('/photoroom/article-images', params);
  }

  async processSingleImage(imageId: string): Promise<ProcessImageResult> {
    return this.post(`/photoroom/process-image/${imageId}`, {});
  }

  // Variant Option Images for BISPix
  async getVariantOptionImagesOverview(params?: {
    page?: number;
    limit?: number;
    search?: string;
    filter?: 'all' | 'with_images' | 'without_images' | 'processed' | 'unprocessed';
  }): Promise<VariantOptionImagesOverviewResponse> {
    return this.get('/photoroom/variant-option-images', params);
  }

  async processVariantOptionImage(valueId: string): Promise<ProcessVariantOptionImageResult> {
    return this.post(`/photoroom/process-variant-option-image/${valueId}`, {});
  }

  // ============================================================
  // AI Article Creation (KI-Artikelanlage)
  // ============================================================

  async analyzeArticleWithAi(data: {
    images: Array<{ base64: string; mime_type: string; label?: string }>;
    article_id?: string;
    existing_data?: Record<string, unknown>;
    additional_info?: {
      properties?: Array<{ key: string; value: string }>;
      platform?: string;
      custom_instructions?: string;
    };
  }): Promise<{
    success: boolean;
    suggestions?: Record<string, unknown>;
    tokens_input?: number;
    tokens_output?: number;
    cost_estimate?: number;
    credits_deducted?: number;
    credit_tx_id?: string;
    balance_cents?: number;
    error?: string;
    // Steht die Auswertungsweiche auf "sitzung", kommt kein Ergebnis zurueck, sondern
    // ein Auftrag. Der Preis fuer den Mandanten aendert sich dadurch nicht.
    wartet?: boolean;
    auftrag_id?: string;
    message?: string;
    required_cents?: number;
    // LMIV-Zweitlesung (Gegenkontrolle Zutaten/Allergene/Nährwerte): ein zweites Modell hat
    // dieselben Bilder blind erneut gelesen. `lmiv_review_id` gesetzt, wenn Zutaten/
    // Nährwerte gefunden ODER ein Injektionsverdacht gemeldet wurde (H1-Fix, 03.09.2026) —
    // muss unverändert an applyAiSuggestions durchgereicht werden, sonst blockiert
    // /articles/ai/apply bei einer Abweichung nicht.
    lmiv_review_id?: string | null;
    lmiv_abgleich?: 'uebereinstimmung' | 'abweichung' | 'sicherheitsverdacht' | 'zweitlesung_fehlgeschlagen' | null;
  }> {
    return this.post('/articles/ai/analyze', data);
  }

  /** Zustand eines wartenden Artikel-Auftrags. */
  async getAiAuftrag(id: string): Promise<{
    success: boolean;
    status?: 'wartet' | 'fertig' | 'gescheitert';
    ergebnis?: Record<string, unknown> | null;
    fehler?: string | null;
    error?: string;
  }> {
    return this.get(`/articles/ai/auftrag/${id}`);
  }

  /**
   * Die eigenen Artikel-Auftraege der letzten Tage.
   *
   * Der Wiedereinstieg, wenn jemand den Assistenten geschlossen hat: Ohne ihn laege
   * die Auftrags-Kennung nur im Browser, und ein bezahltes Ergebnis waere mit dem Tab
   * verloren.
   */
  async getMeineAiAuftraege(): Promise<{
    success: boolean;
    auftraege?: Array<{
      auftragId: string;
      status: 'wartet' | 'fertig';
      preisCents: number;
      erstelltAm: string | null;
      fertigAm: string | null;
    }>;
  }> {
    return this.get('/articles/ai/auftraege');
  }

  async applyAiSuggestions(data: {
    article_id?: string;
    create_new: boolean;
    fields: Record<string, unknown>;
    ingredients?: Array<Record<string, unknown>>;
    nutrition?: Record<string, unknown>;
    credit_tx_id?: string;
    lmiv_review_id?: string | null;
  }): Promise<{ success: boolean; article_id?: string; error?: string; hinweise?: string[] }> {
    return this.post('/articles/ai/apply', data);
  }

  async getAiArticleUsage(): Promise<{
    success: boolean;
    data?: {
      month: {
        total_analyses: number;
        tokens_input: number;
        tokens_output: number;
        total_cost: number;
        total_images: number;
      };
      recent: Array<Record<string, unknown>>;
      monthly_limit: number;
    };
  }> {
    return this.get('/articles/ai/usage');
  }

  async getAiArticleModels(): Promise<{
    success: boolean;
    models: Record<string, Array<{ value: string; label: string; input_price: number; output_price: number; sell_price_cents: number }>>;
    markup_percent: number;
  }> {
    return this.get('/articles/ai/models');
  }

  // ============================================================
  // LMIV-Zweitlesung (Gegenkontrolle KI-Bildanalyse)
  // ============================================================

  /**
   * Prüfliste offener Abweichungen/Sicherheitsverdachte aus der LMIV-Zweitlesung
   * (Pfad 1: Artikel-KI-Wizard, Pfad 2: Zutaten-Tab-OCR). Die Produktdatenbank (Pfad 3)
   * läuft in der bestehenden `/productdb/admin/einreichungen`-Verwaltung mit — kein
   * Parallelbau hier.
   */
  async getLmivReviews(status?: string): Promise<{ success: boolean; data: LmivReviewListItem[] }> {
    return this.get('/articles/lmiv-review', status ? { status } : undefined);
  }

  async getLmivReviewDetail(id: string): Promise<{ success: boolean; data?: LmivReviewDetail; error?: string }> {
    return this.get(`/articles/lmiv-review/${id}`);
  }

  async entscheideLmivReview(
    id: string,
    action: 'freigeben' | 'ablehnen' | 'sicherheitsfall',
  ): Promise<{ success: boolean; status?: string; error?: string }> {
    return this.post(`/articles/lmiv-review/${id}/entscheiden`, { action });
  }

  // ============================================================
  // Image Management (Article Images, Storage, PhotoRoom Integration)
  // ============================================================

  // Storage Settings
  async getStorageSettings(): Promise<StorageSettingsResponse> {
    return this.get('/images/storage-settings');
  }

  async saveStorageSettings(data: StorageSettingsInput): Promise<{ success: boolean; message: string }> {
    return this.post('/images/storage-settings', data);
  }

  async testStorageConnection(): Promise<{ success: boolean; message: string; error?: string }> {
    return this.post('/images/storage-settings/test', {});
  }

  // Image Upload & CRUD
  /**
   * Bild einer Kategorie, eines Herstellers oder einer Marke hochladen.
   *
   * Läuft über dieselbe Strecke wie Artikelbilder (DigitalOcean Spaces + CDN, lokaler
   * Rückfall) — es gibt bewusst keinen zweiten Bildpfad.
   */
  async uploadEntityImage(
    typ: 'category' | 'manufacturer' | 'brand',
    id: string,
    file: File,
  ): Promise<{ success: boolean; url: string; spalten: string[] }> {
    const formData = new FormData();
    formData.append('image', file);

    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    if (this.tenantId) headers['X-Tenant-ID'] = this.tenantId;

    const response = await fetch(`${this.baseUrl}/images/entity/${typ}/${id}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      // 🚨 Bei HTTP-Fehlern steht die Meldung im Feld `error`, nicht in `message` — wer nur
      //    `message` liest, zeigt dem Benutzer eine leere Fehlermeldung an.
      const fehler = await response.json().catch(() => ({}));
      throw new Error(fehler.error || fehler.message || 'Upload fehlgeschlagen');
    }

    return response.json();
  }

  /** Entfernt den Bildverweis. Die Datei selbst bleibt im Speicher — sie kann anderswo hängen. */
  async deleteEntityImage(
    typ: 'category' | 'manufacturer' | 'brand',
    id: string,
  ): Promise<{ success: boolean; spalten: string[] }> {
    return this.delete(`/images/entity/${typ}/${id}`);
  }

  async uploadArticleImage(articleId: string, file: File, options?: ImageUploadOptions): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('image', file);
    if (options?.isPrimary !== undefined) formData.append('isPrimary', String(options.isPrimary));
    if (options?.altText) formData.append('altText', options.altText);
    if (options?.sortOrder !== undefined) formData.append('sortOrder', String(options.sortOrder));

    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    if (this.tenantId) {
      headers['X-Tenant-ID'] = this.tenantId;
    }

    const response = await fetch(`${this.baseUrl}/images/upload/${articleId}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Upload failed');
    }

    return response.json();
  }

  async getArticleImages(articleId: string, options?: { includeParentGlobals?: boolean }): Promise<{ images: ArticleImageRecord[]; total: number }> {
    const params = options?.includeParentGlobals ? '?includeParentGlobals=true' : '';
    return this.get(`/images/article/${articleId}${params}`);
  }

  async updateArticleImage(imageId: string, data: ArticleImageUpdateInput): Promise<{ success: boolean; image: ArticleImageRecord }> {
    return this.put(`/images/${imageId}`, data);
  }

  async deleteArticleImage(imageId: string): Promise<{ success: boolean; message: string }> {
    return this.delete(`/images/${imageId}`);
  }

  async setImageAsPrimary(imageId: string): Promise<{ success: boolean; message: string }> {
    return this.post(`/images/${imageId}/primary`, {});
  }

  async reorderArticleImages(articleId: string, imageIds: string[]): Promise<{ success: boolean; message: string }> {
    return this.post(`/images/article/${articleId}/reorder`, { imageIds });
  }

  async reorderArticleImagesWithGlobals(articleId: string, imageOrder: { imageId: string; isInherited: boolean }[]): Promise<{ success: boolean }> {
    return this.post(`/images/article/${articleId}/reorder-with-globals`, { imageOrder });
  }

  // PhotoRoom Processing (via ImageController)
  async processImageWithPhotoRoom(imageId: string, options?: PhotoRoomProcessOptions): Promise<PhotoRoomProcessResult> {
    return this.post(`/images/${imageId}/photoroom`, options || {});
  }

  // === Article Attachments ===

  async uploadArticleAttachment(articleId: string, file: File, description?: string): Promise<{ success: boolean; attachment: ArticleAttachmentRecord }> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);

    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    if (this.tenantId) headers['X-Tenant-ID'] = this.tenantId;

    const response = await fetch(`${this.baseUrl}/article-attachments/upload/${articleId}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || 'Upload failed');
    }
    return response.json();
  }

  async getArticleAttachments(articleId: string, options?: { includeParentGlobals?: boolean }): Promise<{ attachments: ArticleAttachmentRecord[] }> {
    const params = options?.includeParentGlobals ? '?includeParentGlobals=true' : '';
    return this.get(`/article-attachments/article/${articleId}${params}`);
  }

  async updateArticleAttachment(attachmentId: string, data: ArticleAttachmentUpdateInput): Promise<{ success: boolean }> {
    return this.put(`/article-attachments/${attachmentId}`, data);
  }

  async deleteArticleAttachment(attachmentId: string): Promise<{ success: boolean }> {
    return this.delete(`/article-attachments/${attachmentId}`);
  }

  async reorderArticleAttachments(articleId: string, attachmentIds: string[]): Promise<{ success: boolean }> {
    return this.post(`/article-attachments/article/${articleId}/reorder`, { attachmentIds });
  }

  async reorderArticleAttachmentsWithGlobals(articleId: string, attachmentOrder: { attachmentId: string; isInherited: boolean }[]): Promise<{ success: boolean }> {
    return this.post(`/article-attachments/article/${articleId}/reorder-with-globals`, { attachmentOrder });
  }

  // Image Processing Queue
  async getImageQueueStatus(): Promise<ImageQueueStatusResponse> {
    return this.get('/images/queue/status');
  }

  async processImageQueue(limit?: number): Promise<ImageQueueProcessResponse> {
    return this.post('/images/queue/process', { limit });
  }

  // Bulk Image Import
  async startBulkImageImport(data: {
    sourceType: 'local' | 'google_drive' | 'url_list';
    files?: Array<{ name: string; data: string; mimeType?: string }>;
    urls?: string[];
    googleDriveFolderId?: string;
    deleteAfterImport?: boolean;
  }): Promise<{ success: boolean; jobId: string; totalFiles: number; error?: string }> {
    return this.post('/image-import/start', data);
  }

  async getImageImportJobStatus(jobId: string): Promise<{
    success: boolean;
    job?: {
      id: string;
      status: string;
      totalFiles: number;
      processedFiles: number;
      uploadedImages: number;
      skippedFiles: number;
      failedFiles: number;
      progress: number;
      errors?: Array<{ file: string; error: string }>;
      warnings?: Array<{ file: string; reason: string }>;
      duration?: number;
      startedAt?: string;
      completedAt?: string;
    };
    error?: string;
  }> {
    return this.get(`/image-import/job/${jobId}`);
  }

  async cancelImageImportJob(jobId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    return this.post(`/image-import/job/${jobId}/cancel`, {});
  }

  async getImageImportJobLogs(jobId: string, params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    logs: Array<{
      id: string;
      fileIndex: number;
      status: string;
      fileName: string;
      matchedSku?: string;
      articleId?: string;
      imageId?: string;
      errorMessage?: string;
      createdAt: string;
    }>;
    total: number;
  }> {
    return this.get(`/image-import/job/${jobId}/logs`, params);
  }

  async previewImageImport(fileNames: string[]): Promise<{
    success: boolean;
    preview: Array<{
      fileName: string;
      extractedSku: string;
      sortOrder: number;
      isPrimary: boolean;
      matched: boolean;
      articleId?: string;
      articleName?: string;
    }>;
    summary: {
      total: number;
      matched: number;
      unmatched: number;
    };
  }> {
    return this.post('/image-import/preview', { fileNames });
  }

  async listImageImportJobs(limit?: number): Promise<{
    success: boolean;
    jobs: Array<{
      id: string;
      status: string;
      triggeredByUser: string;
      sourceType: string;
      totalFiles: number;
      uploadedImages: number;
      skippedFiles: number;
      failedFiles: number;
      duration?: number;
      createdAt: string;
      completedAt?: string;
    }>;
  }> {
    return this.get('/image-import/jobs', { limit });
  }

  // ============================================================
  // EAN/GTIN Management
  // ============================================================

  // EAN Pool
  async getEanPool(params?: EanPoolListParams): Promise<EanPoolListResponse> {
    return this.get('/ean-pool', params);
  }

  async importEans(data: EanImportInput): Promise<EanImportResult> {
    return this.post('/ean-pool/import', data);
  }

  async exportEans(params?: EanExportParams): Promise<Blob> {
    const url = this.buildUrl('/ean-pool/export', params);
    const response = await fetch(url, {
      headers: this.getHeaders(),
    });
    return response.blob();
  }

  async assignEan(data: { ean: string; articleId: string; articleSku?: string }): Promise<{ success: boolean }> {
    return this.post('/ean-pool/assign', data);
  }

  async unassignEan(articleId: string): Promise<{ success: boolean }> {
    return this.post('/ean-pool/unassign', { articleId });
  }

  async reserveEan(ean: string, durationMinutes?: number): Promise<{ success: boolean }> {
    return this.post('/ean-pool/reserve', { ean, durationMinutes });
  }

  async moveEans(eanIds: string[], targetListId: string): Promise<{ success: boolean; moved: number }> {
    return this.post('/ean-pool/move', { eanIds, targetListId });
  }

  async getNextAvailableEan(listId?: string): Promise<{ available: boolean; ean: EanPoolEntry | null }> {
    return this.get('/ean-pool/next-available', { listId });
  }

  async validateEan(ean: string): Promise<EanValidationResult> {
    return this.get(`/ean-pool/validate/${ean}`);
  }

  async getEanStatistics(): Promise<EanStatistics> {
    return this.get('/ean-pool/statistics');
  }

  async getEanHistory(params?: EanHistoryParams): Promise<EanHistoryResponse> {
    return this.get('/ean-pool/history', params);
  }

  async getEanSettings(): Promise<Record<string, EanSettingValue>> {
    return this.get('/ean-pool/settings');
  }

  async updateEanSettings(settings: Record<string, unknown>): Promise<{ success: boolean }> {
    return this.put('/ean-pool/settings', settings);
  }

  async parseGs1128(barcode: string): Promise<Gs1128ParseResult> {
    return this.post('/ean-pool/parse-gs1-128', { barcode });
  }

  async retireEans(eanIds: string[], reason?: string): Promise<{ success: boolean; retired: number }> {
    return this.post('/ean-pool/retire', { eanIds, reason });
  }

  async bulkDeleteEans(eanIds: string[]): Promise<{ success: boolean; deleted: number }> {
    return this.post('/ean-pool/bulk-delete', { eanIds });
  }

  // EAN Lists
  async getEanLists(): Promise<{ lists: EanList[] }> {
    return this.get('/ean-lists');
  }

  async createEanList(data: EanListInput): Promise<EanList> {
    return this.post('/ean-lists', data);
  }

  async getEanList(id: string): Promise<EanList> {
    return this.get(`/ean-lists/${id}`);
  }

  async updateEanList(id: string, data: Partial<EanListInput>): Promise<EanList> {
    return this.put(`/ean-lists/${id}`, data);
  }

  async deleteEanList(id: string): Promise<{ success: boolean }> {
    return this.delete(`/ean-lists/${id}`);
  }

  async setEanListPriority(id: string, priority: number): Promise<{ success: boolean; priority: number }> {
    return this.post(`/ean-lists/${id}/set-priority`, { priority });
  }

  async setEanListDefault(id: string): Promise<{ success: boolean }> {
    return this.post(`/ean-lists/${id}/set-default`, {});
  }

  async recalculateEanListCounts(id: string): Promise<{ success: boolean }> {
    return this.post(`/ean-lists/${id}/recalculate`, {});
  }

  async getMarketplaceEanAssignments(): Promise<{ assignments: MarketplaceEanAssignment[] }> {
    return this.get('/ean-lists/marketplace-assignments');
  }

  async saveMarketplaceEanAssignment(data: { marketplace: string; listId: string | null; isActive?: boolean }): Promise<{ success: boolean }> {
    return this.post('/ean-lists/marketplace-assignments', data);
  }

  // GS1 Prefixes
  async getGs1Prefixes(): Promise<{ prefixes: Gs1Prefix[] }> {
    return this.get('/gs1-prefixes');
  }

  async createGs1Prefix(data: Gs1PrefixInput): Promise<Gs1Prefix> {
    return this.post('/gs1-prefixes', data);
  }

  async getGs1Prefix(id: string): Promise<Gs1Prefix> {
    return this.get(`/gs1-prefixes/${id}`);
  }

  async updateGs1Prefix(id: string, data: Partial<Gs1PrefixInput>): Promise<Gs1Prefix> {
    return this.put(`/gs1-prefixes/${id}`, data);
  }

  async deleteGs1Prefix(id: string): Promise<{ success: boolean }> {
    return this.delete(`/gs1-prefixes/${id}`);
  }

  async generateGtinsFromPrefix(prefixId: string, count: number, listId: string): Promise<GtinGenerationResult> {
    return this.post(`/gs1-prefixes/${prefixId}/generate`, { count, listId });
  }

  async previewGtinsFromPrefix(prefixId: string, count?: number): Promise<GtinPreviewResult> {
    return this.get(`/gs1-prefixes/${prefixId}/preview`, { count });
  }

  async resetGs1PrefixCounter(prefixId: string, value?: number): Promise<{ success: boolean; new_value: number }> {
    return this.post(`/gs1-prefixes/${prefixId}/reset-counter`, { value });
  }

  // ============================================================
  // Notifications
  // ============================================================

  async getNotifications(unreadOnly = false): Promise<NotificationsResponse> {
    return this.get('/notifications', { unread_only: unreadOnly });
  }

  async markNotificationRead(id: string): Promise<{ success: boolean }> {
    return this.post(`/notifications/${id}/read`);
  }

  async markAllNotificationsRead(): Promise<{ success: boolean }> {
    return this.post('/notifications/read-all');
  }

  async deleteNotification(id: string): Promise<{ success: boolean }> {
    return this.delete(`/notifications/${id}`);
  }

  // ============================================================
  // Order Import Automation
  // ============================================================

  // Import Sources
  async getOrderImportSources(): Promise<{ sources: any[] }> {
    return this.get('/order-import/sources');
  }

  async getOrderImportSource(id: string): Promise<any> {
    return this.get(`/order-import/sources/${id}`);
  }

  async createOrderImportSource(data: any): Promise<{ success: boolean; id: string }> {
    return this.post('/order-import/sources', data);
  }

  async updateOrderImportSource(id: string, data: any): Promise<{ success: boolean }> {
    return this.put(`/order-import/sources/${id}`, data);
  }

  async deleteOrderImportSource(id: string): Promise<{ success: boolean }> {
    return this.delete(`/order-import/sources/${id}`);
  }

  async testOrderImportSource(id: string): Promise<{ success: boolean; fileCount?: number; error?: string }> {
    return this.post(`/order-import/sources/${id}/test`, {});
  }

  async triggerOrderImport(id: string): Promise<{ success: boolean; imported?: number; skipped?: number; errors?: number }> {
    return this.post(`/order-import/sources/${id}/trigger`, {});
  }

  // Import Templates
  async getOrderImportTemplates(): Promise<{ templates: any[] }> {
    return this.get('/order-import/templates');
  }

  async getOrderImportTemplate(id: string): Promise<any> {
    return this.get(`/order-import/templates/${id}`);
  }

  async createOrderImportTemplate(data: any): Promise<{ success: boolean; id: string }> {
    return this.post('/order-import/templates', data);
  }

  async updateOrderImportTemplate(id: string, data: any): Promise<{ success: boolean }> {
    return this.put(`/order-import/templates/${id}`, data);
  }

  async deleteOrderImportTemplate(id: string): Promise<{ success: boolean }> {
    return this.delete(`/order-import/templates/${id}`);
  }

  // Import Fields
  async getOrderImportFields(): Promise<{ orderFields: any[]; itemFields: any[] }> {
    return this.get('/order-import/fields');
  }

  // Import Runs / History
  async getOrderImportRuns(params?: {
    sourceId?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ runs: any[]; total: number }> {
    return this.get('/order-import/runs', params);
  }

  async getOrderImportRun(id: string): Promise<any> {
    return this.get(`/order-import/runs/${id}`);
  }

  // === Auftragsabgleich JTL <-> BIS ERP: Pruefliste ===
  // Auftraege, die in JTL nachtraeglich geaendert wurden, waehrend sie in BIS bereits in
  // Arbeit oder versandt sind. Der Abgleich fasst sie bewusst nicht an.
  async getOrderSyncReview(params?: { limit?: number; offset?: number }): Promise<{
    items: any[];
    total: number;
    jtl_verbunden: boolean;
  }> {
    return this.get('/orders/sync-review', params);
  }

  async acceptJtlOrderStand(id: string): Promise<{ ok: boolean; message: string }> {
    return this.post(`/orders/sync-review/${id}/accept-jtl`, {});
  }

  async keepBisOrderStand(id: string): Promise<{ ok: boolean; message: string }> {
    return this.post(`/orders/sync-review/${id}/keep-bis`, {});
  }

  // 🚨 Eigener Weg, NICHT `accept-jtl`: Jener schreibt die Positionen neu. Hier geht es um
  // genau ein Feld — den Status eines Auftrags, den JTL nicht (mehr) als storniert führt.
  async unstornoOrderStand(id: string): Promise<{ ok: boolean; status?: string; message: string }> {
    return this.post(`/orders/sync-review/${id}/storno-zuruecknehmen`, {});
  }

  async getOrderImportLogs(runId: string, params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ logs: any[]; total: number }> {
    return this.get(`/order-import/runs/${runId}/logs`, params);
  }

  // Preview import
  async previewOrderImport(data: {
    sourceId?: string;
    templateId?: string;
    content?: string;
  }): Promise<{
    success: boolean;
    preview: any[];
    mappedFields: string[];
    unmappedColumns: string[];
    errors: string[];
  }> {
    return this.post('/order-import/preview', data);
  }
}

// ============================================================
// Notification Types
// ============================================================

export interface Notification {
  id: string;
  type: string;
  title: string;
  message?: string;
  link?: string;
  linkLabel?: string;
  icon: string;
  isRead: boolean;
  data?: Record<string, unknown>;
  createdAt: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
}

// ============================================================
// PhotoRoom Types
// ============================================================

export interface PhotoRoomSettings {
  id?: string;
  isActive: boolean;
  outputFormat: 'png' | 'jpeg' | 'webp';
  autoProcessOnUpload: boolean;
  configured: boolean;

  // Default processing settings
  defaultBgColor: string;
  defaultSizePreset: string;
  defaultCustomWidth: number;
  defaultCustomHeight: number;
  defaultAutoCrop: boolean;

  // Platform service availability (from central config)
  serviceAvailable: boolean;
  serviceEnabled: boolean;
  platformQuota: number;
  platformUsed: number;
  platformRemaining: number;
  pricePerImageCents: number;
  environment: 'sandbox' | 'production';

  // Feature toggles (from platform config)
  features: {
    bgColor: boolean;
    resize: boolean;
    crop: boolean;
  };

  // Tenant usage
  tenantUsedThisMonth: number;
  quotaResetDate: string | null;

  // Legacy compatibility
  apiKey?: string;
  apiKeyMasked?: string;
  monthlyQuota?: number;
  usedThisMonth?: number;
}

export interface PhotoRoomSettingsInput {
  isActive?: boolean;
  outputFormat?: 'png' | 'jpeg' | 'webp';
  autoProcessOnUpload?: boolean;
  defaultBgColor?: string;
  defaultSizePreset?: string;
  defaultCustomWidth?: number;
  defaultCustomHeight?: number;
  defaultAutoCrop?: boolean;
}

export interface PhotoRoomRemoveBgInput {
  imageUrl?: string;
  imageBase64?: string;
  articleId?: string;
  // Optional parameters (only applied if feature is enabled)
  bgColor?: string; // e.g., "#FFFFFF" or "white"
  size?: 'preview' | 'medium' | 'hd' | 'full';
  crop?: boolean;
  // Platform-specific output size (server-side resize)
  width?: number;  // Target width in pixels
  height?: number; // Target height in pixels
}

export interface PhotoRoomRemoveBgResult {
  success: boolean;
  processedImage?: string;
  contentType?: string;
  processingTimeMs?: number;
  error?: string;
  message?: string;
}

export interface PhotoRoomBulkImage {
  imageUrl: string;
  articleId?: string;
}

export interface PhotoRoomBulkResult {
  success: boolean;
  processed: number;
  errors: number;
  skipped: number;
  results: PhotoRoomBulkImageResult[];
}

export interface PhotoRoomBulkImageResult {
  imageUrl: string;
  articleId?: string;
  success: boolean;
  processedImage?: string;
  contentType?: string;
  error?: string;
}

export interface PhotoRoomLogEntry {
  id: string;
  article_id: string | null;
  original_image_url: string;
  processed_image_url: string | null;
  status: 'success' | 'error';
  error_message: string | null;
  processing_time_ms: number | null;
  created_at: string;
}

export interface PhotoRoomUsageResponse {
  quota: {
    used: number;
    total: number;
    resetDate: string | null;
  };
  platformQuota: {
    used: number;
    total: number;
    remaining: number;
    resetDate: string | null;
  };
  pricePerImageCents: number;
  stats: {
    total: number;
    successCount: number;
    errorCount: number;
    avgProcessingTimeMs: number;
  };
  logs: PhotoRoomLogEntry[];
}

// Article Images Overview Types (for BISPix Settings)
export interface ArticleImageInfo {
  id: string;
  fileName: string;
  originalName: string | null;
  fileSize: number;
  mimeType: string;
  width: number | null;
  height: number | null;
  url: string;
  isPrimary: boolean;
  bisPixProcessed: boolean;
  bisPixProcessedAt: string | null;
  sortOrder: number;
}

export interface ArticleWithImages {
  id: string;
  sku: string;
  name: string;
  ean: string | null;
  status: string;
  imageCount: number;
  processedCount: number;
  unprocessedCount: number;
  images: ArticleImageInfo[];
}

export interface ArticleImagesOverviewResponse {
  articles: ArticleWithImages[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats: {
    totalArticles: number;
    articlesWithImages: number;
    articlesWithoutImages: number;
    articlesProcessed: number;
    articlesUnprocessed: number;
    totalImages: number;
    totalProcessedImages: number;
    totalUnprocessedImages: number;
  };
}

export interface ProcessImageResult {
  success: boolean;
  message?: string;
  error?: string;
  processingTimeMs?: number;
  processedImage?: string;
  contentType?: string;
}

export interface VariantOptionImageInfo {
  id: string;
  value: string;
  displayValue: string;
  colorCode?: string;
  imageUrl?: string;
  bisPixProcessed: boolean;
  bisPixProcessedAt?: string;
  sortOrder: number;
  isActive: boolean;
  groupId: string;
  groupName: string;
  groupDisplayName: string;
  groupType: string;
}

export interface VariantOptionImagesOverviewResponse {
  values: VariantOptionImageInfo[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats: {
    totalValues: number;
    withImages: number;
    withoutImages: number;
    processed: number;
    unprocessed: number;
  };
}

export interface ProcessVariantOptionImageResult {
  success: boolean;
  message?: string;
  error?: string;
  processingTimeMs?: number;
  newImageUrl?: string;
}

// ============================================================
// Image Management Types
// ============================================================

export type StorageProvider = 'local' | 'spaces' | 's3';

export interface StorageSettings {
  id?: string;
  provider: StorageProvider;
  spacesKey?: string;
  spacesKeyMasked?: string;
  spacesSecret?: string;
  spacesBucket?: string;
  spacesRegion?: string;
  spacesEndpoint?: string;
  cdnEnabled: boolean;
  cdnUrl?: string;
  localPath?: string;
  autoOptimize: boolean;
  generateThumbnails: boolean;
  maxFileSize: number;
  allowedFormats: string[];
  jpegQuality: number;
  isConfigured: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface StorageSettingsResponse {
  settings: StorageSettings;
  isConfigured: boolean;
  usage?: {
    totalImages: number;
    totalSizeBytes: number;
    queuedItems: number;
  };
}

export interface StorageSettingsInput {
  provider?: StorageProvider;
  spacesKey?: string;
  spacesSecret?: string;
  spacesBucket?: string;
  spacesRegion?: string;
  spacesEndpoint?: string;
  cdnEnabled?: boolean;
  cdnUrl?: string;
  localPath?: string;
  autoOptimize?: boolean;
  generateThumbnails?: boolean;
  maxFileSize?: number;
  allowedFormats?: string[];
  jpegQuality?: number;
}

export interface ImageUploadOptions {
  isPrimary?: boolean;
  altText?: string;
  sortOrder?: number;
  autoOptimize?: boolean;
  generateThumbnails?: boolean;
}

export interface ImageUploadResponse {
  success: boolean;
  image: ArticleImageRecord;
  message?: string;
}

export interface ArticleImageRecord {
  id: string;
  articleId?: string;
  fileName: string;
  originalFileName?: string;
  originalName?: string; // API returns this instead of originalFileName
  fileSize: number;
  mimeType: string;
  width: number | null;
  height: number | null;
  storageType?: StorageProvider;
  storagePath?: string;
  url?: string; // Direct URL from API (cdn_url or file_path)
  cdnUrl?: string;
  isPrimary: boolean;
  sortOrder: number;
  altText?: string;
  photoRoomProcessed: boolean;
  photoRoomOriginalUrl?: string;
  thumbnails?: ImageThumbnails;
  metadata?: Record<string, unknown>;
  forMarketplaces?: string[] | null;
  isGlobal?: boolean;
  isInherited?: boolean;
  parentArticleId?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ImageThumbnails {
  thumb?: string;  // 150px
  small?: string;  // 300px
  medium?: string; // 600px
  large?: string;  // 1200px
}

export interface ArticleImageUpdateInput {
  altText?: string;
  sortOrder?: number;
  isPrimary?: boolean;
  metadata?: Record<string, unknown>;
  forMarketplaces?: string[] | null;
  isGlobal?: boolean;
}

// === Article Attachments (non-image files) ===

export interface ArticleAttachmentRecord {
  id: string;
  articleId?: string;
  filename: string;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  url?: string;
  cdnUrl?: string;
  description?: string;
  sortOrder: number;
  forMarketplaces?: string[] | null;
  isGlobal?: boolean;
  isInherited?: boolean;
  parentArticleId?: string;
  sendOnSale?: boolean;
  is_public?: boolean;
  createdAt: string;
}

export interface ArticleAttachmentUpdateInput {
  description?: string;
  sortOrder?: number;
  forMarketplaces?: string[] | null;
  isGlobal?: boolean;
  sendOnSale?: boolean;
  is_public?: boolean;
}

export interface PhotoRoomProcessOptions {
  bgColor?: string;          // hex color or 'transparent'
  size?: 'preview' | 'medium' | 'hd' | 'full';
  crop?: boolean;
  format?: 'png' | 'jpeg' | 'webp';
  replaceOriginal?: boolean;
}

export interface PhotoRoomProcessResult {
  success: boolean;
  image?: ArticleImageRecord;
  processedUrl?: string;
  processingTimeMs?: number;
  error?: string;
  message?: string;
}

export interface ImageQueueStatusResponse {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  recentItems: ImageQueueItem[];
}

export interface ImageQueueItem {
  id: string;
  imageId: string;
  operation: 'optimize' | 'thumbnail' | 'photoroom' | 'resize' | 'convert';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  params?: Record<string, unknown>;
  errorMessage?: string;
  processedAt?: string;
  createdAt: string;
}

export interface ImageQueueProcessResponse {
  success: boolean;
  processed: number;
  failed: number;
  results: Array<{
    id: string;
    operation: string;
    success: boolean;
    error?: string;
  }>;
}

// Variant types
export interface VariantOptionGroup {
  id: string;
  name: string;
  displayName: string;
  translations?: Record<string, string>; // Multi-language display names (e.g., { de: 'Größe', en: 'Size', th: 'ขนาด' })
  type: 'select' | 'color' | 'size';
  sortOrder: number;
  isActive: boolean;
  marketplaceMappings?: Record<string, string>;
  valueCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface VariantOptionValue {
  id: string;
  value: string;
  displayValue: string;
  translations?: Record<string, string>; // Multi-language display values
  colorCode?: string;
  imageUrl?: string; // Image URL for this option value
  sortOrder: number;
  isActive: boolean;
  marketplaceMappings?: Record<string, string>;
}

export interface VariantOptionGroupWithValues extends Omit<VariantOptionGroup, 'valueCount'> {
  values: VariantOptionValue[];
}

export interface ArticleVariantConfig {
  id: string;
  optionGroupId: string;
  groupName: string;
  groupDisplayName: string;
  groupType: string;
  isRequired: boolean;
  sortOrder: number;
}

export interface ArticleVariant {
  id: string;
  sku: string;
  name: string;
  ean: string | null;
  stock: number;
  price: number;
  status: string;
  isPublished: boolean;
  image: string | null;
  optionValues: Record<string, { value: string; displayValue: string; colorCode?: string }>;
  variantAttributes: Record<string, string> | null;
}

export interface ArticleVariantsResponse {
  configurations: ArticleVariantConfig[];
  variants: ArticleVariant[];
  totalVariants: number;
}

// Shipment response types
interface OrderShipmentResponse {
  id: string;
  shipmentNumber: string;
  orderId: string;
  shippingMethodId: string | null;
  shippingMethodName: string | null;
  carrierType: string | null;
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  status: string;
  splitReason: string | null;
  weight: number | null;
  sequenceNumber: number;
  items: Array<{
    id: string;
    orderItemId: string;
    articleSku: string;
    articleName: string;
    quantity: number;
    pickedQuantity: number;
    packedQuantity: number;
    status: string;
  }>;
  pickedAt: string | null;
  packedAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

interface ShipmentSummaryResponse {
  id: string;
  shipmentNumber: string;
  orderNumber: string | null;
  shippingMethodName: string | null;
  carrierType: string | null;
  carrier: string | null;
  status: string;
  createdAt: string;
}

interface ShippingMethodConfigResponse {
  id: string;
  name: string;
  code: string;
  description: string | null;
  carrierType: string;
  carrier: string | null;
  maxWeight: number | null;
  packagingWeight: number | null;
  allowsOversized: boolean;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
  basePrice: string | null;
  settings: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

interface ArticleShippingRestrictionResponse {
  id: string;
  shippingMethodId: string;
  shippingMethodName: string;
  carrierType: string;
  restrictionType: 'allowed' | 'forbidden';
  priority: number;
}

interface CategoryShippingRestrictionResponse {
  id: string;
  shippingMethodId: string;
  shippingMethodName: string;
  carrierType: string;
  restrictionType: 'allowed' | 'forbidden';
  inheritToChildren: boolean;
}

// ============================================================
// Payment Method Types
// ============================================================

export type PaymentMethodType = 'prepayment' | 'invoice' | 'online' | 'cash' | 'other';

/**
 * Zahlweg fuer Eingangsrechnungen (V-0758, 28.09.2026) — womit koennten WIR (der Betrieb) mit
 * dieser Zahlart eine Eingangsrechnung begleichen? `null` = „noch nicht entschieden" (Altbestand,
 * wird trotzdem angeboten — Server-Werte in `App\Service\Zahlart\EingangsrechnungZahlart`).
 */
export type ZahlwegEingangsrechnung = 'kasse' | 'bank' | 'verrechnung' | 'keine';

export interface PaymentMethodResponse {
  id: string;
  name: string;
  code: string;
  description: string | null;
  type: PaymentMethodType;
  provider: string | null;
  datevAccountNumber: string | null;
  datevBuKey: number | null;
  isInvoicePayment: boolean;
  zahlweg: ZahlwegEingangsrechnung | null;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  settings: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentMethodInput {
  name: string;
  code?: string;
  description?: string | null;
  type?: PaymentMethodType;
  provider?: string | null;
  datevAccountNumber?: string | null;
  datevBuKey?: number | null;
  isInvoicePayment?: boolean;
  // V-0758/V-0782 (Korrektur 28.09.2026): bewusst optional — fehlt der Wert beim Anlegen,
  // ermittelt der Server ihn per Heuristik (EingangsrechnungZahlart::nachtragZahlweg()); beim
  // Aktualisieren bleibt ein vorhandener Wert unangetastet, wenn das Feld fehlt. Ein roher
  // Aufrufer ohne dieses typisierte Feld (z.B. Web-POS) bekommt so kein 400 mehr (V-0782).
  zahlweg?: ZahlwegEingangsrechnung;
  isActive?: boolean;
  isDefault?: boolean;
  sortOrder?: number;
  settings?: Record<string, unknown> | null;
}

// ============================================================
// EAN/GTIN Management Types
// ============================================================

export type GtinType = 'GTIN-8' | 'GTIN-12' | 'GTIN-13' | 'GTIN-14';
export type EanStatus = 'available' | 'assigned' | 'reserved' | 'retired';
export type EanHistoryAction = 'imported' | 'assigned' | 'unassigned' | 'reserved' | 'released' | 'retired' | 'moved' | 'generated';

export interface EanPoolEntry {
  id: string;
  ean: string;
  gtin_type: GtinType;
  status: EanStatus;
  list_id: string | null;
  list_name?: string;
  list_color?: string;
  article_id: string | null;
  article_sku: string | null;
  reserved_by: string | null;
  reserved_at: string | null;
  reserved_until: string | null;
  assigned_at: string | null;
  retired_at: string | null;
  retired_reason: string | null;
  import_batch: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// type statt interface: nur ein Typ-Alias passt implizit zur Index-Signatur von get()/buildUrl()
export type EanPoolListParams = {
  page?: number;
  limit?: number;
  status?: EanStatus;
  listId?: string;
  search?: string;
  gtinType?: GtinType;
};

export interface EanPoolListResponse {
  items: EanPoolEntry[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface EanImportInput {
  csvContent: string;
  listId: string;
  validateCheckDigit?: boolean;
}

export interface EanImportResult {
  imported: number;
  skipped: number;
  invalid: number;
  duplicate: number;
  errors: string[];
}

// type statt interface: nur ein Typ-Alias passt implizit zur Index-Signatur von get()/buildUrl()
export type EanExportParams = {
  listId?: string;
  status?: EanStatus;
  includeArticles?: boolean;
};

export interface EanValidationResult {
  ean: string;
  valid: boolean;
  gtinType: GtinType | null;
  gtin14: string | null;
  calculatedCheckDigit: number | null;
  inPool: boolean;
  poolStatus: EanStatus | null;
  assignedTo: string | null;
}

export interface EanStatistics {
  pool: {
    total: number;
    available: number;
    assigned: number;
    reserved: number;
    retired: number;
  };
  lists: Array<{
    id: string;
    name: string;
    color: string;
    priority: number;
    total_count: number;
    available_count: number;
    assigned_count: number;
    reserved_count: number;
  }>;
  prefixes: Array<{
    id: string;
    prefix: string;
    company_name: string;
    item_reference_current: number;
    item_reference_max: number | null;
  }>;
}

export interface EanHistoryEntry {
  id: string;
  ean: string;
  action: EanHistoryAction;
  old_status: string | null;
  new_status: string | null;
  old_list_id: string | null;
  new_list_id: string | null;
  article_id: string | null;
  article_sku: string | null;
  performed_by_name: string | null;
  notes: string | null;
  created_at: string;
}

// type statt interface: nur ein Typ-Alias passt implizit zur Index-Signatur von get()/buildUrl()
export type EanHistoryParams = {
  page?: number;
  limit?: number;
  ean?: string;
  action?: EanHistoryAction;
};

export interface EanHistoryResponse {
  items: EanHistoryEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface EanSettingValue {
  value: boolean | string | number | Record<string, unknown> | null;
  type: 'boolean' | 'string' | 'number' | 'json';
  description: string;
}

export interface Gs1128ParseResult {
  raw: string;
  valid: boolean;
  error?: string;
  ais: Record<string, string>;
  gtin?: string;
  gtin13?: string;
  batchNumber?: string;
  lotNumber?: string;
  productionDate?: string;
  expiryDate?: string;
  expirationDate?: string;
  serialNumber?: string;
  count?: number;
  quantity?: number;
  netWeightKg?: number;
  grossWeightKg?: number;
  netWeightLbs?: number;
  grossWeightLbs?: number;
  lengthMeters?: number;
  widthMeters?: number;
  heightMeters?: number;
  customerOrderNumber?: string;
  shipToGln?: string;
  locationGln?: string;
  shipToPostalCode?: string;
  companyInternal?: Record<string, string>;
  poolEntry?: EanPoolEntry | null;
  article?: { id: string; sku: string; name: string } | null;
}

export interface EanList {
  id: string;
  name: string;
  description: string | null;
  priority: number;
  is_default: boolean;
  is_active: boolean;
  total_count: number;
  available_count: number;
  assigned_count: number;
  reserved_count: number;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface EanListInput {
  name: string;
  description?: string;
  priority?: number;
  isDefault?: boolean;
  isActive?: boolean;
  color?: string;
}

export interface MarketplaceEanAssignment {
  id: string;
  marketplace: string;
  list_id: string | null;
  list_name: string | null;
  list_color: string | null;
  is_active: boolean;
  created_at: string;
}

export interface Gs1Prefix {
  id: string;
  prefix: string;
  gln: string | null;
  company_name: string;
  item_reference_start: number;
  item_reference_current: number;
  item_reference_max: number | null;
  is_active: boolean;
  valid_from: string | null;
  valid_until: string | null;
  gs1_member_id: string | null;
  notes: string | null;
  available_gtins?: number;
  total_capacity?: number;
  created_at: string;
  updated_at: string;
}

export interface Gs1PrefixInput {
  prefix: string;
  gln?: string;
  companyName: string;
  itemReferenceStart?: number;
  isActive?: boolean;
  validFrom?: string;
  validUntil?: string;
  gs1MemberId?: string;
  notes?: string;
}

export interface GtinGenerationResult {
  generated: number;
  gtins: string[];
  batchId: string;
  nextAvailable: number;
  maxAvailable: number;
}

export interface GtinPreviewResult {
  prefix: string;
  nextItemReference: number;
  maxItemReference: number;
  remainingCapacity: number;
  previews: string[];
}

// Export singleton instance
export const api = new ApiClient(API_URL);

// Export types
export type {
  ApiError,
  PaginatedResponse,
  RequestOptions,
  SmtpSettings,
  SmtpSettingsInput,
  SmtpSettingsResponse,
  SupportAccessRequest,
  SupportAccessRequestResponse,
  SupportSessionResponse,
  PendingSupportRequest,
  ActiveSupportSession,
  SupportSessionInfo,
  Tenant,
  TenantLicense,
  TenantStats,
  NumberRange,
  Article,
  ArticleImage,
  ArticleMarketplaces,
  ArticleListResponse,
  ArticleStats,
  ArticleCreateInput,
  ArticleUpdateInput,
  // Custom Field types
  CustomFieldType,
  CustomFieldDefinition,
  CustomFieldDefinitionInput,
  // Category types
  Category,
  CategoryInput,
  CategoryClearanceRule,
  // BOM (Stücklisten) types
  BomType,
  BomItem,
  BomItemInput,
  Bom,
  BomInput,
  ExpandedBomItem,
  // Customer types
  Customer,
  CustomerAddress,
  CustomerListResponse,
  CustomerInput,
  BillingCycle,
  PreferredLanguage,
  BillingSettingsInput,
  BillingSettingsResponse,
  // Customer Address Book types
  AddressType,
  CustomerAddressBookEntry,
  CustomerAddressInput,
  // Customer CRM types
  ActivityType,
  CommunicationType,
  CommunicationDirection,
  DocumentType,
  CustomerActivity,
  CustomerActivityInput,
  CustomerActivityListResponse,
  CustomerCommunication,
  CustomerCommunicationInput,
  CustomerCommunicationListResponse,
  CustomerDocument,
  CustomerDocumentInput,
  CustomerDocumentListResponse,
  CustomerStatistics,
  CustomerRevenuePeriod,
  CustomerOrderFrequency,
  AbcClass,
  KundenAbcRhythmusGrenzen,
  KundenAbcEinstellungen,
  KundenAbcEinstellungenAntwort,
  KundenAbcEinstellungenEingabe,
  KundenAbcVorschau,
  PotentialClass,
  AssortmentGap,
  AssortmentGapResponse,
  SalesTerritory,
  TerritorySuggestion,
  SalesTerritoryInput,
  TourPreview,
  PackagingOption,
  Customer360Response,
  CustomerOrdersResponse,
  CustomerInvoicesResponse,
  // Order types
  Order,
  OrderItem,
  OrderListResponse,
  OrderStats,
  OrderItemInput,
  OrderInput,
  // Cancellation Reason types
  CancellationReason,
  CancellationReasonInput,
  CustomerGroup,
  CustomerGroupInput,
  HoldReason,
  HoldReasonInput,
  OrderAuditLogEntry,
  // Supplier types
  Supplier,
  SupplierListResponse,
  SupplierInput,
  SupplierSearchResult,
  SupplierReportConfig,
  SupplierReportArticle,
  SupplierReportTotals,
  SupplierReportData,
  SupplierReportRun,
  SupplierReportRunResult,
  // Article-Supplier types
  ArticleSupplier,
  ArticleSupplierInput,
  SupplierArticle,
  // Storage Location types
  StorageLocation,
  StorageLocationInput,
  // Goods Receipt types
  GoodsReceipt,
  GoodsReceiptItem,
  GoodsReceiptListResponse,
  GoodsReceiptItemInput,
  GoodsReceiptInput,
  // Stock Movement types
  StockMovement,
  StockMovementListResponse,
  StockMovementInput,
  // Stock Adjustment types
  StockAdjustment,
  StockAdjustmentListResponse,
  StockAdjustmentInput,
  // Shipment types
  Shipment,
  ShipmentListResponse,
  ShipmentStats,
  ShipmentInput,
  // Order Shipping Method types
  OrderShippingMethod,
  OrderShippingMethodActive,
  OrderShippingMethodInput,
  // Carrier Shipping Method types
  ShippingMethod,
  ShippingMethodInput,
  // Carrier Config types
  CarrierConfig,
  CarrierConfigInput,
  // Pick List types
  PickList,
  PickListItem,
  PendingOrderSummary,
  PickListListResponse,
  PickListStats,
  PickListItemInput,
  PickListInput,
  // Picking Session types
  PickingSession,
  // Purchase Order types
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseOrderListResponse,
  PurchaseOrderItemInput,
  PurchaseOrderInput,
  // MHD Inventory types
  MhdInventory,
  // Serial Number types
  SerialNumberStatus,
  SerialNumber,
  SerialNumberListResponse,
  SerialNumberInput,
  SerialNumberAssignInput,
  // Batch Inventory types
  BatchInventory,
  BatchInventoryListResponse,
  BatchInventoryInput,
  BatchDeductInput,
  // Ausverkaufs-Automatik (Clearance)
  ClearanceStrategy,
  ClearanceStartInput,
  ClearanceState,
  ClearanceStateResponse,
  ClearancePriceHistoryEntry,
  ClearanceSuggestion,
  ClearanceCandidate,
  LmivReviewListItem,
  LmivReviewDetail,
  LmivReviewBefund,
  ClearanceCandidatesResponse,
  ArticleExpiryInfo,
  ArticleExpiryInfoResponse,
  // GPSR types
  GpsrAddress,
  // Manufacturer types
  Manufacturer,
  ManufacturerListResponse,
  ManufacturerInput,
  ManufacturerSearchResult,
  // Country types
  Country,
  CountryListResponse,
  CountryInput,
  CountrySearchResult,
  // Brand types
  Brand,
  BrandListResponse,
  BrandInput,
  BrandSearchResult,
  // RMA types
  RmaType,
  RmaStatus,
  InspectionResult,
  RmaReason,
  RmaItem,
  Rma,
  RmaListResponse,
  RmaStats,
  CreateReplacementInput,
  ReplacementOptions,
  CreateExchangeInput,
  ReceiveReturnInput,
  CompleteRmaInput,
  RelatedOrders,
  // Return (Retoure) types
  ReturnType,
  ReturnStatus,
  ItemCondition,
  ItemAction,
  ReturnReason,
  ReturnImage,
  ReturnItem,
  ReturnItemInput,
  Return,
  ReturnInput,
  ReturnListResponse,
  ReturnStats,
  // Stocktake (Inventur) types
  StocktakeType,
  StocktakeStatus,
  // StocktakeCountBasis / StocktakeArticleSelection standen hier im Export, waren aber
  // nirgends definiert und werden von niemandem importiert — toter Export, entfernt.
  SpotCheckItem,
  SpotCheckResponse,
  SpotCheckRecordInput,
  SpotCheckRecordResponse,
  CycleCountStrategy,
  StocktakeItemStatus,
  StocktakeVarianceSummary,
  StocktakeItem,
  Stocktake,
  StocktakeInput,
  StocktakeListResponse,
  StocktakeStatistics,
  CycleCountConfig,
  CycleCountConfigInput,
  SpotCheckInput,
  // Shopify Integration types
  ShopifyConnection,
  ShopifyConnectionInput,
  ShopifyLocation,
  ShopifyProductMapping,
  ShopifyProductSyncStatus,
  ShopifyProduct,
  ShopifyProductListResponse,
  ShopifyOrderImportStatus,
  ShopifyOrder,
  ShopifyCollection,
  ShopifyMetafieldMapping,
  ShopifyMetafieldMappingInput,
  ShopifyWebhook,
  ShopifySyncLog,
  ShopifyPendingChange,
  ShopifyPendingChangeField,
  // eBay Integration types
  EbayConnection,
  EbayConnectionInput,
  EbayMarketplace,
  EbayAvailableMarketplace,
  EbayGlobalTemplate,
  EbayGlobalTemplateInput,
  EbayDesignTemplate,
  EbayDesignTemplateInput,
  EbayListingTemplate,
  EbayListingTemplateInput,
  EbayListing,
  EbayCategory,
  EbayCategorySuggestion,
  EbayOrder,
  EbaySyncLog,
  EbaySyncStatus,
  EbayPolicy,
  EbayItemAspect,
  EbayCategoryAspectsResponse,
  EbayAttributeMapping,
  EbayAttributeMappingInput,
  EbayAttributeMappingSuggestion,
  // PayPal Integration types
  PayPalConfig,
  PayPalConfigInput,
  PayPalTransaction,
  PayPalTransactionListResponse,
  PayPalSyncResult,
  PayPalRefundStatus,
  PayPalRefund,
  PayPalRefundListResponse,
  PayPalRefundInput,
  PayPalDatevExport,
  PayPalDatevExportListResponse,
  PayPalDatevExportInput,
  PayPalDatevExportResult,
  PayPalStatistics,
  // DHL Integration types
  DhlApiConfig,
  DhlApiConfigInput,
  DhlTenantConfig,
  DhlTenantConfigInput,
  DhlProduct,
  DhlService,
  DhlProductInfo,
  DhlServiceInfo,
  DhlShipment,
  DhlShipmentListItem,
  DhlShipmentListResponse,
  DhlShipmentInput,
  DhlShipmentResult,
  DhlTrackingEvent,
  DhlTrackingResult,
  DhlStats,
  DhlAddressValidation,
  // DPD Integration types
  // Payment Methods
  // Tax & VAT types
  TaxClass,
  TaxClassRate,
  ProductTaxCategory,
  ProductTaxCategoryCountryOverride,
  ArticleTaxInfo,
  TaxCalculationResult,
  TaxCalculationItemResult,
  EUCountryWithRates,
  VatRateInfo,
  VatValidationResult,
  AdminVatRate,
  // OSS types
  OSSDashboardData,
  OSSThresholdStatus,
  OSSSalesByCountry,
  OSSMonthlyTrend,
  OSSReport,
  OSSCountryBreakdown,
  // ZM types
  ZMReport,
  ZMTransaction,
  // Warehouse VAT types
  WarehouseVatRegistration,
  VatComplianceAlert,
  // DATEV types
  DatevExport,
  EuerReport,
  EuerOpenItems,
  EuerReportMonth,
  EuerIncomeAccount,
  EuerExpenseAccount,
  UstvaReport,
  UstvaReportPeriod,
  BwaReport,
  BwaReportMonth,
  FinancialBlock,
  EksSettings,
  EksReport,
  EksReportMonth,
  EksMode,
  EksPlanValues,
  EksForecast,
  EksForecastMonth,
  EksMonthStatus,
  EksHerleitungZeile,
  EksOverrideBody,
  EksQuellen,
  EksNichtEingerechnet,
  ReportDrilldown,
  ReportDrilldownDoc,
  ReportHinweis,
  ReportDrilldownPosSummary,
  ReportDrilldownParams,
  // Thai Tax types
  ThaiPP30Report,
  ThaiPP30Month,
  ThaiWHTReport,
  ThaiWHTEntry,
  ThaiIncomeTaxReport,
  ThaiIncomeTaxHalfYear,
  ThaiThresholdReport,
  ThaiThresholdItem,
  // Company Documents types
  CompanyDocument,
  CompanyDocumentGroup,
  // Backup types
  BackupRecord,
  BackupConfig,
  BackupUsage,
  // Notification types
  // PhotoRoom types
  // Article Images Overview types
  // PhotoRoom Admin Config types (SuperAdmin)
  // Image Management types
  // EAN/GTIN Management types
};
